import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { measureRoyaltyAsync, markRoyaltyPerf, summarizeRoyaltyPayload } from '@/lib/royaltyPerformance'
import { normalizeInnForMatching, normalizeContractNumberForMatching, resolveDkkForMatching } from '@/lib/royaltyUtils'
import { applyAdjustmentsNative, computeForeignRegularRoyaltyNative, resolveObligationEffectiveRate } from '@/lib/royaltyForeignNative'
import { bankImportPaymentDates, partitionBankImportDuplicates } from '@/lib/bankImportDedup'
import {
  runRoyaltyAutoAllocation,
  type RoyaltyAutoAllocationProgress,
  type RoyaltyAutoAllocationResult,
} from '@/lib/royaltyAutoAllocation'
import {
  canExtendRoyaltyProcessSegment,
  findRoyaltyProcessOwnershipForDate,
  groupRoyaltyProcessOwnershipsBySeries,
  isZeroRevenueBillableOwnershipPhase,
} from '@/lib/royaltyProcessOwnership'
import { useAuthStore } from '@/store'
import { toRoyaltyBusinessDateKey } from './royaltyBusinessDate'
import type {
  RoyaltyContractSummary,
  RoyaltyPartnerSummary,
  RoyaltyPartnerReviewStats,
  RoyaltyStudioSummary,
  RoyaltyBalance,
  RoyaltyPeriodSettlement,
  RoyaltyPeriodReviewEvent,
  RoyaltyPayment,
  RoyaltySeriesTerms,
  RoyaltyCharge,
  PartnerLegalEntity,
  PartnerContractEditable,
  ContractForMatching,
  BankStagingRow,
  ProcessingStatus,
  RoyaltyDiagnosticsData,
  RoyaltyDiagnosticsChargePeriod,
  RoyaltyDiagnosticsPaymentsByStatus,
  RoyaltyDiagnosticsStagingByStatus,
  RoyaltyDiagnosticsContractWithoutAllocations,
  RoyaltyDiagnosticsLegalEntityCoverage,
  RoyaltyChargeHint,
  PartnerDepositBalance,
  PartnerPaymentRow,
  PartnerPaymentAllocationSummary,
  RoyaltyStudioOwnershipPeriod,
  RoyaltyDebtWriteoff,
  RoyaltyChargeType,
  RoyaltySeriesContractType,
  RoyaltyPaymentRefundReason,
  RoyaltyRefundCase,
  RoyaltyRefundKind,
  RoyaltyRefundSummary,
  RoyaltyRefundSummaryBreakdown,
  RoyaltyRefundStatus,
  RoyaltyRefundOffset,
  RoyaltyRefundOffsetCandidate,
  RoyaltyPeriodRefundTotals,
  StudioSubscriptionType,
} from '@/types'

function getActorUserId(): string | null {
  return useAuthStore.getState().user?.id ?? null
}

type UntypedRpcClient = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>
  ) => PromiseLike<{ data: unknown; error: unknown }>
}

function getUntypedRpcClient(): UntypedRpcClient {
  return getSupabase() as unknown as UntypedRpcClient
}

export function formatSupabaseLikeError(error: unknown): string {
  const normalizeMessage = (message: string): string => {
    if (message.includes('Cannot clear confirmed royalty charges')) {
      return 'Нельзя отменить выставление роялти: период уже зафиксирован бухгалтерией. Сначала отмените бухгалтерскую фиксацию (шаг 3), затем снимите выставление роялти.'
    }
    if (message.includes('Refusing to delete royalty charge')) {
      return 'Нельзя отменить выставление роялти: по начислениям уже есть разнесенные оплаты или списания. Откройте платежи по этой студии/франчайзи и нажмите «Снять разноску» у связанных платежей, затем повторите отмену.'
    }
    return message
  }

  if (error instanceof Error && error.message) return normalizeMessage(error.message)
  if (typeof error === 'string') return normalizeMessage(error)
  if (!error || typeof error !== 'object') return String(error)

  const maybe = error as {
    message?: unknown
    details?: unknown
    hint?: unknown
    code?: unknown
    error_description?: unknown
  }

  const parts = [
    typeof maybe.message === 'string' ? maybe.message : null,
    typeof maybe.details === 'string' ? maybe.details : null,
    typeof maybe.hint === 'string' ? `Подсказка: ${maybe.hint}` : null,
    typeof maybe.error_description === 'string' ? maybe.error_description : null,
    typeof maybe.code === 'string' ? `Код: ${maybe.code}` : null,
  ].filter(Boolean)

  if (parts.length > 0) return normalizeMessage(parts.join(' | '))

  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}

export interface RoyaltyContractRegisterRow {
  counterparty: string
  inn: string | null
  contract_number: string | null
  contract_date: string | null
  charge_kind: string
  amount_without_vat: number
  vat_amount: number
  amount_with_vat: number
}

export async function loadRoyaltyContractRegister(input: {
  year: number
  month: number
  countries?: string[]
}): Promise<RoyaltyContractRegisterRow[]> {
  const { data, error } = await withAuthRecovery(() => getUntypedRpcClient().rpc(
    'get_royalty_contract_register',
    {
      p_year: input.year,
      p_month: input.month,
      p_countries: input.countries ?? ['РФ', 'БЕЛАРУСЬ'],
    }
  ))
  if (error) throw error
  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    counterparty: String(row.counterparty ?? 'Контрагент не указан'),
    inn: row.inn == null ? null : String(row.inn),
    contract_number: row.contract_number == null ? null : String(row.contract_number),
    contract_date: row.contract_date == null ? null : String(row.contract_date),
    charge_kind: String(row.charge_kind ?? ''),
    amount_without_vat: Number(row.amount_without_vat ?? 0),
    vat_amount: Number(row.vat_amount ?? 0),
    amount_with_vat: Number(row.amount_with_vat ?? 0),
  }))
}

async function fetchAllSupabaseRows<T>(
  buildQuery: () => any,
  pageSize = 1000
): Promise<{ data: T[]; error: unknown | null }> {
  const allRows: T[] = []
  let from = 0

  while (true) {
    const { data, error } = await withAuthRecovery(() =>
      buildQuery().range(from, from + pageSize - 1)
    )

    if (error) return { data: allRows, error }

    const rows = (data ?? []) as T[]
    allRows.push(...rows)

    if (rows.length < pageSize) {
      return { data: allRows, error: null }
    }

    from += pageSize
  }
}

// ---------- Level-1: partner summaries ----------

export type RoyaltyHubSearchResults = {
  partners: Array<Pick<RoyaltyPartnerSummary, 'partner_number' | 'persons_fio' | 'primary_inn'>>
  studios: Array<Pick<RoyaltyStudioSummary, 'series_id' | 'partner_number' | 'studio_number' | 'studio_address' | 'city_name'> & Pick<RoyaltyPartnerSummary, 'persons_fio' | 'primary_inn'>>
}

/** Поиск для лёгкого входного экрана. Запускается только по введённому запросу. */
export async function searchRoyaltyHub(queryText: string): Promise<RoyaltyHubSearchResults> {
  const supabase = getSupabase()
  // PostgREST .or() принимает собственный синтаксис фильтров; оставляем только
  // текстовые символы, цифры и распространённые разделители поиска.
  const term = queryText.trim().replace(/[^\p{L}\p{N}\s./#-]/gu, '').replace(/\s+/g, ' ')
  if (term.length < 2) return { partners: [], studios: [] }

  // Внутри PostgREST .or() символ * используется как URL-безопасная замена %.
  // Для обычного .ilike(column, ...) нужен %, но здесь фильтр передаётся строкой .or().
  const pattern = `*${term}*`
  const parsedNumericTerm = /^\d+$/.test(term) ? Number(term) : null
  // Keep long numeric searches (notably INNs) out of integer ID filters.
  const numericTerm = parsedNumericTerm != null && Number.isSafeInteger(parsedNumericTerm) && parsedNumericTerm <= 2_147_483_647
    ? parsedNumericTerm
    : null
  const partnerFilters = [`persons_fio.ilike.${pattern}`, `primary_inn.ilike.${pattern}`]
  // studio_number в представлении может быть числовым, поэтому ilike по нему
  // способен завершиться PostgreSQL 42883 (operator does not exist).
  const studioFilters = [`studio_address.ilike.${pattern}`, `city_name.ilike.${pattern}`]
  if (numericTerm != null) {
    partnerFilters.push(`partner_number.eq.${numericTerm}`)
    studioFilters.push(`studio_number.eq.${numericTerm}`)

    // studio_number может быть числовой колонкой, поэтому ищем префикс через
    // числовые диапазоны, а не ILIKE. Каждый диапазон соответствует одному
    // возможному числу цифр в номере: для 15 это 150–159, 1500–1599 и т.д.
    if (/^[1-9]\d*$/.test(term)) {
      const maxStudioNumber = 2_147_483_647n
      const prefix = BigInt(numericTerm)
      for (let suffixDigits = 1; suffixDigits <= 10; suffixDigits += 1) {
        const factor = 10n ** BigInt(suffixDigits)
        const lowerBound = prefix * factor
        if (lowerBound > maxStudioNumber) break
        const upperBound = (prefix + 1n) * factor
        const inclusiveUpperBound = upperBound - 1n > maxStudioNumber ? maxStudioNumber : upperBound - 1n
        if (inclusiveUpperBound >= lowerBound) {
          studioFilters.push(`and(studio_number.gte.${lowerBound},studio_number.lte.${inclusiveUpperBound})`)
        }
      }
    }
  }
  const partnerQuery = (supabase as any)
    .from('royalty_partner_summary')
    .select('partner_number,persons_fio,primary_inn')
    .or(partnerFilters.join(','))
    .limit(8)
  const studioQuery = (supabase as any)
    .from('royalty_studio_summary')
    .select('series_id,partner_number,studio_number,studio_address,city_name')
    .or(studioFilters.join(','))
    .order('studio_number', { ascending: true })
    .order('series_id', { ascending: true })
    .limit(8)

  const [partnerResult, studioResult] = await Promise.all([
    withAuthRecovery(() => partnerQuery),
    withAuthRecovery(() => studioQuery),
  ])
  if (partnerResult.error) throw partnerResult.error
  if (studioResult.error) throw studioResult.error

  const directPartners = (partnerResult.data ?? []) as RoyaltyHubSearchResults['partners']
  const studios = (studioResult.data ?? []) as RoyaltyHubSearchResults['studios']
  const studioPartnerNumbers = [...new Set(studios.map(studio => studio.partner_number))]
  const studioPartnerResult = studioPartnerNumbers.length
    ? await withAuthRecovery(() => (supabase as any)
      .from('royalty_partner_summary')
      .select('partner_number,persons_fio,primary_inn')
      .in('partner_number', studioPartnerNumbers)
      .limit(8))
    : { data: [], error: null }
  if (studioPartnerResult.error) throw studioPartnerResult.error

  const studioPartners = (studioPartnerResult.data ?? []) as RoyaltyHubSearchResults['partners']
  const partnerByNumber = new Map([...directPartners, ...studioPartners]
    .filter((row, index, all) => all.findIndex(candidate => candidate.partner_number === row.partner_number) === index)
    .map(partner => [partner.partner_number, partner] as const))
  const studiosWithPartner = studios.map(studio => ({
    ...studio,
    persons_fio: partnerByNumber.get(studio.partner_number)?.persons_fio ?? null,
    primary_inn: partnerByNumber.get(studio.partner_number)?.primary_inn ?? null,
  }))

  return {
    partners: directPartners,
    studios: studiosWithPartner,
  }
}

export async function loadRoyaltyPartnerSummaries(): Promise<RoyaltyPartnerSummary[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_partner_summary')
      .select('*')
      .order('total_debt_rub', { ascending: false, nullsFirst: false })
  )

  if (error) {
    console.error('Failed to load royalty partner summaries:', error)
    throw error
  }

  return (data ?? []) as RoyaltyPartnerSummary[]
}

// ---------- Level-2: studios (series) for a partner, or all ----------

export async function loadRoyaltyStudioSummaries(
  partnerNumber?: number
): Promise<RoyaltyStudioSummary[]> {
  const supabase = getSupabase()
  let query = supabase
    .from('royalty_studio_summary')
    .select('*')
    .order('total_debt_rub', { ascending: false, nullsFirst: false })

  if (typeof partnerNumber === 'number') {
    query = query.eq('partner_number', partnerNumber)
  }

  const { data, error } = await withAuthRecovery(() => query)

  if (error) {
    console.error('Failed to load royalty studio summaries:', error)
    throw error
  }

  return enrichStudioSummariesWithLastRoyaltyPayment((data ?? []) as RoyaltyStudioSummary[])
}

export async function loadRoyaltyStudioSummary(
  seriesId: number
): Promise<RoyaltyStudioSummary | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_studio_summary')
      .select('*')
      .eq('series_id', seriesId)
      .maybeSingle()
  )
  if (error) throw error
  const summary = (data as RoyaltyStudioSummary | null) ?? null
  const [enriched] = await enrichStudioSummariesWithLastRoyaltyPayment(summary ? [summary] : [])
  return enriched ?? null
}

async function enrichStudioSummariesWithLastRoyaltyPayment(
  summaries: RoyaltyStudioSummary[]
): Promise<RoyaltyStudioSummary[]> {
  if (summaries.length === 0) return summaries

  const supabase = getSupabase()
  const seriesIds = Array.from(new Set(
    summaries
      .map((summary) => summary.series_id)
      .filter((seriesId): seriesId is number => typeof seriesId === 'number')
  ))
  if (seriesIds.length === 0) return summaries

  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payments')
      .select('assigned_series_id,payment_date,amount,currency_code,payment_id')
      .in('assigned_series_id', seriesIds)
      .eq('is_deposit', false)
      .or('payment_kind.eq.ROYALTY,payment_kind.is.null')
      .order('payment_date', { ascending: false })
      .order('payment_id', { ascending: false })
  )
  if (error) throw error

  const lastBySeries = new Map<number, {
    payment_date: string | null
    amount: number | null
    currency_code: string | null
  }>()
  for (const row of (data ?? []) as Array<{
    assigned_series_id: number | null
    payment_date: string | null
    amount: number | null
    currency_code: string | null
  }>) {
    if (row.assigned_series_id == null || lastBySeries.has(row.assigned_series_id)) continue
    lastBySeries.set(row.assigned_series_id, {
      payment_date: row.payment_date ?? null,
      amount: row.amount == null ? null : Number(row.amount),
      currency_code: row.currency_code ?? null,
    })
  }

  return summaries.map((summary) => {
    const last = lastBySeries.get(summary.series_id)
    return {
      ...summary,
      last_payment_date: last?.payment_date ?? null,
      last_payment_amount: last?.amount ?? null,
      last_payment_currency: last?.currency_code ?? null,
    }
  })
}

export async function loadRoyaltyStudioOwnershipPeriods(
  seriesIds: number[]
): Promise<RoyaltyStudioOwnershipPeriod[]> {
  if (seriesIds.length === 0) return []

  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('studio_ownerships')
      .select('ownership_id,series_id,studio_internal_id,start_date,end_date,ownership_phase')
      .in('series_id', seriesIds)
      .order('start_date', { ascending: false, nullsFirst: false })
      .order('ownership_id', { ascending: false })
  )

  if (error) {
    console.error('Failed to load royalty studio ownership periods:', error)
    throw error
  }

  return (data ?? []) as RoyaltyStudioOwnershipPeriod[]
}

type RevenueSource = 'portal' | 'listok' | 'equal'

// Domestic-правило (2026-07, уточнено 2026-08-25): для подписочных студий РФ/Беларуси выручку
// к учёту берём из «листка». Единица применения — МЕСЯЦ серии, а не отдельный сегмент: если в
// месяце есть хоть один подписочный сегмент, по листку считаются ОБЕ половины месяца перехода
// абонемент→подписка. Иначе две строки одного месяца берут выручку из разных источников.
// P-14: страну не используем как модель начисления; модель берётся из условий/настроек.
// Если данные всё же дают foreign subscription, force-listok на неё не действует: source
// остаётся обычным `max(listok, portal)`.
const ALWAYS_USE_LISTOK_FOR_SUBSCRIPTION = true
const MANAGEMENT_COMPANY_PARTNER_NUMBER = 1000

function roundMoney(value: number | null | undefined): number {
  return Math.round((Number(value ?? 0) || 0) * 100) / 100
}

function applyNonNegativeChargeAdjustment(baseRub: number, adjustmentRub: number): number {
  return Math.max(roundMoney(baseRub + adjustmentRub), 0)
}

// Единая политика MANUAL_ROYALTY: automaticRub уже содержит месячный min-top-up,
// после него знаковая корректировка применяется до split. ADD уменьшает refund,
// REDUCE увеличивает/создаёт его.
function splitAdjustedManualRoyalty(automaticRub: number, adjustmentRub: number): {
  chargeRub: number
  refundRub: number
} {
  const adjustedSigned = roundMoney(roundMoney(automaticRub) + roundMoney(adjustmentRub))
  return {
    chargeRub: Math.max(adjustedSigned, 0),
    refundRub: Math.max(-adjustedSigned, 0),
  }
}

// Компактный ₽ для диагностических подписей (например «дней без покрытия ... ≈12 345 ₽»).
function formatRubDiagnostic(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`
}

function formatDateDiagnostic(value: string | null | undefined): string {
  if (!value) return '—'
  const [yearRaw, monthRaw, dayRaw] = (toRoyaltyBusinessDateKey(value) ?? String(value).slice(0, 10)).split('-')
  const year = Number(yearRaw)
  const month = Number(monthRaw)
  const day = Number(dayRaw)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return String(value)
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`
}

function pickRevenueSource(portalRub: number, listokRub: number): RevenueSource {
  if (Math.abs(portalRub - listokRub) <= 0.005) return 'equal'
  return portalRub > listokRub ? 'portal' : 'listok'
}

function isDomesticRoyaltyCountry(country: string | null | undefined): boolean {
  const normalized = country?.trim()
  return normalized === 'РФ' || normalized === 'Беларусь' || normalized === 'Россия'
}

function isForeignRoyaltyCountry(country: string | null | undefined): boolean {
  const normalized = country?.trim()
  return Boolean(normalized) && !isDomesticRoyaltyCountry(normalized)
}

// Источник выручки с учётом domestic-правила «подписочный месяц — всегда листок».
// forceListok=true -> всегда 'listok' (даже если портал больше); иначе - выбор по большей.
function resolveRevenueSource(portalRub: number, listokRub: number, forceListok: boolean): RevenueSource {
  return forceListok ? 'listok' : pickRevenueSource(portalRub, listokRub)
}

// Даты сегмента включительно, в формате YYYY-MM-DD (UTC, без сдвига таймзоны).
function eachDateInclusive(startDate: string, endDate: string): string[] {
  const dates: string[] = []
  const start = new Date(`${startDate}T00:00:00Z`)
  const end = new Date(`${endDate}T00:00:00Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return dates
  for (let cursor = start; cursor <= end; cursor = new Date(cursor.getTime() + 86_400_000)) {
    dates.push(cursor.toISOString().slice(0, 10))
  }
  return dates
}

function isListokSubscriptionSale(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === 'да'
}

function calculateAppRevenueSplitFromRubRow(row: {
  new_sales_amount_rub: number | null
  trial_payments_amount_rub: number | null
  renewals_amount_rub: number | null
  upgrades_amount_rub: number | null
  returning_clients_amount_rub: number | null
  private_sessions_amount_rub: number | null
  other_income_rub: number | null
  refunds_amount_rub: number | null
  new_subscriptions_amount_rub: number | null
  membership_fees_new_amount_rub: number | null
  subscription_renewals_amount_rub: number | null
  subscription_upgrades_amount_rub: number | null
  subscription_returning_clients_amount_rub: number | null
  membership_fees_subscription_upgrades_amount_rub: number | null
  membership_fees_subscription_returning_amount_rub: number | null
}): { memberships: number; subscriptions: number } {
  return {
    memberships: roundMoney(
      Number(row.new_sales_amount_rub ?? 0)
      + Number(row.trial_payments_amount_rub ?? 0)
      + Number(row.renewals_amount_rub ?? 0)
      + Number(row.upgrades_amount_rub ?? 0)
      + Number(row.returning_clients_amount_rub ?? 0)
      + Number(row.private_sessions_amount_rub ?? 0)
      + Number(row.other_income_rub ?? 0)
      + Number(row.refunds_amount_rub ?? 0)
    ),
    subscriptions: roundMoney(
      Number(row.new_subscriptions_amount_rub ?? 0)
      + Number(row.membership_fees_new_amount_rub ?? 0)
      + Number(row.subscription_renewals_amount_rub ?? 0)
      + Number(row.subscription_upgrades_amount_rub ?? 0)
      + Number(row.subscription_returning_clients_amount_rub ?? 0)
      + Number(row.membership_fees_subscription_upgrades_amount_rub ?? 0)
      + Number(row.membership_fees_subscription_returning_amount_rub ?? 0)
    ),
  }
}

function calculateSubscriptionRoyaltyBreakdown(input: {
  membershipsRevenueRub: number
  subscriptionsRevenueRub: number
  royaltyPercent: number | null
  agencyPercent: number | null
  vatPercent: number | null
  listokCrmBaseAmountRub: number | null
  minRoyaltyAmountRub: number | null
  // Минималка — месячная «со студии» (решение 2026-07-07): применяется только когда
  // breakdown считается за весь месяц (один сегмент). Для сегмента части месяца
  // передавать false — минималку доначисляет месячный top-up.
  applyMinRoyalty: boolean
  // P-29: потранзакционные суммы удержаний (уже с НДС) из реестра автосписаний
  // `report_payment_items`. Если заданы — подписочная сторона берётся из них, а не из
  // `subscriptionsRevenueRub × ставку`: номинальный счёт округляет КАЖДУЮ транзакцию,
  // поэтому `Σ round(...)` ≠ `round(Σ ...)`. Зеркало ветки v_tx_mode в
  // sync_royalty_charge_from_accrual.
  perTransactionSplitRoyaltyRub?: number | null
  perTransactionSplitAgencyRub?: number | null
}): {
  split_royalty_rub: number
  split_agency_rub: number
  manual_royalty_rub: number
  listok_crm_rub: number
  min_royalty_topup_rub: number
  split_min_royalty_topup_rub: number
  manual_min_royalty_topup_rub: number
  total_rub: number
} {
  if (input.royaltyPercent == null) {
    return {
      split_royalty_rub: 0,
      split_agency_rub: 0,
      manual_royalty_rub: 0,
      listok_crm_rub: 0,
      min_royalty_topup_rub: 0,
      split_min_royalty_topup_rub: 0,
      manual_min_royalty_topup_rub: 0,
      total_rub: 0,
    }
  }

  const membershipsRevenueRub = roundMoney(input.membershipsRevenueRub)
  const subscriptionsRevenueRub = roundMoney(input.subscriptionsRevenueRub)
  const vatPercent = Number(input.vatPercent ?? 22)
  const agencyPercent = Number(input.agencyPercent ?? 0)
  const minRoyaltyAmountRub = roundMoney(input.minRoyaltyAmountRub ?? 0)
  // Без хардкода: если в условиях нет базы ListokCRM — поток = 0 (а не магические 10384),
  // чтобы незаполненная база была видна, а не подменялась тихо. См. P2 в
  // docs/SUBSCRIPTION_ROYALTY_SPLIT_REVIEW.md.
  const listokBaseRub = roundMoney(input.listokCrmBaseAmountRub ?? 0)

  // Процентное роялти считаем по компонентам отдельно. Если месячная сумма не
  // дотягивает до min, недостающая база целиком доначисляется в ручное роялти.
  const royaltyPercent = Number(input.royaltyPercent ?? 0)
  // P-29: в потранзакционном режиме сумма удержания приходит уже с НДС (так её считает
  // номинальный счёт), поэтому база восстанавливается обратным ходом — как в движке.
  const perTxSplitRoyaltyRub = input.perTransactionSplitRoyaltyRub ?? null
  const perTxSplitAgencyRub = input.perTransactionSplitAgencyRub ?? null
  const baseFromTotal = (totalRub: number) =>
    vatPercent > 0 ? roundMoney(totalRub / (1 + vatPercent / 100)) : roundMoney(totalRub)
  const withVat = (baseRub: number) => roundMoney(baseRub + (baseRub * vatPercent) / 100)
  const splitPercentBaseRub = perTxSplitRoyaltyRub != null
    ? baseFromTotal(perTxSplitRoyaltyRub)
    : roundMoney((subscriptionsRevenueRub * royaltyPercent) / 100)
  const manualPercentBaseRub = roundMoney((membershipsRevenueRub * royaltyPercent) / 100)
  const percentRoyaltyBaseRub = roundMoney(splitPercentBaseRub + manualPercentBaseRub)
  const minRoyaltyTopupBaseRub = input.applyMinRoyalty
    ? roundMoney(Math.max(0, minRoyaltyAmountRub - percentRoyaltyBaseRub))
    : 0
  const splitMinTopupBaseRub = 0
  const manualMinTopupBaseRub = minRoyaltyTopupBaseRub
  const splitRoyaltyBaseRub = roundMoney(splitPercentBaseRub + splitMinTopupBaseRub)
  const manualRoyaltyBaseRub = roundMoney(manualPercentBaseRub + manualMinTopupBaseRub)
  const splitAgencyBaseRub = perTxSplitAgencyRub != null
    ? baseFromTotal(perTxSplitAgencyRub)
    : roundMoney((subscriptionsRevenueRub * agencyPercent) / 100)

  // Потранзакционные итоги пишем как есть: база у них производная, поэтому обратный
  // пересчёт `base × (1 + НДС)` мог бы разойтись с фактическим удержанием на копейку.
  const splitRoyaltyRub = perTxSplitRoyaltyRub != null
    ? roundMoney(perTxSplitRoyaltyRub + splitMinTopupBaseRub * (1 + vatPercent / 100))
    : withVat(splitRoyaltyBaseRub)
  const splitAgencyRub = perTxSplitAgencyRub != null
    ? roundMoney(perTxSplitAgencyRub)
    : withVat(splitAgencyBaseRub)
  const manualRoyaltyRub = withVat(manualRoyaltyBaseRub)
  const listokCrmRub = withVat(listokBaseRub)
  const splitMinTopupRub = withVat(splitMinTopupBaseRub)
  const manualMinTopupRub = withVat(manualMinTopupBaseRub)

  return {
    split_royalty_rub: splitRoyaltyRub,
    split_agency_rub: splitAgencyRub,
    manual_royalty_rub: manualRoyaltyRub,
    listok_crm_rub: listokCrmRub,
    min_royalty_topup_rub: roundMoney(splitMinTopupRub + manualMinTopupRub),
    split_min_royalty_topup_rub: splitMinTopupRub,
    manual_min_royalty_topup_rub: manualMinTopupRub,
    total_rub: roundMoney(splitRoyaltyRub + splitAgencyRub + manualRoyaltyRub + listokCrmRub),
  }
}

export interface RoyaltyChargeComponentAdjustment {
  charge_type: 'ROYALTY' | 'MANUAL_ROYALTY' | 'LISTOK_CRM' | 'FIXED_ROYALTY'
  adjustment_type: 'ADD' | 'REDUCE'
  amount_rub: number
  reason: string | null
  updated_at: string | null
  updated_by_user_id: string | null
}

/**
 * §7.5 / B6: оплата charge'а в разрезе валюты платежа. Один charge может гасить несколько
 * платежей в разных валютах — единого кросс-курса тогда не существует, и «Факт» обязан
 * показывать состав. `applied_cross_rate` — ФАКТИЧЕСКИ применённый курс (выведен из самих
 * сумм разноски), а не рыночный: долг погасил именно он.
 */
export interface RoyaltyChargePaymentCurrencyRow {
  payment_currency: string
  charge_currency: string
  payment_count: number
  paid_payment_amount: number
  paid_charge_amount: number | null
  paid_rub: number
  applied_cross_rate: number | null
  first_payment_date: string | null
  last_payment_date: string | null
}

export interface RoyaltyAccrualProcessRow {
  process_row_key: string
  process_mode: 'regular' | 'subscription'
  row_kind: 'studio_ownership' | 'contract_without_studio' | 'open_studio_without_ownership'
  // Почему у строки нет студии (только для row_kind = 'contract_without_studio'):
  // 'closed' — студия закрыта, 'sold' — передана другому франчайзи, 'future' — владение
  // начинается позже месяца, 'never' — владения у серии не было вовсе, null — payload
  // без series_ownership_context_rows.
  no_studio_reason?: 'closed' | 'sold' | 'future' | 'never' | null
  data_quality_issues: string[]
  period_segment_start: string | null
  period_segment_end: string | null
  period_segment_days_count: number
  contract_id: number | null
  contract_number: string | null
  contract_period_start: string | null
  contract_period_end: string | null
  contract_actual_start: string | null
  contract_actual_end: string | null
  series_id: number
  partner_number: number | null
  persons_fio: string | null
  studio_internal_id: number | null
  studio_number: string | null
  studio_address: string | null
  studio_status: string | null
  city_name: string | null
  country: string | null
  subscription_type: StudioSubscriptionType
  term_id: number | null
  term_contract_type: RoyaltySeriesContractType | null
  // P-22: носитель месячного CRM-фикса LISTOK_CRM. `listok_crm_carrier` — терм этого
  // сегмента помечен носителем; `listok_crm_carrier_term_id` — term_id для переключения
  // флага из редактора условий; `listok_crm_base_amount_rub` — сырая база фикса (без
  // гейта носителя), чтобы диагностика видела «база есть, но носитель не выбран».
  listok_crm_carrier: boolean
  fixed_royalty_carrier: boolean
  listok_crm_carrier_term_id: number | null
  listok_crm_base_amount_rub: number | null
  term_intervals: Array<{
    term_id: number | null
    contract_id: number | null
    contract_number: string | null
    legal_effective_from: string | null
    legal_expected_end_date: string | null
    legal_actual_end_date: string | null
    effective_from: string | null
    effective_to: string | null
    contract_type: RoyaltySeriesContractType | null
    royalty_percent: number | null
    agency_percent: number | null
    vat_percent: number | null
    listok_crm_base_amount: number | null
    min_royalty_amount: number | null
    is_listok_crm_carrier: boolean
    is_fixed_royalty_carrier: boolean
  }>
  // True when the month is split across >1 royalty condition (mid-month step-up / model switch).
  // Charge generation is single-family-per-month (Phase 1) → blocked for these rows until Phase 2.
  is_term_segmented: boolean
  /**
   * Дни периода без покрытия действующим MAIN-договором и оценочная выручка на них.
   * Движок начисления требует покрытие договором на каждый день; непокрытые дни могут
   * дать расхождение оценки UI и итоговых charge. Показываем количественно, а не булевым
   * флагом. См. docs/ROYALTY_PROCESS_TERM_SEGMENTATION_LOGIC.md (P1 «UI/движок по разным осям»).
   */
  contract_coverage_gap_days: number
  contract_coverage_gap_revenue_rub: number
  /**
   * Дни периода, где MAIN-договор есть, но нет активного условия роялти этого договора.
   * Это отдельная ошибка покрытия: выручка фактически есть по договору, но начислить её
   * нельзя без term, поэтому нельзя молча резать месяц по границе условий.
   */
  term_coverage_gap_days: number
  term_coverage_gap_revenue_rub: number
  /**
   * Валютный контур (план §14.11.5 items 2/3). Валюта роялти = валюта терма
   * сегмента (`royalty_series_terms.currency_code`, дефолт RUB). Курс обязательство→RUB
   * на конец месяца (как в движке: `resolve_royalty_fx_rate` на month_end) — из
   * `get_royalty_process_fx_supplement`. `charge_fx_rate === null` при нерублёвой валюте
   * означает FX_MISSING: движок заблокирует начисление, поэтому и UI блокирует. Нативные
   * charged/debt — из `royalty_balance` (точные booked-суммы), а не RUB÷курс. Для RUB-терма
   * всё вырождается в RUB (курс 1, native === RUB) — 100% живых данных не меняются.
   */
  charge_currency: string
  charge_fx_rate: number | null
  /**
   * §1.8: ЭФФЕКТИВНЫЙ (посуточно взвешенный) курс обязательства сегмента — тот, который
   * фактически применит движок. `charge_fx_rate` остался month-end котировкой и годится
   * только для подписи «курс от <дата>»; для любого пересчёта сумм брать надо этот.
   */
  charge_fx_effective_rate: number | null
  charge_fx_source: string | null
  charge_fx_effective_date: string | null
  fx_missing: boolean
  // Валюта, курса которой не хватает (обязательство или выручка) — для точной формулировки
  // блокировки. null, когда fx_missing = false.
  fx_missing_currency: string | null
  estimated_royalty_native: number | null
  /**
   * «Расчёт» в валюте обязательства для иностранной regular-строки: процент от выручки в
   * валюте обязательства плюс доплата до минимума (и месячный top-up последнего сегмента),
   * БЕЗ корректировок. Единственная реализация — `computeForeignRegularRoyaltyNative`
   * (src/lib/royaltyForeignNative.ts); колонка «Расчёт», окно подтверждения и снимок
   * сегмента читают это поле, а не делят рубли на курс. null для RUB-обязательства и для
   * подписочных строк.
   */
  base_royalty_native: number | null
  /** Доплата до минимума в валюте обязательства (та же природа, что `min_royalty_topup_rub`). */
  min_royalty_topup_native: number | null
  charged_native: number | null
  debt_native: number | null
  /** Оплата в валюте начисления (§7.5). Из `royalty_balance.paid_amount`, не ₽ ÷ курс. */
  paid_native: number | null
  // Выручка в валюте студии (§14.11.5 item 3). revenue_currency — из fx-supplement
  // (fx_country_currency по стране студии, дефолт RUB). estimated_revenue_native —
  // индикативно RUB-снимок ÷ курс выручки. Для RUB-студии всё вырождается в RUB.
  revenue_currency: string
  /**
   * Этап 1 в локальной валюте (§7.2). ФАКТИЧЕСКИЕ нативные суммы из
   * `app_revenue_native_rows` / `listok_revenue_native_rows`, а не обратное деление рублей на
   * month-end курс: последнее было оценкой и не сходилось с посуточной конвертацией витрины.
   * Для RUB-студии равны рублёвым.
   */
  portal_revenue_native: number
  listok_revenue_native: number
  selected_revenue_native: number
  portal_memberships_revenue_native: number
  listok_memberships_revenue_native: number
  selected_memberships_revenue_native: number
  portal_subscriptions_revenue_native: number
  listok_subscriptions_revenue_native: number
  selected_subscriptions_revenue_native: number
  /**
   * Выбранная выручка в валюте ОБЯЗАТЕЛЬСТВА (§1.8, §7.3). Посуточная сумма: каждый день
   * приведён кросс-курсом своего дня, а не месячная сумма — курсом конца месяца. Именно от
   * неё считается роялти на этапе 2, и именно её воспроизводит движок. Для RUB-обязательства
   * равна рублёвой.
   */
  selected_revenue_obl: number
  selected_memberships_revenue_obl: number
  selected_subscriptions_revenue_obl: number
  /**
   * Живой расчёт выручки по текущим данным портала/листка — то, что было бы зафиксировано,
   * нажми оператор кнопку сейчас. Нужен ровно для одного: показать, что данные изменились
   * ПОСЛЕ фиксации (догрузился платёж, переехал синк). Канон «зафиксированное держится до
   * перефиксации» означает, что в начисление это не попадёт, поэтому расхождение обязано
   * быть видимым — иначе оператор смотрит на живые колонки «Портал/Листок» и не понимает,
   * почему «К выставлению» другое.
   */
  preview_selected_revenue_rub: number
  preview_selected_revenue_native: number
  preview_selected_memberships_revenue_rub: number
  preview_selected_subscriptions_revenue_rub: number
  /**
   * Integrity of the persisted step-1 snapshot against the current contract/term timeline.
   * `identity_stale` means the same segment number now points at different dates/contract/term;
   * `sum_mismatch` means persisted segment totals no longer reproduce the monthly snapshot.
   */
  revenue_snapshot_identity_stale?: boolean
  revenue_snapshot_sum_mismatch?: boolean
  revenue_snapshot_selected_rub?: number | null
  revenue_snapshot_monthly_selected_rub?: number | null
  revenue_snapshot_segments_total_rub?: number | null
  /**
   * §7.3 / B5: минималка в валюте обязательства (`min_royalty_currency = NULL`
   * означает `currency_code`).
   */
  royalty_min_amount_native: number | null
  royalty_min_currency: string | null
  /** §7.5 / B6: оплата этого сегмента в разрезе валют платежей. */
  payment_currency_rows: RoyaltyChargePaymentCurrencyRow[]
  /**
   * Свежесть курсов обоих плеч (§7.6 п.2-3): дата ФАКТИЧЕСКОЙ котировки и её давность в днях
   * относительно конца месяца. Carry-forward — верная семантика ЦБ (в выходные курс не
   * устанавливается), но давность переноса обязана быть видимой, а плечи кросс-курса могут
   * быть собраны из разных дней.
   */
  revenue_fx_quote_date: string | null
  revenue_fx_quote_age_days: number | null
  charge_fx_quote_date: string | null
  charge_fx_quote_age_days: number | null
  /**
   * Порядковый номер сегмента в серии-месяце (совпадает с segment_no движка): адрес
   * для компонентных корректировок `royalty_charge_adjustments`.
   */
  period_segment_no: number
  /** Адресные корректировки этой строки-сегмента (regular: ROYALTY; subscription: MANUAL_ROYALTY, LISTOK_CRM). */
  component_adjustments: RoyaltyChargeComponentAdjustment[]
  /**
   * Месячная минималка «со студии» (решение 2026-07-07): если сумма роялти-потоков
   * всех сегментов месяца меньше min последнего сегмента, разница доначисляется
   * top-up'ом на последний сегмент. Заполнено только на строке-получателе.
   */
  month_min_topup_rub: number | null
  /** Доплата до минимального роялти, уже включенная в сохраняемые роялти-компоненты. */
  min_royalty_topup_rub: number
  split_min_royalty_topup_rub: number
  manual_min_royalty_topup_rub: number
  portal_revenue_rub: number
  listok_revenue_rub: number
  selected_revenue_rub: number
  selected_revenue_source: RevenueSource
  /** Возвраты, вошедшие в выбранную выручку сегмента (отрицательная сумма). */
  selected_refunds_rub: number
  portal_memberships_revenue_rub: number
  listok_memberships_revenue_rub: number
  selected_memberships_revenue_rub: number
  selected_memberships_revenue_source: RevenueSource
  portal_subscriptions_revenue_rub: number
  listok_subscriptions_revenue_rub: number
  selected_subscriptions_revenue_rub: number
  selected_subscriptions_revenue_source: RevenueSource
  revenue_checked: boolean
  royalty_checked: boolean
  // Кто и когда зафиксировал выручку / выставил роялти (снапшот имени из
  // royalty_period_accruals.*_checked_by, время — *_checked_at).
  revenue_checked_at: string | null
  revenue_checked_by: string | null
  royalty_checked_at: string | null
  royalty_checked_by: string | null
  foreign_payment_method: 'invoice' | 'link' | 'details' | null
  revenue_days_count: number
  revenue_confirmed_days_count: number
  royalty_percent: number | null
  royalty_agency_percent: number | null
  royalty_vat_percent: number | null
  /**
   * P-29: подписочные SPLIT_ROYALTY/SPLIT_AGENCY этого сегмента посчитаны потранзакционно
   * от реестра автосписаний (`report_payment_items`), а не месячной суммой. `false` —
   * сегмент раньше `royalty_per_transaction_effective_from()` либо реестр недоступен.
   */
  per_transaction_mode?: boolean
  per_transaction_tx_count?: number | null
  per_transaction_subscriptions_revenue_rub?: number | null
  royalty_min_amount_rub: number | null
  royalty_listok_crm_base_amount_rub: number | null
  charge_revenue_rub: number
  charge_memberships_revenue_rub: number
  charge_subscriptions_revenue_rub: number
  charged_revenue_rub: number
  charged_memberships_revenue_rub: number
  charged_subscriptions_revenue_rub: number
  adjustment_rub: number | null
  adjustment_comment: string | null
  adjustment_updated_at: string | null
  adjustment_updated_by_user_id: string | null
  adjustment_updated_by_name: string | null
  base_royalty_rub: number
  estimated_royalty_rub: number
  estimated_split_royalty_rub: number
  estimated_split_agency_rub: number
  /**
   * Знаковое автоматическое ручное роялти после месячной минималки, НДС и FX, но ДО адресных
   * корректировок. Может быть отрицательным (возвраты абонементов прошлых месяцев) — тогда
   * обязательство уходит не в charge, а в возврат партнёру (`NEGATIVE_ACCRUAL`).
   * См. docs/ROYALTY_UNIFIED_REFUNDS_PLAN.md §8.2.
   */
  estimated_manual_royalty_rub: number
  /** Charge после min, знаковой корректировки и split. */
  manual_royalty_charge_rub: number
  /** Расчётный refund после min и знаковой корректировки. */
  negative_accrual_refund_rub: number
  /** Фактически сохранённый active NEGATIVE_ACCRUAL; null, если refund case не сохранён. */
  persisted_negative_accrual_refund_rub: number | null
  persisted_negative_accrual_refund_status: RoyaltyRefundStatus | null
  /** Часть сохранённого возврата, погашенная зачётом, а не деньгами (§20). */
  persisted_negative_accrual_refund_offset_rub: number | null
  estimated_listok_crm_rub: number
  estimated_fixed_royalty_rub: number
  charged_rub: number
  paid_rub: number
  /**
   * Долг закрыт без денег: зачёт возврата (§20) + списание долга (P-34). В `paid_rub`
   * не входит, из `debt_rub`/`delta_rub` вычтен — иначе экран выставления показывал бы
   * долг там, где `royalty_balance` его уже не видит.
   */
  covered_rub: number
  /** Часть `covered_rub`, закрытая зачётом РБ (налог, удержанный в РБ). */
  rb_tax_offset_rub: number
  delta_rub: number
  debt_rub: number
  charge_count: number
  charged_split_royalty_rub: number
  charged_split_agency_rub: number
  charged_manual_royalty_rub: number
  charged_listok_crm_rub: number
  charged_fixed_royalty_rub: number
  paid_split_royalty_rub: number
  paid_split_agency_rub: number
  paid_manual_royalty_rub: number
  paid_listok_crm_rub: number
  paid_fixed_royalty_rub: number
  covered_split_royalty_rub: number
  covered_split_agency_rub: number
  covered_manual_royalty_rub: number
  covered_listok_crm_rub: number
  covered_fixed_royalty_rub: number
  settlement_status: RoyaltyPeriodSettlement['status'] | null
  adjustment_balance_hint: RoyaltyAdjustmentBalanceHint | null
  adjustment_balance_history: RoyaltyAdjustmentBalanceHint[]
  is_reviewed: boolean
  is_confirmed: boolean
  /** Кто и когда зафиксировал начисления на бухгалтерском шаге. */
  accounting_confirmed_at: string | null
  accounting_confirmed_by: string | null
}

export interface RoyaltyAdjustmentBalanceHint {
  year: number
  month: number
  closing_balance_rub: number
  /** Долг начислений месяца отдельно от переплаты (нет в payload до миграции 20260915200000). */
  debt_rub?: number
  /** Свободный остаток платежей месяца отдельно от долга. */
  credit_rub?: number
}

export interface RoyaltyAccrualSegmentSnapshot {
  segment_no: number
  segment_start?: string | null
  segment_end?: string | null
  contract_id?: number | null
  term_id?: number | null
  process_mode?: 'regular' | 'subscription' | null
  revenue_portal_rub: number
  revenue_listok_rub: number
  revenue_selected_rub: number
  revenue_selected_source: RevenueSource
  revenue_portal_memberships_rub?: number
  revenue_listok_memberships_rub?: number
  revenue_selected_memberships_rub?: number
  revenue_memberships_selected_source?: RevenueSource
  revenue_portal_subscriptions_rub?: number
  revenue_listok_subscriptions_rub?: number
  revenue_selected_subscriptions_rub?: number
  revenue_subscriptions_selected_source?: RevenueSource
  royalty_revenue_rub?: number
  royalty_percent?: number | null
  royalty_amount_rub?: number
  royalty_memberships_revenue_rub?: number
  royalty_subscriptions_revenue_rub?: number
  royalty_split_royalty_rub?: number
  royalty_split_agency_rub?: number
  royalty_manual_royalty_rub?: number
  royalty_listok_crm_rub?: number
  /** Фиксированная часть роялти обычного терма (FIXED_ROYALTY), без НДС. */
  royalty_fixed_royalty_rub?: number
  /**
   * Нативная сторона снимка (§7.7 A, native-primary). `revenue_*_native` — в валюте
   * ВЫРУЧКИ студии (этап 1), `royalty_*_native` — в валюте ОБЯЗАТЕЛЬСТВА (этап 2).
   * Присланы вместе с рублями → сервер сохраняет обе стороны как есть; не присланы →
   * прежнее поведение (рубли канон, натив выводится делением). Для рублёвых строк не
   * шлём вовсе: натив тождествен рублю.
   */
  revenue_portal_native?: number
  revenue_listok_native?: number
  revenue_selected_native?: number
  revenue_portal_memberships_native?: number
  revenue_listok_memberships_native?: number
  revenue_selected_memberships_native?: number
  revenue_portal_subscriptions_native?: number
  revenue_listok_subscriptions_native?: number
  revenue_selected_subscriptions_native?: number
  royalty_revenue_native?: number
  royalty_amount_native?: number
  royalty_memberships_revenue_native?: number
  royalty_subscriptions_revenue_native?: number
  royalty_split_royalty_native?: number
  royalty_split_agency_native?: number
  royalty_manual_royalty_native?: number
  royalty_listok_crm_native?: number
  royalty_fixed_royalty_native?: number
}

export interface RoyaltyAccrualProcessDiagnosticsStudio {
  studio_internal_id: number
  studio_number: string | null
  studio_address: string | null
  studio_status: string | null
  city_name: string | null
  country: string | null
  active_start: string
  active_end: string
  active_days_count: number
}

export interface RoyaltyAccrualProcessDiagnostics {
  month_days_count: number
  open_studios_count: number
  opened_during_month_count: number
  active_contracts_count: number
  active_contracts_without_ownership_count: number
  open_studios_without_ownership_count: number
  opened_during_month: RoyaltyAccrualProcessDiagnosticsStudio[]
  open_studios_without_ownership: RoyaltyAccrualProcessDiagnosticsStudio[]
}

/**
 * Покрытие валют по факту месяца (§7.6 п.5): проверка уровня экрана, а не строки — отвечает на
 * вопрос «можем ли мы вообще закрыть этот месяц». Валюты собираются из ДВУХ источников (страна
 * студии и фактическая валюта листка), расхождение между ними — сама по себе диагностика.
 */
export interface RoyaltyLegalPreflightTerm {
  term_id: number
  contract_id: number | null
  source_contract_id: number | null
  contract_number: string | null
  contract_legal_end_date: string | null
  effective_from: string | null
  effective_to: string | null
  contract_type: string | null
  royalty_percent: number | null
  min_royalty_amount: number | null
  min_royalty_currency: string | null
  currency_code: string | null
  source_document_note: string | null
}

export interface RoyaltyLegalPreflightRow {
  series_id: number
  partner_number: number | null
  persons_fio: string | null
  studio_internal_id: number | null
  studio_number: string | null
  studio_address: string | null
  city_name: string | null
  country: string | null
  reason_codes: string[]
  reason_details: Array<{
    reason_code: string
    event_at: string | null
    actor_user_id: string | null
    actor_name: string | null
    summary: string | null
    details: Record<string, unknown> | null
  }>
  terms: RoyaltyLegalPreflightTerm[]
  is_reviewed: boolean
  reviewed_at: string | null
  reviewed_by_user_id: string | null
  reviewed_by: string | null
  note: string | null
  carried_from_previous_month: boolean
  last_action: string | null
  last_action_at: string | null
  last_action_by: string | null
}

export interface RoyaltyLegalPreflight {
  year: number
  month: number
  total_count: number
  reviewed_count: number
  unreviewed_count: number
  rows: RoyaltyLegalPreflightRow[]
}

export interface RoyaltyProcessCurrencyCoverage {
  currency_code: string
  studio_count_country: number
  studio_count_listok: number
  to_rub_rate: number | null
  quote_date: string | null
  quote_age_days: number | null
  /** FRESH — курс свежий; STALE — перенесён дольше порога; NO_RATE — валюты нет в карте ЦБ. */
  state: 'FRESH' | 'STALE' | 'NO_RATE'
}

export interface RoyaltyAccrualProcessResult {
  rows: RoyaltyAccrualProcessRow[]
  diagnostics: RoyaltyAccrualProcessDiagnostics
  currency_coverage: RoyaltyProcessCurrencyCoverage[]
  /**
   * Валютный supplement обязателен для foreign-вкладки и выставления foreign-строк.
   * Если RPC недоступен, не падаем всей загрузкой страницы, но UI должен показать
   * блокирующий баннер вместо молчаливого RUB-only режима.
   */
  fx_supplement_error_message: string | null
  /** Порог «курс устарел», в днях. Приходит с сервера — держим в одном месте (§7.6 п.2). */
  fx_staleness_threshold_days: number
}

export interface RoyaltyProcessCompactRowsCursorPage {
  rows: RoyaltyProcessCompactRow[]
  hasMore: boolean
  nextCursor: string | null
  summary: {
    rows_returned: number
    has_more: boolean
  }
}

export type RoyaltyProcessDirectionType = 'tu' | 'lm' | 'shmp'

export interface RoyaltyProcessDirectionScopeRow {
  process_scope_key: string
  series_id: number
  segment_no: number
  contract_id: number | null
  segment_start: string | null
  segment_end: string | null
  partner_number: number | null
  persons_fio: string | null
  studio_internal_id: number | null
  studio_number: string | null
  city_name: string | null
  tu_manager_user_ids: string[]
  tu_manager_names: string[]
  lm_manager_user_ids: string[]
  lm_manager_names: string[]
  shmp_course_numbers: number[]
}

export interface RoyaltyProcessCompactRow {
  process_row_key: string
  sort_key: string
  process_bucket: 'needs_royalty' | 'needs_accounting' | 'needs_payment' | 'ready' | string
  process_mode: 'regular' | 'subscription'
  data_quality_issues: string[]
  series_id: number
  contract_id: number | null
  contract_number: string | null
  partner_number: number | null
  persons_fio: string | null
  studio_internal_id: number | null
  studio_number: string | null
  studio_address: string | null
  studio_status: string | null
  city_name: string | null
  country: string | null
  segment_no: number
  segment_start: string | null
  segment_end: string | null
  segment_days_count: number
  term_id: number | null
  revenue_currency: string | null
  royalty_currency: string | null
  selected_revenue_rub: number
  selected_revenue_native: number
  selected_revenue_source: RevenueSource | null
  portal_revenue_rub: number
  listok_revenue_rub: number
  estimated_royalty_rub: number
  estimated_royalty_native: number
  estimated_split_royalty_rub: number
  estimated_split_agency_rub: number
  estimated_manual_royalty_rub: number
  estimated_listok_crm_rub: number
  charge_count: number
  charged_rub: number
  paid_rub: number
  debt_rub: number
  remaining_debt_rub: number
  is_confirmed: boolean
  is_reviewed: boolean
  reviewed_at: string | null
  reviewed_by: string | null
  charge_components: Record<string, {
    charge_id: number
    charged_rub: number
    paid_rub: number
    debt_rub: number
    remaining_debt_rub: number
    is_confirmed: boolean
  }>
  tu_manager_user_ids?: string[]
  tu_manager_names?: string[]
  lm_manager_user_ids?: string[]
  lm_manager_names?: string[]
  shmp_course_numbers?: number[]
  has_details: boolean
}

export interface RoyaltyProcessCompactSummary {
  year: number
  month: number
  mode: 'regular' | 'subscription' | 'foreign' | null
  segment_count: number
  series_count: number
  contract_count: number
  selected_revenue_rub: number
  estimated_royalty_rub: number
  charged_rub: number
  paid_rub: number
  debt_rub: number
  remaining_debt_rub: number
  buckets: Record<string, number>
}

export interface RoyaltyProcessCompactRowDetails {
  process_row_key: string
  segment: Record<string, unknown>
  charges: Array<Record<string, unknown>>
}

export async function loadRoyaltyProcessCompactSummary(input: {
  year: number
  month: number
  mode?: 'regular' | 'subscription' | 'foreign' | null
  directionType?: RoyaltyProcessDirectionType | null
  directionValue?: string | null
}): Promise<RoyaltyProcessCompactSummary> {
  const supabase = getSupabase()
  const { data, error } = await measureRoyaltyAsync(
    'rpc.get_royalty_process_summary_v2',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_process_summary_v2', {
        p_year: input.year,
        p_month: input.month,
        p_mode: input.mode || null,
        p_direction_type: input.directionType || null,
        p_direction_value: input.directionValue || null,
      })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      mode: input.mode ?? null,
      directionType: input.directionType ?? null,
      directionValue: input.directionValue ? true : false,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (error) throw error
  const parsed = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
  return {
    year: Number(parsed.year ?? input.year),
    month: Number(parsed.month ?? input.month),
    mode: (parsed.mode ?? null) as 'regular' | 'subscription' | 'foreign' | null,
    segment_count: Number(parsed.segment_count ?? 0),
    series_count: Number(parsed.series_count ?? 0),
    contract_count: Number(parsed.contract_count ?? 0),
    selected_revenue_rub: Number(parsed.selected_revenue_rub ?? 0),
    estimated_royalty_rub: Number(parsed.estimated_royalty_rub ?? 0),
    charged_rub: Number(parsed.charged_rub ?? 0),
    paid_rub: Number(parsed.paid_rub ?? 0),
    debt_rub: Number(parsed.debt_rub ?? 0),
    remaining_debt_rub: Number(parsed.remaining_debt_rub ?? 0),
    buckets: (parsed.buckets ?? {}) as Record<string, number>,
  }
}

export async function loadRoyaltyProcessCompactRowDetails(input: {
  year: number
  month: number
  processRowKey: string
}): Promise<RoyaltyProcessCompactRowDetails> {
  const supabase = getSupabase()
  const { data, error } = await measureRoyaltyAsync(
    'rpc.get_royalty_process_row_details_v1',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_process_row_details_v1', {
        p_year: input.year,
        p_month: input.month,
        p_process_row_key: input.processRowKey,
      })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      rowKey: input.processRowKey,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (error) throw error
  const parsed = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
  return {
    process_row_key: String(parsed.process_row_key ?? input.processRowKey),
    segment: (parsed.segment ?? {}) as Record<string, unknown>,
    charges: Array.isArray(parsed.charges) ? parsed.charges as Array<Record<string, unknown>> : [],
  }
}

function parseRoyaltyProcessDiagnostics(
  raw: unknown,
  fallback?: Pick<RoyaltyAccrualProcessDiagnostics, 'month_days_count'>
): RoyaltyAccrualProcessDiagnostics {
  const parsed = typeof raw === 'string' ? JSON.parse(raw) : ((raw ?? {}) as Record<string, any>)
  const parseStudioRows = (rows: unknown): RoyaltyAccrualProcessDiagnosticsStudio[] => (
    Array.isArray(rows)
      ? rows.map((row) => ({
          studio_internal_id: Number(row?.studio_internal_id ?? 0),
          studio_number: row?.studio_number == null ? null : String(row.studio_number),
          studio_address: row?.studio_address ?? null,
          studio_status: row?.studio_status ?? null,
          city_name: row?.city_name ?? null,
          country: row?.country ?? null,
          active_start: String(row?.active_start ?? ''),
          active_end: String(row?.active_end ?? ''),
          active_days_count: Number(row?.active_days_count ?? 0),
        }))
      : []
  )

  return {
    month_days_count: Number(parsed.month_days_count ?? fallback?.month_days_count ?? 0),
    open_studios_count: Number(parsed.open_studios_count ?? 0),
    opened_during_month_count: Number(parsed.opened_during_month_count ?? 0),
    active_contracts_count: Number(parsed.active_contracts_count ?? 0),
    active_contracts_without_ownership_count: Number(parsed.active_contracts_without_ownership_count ?? 0),
    open_studios_without_ownership_count: Number(parsed.open_studios_without_ownership_count ?? 0),
    opened_during_month: parseStudioRows(parsed.opened_during_month),
    open_studios_without_ownership: parseStudioRows(parsed.open_studios_without_ownership),
  }
}

export async function loadRoyaltyProcessDiagnostics(input: {
  year: number
  month: number
}): Promise<RoyaltyAccrualProcessDiagnostics> {
  const supabase = getSupabase()
  const { data, error } = await measureRoyaltyAsync(
    'rpc.get_royalty_process_diagnostics_v1',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_process_diagnostics_v1', {
        p_year: input.year,
        p_month: input.month,
      })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (error) throw error
  return parseRoyaltyProcessDiagnostics(data)
}

export async function loadRoyaltyLegalPreflight(input: {
  year: number
  month: number
}): Promise<RoyaltyLegalPreflight> {
  const supabase = getSupabase()
  const { data, error } = await measureRoyaltyAsync(
    'rpc.get_royalty_legal_preflight_v1',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_legal_preflight_v1', {
        p_year: input.year,
        p_month: input.month,
      })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (error) throw error

  const parsed = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
  const rows = Array.isArray(parsed.rows) ? parsed.rows : []
  const parsedRows: RoyaltyLegalPreflightRow[] = rows.map((row: any) => ({
    series_id: Number(row.series_id ?? 0),
    partner_number: row.partner_number == null ? null : Number(row.partner_number),
    persons_fio: row.persons_fio ?? null,
    studio_internal_id: row.studio_internal_id == null ? null : Number(row.studio_internal_id),
    studio_number: row.studio_number == null ? null : String(row.studio_number),
    studio_address: row.studio_address ?? null,
    city_name: row.city_name ?? null,
    country: row.country ?? null,
    reason_codes: Array.isArray(row.reason_codes) ? row.reason_codes.map(String) : [],
    reason_details: Array.isArray(row.reason_details)
      ? row.reason_details.map((detail: any) => ({
          reason_code: String(detail.reason_code ?? ''),
          event_at: detail.event_at ?? null,
          actor_user_id: detail.actor_user_id ?? null,
          actor_name: detail.actor_name ?? null,
          summary: detail.summary ?? null,
          details: detail.details && typeof detail.details === 'object' ? detail.details : null,
        }))
      : [],
    terms: Array.isArray(row.terms)
      ? row.terms.map((term: any) => ({
          term_id: Number(term.term_id ?? 0),
          contract_id: term.contract_id == null ? null : Number(term.contract_id),
          source_contract_id: term.source_contract_id == null ? null : Number(term.source_contract_id),
          contract_number: term.contract_number == null ? null : String(term.contract_number),
          contract_legal_end_date: term.contract_legal_end_date ?? null,
          effective_from: term.effective_from ?? null,
          effective_to: term.effective_to ?? null,
          contract_type: term.contract_type ?? null,
          royalty_percent: term.royalty_percent == null ? null : Number(term.royalty_percent),
          min_royalty_amount: term.min_royalty_amount == null ? null : Number(term.min_royalty_amount),
          min_royalty_currency: term.min_royalty_currency ?? null,
          currency_code: term.currency_code ?? null,
          source_document_note: term.source_document_note ?? null,
        }))
      : [],
    is_reviewed: Boolean(row.is_reviewed),
    reviewed_at: row.reviewed_at ?? null,
    reviewed_by_user_id: row.reviewed_by_user_id ?? null,
    reviewed_by: row.reviewed_by ?? null,
    note: row.note ?? null,
    carried_from_previous_month: Boolean(row.carried_from_previous_month),
    last_action: row.last_action ?? null,
    last_action_at: row.last_action_at ?? null,
    last_action_by: row.last_action_by ?? null,
  }))

  const seriesIds = [...new Set(parsedRows.map((row) => row.series_id).filter((id) => id > 0))]
  const monthStart = `${input.year}-${String(input.month).padStart(2, '0')}-01`
  const monthEnd = new Date(Date.UTC(input.year, input.month, 0)).toISOString().slice(0, 10)
  const termsStart = new Date(Date.UTC(input.year, input.month - 1, 0)).toISOString().slice(0, 10)
  const termsBySeries = new Map<number, RoyaltyLegalPreflightTerm[]>()

  if (seriesIds.length > 0) {
    const { data: termRows, error: termsError } = await fetchAllSupabaseRows<{
      term_id: number
      series_id: number
      contract_id: number | null
      source_contract_id: number | null
      effective_from: string | null
      effective_to: string | null
      contract_type: string | null
      royalty_percent: number | null
      min_royalty_amount: number | null
      min_royalty_currency: string | null
      currency_code: string | null
      source_document_note: string | null
    }>(() =>
      supabase
        .from('royalty_series_terms')
        .select('term_id,series_id,contract_id,source_contract_id,effective_from,effective_to,contract_type,royalty_percent,min_royalty_amount,min_royalty_currency,currency_code,source_document_note')
        .in('series_id', seriesIds)
        .lte('effective_from', monthEnd)
        .or(`effective_to.is.null,effective_to.gte.${termsStart}`)
        .order('effective_from', { ascending: true })
        .order('term_id', { ascending: true })
    )
    if (termsError) throw termsError

    const contractIds = [...new Set((termRows ?? [])
      .flatMap((term) => [term.contract_id, term.source_contract_id])
      .filter((id): id is number => id != null))]
    const contractNumbersById = new Map<number, string | null>()
    const contractLegalEndDateById = new Map<number, string | null>()

    if (contractIds.length > 0) {
      const { data: contractRows, error: contractsError } = await fetchAllSupabaseRows<{
        contract_id: number
        contract_number: string | null
        legal_end_date: string | null
      }>(() =>
        supabase
          .from('contract_legal_state_v')
          .select('contract_id,contract_number,legal_end_date')
          .in('contract_id', contractIds)
      )
      if (contractsError) throw contractsError
      for (const contract of contractRows ?? []) {
        contractNumbersById.set(Number(contract.contract_id), contract.contract_number ?? null)
        contractLegalEndDateById.set(Number(contract.contract_id), contract.legal_end_date ?? null)
      }
    }

    for (const term of termRows ?? []) {
      const contractId = term.contract_id ?? term.source_contract_id ?? null
      const normalized: RoyaltyLegalPreflightTerm = {
        term_id: Number(term.term_id ?? 0),
        contract_id: term.contract_id == null ? null : Number(term.contract_id),
        source_contract_id: term.source_contract_id == null ? null : Number(term.source_contract_id),
        contract_number: contractId == null ? null : contractNumbersById.get(contractId) ?? null,
        contract_legal_end_date: contractId == null ? null : contractLegalEndDateById.get(contractId) ?? null,
        effective_from: term.effective_from ?? null,
        effective_to: term.effective_to ?? null,
        contract_type: term.contract_type ?? null,
        royalty_percent: term.royalty_percent == null ? null : Number(term.royalty_percent),
        min_royalty_amount: term.min_royalty_amount == null ? null : Number(term.min_royalty_amount),
        min_royalty_currency: term.min_royalty_currency ?? null,
        currency_code: term.currency_code ?? null,
        source_document_note: term.source_document_note ?? null,
      }
      const list = termsBySeries.get(Number(term.series_id)) ?? []
      list.push(normalized)
      termsBySeries.set(Number(term.series_id), list)
    }
  }

  return {
    year: Number(parsed.year ?? input.year),
    month: Number(parsed.month ?? input.month),
    total_count: Number(parsed.total_count ?? rows.length),
    reviewed_count: Number(parsed.reviewed_count ?? 0),
    unreviewed_count: Number(parsed.unreviewed_count ?? 0),
    rows: parsedRows.map((row) => ({
      ...row,
      terms: termsBySeries.get(row.series_id) ?? row.terms,
    })),
  }
}

export async function setRoyaltyLegalPeriodReview(params: {
  seriesId: number
  year: number
  month: number
  isReviewed: boolean
  reviewedBy?: string | null
  note?: string | null
  reviewedAt?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_legal_period_review', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
      p_is_reviewed: params.isReviewed,
      p_reviewed_by: params.reviewedBy ?? null,
      p_note: params.note ?? null,
      p_reviewed_at: params.reviewedAt ?? null,
    })
  )
  if (error) throw error
}

export async function setRoyaltyLegalPeriodReviewBulk(params: {
  seriesIds: number[]
  year: number
  month: number
  isReviewed: boolean
  reviewedBy?: string | null
  note?: string | null
  reviewedAt?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_legal_period_review_bulk', {
      p_series_ids: params.seriesIds,
      p_year: params.year,
      p_month: params.month,
      p_is_reviewed: params.isReviewed,
      p_reviewed_by: params.reviewedBy ?? null,
      p_note: params.note ?? null,
      p_reviewed_at: params.reviewedAt ?? null,
    })
  )
  if (error) throw error
  return Number(data ?? 0)
}

export async function loadRoyaltyProcessCompactRows(input: {
  year: number
  month: number
  stage?: string | null
  mode?: 'regular' | 'subscription' | 'foreign' | null
  cursor?: string | null
  limit?: number
  directionType?: RoyaltyProcessDirectionType | null
  directionValue?: string | null
}): Promise<RoyaltyProcessCompactRowsCursorPage> {
  const supabase = getSupabase()
  const limit = Math.max(1, Math.min(input.limit ?? 100, 500))
  const payload = {
    p_year: input.year,
    p_month: input.month,
    p_stage: input.stage || null,
    p_mode: input.mode || null,
    p_cursor: input.cursor || null,
    p_limit: limit,
    p_direction_type: input.directionType || null,
    p_direction_value: input.directionValue || null,
  }

  const { data, error } = await measureRoyaltyAsync(
    'rpc.get_royalty_process_rows_v2',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_process_rows_v2', payload)
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      stage: input.stage ?? null,
      mode: input.mode ?? null,
      cursor: input.cursor ? true : false,
      limit,
      directionType: input.directionType ?? null,
      directionValue: input.directionValue ? true : false,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (error) throw error

  const parsed = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
  const rows = Array.isArray(parsed.rows) ? parsed.rows as RoyaltyProcessCompactRow[] : []
  return {
    rows,
    hasMore: Boolean(parsed.hasMore),
    nextCursor: typeof parsed.nextCursor === 'string' ? parsed.nextCursor : null,
    summary: {
      rows_returned: Number(parsed.summary?.rows_returned ?? rows.length),
      has_more: Boolean(parsed.summary?.has_more ?? parsed.hasMore),
    },
  }
}

export async function loadRoyaltyProcessDirectionScope(input: {
  year: number
  month: number
}): Promise<RoyaltyProcessDirectionScopeRow[]> {
  const supabase = getSupabase()
  const { data, error } = await measureRoyaltyAsync(
    'rpc.get_royalty_process_direction_scope',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_process_direction_scope', {
        p_year: input.year,
        p_month: input.month,
      })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (error) throw error

  const parsed = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
  const rows = Array.isArray(parsed.rows) ? parsed.rows : []
  return rows.map((row: any) => ({
    process_scope_key: String(row.process_scope_key ?? ''),
    series_id: Number(row.series_id ?? 0),
    segment_no: Number(row.segment_no ?? 1),
    contract_id: row.contract_id == null ? null : Number(row.contract_id),
    segment_start: row.segment_start ?? null,
    segment_end: row.segment_end ?? null,
    partner_number: row.partner_number == null ? null : Number(row.partner_number),
    persons_fio: row.persons_fio ?? null,
    studio_internal_id: row.studio_internal_id == null ? null : Number(row.studio_internal_id),
    studio_number: row.studio_number == null ? null : String(row.studio_number),
    city_name: row.city_name ?? null,
    tu_manager_user_ids: Array.isArray(row.tu_manager_user_ids) ? row.tu_manager_user_ids.map(String) : [],
    tu_manager_names: Array.isArray(row.tu_manager_names) ? row.tu_manager_names.map(String) : [],
    lm_manager_user_ids: Array.isArray(row.lm_manager_user_ids) ? row.lm_manager_user_ids.map(String) : [],
    lm_manager_names: Array.isArray(row.lm_manager_names) ? row.lm_manager_names.map(String) : [],
    shmp_course_numbers: Array.isArray(row.shmp_course_numbers) ? row.shmp_course_numbers.map((value: unknown) => Number(value)).filter(Number.isFinite) : [],
  }))
}

type RoyaltyProcessDirectionScopeInputRow = Pick<
  RoyaltyAccrualProcessRow,
  | 'series_id'
  | 'period_segment_no'
  | 'contract_id'
  | 'period_segment_start'
  | 'period_segment_end'
  | 'partner_number'
  | 'persons_fio'
  | 'studio_internal_id'
  | 'studio_number'
  | 'city_name'
>

type RoyaltyPartnerManagerAssignmentRow = {
  manager_user_id: string
  manager_name?: string
  partner_number: number
  assigned_from: string
  assigned_to: string | null
}

type RoyaltyProcessDirectionAssignmentsPayload = {
  tu?: RoyaltyPartnerManagerAssignmentRow[]
  lm?: RoyaltyPartnerManagerAssignmentRow[]
  shmp?: RoyaltyShmpMembershipRow[]
}

type RoyaltyShmpMembershipRow = {
  partner_number: number
  course_number: number
  enrolled_from: string
  enrolled_to: string | null
}

/**
 * Builds the TУ/ЛМ/ШМП filter scope from the same live process rows that the
 * full /royalties/process preview already renders. Persisted segment snapshots
 * appear only after step saves; using them here hides pre-save rows from
 * direction filters.
 */
export async function buildRoyaltyProcessDirectionScopeFromRows(input: {
  rows: RoyaltyProcessDirectionScopeInputRow[]
  asOfDate: string
}): Promise<RoyaltyProcessDirectionScopeRow[]> {
  const processRows = input.rows.filter((row) => row.series_id > 0)
  if (processRows.length === 0) return []

  const supabase = getSupabase()
  const partnerNumbers = [...new Set(processRows
    .map((row) => row.partner_number)
    .filter((value): value is number => Number.isFinite(value as number))
  )]
  if (partnerNumbers.length === 0) {
    return processRows.map((row) => ({
      process_scope_key: `${row.series_id}:${row.period_segment_no ?? 1}:${row.contract_id ?? 'null'}`,
      series_id: row.series_id,
      segment_no: row.period_segment_no ?? 1,
      contract_id: row.contract_id ?? null,
      segment_start: row.period_segment_start ?? null,
      segment_end: row.period_segment_end ?? null,
      partner_number: row.partner_number ?? null,
      persons_fio: row.persons_fio ?? null,
      studio_internal_id: row.studio_internal_id ?? null,
      studio_number: row.studio_number == null ? null : String(row.studio_number),
      city_name: row.city_name ?? null,
      tu_manager_user_ids: [],
      tu_manager_names: [],
      lm_manager_user_ids: [],
      lm_manager_names: [],
      shmp_course_numbers: [],
    }))
  }

  const { data: assignmentsPayload, error: assignmentsError } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_process_direction_assignments', {
      p_partner_numbers: partnerNumbers,
      p_segment_start: input.asOfDate,
      p_segment_end: input.asOfDate,
    })
  )
  if (assignmentsError) throw assignmentsError

  const payload = (assignmentsPayload ?? {}) as RoyaltyProcessDirectionAssignmentsPayload
  const tuRows = payload.tu ?? []
  const lmRows = payload.lm ?? []
  const shmpRows = payload.shmp ?? []

  const tuByPartner = new Map<number, RoyaltyPartnerManagerAssignmentRow[]>()
  const lmByPartner = new Map<number, RoyaltyPartnerManagerAssignmentRow[]>()
  const shmpByPartner = new Map<number, RoyaltyShmpMembershipRow[]>()
  for (const row of tuRows) tuByPartner.set(row.partner_number, [...(tuByPartner.get(row.partner_number) ?? []), row])
  for (const row of lmRows) lmByPartner.set(row.partner_number, [...(lmByPartner.get(row.partner_number) ?? []), row])
  for (const row of shmpRows) shmpByPartner.set(row.partner_number, [...(shmpByPartner.get(row.partner_number) ?? []), row])

  return processRows.map((row) => {
    const partnerNumber = row.partner_number ?? null
    const segmentStart = row.period_segment_start
    const segmentEnd = row.period_segment_end
    const tuAssignments = partnerNumber == null ? [] : (tuByPartner.get(partnerNumber) ?? [])
    const lmAssignments = partnerNumber == null ? [] : (lmByPartner.get(partnerNumber) ?? [])
    const shmpMemberships = partnerNumber == null ? [] : (shmpByPartner.get(partnerNumber) ?? [])

    const tuManagerIds = [...new Set(tuAssignments.map((assignment) => assignment.manager_user_id))].sort()
    const lmManagerIds = [...new Set(lmAssignments.map((assignment) => assignment.manager_user_id))].sort()
    const shmpCourseNumbers = [...new Set(shmpMemberships.map((membership) => Number(membership.course_number)).filter(Number.isFinite))]
      .sort((left, right) => left - right)

    return {
      process_scope_key: `${row.series_id}:${row.period_segment_no ?? 1}:${row.contract_id ?? 'null'}`,
      series_id: row.series_id,
      segment_no: row.period_segment_no ?? 1,
      contract_id: row.contract_id ?? null,
      segment_start: segmentStart ?? null,
      segment_end: segmentEnd ?? null,
      partner_number: partnerNumber,
      persons_fio: row.persons_fio ?? null,
      studio_internal_id: row.studio_internal_id ?? null,
      studio_number: row.studio_number == null ? null : String(row.studio_number),
      city_name: row.city_name ?? null,
      tu_manager_user_ids: tuManagerIds,
      tu_manager_names: tuAssignments.map((assignment) => assignment.manager_name || assignment.manager_user_id),
      lm_manager_user_ids: lmManagerIds,
      lm_manager_names: lmAssignments.map((assignment) => assignment.manager_name || assignment.manager_user_id),
      shmp_course_numbers: shmpCourseNumbers,
    }
  })
}

export async function loadRoyaltyAccrualProcessRows(input: {
  year: number
  month: number
}): Promise<RoyaltyAccrualProcessResult> {
  const supabase = getSupabase()
  const monthStart = `${input.year}-${String(input.month).padStart(2, '0')}-01`
  const monthEndDate = new Date(Date.UTC(input.year, input.month, 0))
  const monthEnd = `${input.year}-${String(input.month).padStart(2, '0')}-${String(monthEndDate.getUTCDate()).padStart(2, '0')}`
  const monthDaysCount = monthEndDate.getUTCDate()
  const historyPeriods = Array.from({ length: 6 }, (_, index) => {
    const date = new Date(Date.UTC(input.year, input.month - 2 - index, 1))
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
    }
  })
  const previousPeriod = historyPeriods[0] ?? null

  const processStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
  const { data: payloadData, error: payloadError } = await measureRoyaltyAsync(
    'rpc.get_royalty_accrual_process_payload',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_accrual_process_payload', {
        p_year: input.year,
        p_month: input.month,
      })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )

  if (payloadError) {
    console.error('Failed to load royalty accrual process payload:', payloadError)
    throw payloadError
  }

  const payload = (payloadData ?? {}) as Record<string, unknown>
  const { data: paymentMethodRows, error: paymentMethodError } = await withAuthRecovery(() =>
    (supabase as any).from('royalty_foreign_payment_methods').select('series_id,method').eq('year', input.year).eq('month', input.month)
  )
  const paymentMethodTableMissing =
    paymentMethodError?.code === '42P01' ||
    (paymentMethodError?.code === 'PGRST205' &&
      String(paymentMethodError?.message ?? '').includes('royalty_foreign_payment_methods'))
  // Keep the process screen loadable while the schema migration is pending.
  // The method-edit RPC and foreign step-2 workflow still require the migration.
  if (paymentMethodError && !paymentMethodTableMissing) throw paymentMethodError
  const foreignPaymentMethodBySeries = new Map<number, 'invoice' | 'link' | 'details'>()
  for (const item of (paymentMethodRows ?? []) as Array<{ series_id: number; method: 'invoice' | 'link' | 'details' }>) {
    foreignPaymentMethodBySeries.set(Number(item.series_id), item.method)
  }
  const payloadRows = <T>(key: string): T[] => ((payload[key] ?? []) as T[])

  const ownerships = payloadRows<{
    series_id: number
    studio_internal_id: number | null
    start_date: string | null
    end_date: string | null
    ownership_phase: string | null
  }>('ownership_rows')

  const activeContracts = payloadRows<{
    contract_id: number
    series_id: number
    contract_number: string | null
    contract_status: string | null
    contract_type: string | null
    signed_at: string | null
    effective_from: string | null
    expected_end_date: string | null
    actual_end_date: string | null
  }>('active_contract_rows')

  const seriesRes = { data: payloadRows('series_rows') }
  const studioRes = { data: payloadRows('studio_rows') }
  const cityRes = { data: payloadRows('city_rows') }
  const partnerRes = { data: payloadRows('partner_rows') }
  const balanceRes = { data: payloadRows('balance_rows') }
  const settlementRes = { data: payloadRows('settlement_rows') }
  const settlementHistoryRes = { data: payloadRows('settlement_history_rows') }
  const termsRes = { data: payloadRows('term_rows') }
  const accrualRes = { data: payloadRows('accrual_rows') }
  const seriesIdsForSegmentAccruals = ((seriesRes.data ?? []) as Array<{ series_id: number }>).map((row) => row.series_id)
  const segmentAccrualPromise = seriesIdsForSegmentAccruals.length > 0
    ? measureRoyaltyAsync(
        'query.royalty_period_accrual_segments',
        () => withAuthRecovery(() =>
          supabase
            .from('royalty_period_accrual_segments')
            .select('*')
            .eq('year', input.year)
            .eq('month', input.month)
            .in('series_id', seriesIdsForSegmentAccruals)
        ),
        (result) => ({
          year: input.year,
          month: input.month,
          error: Boolean((result as any).error),
          ...summarizeRoyaltyPayload((result as any).data),
        })
      )
    : { data: [], error: null }
  const negativeAccrualRefundPromise = measureRoyaltyAsync(
    'query.royalty_negative_accrual_refunds_v',
    () => withAuthRecovery(() =>
      (supabase as any)
        .from('royalty_negative_accrual_refunds_v')
        .select('series_id,contract_id,segment_no,status,amount_rub')
        .eq('year', input.year)
        .eq('month', input.month)
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  const fxSupplementPromise = measureRoyaltyAsync(
    'rpc.get_royalty_process_fx_supplement',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_process_fx_supplement', { p_year: input.year, p_month: input.month })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  const txSupplementPromise = measureRoyaltyAsync(
    'rpc.get_royalty_process_subscription_tx_supplement',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_process_subscription_tx_supplement', { p_year: input.year, p_month: input.month })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  const refundSupplementPromise = measureRoyaltyAsync(
    'rpc.get_royalty_process_refund_supplement',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('get_royalty_process_refund_supplement', { p_year: input.year, p_month: input.month })
    ),
    (result) => ({
      year: input.year,
      month: input.month,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  const carrierTermsPromise = seriesIdsForSegmentAccruals.length > 0
    ? measureRoyaltyAsync(
        'query.royalty_series_terms_carrier',
        () => withAuthRecovery(async () => {
          const withFixed = await supabase
            .from('royalty_series_terms')
            .select('term_id,contract_id,is_listok_crm_carrier,is_fixed_royalty_carrier')
            .in('series_id', seriesIdsForSegmentAccruals)
          // Фронт впереди миграции 20261016160000: без новой колонки запрос падал целиком, и
          // вместе с ним терялся носитель LISTOK_CRM — шаг 2 записал бы в snapshot ноль.
          if (withFixed.error && String(withFixed.error.message ?? '').includes('is_fixed_royalty_carrier')) {
            return supabase
              .from('royalty_series_terms')
              .select('term_id,contract_id,is_listok_crm_carrier')
              .in('series_id', seriesIdsForSegmentAccruals)
          }
          return withFixed
        }),
        (result) => ({
          year: input.year,
          month: input.month,
          error: Boolean((result as any).error),
          ...summarizeRoyaltyPayload((result as any).data),
        })
      )
    : { data: [], error: null }
  const confirmedChargeMetadataPromise = measureRoyaltyAsync(
    'query.royalty_charges_confirmation_metadata',
    () => withAuthRecovery(() =>
      supabase
        .from('royalty_charges')
        .select('charge_id,confirmed_by,confirmed_at')
        .eq('year', input.year)
        .eq('month', input.month)
        .eq('is_confirmed', true)
    ),
    (result) => {
      const queryResult = result as { error?: unknown; data?: unknown }
      return {
        year: input.year,
        month: input.month,
        error: Boolean(queryResult.error),
        ...summarizeRoyaltyPayload(queryResult.data),
      }
    }
  )
  const [
    segmentAccrualRes,
    negativeAccrualRefundRes,
    fxSupplementRes,
    txSupplementRes,
    refundSupplementRes,
    carrierTermsRes,
    confirmedChargeMetadataRes,
  ] = await Promise.all([
    segmentAccrualPromise,
    negativeAccrualRefundPromise,
    fxSupplementPromise,
    txSupplementPromise,
    refundSupplementPromise,
    carrierTermsPromise,
    confirmedChargeMetadataPromise,
  ])

  if (segmentAccrualRes.error) throw segmentAccrualRes.error
  if (confirmedChargeMetadataRes.error) throw confirmedChargeMetadataRes.error
  if (negativeAccrualRefundRes.error) {
    console.warn('Failed to load persisted negative-accrual refunds for process page:', negativeAccrualRefundRes.error)
  }
  const persistedRefundKey = (
    seriesId: number,
    segmentNo: number | null | undefined,
    contractId: number | null | undefined
  ) => `${seriesId}|${segmentNo == null ? 1 : Number(segmentNo)}|${contractId == null ? 'null' : Number(contractId)}`
  const persistedNegativeRefundByKey = new Map<
    string,
    { amountRub: number; status: RoyaltyRefundStatus; offsetRub: number }
  >()
  for (const row of ((negativeAccrualRefundRes.data ?? []) as Array<{
    series_id: number
    contract_id: number | null
    segment_no: number | null
    status: RoyaltyRefundStatus
    amount_rub: number | null
    offset_amount_rub?: number | null
  }>)) {
    persistedNegativeRefundByKey.set(
      persistedRefundKey(row.series_id, row.segment_no, row.contract_id),
      {
        amountRub: roundMoney(row.amount_rub),
        status: row.status,
        // Зачёт (§20) — способ ПОГАШЕНИЯ: обязательство месяца он не меняет, поэтому
        // складывается рядом с суммой, а не вычитается из неё.
        offsetRub: roundMoney(row.offset_amount_rub ?? 0),
      }
    )
  }
  // Валютный/нативный сайд-пейлоад (план §14.11.5 items 2/3). Отдельный лёгкий RPC
  // (как carrier-запрос ниже), чтобы не трогать 500-строчный основной payload. Non-fatal
  // только для загрузки страницы: UI обязан показать блокирующий баннер, иначе foreign
  // экран молча становится RUB-only.
  const fxSupplementErrorMessage = fxSupplementRes?.error
    ? formatSupabaseLikeError(fxSupplementRes.error)
    : null
  const fxSupplement = ((fxSupplementRes && !fxSupplementRes.error ? fxSupplementRes.data : null) ?? {}) as {
    month_end?: string | null
    fx_staleness_threshold_days?: number | null
    fx_rows?: Array<{ currency_code: string; to_rub_rate: number | null; source: string | null; effective_date: string | null; quote_date?: string | null; quote_age_days?: number | null }>
    currency_coverage_rows?: Array<{ currency_code: string; studio_count_country: number | null; studio_count_listok: number | null; to_rub_rate: number | null; quote_date: string | null; quote_age_days: number | null; state: 'FRESH' | 'STALE' | 'NO_RATE' }>
    app_revenue_native_rows?: Array<Record<string, number | string | null>>
    listok_revenue_native_rows?: Array<{ studio_number: string; date: string; currency_code: string | null; amount: number | null; subscription_sale?: string | null }>
    term_currency_rows?: Array<{ term_id: number | null; currency_code: string | null; min_royalty_currency: string | null; min_royalty_amount?: number | null }>
    studio_revenue_currency_rows?: Array<{ studio_internal_id: number; revenue_currency: string | null }>
    balance_native_rows?: Array<{ charge_id: number; series_id: number; segment_no: number | null; charge_type: string; royalty_currency: string | null; royalty_amount: number | null; paid_amount: number | null; debt_amount: number | null; remaining_debt_amount: number | null; gross_revenue?: number | null; gross_revenue_currency?: string | null }>
    /** §1.8: курс каждого дня месяца. Без него фронт не может воспроизвести посуточную формулу движка. */
    fx_daily_rows?: Array<{ currency_code: string; rate_date: string; to_rub_rate: number | null }>
    /** §7.5 / B6: оплата charge'а в разрезе валюты платежа. */
    allocation_payment_rows?: Array<{
      charge_id: number
      payment_currency: string
      charge_currency: string
      payment_count: number
      allocation_count: number
      paid_payment_amount: number | null
      paid_charge_amount: number | null
      paid_rub: number | null
      applied_cross_rate: number | null
      first_payment_date: string | null
      last_payment_date: string | null
    }>
  }
  if (fxSupplementRes?.error) {
    console.warn('Royalty process FX supplement unavailable:', fxSupplementRes.error)
  }
  const fxToRubByCurrency = new Map<string, {
    rate: number | null
    source: string | null
    effective_date: string | null
    quote_date: string | null
    quote_age_days: number | null
  }>()
  for (const r of (fxSupplement.fx_rows ?? [])) {
    fxToRubByCurrency.set(r.currency_code, {
      rate: r.to_rub_rate == null ? null : Number(r.to_rub_rate),
      source: r.source ?? null,
      effective_date: r.effective_date ?? null,
      quote_date: r.quote_date ?? null,
      quote_age_days: r.quote_age_days == null ? null : Number(r.quote_age_days),
    })
  }
  // §7.2: фактическая нативная выручка по дням — портал и листок. Только для не-рублёвых
  // студий (RPC их и отдаёт), для остальных нативная сумма тождественна рублёвой.
  const portalNativeByStudioDate = new Map<number, Map<string, { total: number; memberships: number; subscriptions: number }>>()
  for (const r of (fxSupplement.app_revenue_native_rows ?? [])) {
    const studioId = Number(r.studio_internal_id)
    const metricDate = String(r.metric_date ?? '').slice(0, 10)
    if (!Number.isFinite(studioId) || !metricDate) continue
    // Та же формула разбиения, что и для рублей — просто по нативным колонкам. Сервер отдаёт
    // сырые поля именно ради этого: держать формулу в одном месте.
    const split = calculateAppRevenueSplitFromRubRow({
      new_sales_amount_rub: Number(r.new_sales_amount ?? 0),
      trial_payments_amount_rub: Number(r.trial_payments_amount ?? 0),
      renewals_amount_rub: Number(r.renewals_amount ?? 0),
      upgrades_amount_rub: Number(r.upgrades_amount ?? 0),
      returning_clients_amount_rub: Number(r.returning_clients_amount ?? 0),
      private_sessions_amount_rub: Number(r.private_sessions_amount ?? 0),
      other_income_rub: Number(r.other_income ?? 0),
      refunds_amount_rub: Number(r.refunds_amount ?? 0),
      new_subscriptions_amount_rub: Number(r.new_subscriptions_amount ?? 0),
      membership_fees_new_amount_rub: Number(r.membership_fees_new_amount ?? 0),
      subscription_renewals_amount_rub: Number(r.subscription_renewals_amount ?? 0),
      subscription_upgrades_amount_rub: Number(r.subscription_upgrades_amount ?? 0),
      subscription_returning_clients_amount_rub: Number(r.subscription_returning_clients_amount ?? 0),
      membership_fees_subscription_upgrades_amount_rub: Number(r.membership_fees_subscription_upgrades_amount ?? 0),
      membership_fees_subscription_returning_amount_rub: Number(r.membership_fees_subscription_returning_amount ?? 0),
    })
    const byDate = portalNativeByStudioDate.get(studioId) ?? new Map<string, { total: number; memberships: number; subscriptions: number }>()
    byDate.set(metricDate, {
      total: Number(r.gross_revenue ?? 0),
      memberships: split.memberships,
      subscriptions: split.subscriptions,
    })
    portalNativeByStudioDate.set(studioId, byDate)
  }
  const listokNativeByStudioNumberDate = new Map<string, Map<string, { total: number; memberships: number; subscriptions: number }>>()
  for (const r of (fxSupplement.listok_revenue_native_rows ?? [])) {
    const studioNumberKey = r.studio_number == null ? '' : String(r.studio_number)
    const date = String(r.date ?? '').slice(0, 10)
    if (!studioNumberKey || !date) continue
    const amount = Number(r.amount ?? 0)
    const isSubscription = isListokSubscriptionSale(r.subscription_sale)
    const byDate = listokNativeByStudioNumberDate.get(studioNumberKey) ?? new Map<string, { total: number; memberships: number; subscriptions: number }>()
    const current = byDate.get(date) ?? { total: 0, memberships: 0, subscriptions: 0 }
    byDate.set(date, {
      total: roundMoney(current.total + amount),
      memberships: roundMoney(current.memberships + (isSubscription ? 0 : amount)),
      subscriptions: roundMoney(current.subscriptions + (isSubscription ? amount : 0)),
    })
    listokNativeByStudioNumberDate.set(studioNumberKey, byDate)
  }
  const termCurrencyByTermId = new Map<number, string>()
  const termMinCurrencyByTermId = new Map<number, string | null>()
  const termMinAmountByTermId = new Map<number, number | null>()
  for (const r of (fxSupplement.term_currency_rows ?? [])) {
    if (r.term_id != null) {
      termCurrencyByTermId.set(Number(r.term_id), r.currency_code ?? 'RUB')
      termMinCurrencyByTermId.set(Number(r.term_id), r.min_royalty_currency ?? null)
      termMinAmountByTermId.set(Number(r.term_id), r.min_royalty_amount == null ? null : Number(r.min_royalty_amount))
    }
  }
  // §1.8: курс дня по валютам. Ключ — валюта, значение — дата → курс к рублю. Пустая
  // карта (фронт впереди миграции) означает «дневных курсов нет» — тогда приведение к
  // валюте обязательства падает на month-end, как было до §1.8.
  const dailyFxByCurrency = new Map<string, Map<string, number>>()
  for (const r of (fxSupplement.fx_daily_rows ?? [])) {
    if (!r.currency_code || !r.rate_date || r.to_rub_rate == null) continue
    const byDate = dailyFxByCurrency.get(r.currency_code) ?? new Map<string, number>()
    byDate.set(String(r.rate_date).slice(0, 10), Number(r.to_rub_rate))
    dailyFxByCurrency.set(r.currency_code, byDate)
  }
  // Курс валюты к рублю на КОНКРЕТНЫЙ день. Зеркало серверной royalty_daily_fx_to_rub,
  // включая фолбэк на month-end: две разные реализации «курса дня» разъехались бы на
  // первом же пропуске в книге курсов, а с ними разъедутся preview и charge.
  const dailyFxToRub = (currency: string | null | undefined, date: string): number | null => {
    const code = currency ?? 'RUB'
    if (code === 'RUB') return 1
    const daily = dailyFxByCurrency.get(code)?.get(date)
    if (daily != null && Number.isFinite(daily)) return daily
    const monthEnd = fxToRubByCurrency.get(code)?.rate
    return monthEnd == null || !Number.isFinite(monthEnd) ? null : monthEnd
  }
  // §7.5 / B6: оплата charge'а по валютам платежей. Один charge может гасить несколько
  // платежей в разных валютах — тогда единого кросс-курса не существует и «Факт» обязан
  // показывать состав, а не одно число с выдуманным курсом.
  const paymentBreakdownByChargeId = new Map<number, RoyaltyChargePaymentCurrencyRow[]>()
  for (const r of (fxSupplement.allocation_payment_rows ?? [])) {
    if (r.charge_id == null) continue
    const list = paymentBreakdownByChargeId.get(Number(r.charge_id)) ?? []
    list.push({
      payment_currency: r.payment_currency ?? 'RUB',
      charge_currency: r.charge_currency ?? 'RUB',
      payment_count: Number(r.payment_count ?? 0),
      paid_payment_amount: Number(r.paid_payment_amount ?? 0),
      paid_charge_amount: r.paid_charge_amount == null ? null : Number(r.paid_charge_amount),
      paid_rub: Number(r.paid_rub ?? 0),
      applied_cross_rate: r.applied_cross_rate == null ? null : Number(r.applied_cross_rate),
      first_payment_date: r.first_payment_date ?? null,
      last_payment_date: r.last_payment_date ?? null,
    })
    paymentBreakdownByChargeId.set(Number(r.charge_id), list)
  }
  // Валюта выручки студии (страна→валюта, только non-RUB). Дефолт RUB. Источник истины —
  // fx_country_currency в БД (фронт не хардкодит country→currency).
  const revenueCurrencyByStudioId = new Map<number, string>()
  for (const r of (fxSupplement.studio_revenue_currency_rows ?? [])) {
    if (r.studio_internal_id != null && r.revenue_currency) {
      revenueCurrencyByStudioId.set(Number(r.studio_internal_id), r.revenue_currency)
    }
  }
  // P-29: реестр подписочных автосписаний (`report_payment_items`) — источник
  // потранзакционного начисления SPLIT_ROYALTY/SPLIT_AGENCY. RPC отдаёт СЫРЫЕ суммы
  // транзакций по (студия, день); ставку применяем ниже по терму сегмента, округляя
  // каждую транзакцию, — так же, как удерживает номинальный счёт и как считает движок.
  // Non-fatal: фронт впереди миграции → карта пустая, расчёт остаётся месячным.
  const txSupplement = ((txSupplementRes && !txSupplementRes.error ? txSupplementRes.data : null) ?? {}) as {
    effective_from?: string | null
    tx_rows?: Array<{
      studio_internal_id: number
      metric_date: string
      currency_code: string | null
      tx_count: number
      revenue_native: number
      amounts: number[]
    }>
  }
  if (txSupplementRes?.error) {
    console.warn('Royalty per-transaction supplement unavailable (subscription royalty stays monthly):', txSupplementRes.error)
  }
  const perTransactionEffectiveFrom = txSupplement.effective_from ?? null
  // studio_internal_id → дата (YYYY-MM-DD) → суммы транзакций этого дня.
  const txAmountsByStudioDate = new Map<number, Map<string, number[]>>()
  for (const r of (txSupplement.tx_rows ?? [])) {
    if (r.studio_internal_id == null || !r.metric_date) continue
    const studioId = Number(r.studio_internal_id)
    const byDate = txAmountsByStudioDate.get(studioId) ?? new Map<string, number[]>()
    const amounts = (r.amounts ?? []).map((value) => Number(value)).filter((value) => Number.isFinite(value))
    byDate.set(String(r.metric_date).slice(0, 10), amounts)
    txAmountsByStudioDate.set(studioId, byDate)
  }
  // Возвраты Листка — из реестра платежей (`report_payment_items`), а не из дневного
  // агрегата `report_payments`. В агрегате возврат схлопывается с продажами своей группы,
  // поэтому признак «строка агрегата отрицательная» теряет часть возвратов и берёт нетто.
  // Non-fatal: фронт впереди миграции или есть неконвертируемая валюта -> legacy-расчёт.
  const refundSupplement = ((refundSupplementRes && !refundSupplementRes.error ? refundSupplementRes.data : null) ?? {}) as {
    month_start?: string | null
    registry_effective_from?: string | null
    can_use_registry?: boolean | null
    unconverted_count?: number | null
    refund_rows?: Array<{ studio_number: string | number | null; date: string | null; amount_rub: number | null }>
  }
  if (refundSupplementRes?.error) {
    console.warn('Royalty refund supplement unavailable (refunds stay aggregate-derived):', refundSupplementRes.error)
  }
  const refundRegistryEffectiveFrom = refundSupplement.registry_effective_from ?? null
  const refundRegistryMonthStart = refundSupplement.month_start ?? null
  const refundRegistryUnconvertedCount = Number(refundSupplement.unconverted_count ?? 0)
  if (!refundSupplementRes?.error && refundRegistryUnconvertedCount > 0) {
    console.warn(
      'Royalty refund supplement has unconverted refund currencies; refunds stay aggregate-derived:',
      { unconverted_count: refundRegistryUnconvertedCount }
    )
  }
  const useRefundRegistry = Boolean(
    refundSupplementRes
    && !refundSupplementRes.error
    && refundSupplement.can_use_registry !== false
    && refundRegistryUnconvertedCount === 0
    && refundRegistryEffectiveFrom
    && refundRegistryMonthStart
    && String(refundRegistryMonthStart).slice(0, 10) >= String(refundRegistryEffectiveFrom).slice(0, 10)
  )
  const listokRegistryRefundsByStudioNumberDate = new Map<string, Map<string, number>>()
  for (const r of (refundSupplement.refund_rows ?? [])) {
    const studioNumberKey = r.studio_number == null ? '' : String(r.studio_number)
    const date = String(r.date ?? '').slice(0, 10)
    const amountRub = Number(r.amount_rub)
    if (!studioNumberKey || !date || !Number.isFinite(amountRub)) continue
    const byDate = listokRegistryRefundsByStudioNumberDate.get(studioNumberKey) ?? new Map<string, number>()
    byDate.set(date, roundMoney((byDate.get(date) ?? 0) + amountRub))
    listokRegistryRefundsByStudioNumberDate.set(studioNumberKey, byDate)
  }
  const chargeAdjustmentsRes = { data: payloadRows('charge_adjustment_rows') }
  const appRevenueRes = { data: payloadRows('app_revenue_rows') }
  const revenueConfirmationRes = { data: payloadRows('revenue_confirmation_rows') }
  const listokRevenueRes = { data: payloadRows('listok_revenue_rows') }
  const subscriptionSettingsRes = { data: payloadRows('subscription_setting_rows') }
  const subscriptionHistoryRes = { data: payloadRows('subscription_history_rows') }
  const statusHistoryRes = { data: payloadRows('status_history_rows') }
  const allStudiosRes = { data: payloadRows('all_studio_rows') }
  const allCitiesRes = { data: payloadRows('all_city_rows') }
  const allStatusHistoryRes = { data: payloadRows('all_status_history_rows') }
  // Контекст владения для серий, у которых в этом месяце владения нет вовсе
  // (миграция 20260806120000). Нужен, чтобы строка «договор без студии» называла
  // причину: студия закрыта / продана / владение ещё не началось. Payload постарше
  // ключа не отдаёт — тогда падаем в обобщённую формулировку.
  const seriesOwnershipContextRes = { data: payloadRows('series_ownership_context_rows') }
  const revenueConfirmationRows = ((revenueConfirmationRes.data ?? []) as Array<{
    studio_internal_id: number | null
    metric_date?: string | null
    gross_revenue: number | null
    gross_revenue_confirmed: boolean | null
  }>)
  const effectiveRevenueConfirmationRows = revenueConfirmationRows.length > 0
    ? revenueConfirmationRows
    : ((appRevenueRes.data ?? []) as Array<{
      studio_internal_id: number | null
      metric_date?: string | null
      gross_revenue: number | null
      gross_revenue_confirmed: boolean | null
    }>)

  const partnerBySeriesId = new Map<number, number | null>()
  for (const row of ((seriesRes.data ?? []) as Array<{ series_id: number; partner_number: number | null }>)) {
    partnerBySeriesId.set(row.series_id, row.partner_number ?? null)
  }

  const studioById = new Map<number, {
    studio_number: string | null
    city_id: number | null
    address: string | null
    status_code: string | null
  }>()
  for (const row of ((studioRes.data ?? []) as Array<{
    studio_internal_id: number
    studio_number: string | null
    city_id: number | null
    address: string | null
    status_code: string | null
  }>)) {
    studioById.set(row.studio_internal_id, {
      studio_number: row.studio_number ?? null,
      city_id: row.city_id ?? null,
      address: row.address ?? null,
      status_code: row.status_code ?? null,
    })
  }

  // Студии всей сети: у серии без владения в месяце студия в studioById отсутствует
  // (тот собран из studio_rows, а это студии с владением внутри месяца).
  const anyStudioById = new Map<number, {
    studio_number: string | null
    city_id: number | null
    address: string | null
    status_code: string | null
  }>(studioById)
  for (const row of ((allStudiosRes.data ?? []) as Array<{
    studio_internal_id: number
    studio_number: string | number | null
    city_id: number | null
    address: string | null
    status_code: string | null
  }>)) {
    anyStudioById.set(row.studio_internal_id, {
      studio_number: row.studio_number == null ? null : String(row.studio_number),
      city_id: row.city_id ?? null,
      address: row.address ?? null,
      status_code: row.status_code ?? null,
    })
  }

  const cityById = new Map<number, { city_name: string | null; country: string | null }>()
  for (const row of ((allCitiesRes.data ?? cityRes.data ?? []) as Array<{ city_id: number; city_name: string | null; country: string | null }>)) {
    cityById.set(row.city_id, {
      city_name: row.city_name ?? null,
      country: row.country ?? null,
    })
  }

  type SeriesOwnershipContext = {
    prev_studio_internal_id: number | null
    prev_ownership_phase: string | null
    prev_start_date: string | null
    prev_end_date: string | null
    next_studio_internal_id: number | null
    next_ownership_phase: string | null
    next_start_date: string | null
  }
  const seriesOwnershipContextBySeriesId = new Map<number, SeriesOwnershipContext>()
  for (const row of ((seriesOwnershipContextRes.data ?? []) as Array<SeriesOwnershipContext & { series_id: number }>)) {
    seriesOwnershipContextBySeriesId.set(row.series_id, row)
  }

  const personsByPartner = new Map<number, string | null>()
  for (const row of ((partnerRes.data ?? []) as Array<{ partner_number: number; persons_fio: string | null }>)) {
    personsByPartner.set(row.partner_number, row.persons_fio ?? null)
  }

  type ProcessBalance = {
    royalty_percent: number | null
    charged_rub: number
    paid_rub: number
    charge_count: number
    is_confirmed: boolean
    accounting_confirmed_at: string | null
    accounting_confirmed_by_user_id: string | null
    gross_by_type: Record<RoyaltyChargeType, number>
    charged_by_type: Record<RoyaltyChargeType, number>
    paid_by_type: Record<RoyaltyChargeType, number>
    /**
     * Не-денежное покрытие начисления: зачёт возврата (§20) + списание долга (P-34)
     * + зачёт РБ. Долг = начислено − оплачено − это. Без него экран выставления считал
     * долг там, где `royalty_balance.debt_amount_rub` его уже не видит, а compact-путь
     * (`get_royalty_process_rows_v2`) читает витрину и показывал другое число.
     */
    covered_by_type: Record<RoyaltyChargeType, number>
    /** Часть `covered_by_type`, закрытая зачётом РБ, — для отдельной подписи. */
    rb_tax_by_type: Record<RoyaltyChargeType, number>
  }
  type ProcessBalanceRow = {
    charge_id?: number | null
    series_id: number
    contract_id?: number | null
    segment_no?: number | null
    segment_start?: string | null
    segment_end?: string | null
    charge_type: RoyaltyChargeType
    gross_revenue_rub?: number | null
    royalty_amount_rub: number | null
    royalty_percent_applied: number | null
    paid_amount_rub: number | null
    /** Появились в payload 07.09.2026; у старого сервера отсутствуют — читаем через ?? 0. */
    offset_amount_rub?: number | null
    written_off_amount_rub?: number | null
    /** Зачёт РБ (миграция 20260925140000); у старого сервера отсутствует. */
    rb_tax_offset_amount_rub?: number | null
    is_confirmed: boolean | null
    confirmed_at?: string | null
    confirmed_by?: string | null
  }
  const createEmptyProcessBalance = (): ProcessBalance => ({
    royalty_percent: null,
    charged_rub: 0,
    paid_rub: 0,
    charge_count: 0,
    is_confirmed: false,
    accounting_confirmed_at: null,
    accounting_confirmed_by_user_id: null,
    gross_by_type: {
      ROYALTY: 0,
      SPLIT_ROYALTY: 0,
      SPLIT_AGENCY: 0,
      MANUAL_ROYALTY: 0,
      LISTOK_CRM: 0,
      FIXED_ROYALTY: 0,
    },
    charged_by_type: {
      ROYALTY: 0,
      SPLIT_ROYALTY: 0,
      SPLIT_AGENCY: 0,
      MANUAL_ROYALTY: 0,
      LISTOK_CRM: 0,
      FIXED_ROYALTY: 0,
    },
    paid_by_type: {
      ROYALTY: 0,
      SPLIT_ROYALTY: 0,
      SPLIT_AGENCY: 0,
      MANUAL_ROYALTY: 0,
      LISTOK_CRM: 0,
      FIXED_ROYALTY: 0,
    },
    covered_by_type: {
      ROYALTY: 0,
      SPLIT_ROYALTY: 0,
      SPLIT_AGENCY: 0,
      MANUAL_ROYALTY: 0,
      LISTOK_CRM: 0,
      FIXED_ROYALTY: 0,
    },
    rb_tax_by_type: {
      ROYALTY: 0,
      SPLIT_ROYALTY: 0,
      SPLIT_AGENCY: 0,
      MANUAL_ROYALTY: 0,
      LISTOK_CRM: 0,
      FIXED_ROYALTY: 0,
    },
  })
  const balanceSegmentKey = (seriesId: number, segmentNo: number | null | undefined): string =>
    `${seriesId}|${segmentNo == null ? 1 : Number(segmentNo)}`
  const applyBalanceRow = (current: ProcessBalance, row: {
    charge_type: RoyaltyChargeType
    gross_revenue_rub: number | null
    royalty_amount_rub: number | null
    royalty_percent_applied: number | null
    paid_amount_rub: number | null
    offset_amount_rub?: number | null
    written_off_amount_rub?: number | null
    rb_tax_offset_amount_rub?: number | null
    is_confirmed: boolean | null
    confirmed_at?: string | null
    confirmed_by?: string | null
  }): ProcessBalance => {
    current.charged_rub += Number(row.royalty_amount_rub ?? 0)
    current.paid_rub += Number(row.paid_amount_rub ?? 0)
    current.charge_count += 1
    current.is_confirmed = current.is_confirmed || Boolean(row.is_confirmed)
    if (row.is_confirmed && row.confirmed_at && (
      current.accounting_confirmed_at == null || row.confirmed_at > current.accounting_confirmed_at
    )) {
      current.accounting_confirmed_at = row.confirmed_at
      current.accounting_confirmed_by_user_id = row.confirmed_by ?? null
    }
    current.gross_by_type[row.charge_type] += Number(row.gross_revenue_rub ?? 0)
    current.charged_by_type[row.charge_type] += Number(row.royalty_amount_rub ?? 0)
    current.paid_by_type[row.charge_type] += Number(row.paid_amount_rub ?? 0)
    current.covered_by_type[row.charge_type] +=
      Number(row.offset_amount_rub ?? 0)
      + Number(row.written_off_amount_rub ?? 0)
      + Number(row.rb_tax_offset_amount_rub ?? 0)
    current.rb_tax_by_type[row.charge_type] += Number(row.rb_tax_offset_amount_rub ?? 0)
    if (current.royalty_percent == null && row.royalty_percent_applied != null) {
      current.royalty_percent = Number(row.royalty_percent_applied)
    }
    return current
  }
  const rawBalanceRows = ((balanceRes.data ?? []) as ProcessBalanceRow[])
  const confirmationMetadataByChargeId = new Map<number, { confirmed_at: string | null; confirmed_by: string | null }>()
  for (const row of ((confirmedChargeMetadataRes.data ?? []) as Array<{
    charge_id: number | null
    confirmed_at: string | null
    confirmed_by: string | null
  }>)) {
    if (row.charge_id == null) continue
    confirmationMetadataByChargeId.set(Number(row.charge_id), {
      confirmed_at: row.confirmed_at ?? null,
      confirmed_by: row.confirmed_by ?? null,
    })
  }
  const grossRevenueRubByChargeId = new Map<number, number>()
  const chargeIdsMissingGross = rawBalanceRows
    .filter((row) => row.gross_revenue_rub == null && Number.isFinite(row.charge_id))
    .map((row) => Number(row.charge_id))
  for (const row of rawBalanceRows) {
    if (row.charge_id != null && row.gross_revenue_rub != null) {
      grossRevenueRubByChargeId.set(Number(row.charge_id), Number(row.gross_revenue_rub))
    }
  }
  if (chargeIdsMissingGross.length > 0) {
    const { data, error } = await withAuthRecovery(() =>
      supabase
        .from('royalty_charges')
        .select('charge_id,gross_revenue_rub')
        .in('charge_id', Array.from(new Set(chargeIdsMissingGross)))
    )
    if (error) {
      console.warn('Failed to load royalty charge gross revenue for process page:', error)
    } else {
      for (const row of ((data ?? []) as Array<{ charge_id: number | null; gross_revenue_rub: number | null }>)) {
        if (row.charge_id != null) {
          grossRevenueRubByChargeId.set(Number(row.charge_id), Number(row.gross_revenue_rub ?? 0))
        }
      }
    }
  }
  const balanceBySeriesId = new Map<number, ProcessBalance>()
  const balanceBySegmentKey = new Map<string, ProcessBalance>()
  for (const row of rawBalanceRows) {
    const confirmationMetadata = row.charge_id == null
      ? null
      : confirmationMetadataByChargeId.get(Number(row.charge_id)) ?? null
    const normalizedRow = {
      ...row,
      gross_revenue_rub: row.gross_revenue_rub ?? (row.charge_id != null ? grossRevenueRubByChargeId.get(Number(row.charge_id)) : null) ?? null,
      confirmed_at: row.confirmed_at ?? confirmationMetadata?.confirmed_at ?? null,
      confirmed_by: row.confirmed_by ?? confirmationMetadata?.confirmed_by ?? null,
    }
    balanceBySeriesId.set(row.series_id, applyBalanceRow(
      balanceBySeriesId.get(row.series_id) ?? createEmptyProcessBalance(),
      normalizedRow
    ))
    balanceBySegmentKey.set(balanceSegmentKey(row.series_id, row.segment_no), applyBalanceRow(
      balanceBySegmentKey.get(balanceSegmentKey(row.series_id, row.segment_no)) ?? createEmptyProcessBalance(),
      normalizedRow
    ))
  }

  // Нативные (в валюте обязательства) charged/debt per сегмент — из fx-supplement
  // (точные booked-суммы royalty_balance). Внутри сегмента все charge одной валюты,
  // поэтому сумма по типам когерентна. Показываем только для нерублёвой серии.
  const nativeBySegmentKey = new Map<string, { currency: string | null; charged: number; debt: number; paid: number; chargeIds: number[] }>()
  for (const r of (fxSupplement.balance_native_rows ?? [])) {
    const key = balanceSegmentKey(r.series_id, r.segment_no)
    const acc = nativeBySegmentKey.get(key) ?? { currency: r.royalty_currency ?? null, charged: 0, debt: 0, paid: 0, chargeIds: [] as number[] }
    if (r.charge_id != null) acc.chargeIds.push(Number(r.charge_id))
    acc.charged += Number(r.royalty_amount ?? 0)
    acc.debt += Number(r.debt_amount ?? 0)
    // Оплата в валюте начисления (§7.5): `royalty_balance.paid_amount` уже приведён к валюте
    // charge'а, поэтому «Разница» на этапе оплаты считается в валюте обязательства, а не в ₽.
    acc.paid += Number(r.paid_amount ?? 0)
    acc.currency = acc.currency ?? (r.royalty_currency ?? null)
    nativeBySegmentKey.set(key, acc)
  }

  // Минималка канонична в валюте min_royalty_currency (по умолчанию = валюта обязательства).
  // В валюту обязательства она приводится кросс-курсом на конец месяца (фиксированная
  // месячная величина, дневного распределения у неё нет) — ровно как в движке; при
  // совпадении валют это сам номинал, без курса. FX_MISSING возвращает null.
  const termMinRoyaltyNative = (term: ProcessTermInterval | null): number | null => {
    const amount = Number(term?.min_royalty_amount ?? 0)
    if (!amount) return 0
    const minCurrency = term?.min_royalty_currency ?? term?.currency_code ?? 'RUB'
    const chargeCurrency = term?.currency_code ?? 'RUB'
    if (minCurrency === chargeCurrency) return roundMoney(amount)
    const rateToRub = (ccy: string): number | null => (ccy === 'RUB' ? 1 : (fxToRubByCurrency.get(ccy)?.rate ?? null))
    const minToRub = rateToRub(minCurrency)
    const chargeToRub = rateToRub(chargeCurrency)
    if (minToRub == null || chargeToRub == null) return null
    return roundMoney(amount * (minToRub / chargeToRub))
  }
  // Рублёвая сторона минималки — как `v_min_royalty_rub` в движке (§1.8): натив в валюте
  // обязательства × ЭФФЕКТИВНЫЙ курс сегмента (без выручки — month-end). Раньше здесь стоял
  // курс конца месяца, и рублёвая минималка жила по одному курсу, а процентная часть — по
  // другому: в полосе ±3 % минималка срабатывала в рублях, но не в валюте (или наоборот), а
  // деление такой рублёвой суммы на эффективный курс превращало 1 000 AED в 1 016,87 AED.
  // Для RUB-терма оба множителя = 1 → значение не меняется.
  const termMinRoyaltyRub = (term: ProcessTermInterval | null, obligationRate: number | null): number | null => {
    const minNative = termMinRoyaltyNative(term)
    if (minNative == null) return null
    if (minNative === 0) return 0
    const chargeCurrency = term?.currency_code ?? 'RUB'
    if (chargeCurrency === 'RUB') return roundMoney(minNative)
    const rate = obligationRate ?? fxToRubByCurrency.get(chargeCurrency)?.rate ?? null
    return rate != null && rate > 0 ? roundMoney(minNative * rate) : null
  }

  // Валютный/нативный блок строки (план §14.11.5 items 2/3). RUB-терм и RUB-студия → всё RUB.
  const buildRowFxNative = (
    term: ProcessTermInterval | null,
    estimatedRoyaltyRub: number,
    seriesId: number,
    segmentNo: number | null,
    studioInternalId: number | null,
    selectedRevenueRub: number,
    selectedRevenueObl: number,
    estimatedRoyaltyNativeOverride: number | null = null,
    baseRoyaltyNative: number | null = null,
    minRoyaltyTopupNative: number | null = null,
  ): {
    charge_currency: string
    charge_fx_rate: number | null
    charge_fx_effective_rate: number | null
    charge_fx_source: string | null
    charge_fx_effective_date: string | null
    fx_missing: boolean
    fx_missing_currency: string | null
    estimated_royalty_native: number | null
    base_royalty_native: number | null
    min_royalty_topup_native: number | null
    charged_native: number | null
    debt_native: number | null
    paid_native: number | null
    revenue_currency: string
    revenue_fx_quote_date: string | null
    revenue_fx_quote_age_days: number | null
    charge_fx_quote_date: string | null
    charge_fx_quote_age_days: number | null
    royalty_min_amount_native: number | null
    royalty_min_currency: string | null
    payment_currency_rows: RoyaltyChargePaymentCurrencyRow[]
  } => {
    const monthEnd = (fxSupplement.month_end ?? null) as string | null
    const chargeCurrency = term?.currency_code ?? 'RUB'
    const fx = chargeCurrency === 'RUB'
      ? {
        rate: 1 as number | null,
        source: 'SAME_CURRENCY' as string | null,
        effective_date: monthEnd,
        quote_date: monthEnd,
        quote_age_days: 0 as number | null,
      }
      : (fxToRubByCurrency.get(chargeCurrency) ?? {
        rate: null as number | null,
        source: null as string | null,
        effective_date: null as string | null,
        quote_date: null as string | null,
        quote_age_days: null as number | null,
      })
    const chargeFxMissing = chargeCurrency !== 'RUB' && fx.rate == null

    const revenueCurrency = studioInternalId != null ? (revenueCurrencyByStudioId.get(studioInternalId) ?? 'RUB') : 'RUB'
    const revFx = revenueCurrency === 'RUB'
      ? { rate: 1 as number | null, quote_date: monthEnd, quote_age_days: 0 as number | null }
      : (fxToRubByCurrency.get(revenueCurrency) ?? { rate: null as number | null, quote_date: null as string | null, quote_age_days: null as number | null })
    const revRate = revFx.rate
    const revenueFxMissing = revenueCurrency !== 'RUB' && revRate == null

    // Движок конвертирует и выручку (revenue→RUB), и обязательство (royalty→RUB); нет любого
    // курса → FX_MISSING. Называем первую отсутствующую валюту для точной формулировки.
    const fxMissing = chargeFxMissing || revenueFxMissing
    const fxMissingCurrency = chargeFxMissing ? chargeCurrency : (revenueFxMissing ? revenueCurrency : null)
    const nativeSeg = nativeBySegmentKey.get(balanceSegmentKey(seriesId, segmentNo))

    // §1.8: ЭФФЕКТИВНЫЙ курс обязательства сегмента — тот, что фактически применит движок
    // (посуточно взвешенный), а не month-end. Выводим из пары «рубли ↔ валюта
    // обязательства», собранных по дням. Нет выручки (нечем взвешивать) → month-end,
    // ровно как в движке.
    const effectiveObligationRate = resolveObligationEffectiveRate({
      chargeCurrency,
      revenueRub: selectedRevenueRub,
      revenueObl: selectedRevenueObl,
      monthEndRate: fx.rate,
    })
    const paymentCurrencyRows = (nativeSeg?.chargeIds ?? [])
      .flatMap((chargeId) => paymentBreakdownByChargeId.get(chargeId) ?? [])

    return {
      charge_currency: chargeCurrency,
      charge_fx_rate: fx.rate,
      charge_fx_effective_rate: effectiveObligationRate == null || !Number.isFinite(effectiveObligationRate)
        ? null
        : effectiveObligationRate,
      charge_fx_source: fx.source,
      charge_fx_effective_date: fx.effective_date,
      fx_missing: fxMissing,
      fx_missing_currency: fxMissingCurrency,
      // Оценка роялти в валюте обязательства — по ЭФФЕКТИВНОМУ курсу: делить на month-end
      // значило бы показывать число, которое движок не воспроизведёт.
      estimated_royalty_native: chargeCurrency === 'RUB'
        ? null
        : estimatedRoyaltyNativeOverride != null
          ? estimatedRoyaltyNativeOverride
          : (effectiveObligationRate != null && effectiveObligationRate > 0
          ? roundMoney(estimatedRoyaltyRub / effectiveObligationRate)
          : null),
      base_royalty_native: chargeCurrency === 'RUB' ? null : baseRoyaltyNative,
      min_royalty_topup_native: chargeCurrency === 'RUB' ? null : minRoyaltyTopupNative,
      charged_native: nativeSeg ? roundMoney(nativeSeg.charged) : null,
      debt_native: nativeSeg ? roundMoney(nativeSeg.debt) : null,
      paid_native: nativeSeg ? roundMoney(nativeSeg.paid) : null,
      revenue_currency: revenueCurrency,
      revenue_fx_quote_date: revFx.quote_date ?? null,
      revenue_fx_quote_age_days: revFx.quote_age_days ?? null,
      charge_fx_quote_date: fx.quote_date ?? null,
      charge_fx_quote_age_days: fx.quote_age_days ?? null,
      // §7.3 / B5: минималка канонична в СВОЕЙ валюте — она может не совпадать ни с
      // выручкой, ни с обязательством. Отдаём как есть, приведение делает экран.
      royalty_min_amount_native: term?.min_royalty_amount == null ? null : Number(term.min_royalty_amount),
      royalty_min_currency: term?.min_royalty_currency ?? term?.currency_code ?? 'RUB',
      payment_currency_rows: paymentCurrencyRows,
    }
  }

  const settlementBySeriesId = new Map<number, {
    charged_rub: number
    paid_rub: number
    debt_rub: number
    charge_count: number
    status: RoyaltyPeriodSettlement['status'] | null
    is_reviewed: boolean
  }>()
  for (const row of ((settlementRes.data ?? []) as Array<{
    series_id: number
    charged_rub: number | null
    recognized_rub: number | null
    debt_rub: number | null
    charge_count: number | null
    status: RoyaltyPeriodSettlement['status'] | null
    is_reviewed: boolean | null
  }>)) {
    settlementBySeriesId.set(row.series_id, {
      charged_rub: Number(row.charged_rub ?? 0),
      paid_rub: Number(row.recognized_rub ?? 0),
      debt_rub: Number(row.debt_rub ?? 0),
      charge_count: Number(row.charge_count ?? 0),
      status: row.status ?? null,
      is_reviewed: Boolean((row as any).is_reviewed),
    })
  }

  const latestTermsBySeriesId = new Map<number, {
    contract_type: RoyaltySeriesContractType | null
    royalty_percent: number | null
    agency_percent: number | null
    vat_percent: number | null
    listok_crm_base_amount: number | null
    min_royalty_amount: number | null
  }>()
  for (const row of ((termsRes.data ?? []) as Array<{
    series_id: number
    contract_type: RoyaltySeriesContractType | null
    royalty_percent: number | null
    agency_percent: number | null
    vat_percent: number | null
    listok_crm_base_amount: number | null
    min_royalty_amount: number | null
  }>)) {
    if (!latestTermsBySeriesId.has(row.series_id)) {
      latestTermsBySeriesId.set(row.series_id, {
        contract_type: row.contract_type ?? null,
        royalty_percent: row.royalty_percent == null ? null : Number(row.royalty_percent),
        agency_percent: row.agency_percent == null ? null : Number(row.agency_percent),
        vat_percent: row.vat_percent == null ? null : Number(row.vat_percent),
        listok_crm_base_amount: row.listok_crm_base_amount == null ? null : Number(row.listok_crm_base_amount),
        min_royalty_amount: row.min_royalty_amount == null ? null : Number(row.min_royalty_amount),
      })
    }
  }

  // Term intervals per series, sorted by effective_from — used to segment a month by
  // royalty condition (step-up / model switch mid-period). See
  // docs/ROYALTY_PROCESS_TERM_SEGMENTATION_LOGIC.md.
  type ProcessTermInterval = {
    term_id: number | null
    contract_id: number | null
    contract_number: string | null
    legal_effective_from: string | null
    legal_expected_end_date: string | null
    legal_actual_end_date: string | null
    effective_from: string | null
    effective_to: string | null
    contract_type: RoyaltySeriesContractType | null
    royalty_percent: number | null
    agency_percent: number | null
    vat_percent: number | null
    listok_crm_base_amount: number | null
    min_royalty_amount: number | null
    // Валюта роялти и валюта минимума (план §4.1 / §14.11.5). currency_code —
    // из payload term_rows; min_royalty_currency — из fx-supplement (NULL = валюта
    // обязательства). Для всех живых термов = RUB.
    currency_code: string
    min_royalty_currency: string | null
    is_listok_crm_carrier: boolean
    is_fixed_royalty_carrier: boolean
  }
  // P-22: договор-носитель CRM-фикса помечается флагом `is_listok_crm_carrier` на терме.
  // Payload RPC исторически не отдавал все identity-поля, поэтому дотягиваем их лёгким запросом.
  // Non-fatal: если колонки ещё нет (фронт впереди миграции) — считаем всех НЕ носителями.
  const listokCarrierByTermId = new Map<number, boolean>()
  const fixedRoyaltyCarrierByTermId = new Map<number, boolean>()
  const termContractByTermId = new Map<number, number | null>()
  if (!carrierTermsRes.error) {
    for (const row of ((carrierTermsRes.data ?? []) as Array<{ term_id: number | null; contract_id?: number | null; is_listok_crm_carrier: boolean | null; is_fixed_royalty_carrier: boolean | null }>)) {
      if (row.term_id != null) {
        listokCarrierByTermId.set(Number(row.term_id), row.is_listok_crm_carrier === true)
        fixedRoyaltyCarrierByTermId.set(Number(row.term_id), row.is_fixed_royalty_carrier === true)
        termContractByTermId.set(Number(row.term_id), row.contract_id == null ? null : Number(row.contract_id))
      }
    }
  }
  const termIntervalsBySeriesId = new Map<number, ProcessTermInterval[]>()
  for (const row of ((termsRes.data ?? []) as Array<{
    term_id: number | null
    series_id: number
    contract_id?: number | null
    contract_number?: string | null
    legal_effective_from?: string | null
    legal_expected_end_date?: string | null
    legal_actual_end_date?: string | null
    effective_from: string | null
    effective_to: string | null
    contract_type: RoyaltySeriesContractType | null
    royalty_percent: number | null
    agency_percent: number | null
    vat_percent: number | null
    listok_crm_base_amount: number | null
    min_royalty_amount: number | null
    currency_code?: string | null
  }>)) {
    const list = termIntervalsBySeriesId.get(row.series_id) ?? []
    const termId = row.term_id == null ? null : Number(row.term_id)
    list.push({
      term_id: termId,
      contract_id: row.contract_id == null && termId != null
        ? (termContractByTermId.get(termId) ?? null)
        : row.contract_id == null ? null : Number(row.contract_id),
      contract_number: row.contract_number ?? null,
      legal_effective_from: toRoyaltyBusinessDateKey(row.legal_effective_from),
      legal_expected_end_date: toRoyaltyBusinessDateKey(row.legal_expected_end_date),
      legal_actual_end_date: toRoyaltyBusinessDateKey(row.legal_actual_end_date),
      effective_from: toRoyaltyBusinessDateKey(row.effective_from),
      effective_to: toRoyaltyBusinessDateKey(row.effective_to),
      contract_type: row.contract_type ?? null,
      royalty_percent: row.royalty_percent == null ? null : Number(row.royalty_percent),
      agency_percent: row.agency_percent == null ? null : Number(row.agency_percent),
      vat_percent: row.vat_percent == null ? null : Number(row.vat_percent),
      listok_crm_base_amount: row.listok_crm_base_amount == null ? null : Number(row.listok_crm_base_amount),
      min_royalty_amount: row.min_royalty_amount == null ? null : Number(row.min_royalty_amount),
      currency_code: row.currency_code ?? (termId != null ? termCurrencyByTermId.get(termId) : null) ?? 'RUB',
      min_royalty_currency: termId != null ? (termMinCurrencyByTermId.get(termId) ?? null) : null,
      is_listok_crm_carrier: termId != null && listokCarrierByTermId.get(termId) === true,
      is_fixed_royalty_carrier: termId != null && fixedRoyaltyCarrierByTermId.get(termId) === true,
    })
    termIntervalsBySeriesId.set(row.series_id, list)
  }
  for (const list of termIntervalsBySeriesId.values()) {
    list.sort((a, b) => (a.effective_from ?? '').localeCompare(b.effective_from ?? ''))
  }
  // Term active on `date` and owned by the active MAIN contract. No carry-forward past
  // effective_to: gaps must stay gaps, matching resolve_royalty_contract_term_for_date().
  const getTermForDate = (seriesId: number, date: string, contractId: number | null): ProcessTermInterval | null => {
    const list = termIntervalsBySeriesId.get(seriesId)
    if (!list || list.length === 0) return null
    for (const interval of list) {
      const from = interval.effective_from
      const to = interval.effective_to
      if (contractId != null && interval.contract_id !== contractId) continue
      if (from != null && from > date) continue // hasn't started yet
      if (to == null || to >= date) return interval
    }
    return null
  }
  const getTermsForRange = (seriesId: number, rangeStart: string, rangeEnd: string): ProcessTermInterval[] => {
    const list = termIntervalsBySeriesId.get(seriesId) ?? []
    return list.filter((interval) => {
      const from = interval.effective_from ?? '0000-01-01'
      const to = interval.effective_to ?? '9999-12-31'
      return from <= rangeEnd && to >= rangeStart
    })
  }

  const accrualBySeriesId = new Map<number, {
    revenue_checked: boolean
    royalty_checked: boolean
    revenue_checked_at: string | null
    revenue_checked_by: string | null
    royalty_checked_at: string | null
    royalty_checked_by: string | null
    revenue_portal_rub: number | null
    revenue_listok_rub: number | null
    revenue_selected_rub: number | null
    revenue_selected_source: RevenueSource | null
    revenue_portal_memberships_rub: number | null
    revenue_listok_memberships_rub: number | null
    revenue_selected_memberships_rub: number | null
    revenue_memberships_selected_source: RevenueSource | null
    revenue_portal_subscriptions_rub: number | null
    revenue_listok_subscriptions_rub: number | null
    revenue_selected_subscriptions_rub: number | null
    revenue_subscriptions_selected_source: RevenueSource | null
    royalty_revenue_rub: number | null
    royalty_percent: number | null
    royalty_amount_rub: number | null
    royalty_memberships_revenue_rub: number | null
    royalty_subscriptions_revenue_rub: number | null
    royalty_split_royalty_rub: number | null
    royalty_split_agency_rub: number | null
    royalty_manual_royalty_rub: number | null
    royalty_listok_crm_rub: number | null
    adjustment_rub: number | null
    adjustment_comment: string | null
    adjustment_updated_at: string | null
    adjustment_updated_by_user_id: string | null
  }>()
  for (const row of ((accrualRes.data ?? []) as Array<{
    series_id: number
    revenue_checked_at: string | null
    revenue_checked_by: string | null
    revenue_portal_rub: number | null
    revenue_listok_rub: number | null
    revenue_selected_rub: number | null
    revenue_selected_source: RevenueSource | null
    revenue_portal_memberships_rub: number | null
    revenue_listok_memberships_rub: number | null
    revenue_selected_memberships_rub: number | null
    revenue_memberships_selected_source: RevenueSource | null
    revenue_portal_subscriptions_rub: number | null
    revenue_listok_subscriptions_rub: number | null
    revenue_selected_subscriptions_rub: number | null
    revenue_subscriptions_selected_source: RevenueSource | null
    royalty_checked_at: string | null
    royalty_checked_by: string | null
    royalty_revenue_rub: number | null
    royalty_percent: number | null
    royalty_amount_rub: number | null
    royalty_memberships_revenue_rub: number | null
    royalty_subscriptions_revenue_rub: number | null
    royalty_split_royalty_rub: number | null
    royalty_split_agency_rub: number | null
    royalty_manual_royalty_rub: number | null
    royalty_listok_crm_rub: number | null
    adjustment_rub: number | null
    adjustment_comment: string | null
    adjustment_updated_at: string | null
    adjustment_updated_by_user_id: string | null
  }>)) {
    accrualBySeriesId.set(row.series_id, {
      revenue_checked: Boolean(row.revenue_checked_at),
      royalty_checked: Boolean(row.royalty_checked_at),
      revenue_checked_at: row.revenue_checked_at ?? null,
      revenue_checked_by: row.revenue_checked_by ?? null,
      royalty_checked_at: row.royalty_checked_at ?? null,
      royalty_checked_by: row.royalty_checked_by ?? null,
      revenue_portal_rub: row.revenue_portal_rub == null ? null : Number(row.revenue_portal_rub),
      revenue_listok_rub: row.revenue_listok_rub == null ? null : Number(row.revenue_listok_rub),
      revenue_selected_rub: row.revenue_selected_rub == null ? null : Number(row.revenue_selected_rub),
      revenue_selected_source: row.revenue_selected_source ?? null,
      revenue_portal_memberships_rub: row.revenue_portal_memberships_rub == null ? null : Number(row.revenue_portal_memberships_rub),
      revenue_listok_memberships_rub: row.revenue_listok_memberships_rub == null ? null : Number(row.revenue_listok_memberships_rub),
      revenue_selected_memberships_rub: row.revenue_selected_memberships_rub == null ? null : Number(row.revenue_selected_memberships_rub),
      revenue_memberships_selected_source: row.revenue_memberships_selected_source ?? null,
      revenue_portal_subscriptions_rub: row.revenue_portal_subscriptions_rub == null ? null : Number(row.revenue_portal_subscriptions_rub),
      revenue_listok_subscriptions_rub: row.revenue_listok_subscriptions_rub == null ? null : Number(row.revenue_listok_subscriptions_rub),
      revenue_selected_subscriptions_rub: row.revenue_selected_subscriptions_rub == null ? null : Number(row.revenue_selected_subscriptions_rub),
      revenue_subscriptions_selected_source: row.revenue_subscriptions_selected_source ?? null,
      royalty_revenue_rub: row.royalty_revenue_rub == null ? null : Number(row.royalty_revenue_rub),
      royalty_percent: row.royalty_percent == null ? null : Number(row.royalty_percent),
      royalty_amount_rub: row.royalty_amount_rub == null ? null : Number(row.royalty_amount_rub),
      royalty_memberships_revenue_rub: row.royalty_memberships_revenue_rub == null ? null : Number(row.royalty_memberships_revenue_rub),
      royalty_subscriptions_revenue_rub: row.royalty_subscriptions_revenue_rub == null ? null : Number(row.royalty_subscriptions_revenue_rub),
      royalty_split_royalty_rub: row.royalty_split_royalty_rub == null ? null : Number(row.royalty_split_royalty_rub),
      royalty_split_agency_rub: row.royalty_split_agency_rub == null ? null : Number(row.royalty_split_agency_rub),
      royalty_manual_royalty_rub: row.royalty_manual_royalty_rub == null ? null : Number(row.royalty_manual_royalty_rub),
      royalty_listok_crm_rub: row.royalty_listok_crm_rub == null ? null : Number(row.royalty_listok_crm_rub),
      adjustment_rub: row.adjustment_rub == null ? null : Number(row.adjustment_rub),
      adjustment_comment: row.adjustment_comment ?? null,
      adjustment_updated_at: row.adjustment_updated_at ?? null,
      adjustment_updated_by_user_id: row.adjustment_updated_by_user_id ?? null,
    })
  }

  type SegmentAccrualSnapshot = RoyaltyAccrualSegmentSnapshot
  const segmentAccrualByKey = new Map<string, SegmentAccrualSnapshot>()
  const segmentSnapshotTotalsBySeriesId = new Map<number, number>()
  for (const row of ((segmentAccrualRes.data ?? []) as Array<any>)) {
    const segmentNo = Number(row.segment_no ?? 1)
    const seriesId = Number(row.series_id)
    segmentSnapshotTotalsBySeriesId.set(
      seriesId,
      roundMoney((segmentSnapshotTotalsBySeriesId.get(seriesId) ?? 0) + Number(row.revenue_selected_rub ?? 0))
    )
    segmentAccrualByKey.set(`${row.series_id}|${segmentNo}`, {
      segment_no: segmentNo,
      segment_start: row.segment_start ?? null,
      segment_end: row.segment_end ?? null,
      contract_id: row.contract_id == null ? null : Number(row.contract_id),
      term_id: row.term_id == null ? null : Number(row.term_id),
      process_mode: row.process_mode ?? null,
      revenue_portal_rub: Number(row.revenue_portal_rub ?? 0),
      revenue_listok_rub: Number(row.revenue_listok_rub ?? 0),
      revenue_selected_rub: Number(row.revenue_selected_rub ?? 0),
      revenue_selected_source: row.revenue_selected_source ?? 'equal',
      revenue_portal_memberships_rub: Number(row.revenue_portal_memberships_rub ?? 0),
      revenue_listok_memberships_rub: Number(row.revenue_listok_memberships_rub ?? 0),
      revenue_selected_memberships_rub: Number(row.revenue_selected_memberships_rub ?? 0),
      revenue_memberships_selected_source: row.revenue_memberships_selected_source ?? 'equal',
      revenue_portal_subscriptions_rub: Number(row.revenue_portal_subscriptions_rub ?? 0),
      revenue_listok_subscriptions_rub: Number(row.revenue_listok_subscriptions_rub ?? 0),
      revenue_selected_subscriptions_rub: Number(row.revenue_selected_subscriptions_rub ?? 0),
      revenue_subscriptions_selected_source: row.revenue_subscriptions_selected_source ?? 'equal',
      royalty_revenue_rub: row.royalty_revenue_rub == null ? undefined : Number(row.royalty_revenue_rub),
      royalty_percent: row.royalty_percent == null ? null : Number(row.royalty_percent),
      royalty_amount_rub: row.royalty_amount_rub == null ? undefined : Number(row.royalty_amount_rub),
      royalty_memberships_revenue_rub: row.royalty_memberships_revenue_rub == null ? undefined : Number(row.royalty_memberships_revenue_rub),
      royalty_subscriptions_revenue_rub: row.royalty_subscriptions_revenue_rub == null ? undefined : Number(row.royalty_subscriptions_revenue_rub),
      royalty_split_royalty_rub: row.royalty_split_royalty_rub == null ? undefined : Number(row.royalty_split_royalty_rub),
      royalty_split_agency_rub: row.royalty_split_agency_rub == null ? undefined : Number(row.royalty_split_agency_rub),
      royalty_manual_royalty_rub: row.royalty_manual_royalty_rub == null ? undefined : Number(row.royalty_manual_royalty_rub),
      royalty_listok_crm_rub: row.royalty_listok_crm_rub == null ? undefined : Number(row.royalty_listok_crm_rub),
      royalty_fixed_royalty_rub: row.royalty_fixed_royalty_rub == null ? undefined : Number(row.royalty_fixed_royalty_rub),
      // §7.7 A: нативная сторона снимка. Читать её обязательно вместе с рублями — иначе
      // экран показывает сохранённый рубль рядом с ЖИВЫМ пересчётом натива, и расхождение
      // «выставлено ≠ показано» становится невидимым (боевой случай: Гродно, июль 2026 —
      // в charge 37 076,12 BYN, на экране 37 694,23 BYN, никакого признака).
      revenue_portal_native: row.revenue_portal_native == null ? undefined : Number(row.revenue_portal_native),
      revenue_listok_native: row.revenue_listok_native == null ? undefined : Number(row.revenue_listok_native),
      revenue_selected_native: row.revenue_selected_native == null ? undefined : Number(row.revenue_selected_native),
      revenue_portal_memberships_native: row.revenue_portal_memberships_native == null ? undefined : Number(row.revenue_portal_memberships_native),
      revenue_listok_memberships_native: row.revenue_listok_memberships_native == null ? undefined : Number(row.revenue_listok_memberships_native),
      revenue_selected_memberships_native: row.revenue_selected_memberships_native == null ? undefined : Number(row.revenue_selected_memberships_native),
      revenue_portal_subscriptions_native: row.revenue_portal_subscriptions_native == null ? undefined : Number(row.revenue_portal_subscriptions_native),
      revenue_listok_subscriptions_native: row.revenue_listok_subscriptions_native == null ? undefined : Number(row.revenue_listok_subscriptions_native),
      revenue_selected_subscriptions_native: row.revenue_selected_subscriptions_native == null ? undefined : Number(row.revenue_selected_subscriptions_native),
      royalty_revenue_native: row.royalty_revenue_native == null ? undefined : Number(row.royalty_revenue_native),
      royalty_amount_native: row.royalty_amount_native == null ? undefined : Number(row.royalty_amount_native),
      royalty_memberships_revenue_native: row.royalty_memberships_revenue_native == null ? undefined : Number(row.royalty_memberships_revenue_native),
      royalty_subscriptions_revenue_native: row.royalty_subscriptions_revenue_native == null ? undefined : Number(row.royalty_subscriptions_revenue_native),
    })
  }

  // Адресные корректировки по obligation-line, ключ `series|segment_no|charge_type`.
  const componentAdjustmentByKey = new Map<string, RoyaltyChargeComponentAdjustment>()
  for (const row of ((chargeAdjustmentsRes.data ?? []) as Array<{
    series_id: number
    segment_no: number | null
    charge_type: string
    adjustment_type: string
    amount_rub: number | null
    reason: string | null
    updated_at: string | null
    updated_by_user_id: string | null
  }>)) {
    const segmentNo = row.segment_no == null ? 1 : Number(row.segment_no)
    componentAdjustmentByKey.set(`${row.series_id}|${segmentNo}|${row.charge_type}`, {
      charge_type: row.charge_type as RoyaltyChargeComponentAdjustment['charge_type'],
      adjustment_type: row.adjustment_type === 'REDUCE' ? 'REDUCE' : 'ADD',
      amount_rub: Number(row.amount_rub ?? 0),
      reason: row.reason ?? null,
      updated_at: row.updated_at ?? null,
      updated_by_user_id: row.updated_by_user_id ?? null,
    })
  }
  const correctableChargeTypesFor = (mode: 'regular' | 'subscription'): RoyaltyChargeComponentAdjustment['charge_type'][] =>
    mode === 'subscription' ? ['MANUAL_ROYALTY', 'LISTOK_CRM'] : ['ROYALTY', 'FIXED_ROYALTY']
  const componentAdjustmentsFor = (seriesId: number, segmentNo: number, mode: 'regular' | 'subscription') =>
    correctableChargeTypesFor(mode)
      .map((chargeType) => componentAdjustmentByKey.get(`${seriesId}|${segmentNo}|${chargeType}`))
      .filter((value): value is RoyaltyChargeComponentAdjustment => Boolean(value))

  // Знаковая сумма адресных корректировок (ADD → +, REDUCE → −). Минималка её не учитывает;
  // после min она применяется по политике конкретного charge-компонента.
  const componentAdjustmentsTotal = (adjustments: RoyaltyChargeComponentAdjustment[]) =>
    roundMoney(adjustments.reduce(
      (sum, adj) => sum + (adj.adjustment_type === 'REDUCE' ? -adj.amount_rub : adj.amount_rub),
      0
    ))

  const royaltyMetadataUserIds = Array.from(new Set([
    ...[...accrualBySeriesId.values()]
      .map((row) => row.adjustment_updated_by_user_id)
      .filter((value): value is string => Boolean(value)),
    ...[...balanceBySeriesId.values()]
      .map((row) => row.accounting_confirmed_by_user_id)
      .filter((value): value is string => Boolean(value)),
  ]))
  const royaltyUserDisplayName = new Map<string, string>()
  if (royaltyMetadataUserIds.length > 0) {
    const { data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('list_royalty_user_display_names', {
        p_user_ids: royaltyMetadataUserIds,
      })
    )
    if (error) {
      console.error('Failed to load royalty metadata user display names:', error)
      throw error
    }
    for (const row of ((data ?? []) as Array<{ user_id: string | null; display_name: string | null }>)) {
      if (!row.user_id) continue
      royaltyUserDisplayName.set(row.user_id, row.display_name ?? row.user_id)
    }
  }

  const adjustmentHistoryBySeriesId = new Map<number, RoyaltyAdjustmentBalanceHint[]>()
  const historyPeriodKeys = new Set(historyPeriods.map((period) => `${period.year}-${period.month}`))
  for (const row of ((settlementHistoryRes.data ?? []) as Array<{
    series_id: number
    year: number | null
    month: number | null
    closing_balance_rub: number | null
    debt_rub?: number | null
    credit_rub?: number | null
  }>)) {
    if (!Number.isFinite(row.year) || !Number.isFinite(row.month)) continue
    if (!historyPeriodKeys.has(`${row.year}-${row.month}`)) continue
    const debtRub = row.debt_rub == null ? undefined : Number(row.debt_rub)
    const creditRub = row.credit_rub == null ? undefined : Number(row.credit_rub)
    if (
      Math.abs(Number(row.closing_balance_rub ?? 0)) <= 0.005
      && (debtRub ?? 0) <= 0.005
      && (creditRub ?? 0) <= 0.005
    ) continue
    const history = adjustmentHistoryBySeriesId.get(row.series_id) ?? []
    history.push({
      year: Number(row.year),
      month: Number(row.month),
      closing_balance_rub: Number(row.closing_balance_rub ?? 0),
      debt_rub: debtRub,
      credit_rub: creditRub,
    })
    adjustmentHistoryBySeriesId.set(row.series_id, history)
  }
  for (const history of adjustmentHistoryBySeriesId.values()) {
    history.sort((left, right) => {
      if (left.year !== right.year) return right.year - left.year
      return right.month - left.month
    })
  }

  const portalRevenueByStudioId = new Map<number, { total: number; memberships: number; subscriptions: number }>()
  for (const row of ((appRevenueRes.data ?? []) as Array<{
    studio_internal_id: number | null
    gross_revenue_rub: number | null
    new_sales_amount_rub: number | null
    trial_payments_amount_rub: number | null
    renewals_amount_rub: number | null
    upgrades_amount_rub: number | null
    returning_clients_amount_rub: number | null
    private_sessions_amount_rub: number | null
    other_income_rub: number | null
    refunds_amount_rub: number | null
    new_subscriptions_amount_rub: number | null
    membership_fees_new_amount_rub: number | null
    subscription_renewals_amount_rub: number | null
    subscription_upgrades_amount_rub: number | null
    subscription_returning_clients_amount_rub: number | null
    membership_fees_subscription_upgrades_amount_rub: number | null
    membership_fees_subscription_returning_amount_rub: number | null
  }>)) {
    if (!Number.isFinite(row.studio_internal_id)) continue
    const studioId = row.studio_internal_id as number
    const current = portalRevenueByStudioId.get(studioId) ?? { total: 0, memberships: 0, subscriptions: 0 }
    const split = calculateAppRevenueSplitFromRubRow(row)
    current.total += Number(row.gross_revenue_rub ?? 0)
    current.memberships += split.memberships
    current.subscriptions += split.subscriptions
    portalRevenueByStudioId.set(studioId, current)
  }

  const confirmationByStudioId = new Map<number, { revenue_days_count: number; revenue_confirmed_days_count: number }>()
  for (const row of effectiveRevenueConfirmationRows) {
    if (!Number.isFinite(row.studio_internal_id)) continue
    const studioId = row.studio_internal_id as number
    const current = confirmationByStudioId.get(studioId) ?? {
      revenue_days_count: 0,
      revenue_confirmed_days_count: 0,
    }
    if (Number(row.gross_revenue ?? 0) > 0) {
      current.revenue_days_count += 1
      if (row.gross_revenue_confirmed === true) current.revenue_confirmed_days_count += 1
    }
    confirmationByStudioId.set(studioId, current)
  }

  const listokRevenueByStudioNumber = new Map<string, { total: number; memberships: number; subscriptions: number }>()
  for (const row of ((listokRevenueRes.data ?? []) as Array<{
    studio_number: string | number | null
    amount_rub: number | null
    subscription_sale: string | null
  }>)) {
    const studioNumberKey = row.studio_number == null ? '' : String(row.studio_number)
    if (!studioNumberKey) continue
    const current = listokRevenueByStudioNumber.get(studioNumberKey) ?? { total: 0, memberships: 0, subscriptions: 0 }
    const amountRub = Number(row.amount_rub ?? 0)
    current.total += amountRub
    if (isListokSubscriptionSale(row.subscription_sale)) {
      current.subscriptions += amountRub
    } else {
      current.memberships += amountRub
    }
    listokRevenueByStudioNumber.set(studioNumberKey, current)
  }

  const subscriptionTypeByStudioId = new Map<number, StudioSubscriptionType>()
  for (const row of ((subscriptionSettingsRes.data ?? []) as Array<{
    studio_internal_id: number
    subscription_type: StudioSubscriptionType | null
  }>)) {
    subscriptionTypeByStudioId.set(row.studio_internal_id, row.subscription_type ?? 'REGULAR')
  }

  const toProcessMode = (subscriptionType: StudioSubscriptionType | null | undefined): 'regular' | 'subscription' => (
    subscriptionType === 'REGULAR' || subscriptionType == null ? 'regular' : 'subscription'
  )

  type StudioStatusHistoryRow = {
    history_id: number | null
    studio_internal_id: number
    old_status_code: string | null
    new_status_code: string | null
    changed_at: string | null
  }

  const compareStatusHistoryRows = (left: StudioStatusHistoryRow, right: StudioStatusHistoryRow): number => {
    const leftDate = String(left.changed_at ?? '')
    const rightDate = String(right.changed_at ?? '')
    if (leftDate !== rightDate) return leftDate.localeCompare(rightDate)
    return Number(left.history_id ?? 0) - Number(right.history_id ?? 0)
  }

  const currentStatusByStudioId = new Map<number, string>()
  for (const row of ((allStudiosRes.data ?? studioRes.data ?? []) as Array<{
    studio_internal_id: number
    status_code: string | null
  }>)) {
    currentStatusByStudioId.set(row.studio_internal_id, row.status_code ?? 'CLOSED')
  }

  const subscriptionHistoryByStudioId = new Map<number, Array<{
    old_subscription_type: StudioSubscriptionType | null
    new_subscription_type: StudioSubscriptionType | null
    changed_at: string
  }>>()
  for (const row of ((subscriptionHistoryRes.data ?? []) as Array<{
    studio_internal_id: number
    old_subscription_type: StudioSubscriptionType | null
    new_subscription_type: StudioSubscriptionType | null
    changed_at: string | null
  }>)) {
    if (!row.changed_at) continue
    const current = subscriptionHistoryByStudioId.get(row.studio_internal_id) ?? []
    current.push({
      old_subscription_type: row.old_subscription_type ?? null,
      new_subscription_type: row.new_subscription_type ?? null,
      changed_at: row.changed_at,
    })
    subscriptionHistoryByStudioId.set(row.studio_internal_id, current)
  }

  const statusHistoryByStudioId = new Map<number, StudioStatusHistoryRow[]>()
  for (const row of ((allStatusHistoryRes.data ?? statusHistoryRes.data ?? []) as StudioStatusHistoryRow[])) {
    if (!row.changed_at) continue
    const current = statusHistoryByStudioId.get(row.studio_internal_id) ?? []
    current.push(row)
    statusHistoryByStudioId.set(row.studio_internal_id, current)
  }

  const getEffectiveSubscriptionType = (
    studioId: number | null,
    metricDate: string
  ): StudioSubscriptionType => {
    if (studioId == null) return 'REGULAR'

    const configuredType = subscriptionTypeByStudioId.get(studioId) ?? 'REGULAR'
    const historyRows = subscriptionHistoryByStudioId.get(studioId) ?? []
    const latestPastChange = [...historyRows]
      .reverse()
      .find((row) => String(row.changed_at).slice(0, 10) <= metricDate)
    if (latestPastChange?.new_subscription_type) return latestPastChange.new_subscription_type

    const nearestFutureChange = historyRows.find((row) => String(row.changed_at).slice(0, 10) > metricDate)
    if (nearestFutureChange?.old_subscription_type) return nearestFutureChange.old_subscription_type

    return configuredType
  }

  const getEffectiveStudioStatus = (
    studioId: number | null,
    metricDate: string
  ): string => {
    if (studioId == null) return 'CLOSED'

    const currentStatus = currentStatusByStudioId.get(studioId) ?? 'CLOSED'
    const historyRows = statusHistoryByStudioId.get(studioId) ?? []
    const latestPastChange = [...historyRows]
      .sort((left, right) => compareStatusHistoryRows(right, left))
      .find((row) => String(row.changed_at).slice(0, 10) <= metricDate)
    if (latestPastChange?.new_status_code) return latestPastChange.new_status_code

    const nearestFutureChange = [...historyRows]
      .sort(compareStatusHistoryRows)
      .find((row) => String(row.changed_at).slice(0, 10) > metricDate)
    if (nearestFutureChange?.old_status_code) return nearestFutureChange.old_status_code

    return currentStatus
  }

  const isActiveStudioStatus = (statusCode: string | null | undefined): boolean => (
    statusCode === 'PRE-OPEN' ||
    statusCode === 'OPEN' ||
    statusCode === 'SUSPENDED' ||
    statusCode === 'SERVICE'
  )

  const addByDateRevenue = (
    target: Map<string, { total: number; memberships: number; subscriptions: number }>,
    dateKey: string,
    value: { total: number; memberships: number; subscriptions: number }
  ) => {
    const current = target.get(dateKey) ?? { total: 0, memberships: 0, subscriptions: 0 }
    current.total += value.total
    current.memberships += value.memberships
    current.subscriptions += value.subscriptions
    target.set(dateKey, current)
  }

  const portalRevenueByStudioDate = new Map<number, Map<string, { total: number; memberships: number; subscriptions: number }>>()
  const portalRefundsByStudioDate = new Map<number, Map<string, number>>()
  for (const row of ((appRevenueRes.data ?? []) as Array<{
    studio_internal_id: number | null
    metric_date: string | null
    gross_revenue_rub: number | null
    new_sales_amount_rub: number | null
    trial_payments_amount_rub: number | null
    renewals_amount_rub: number | null
    upgrades_amount_rub: number | null
    returning_clients_amount_rub: number | null
    private_sessions_amount_rub: number | null
    other_income_rub: number | null
    refunds_amount_rub: number | null
    new_subscriptions_amount_rub: number | null
    membership_fees_new_amount_rub: number | null
    subscription_renewals_amount_rub: number | null
    subscription_upgrades_amount_rub: number | null
    subscription_returning_clients_amount_rub: number | null
    membership_fees_subscription_upgrades_amount_rub: number | null
    membership_fees_subscription_returning_amount_rub: number | null
  }>)) {
    if (!Number.isFinite(row.studio_internal_id) || !row.metric_date) continue
    const studioId = row.studio_internal_id as number
    const byDate = portalRevenueByStudioDate.get(studioId) ?? new Map<string, { total: number; memberships: number; subscriptions: number }>()
    const split = calculateAppRevenueSplitFromRubRow(row)
    addByDateRevenue(byDate, row.metric_date, {
      total: Number(row.gross_revenue_rub ?? 0),
      memberships: split.memberships,
      subscriptions: split.subscriptions,
    })
    portalRevenueByStudioDate.set(studioId, byDate)
    const refundsByDate = portalRefundsByStudioDate.get(studioId) ?? new Map<string, number>()
    refundsByDate.set(
      row.metric_date,
      roundMoney((refundsByDate.get(row.metric_date) ?? 0) + Number(row.refunds_amount_rub ?? 0))
    )
    portalRefundsByStudioDate.set(studioId, refundsByDate)
  }

  const confirmationByStudioDate = new Map<number, Map<string, { revenue_days_count: number; revenue_confirmed_days_count: number }>>()
  for (const row of effectiveRevenueConfirmationRows) {
    if (!Number.isFinite(row.studio_internal_id) || !row.metric_date) continue
    const studioId = row.studio_internal_id as number
    const byDate = confirmationByStudioDate.get(studioId) ?? new Map<string, { revenue_days_count: number; revenue_confirmed_days_count: number }>()
    byDate.set(row.metric_date, {
      revenue_days_count: Number(row.gross_revenue ?? 0) > 0 ? 1 : 0,
      revenue_confirmed_days_count: Number(row.gross_revenue ?? 0) > 0 && row.gross_revenue_confirmed === true ? 1 : 0,
    })
    confirmationByStudioDate.set(studioId, byDate)
  }

  const listokRevenueByStudioNumberDate = new Map<string, Map<string, { total: number; memberships: number; subscriptions: number }>>()
  const listokMembershipRefundsByStudioNumberDate = new Map<string, Map<string, number>>()
  for (const row of ((listokRevenueRes.data ?? []) as Array<{
    studio_number: string | number | null
    date: string | null
    amount_rub: number | null
    subscription_sale: string | null
  }>)) {
    const studioNumberKey = row.studio_number == null ? '' : String(row.studio_number)
    if (!studioNumberKey || !row.date) continue
    const byDate = listokRevenueByStudioNumberDate.get(studioNumberKey) ?? new Map<string, { total: number; memberships: number; subscriptions: number }>()
    const amountRub = Number(row.amount_rub ?? 0)
    addByDateRevenue(byDate, row.date, {
      total: amountRub,
      memberships: isListokSubscriptionSale(row.subscription_sale) ? 0 : amountRub,
      subscriptions: isListokSubscriptionSale(row.subscription_sale) ? amountRub : 0,
    })
    listokRevenueByStudioNumberDate.set(studioNumberKey, byDate)
    // Legacy-расчёт возвратов по дневному агрегату: используется только когда реестр
    // платежей за месяц неполон, RPC недоступен или RPC нашёл неконвертируемую валюту.
    if (amountRub < 0 && !isListokSubscriptionSale(row.subscription_sale)) {
      const refundsByDate = listokMembershipRefundsByStudioNumberDate.get(studioNumberKey) ?? new Map<string, number>()
      refundsByDate.set(row.date, roundMoney((refundsByDate.get(row.date) ?? 0) + amountRub))
      listokMembershipRefundsByStudioNumberDate.set(studioNumberKey, refundsByDate)
    }
  }

  const toDateKey = (value: string | null | undefined): string | null => {
    return toRoyaltyBusinessDateKey(value)
  }
  const sumAmountsForRange = (
    valuesByDate: Map<string, number> | undefined,
    rangeStart: string,
    rangeEnd: string
  ): number => {
    if (!valuesByDate) return 0
    let total = 0
    for (const [date, value] of valuesByDate) {
      if (date >= rangeStart && date <= rangeEnd) total += value
    }
    return roundMoney(total)
  }

  const parseIsoDate = (value: string): Date => new Date(`${value}T00:00:00Z`)
  const formatIsoDate = (value: Date): string => value.toISOString().slice(0, 10)
  const latestDateKey = (values: Array<string | null | undefined>): string | null => (
    values
      .map(toDateKey)
      .filter((value): value is string => Boolean(value))
      .sort((left, right) => right.localeCompare(left))[0] ?? null
  )

  const activeContractIds = activeContracts
    .map((row) => row.contract_id)
    .filter((value): value is number => Number.isFinite(value))
  const { data: activeContractLegalRows, error: activeContractLegalError } = activeContractIds.length === 0
    ? { data: [], error: null as any }
    : await withAuthRecovery(() =>
        (supabase as any)
          .from('contract_legal_state_v')
          .select('contract_id,effective_from,current_expected_end_date,actual_end_date')
          .in('contract_id', activeContractIds)
      )
  if (activeContractLegalError && (activeContractLegalError as any)?.code !== '42P01') {
    console.warn('Failed to load legal dates for royalty active contracts:', activeContractLegalError)
  }

  const activeContractLegalById = new Map<number, {
    effective_from: string | null
    current_expected_end_date: string | null
    actual_end_date: string | null
  }>()
  if (!activeContractLegalError) {
    for (const row of ((activeContractLegalRows ?? []) as Array<{
      contract_id: number
      effective_from: string | null
      current_expected_end_date: string | null
      actual_end_date: string | null
    }>)) {
      activeContractLegalById.set(row.contract_id, {
        effective_from: row.effective_from ?? null,
        current_expected_end_date: row.current_expected_end_date ?? null,
        actual_end_date: row.actual_end_date ?? null,
      })
    }
  }

  type ActiveContractPeriod = {
    contract_id: number
    contract_number: string | null
    period_start: string | null
    period_end: string | null
    actual_start: string | null
    actual_end: string | null
    expected_end_date: string | null
    actual_end_date: string | null
  }
  const activeContractsBySeriesId = new Map<number, ActiveContractPeriod[]>()
  for (const row of activeContracts) {
    const legal = activeContractLegalById.get(row.contract_id)
    const contractStart = toDateKey(legal?.effective_from) ?? toDateKey(row.effective_from) ?? toDateKey(row.signed_at) ?? monthStart
    const contractEnd = toDateKey(legal?.actual_end_date) ?? toDateKey(row.actual_end_date)
    if (contractStart > monthEnd || (contractEnd != null && contractEnd < monthStart)) continue

    const list = activeContractsBySeriesId.get(row.series_id) ?? []
    list.push({
      contract_id: row.contract_id,
      contract_number: row.contract_number ?? null,
      period_start: contractStart > monthStart ? contractStart : monthStart,
      period_end: contractEnd && contractEnd < monthEnd ? contractEnd : monthEnd,
      actual_start: contractStart,
      actual_end: contractEnd ?? toDateKey(legal?.current_expected_end_date) ?? toDateKey(row.expected_end_date),
      expected_end_date: toDateKey(legal?.current_expected_end_date) ?? toDateKey(row.expected_end_date),
      actual_end_date: contractEnd,
    })
    activeContractsBySeriesId.set(row.series_id, list)
  }

  const ownershipSeriesIdList = Array.from(new Set(
    ownerships
      .map((row) => row.series_id)
      .filter((value): value is number => Number.isFinite(value))
  ))
  const missingActiveContractSeriesIds = ownershipSeriesIdList.filter((seriesId) => {
    const contracts = activeContractsBySeriesId.get(seriesId)
    return !contracts || contracts.length === 0
  })

  const addRecognizedExpiredContract = (input: {
    contract_id: number
    series_id: number
    contract_number: string | null
    effective_from: string | null
    signed_at?: string | null
    current_expected_end_date: string | null
    actual_end_date: string | null
  }) => {
    const contractStart = toDateKey(input.effective_from) ?? toDateKey(input.signed_at) ?? monthStart
    const expectedEnd = toDateKey(input.current_expected_end_date)
    const actualEnd = toDateKey(input.actual_end_date)

    if (!Number.isFinite(input.series_id) || actualEnd != null || expectedEnd == null) return
    if (contractStart > monthEnd || expectedEnd >= monthEnd) return

    const list = activeContractsBySeriesId.get(input.series_id) ?? []
    if (list.some((contract) => contract.contract_id === input.contract_id)) return

    list.push({
      contract_id: input.contract_id,
      contract_number: input.contract_number ?? null,
      period_start: contractStart > monthStart ? contractStart : monthStart,
      period_end: monthEnd,
      actual_start: contractStart,
      actual_end: expectedEnd,
      expected_end_date: expectedEnd,
      actual_end_date: null,
    })
    activeContractsBySeriesId.set(input.series_id, list)
  }

  if (missingActiveContractSeriesIds.length > 0) {
    let loadedRecognizedExpiredContracts = false

    const { data: legalRows, error: legalRowsError } = await withAuthRecovery(() =>
      (supabase as any)
        .from('contract_legal_state_v')
        .select('contract_id,series_id,contract_number,effective_from,current_expected_end_date,actual_end_date')
        .in('series_id', missingActiveContractSeriesIds)
    )

    if (!legalRowsError) {
      loadedRecognizedExpiredContracts = true
      for (const row of ((legalRows ?? []) as Array<{
        contract_id: number
        series_id: number
        contract_number: string | null
        effective_from: string | null
        current_expected_end_date: string | null
        actual_end_date: string | null
      }>)) {
        addRecognizedExpiredContract(row)
      }
    } else if ((legalRowsError as any)?.code !== '42P01') {
      console.warn('Failed to load recognized expired contracts from legal state view:', legalRowsError)
    }

    if (!loadedRecognizedExpiredContracts) {
      const { data: rawContractRows, error: rawContractRowsError } = await withAuthRecovery(() =>
        (supabase as any)
          .from('contracts')
          .select('contract_id,series_id,contract_number,signed_at,effective_from,expected_end_date,actual_end_date,contract_type')
          .in('series_id', missingActiveContractSeriesIds)
          .eq('contract_type', 'MAIN')
      )

      if (!rawContractRowsError) {
        const rawContracts = (rawContractRows ?? []) as Array<{
          contract_id: number
          series_id: number
          contract_number: string | null
          signed_at: string | null
          effective_from: string | null
          expected_end_date: string | null
          actual_end_date: string | null
        }>
        const contractIds = rawContracts.map((row) => row.contract_id)
        const { data: amendmentRows, error: amendmentRowsError } = contractIds.length === 0
          ? { data: [], error: null as any }
          : await withAuthRecovery(() =>
              (supabase as any)
                .from('contract_amendments')
                .select('contract_id,amendment_type,effective_from,new_expected_end_date')
                .in('contract_id', contractIds)
            )

        if (amendmentRowsError) {
          console.warn('Failed to load contract amendments for recognized expired fallback:', amendmentRowsError)
        }

        const amendmentsByContractId = new Map<number, Array<{
          amendment_type: string | null
          effective_from: string | null
          new_expected_end_date: string | null
        }>>()
        for (const row of ((amendmentRows ?? []) as Array<{
          contract_id: number
          amendment_type: string | null
          effective_from: string | null
          new_expected_end_date: string | null
        }>)) {
          const list = amendmentsByContractId.get(row.contract_id) ?? []
          list.push({
            amendment_type: row.amendment_type ?? null,
            effective_from: row.effective_from ?? null,
            new_expected_end_date: row.new_expected_end_date ?? null,
          })
          amendmentsByContractId.set(row.contract_id, list)
        }

        for (const row of rawContracts) {
          const amendments = amendmentsByContractId.get(row.contract_id) ?? []
          const currentExpectedEnd = latestDateKey([
            row.expected_end_date,
            ...amendments
              .filter((item) => item.amendment_type === 'TERM_EXTENSION')
              .map((item) => item.new_expected_end_date),
          ])
          const terminationEnd = amendments
            .filter((item) => item.amendment_type === 'TERMINATION')
            .map((item) => toDateKey(item.effective_from))
            .filter((value): value is string => Boolean(value))
            .sort((left, right) => left.localeCompare(right))[0]

          addRecognizedExpiredContract({
            contract_id: row.contract_id,
            series_id: row.series_id,
            contract_number: row.contract_number ?? null,
            effective_from: row.effective_from ?? null,
            signed_at: row.signed_at ?? null,
            current_expected_end_date: currentExpectedEnd,
            actual_end_date: row.actual_end_date ?? terminationEnd ?? null,
          })
        }
      } else {
        console.warn('Failed to load recognized expired contracts fallback:', rawContractRowsError)
      }
    }
  }

  for (const list of activeContractsBySeriesId.values()) {
    list.sort((left, right) => {
      const byStart = (left.period_start ?? '').localeCompare(right.period_start ?? '')
      if (byStart !== 0) return byStart
      return left.contract_id - right.contract_id
    })
  }
  const contractCoversDate = (contract: ActiveContractPeriod, date: string): boolean =>
    contract.period_start != null
    && contract.period_end != null
    && date >= contract.period_start
    && date <= contract.period_end
  const contractOverlapsRange = (contract: ActiveContractPeriod, rangeStart: string, rangeEnd: string): boolean =>
    contract.period_start != null
    && contract.period_end != null
    && contract.period_start <= rangeEnd
    && contract.period_end >= rangeStart
  const pickContractForDate = (seriesId: number, date: string): ActiveContractPeriod | null =>
    [...(activeContractsBySeriesId.get(seriesId) ?? [])]
      .filter((contract) => contractCoversDate(contract, date))
      .sort((left, right) => {
        const byStart = (right.period_start ?? '').localeCompare(left.period_start ?? '')
        return byStart !== 0 ? byStart : right.contract_id - left.contract_id
      })[0] ?? null
  const pickContractForSegment = (seriesId: number, segmentStart: string, segmentEnd: string): ActiveContractPeriod | null => {
    const contracts = activeContractsBySeriesId.get(seriesId) ?? []
    return pickContractForDate(seriesId, segmentStart)
      ?? contracts.find((contract) => contractOverlapsRange(contract, segmentStart, segmentEnd))
      ?? null
  }

  const ownershipSeriesIds = new Set(ownershipSeriesIdList)
  const isManagementCompanySeries = (seriesId: number | null | undefined): boolean =>
    seriesId != null && partnerBySeriesId.get(seriesId) === MANAGEMENT_COMPANY_PARTNER_NUMBER
  const isManagementCompanyPartner = (partnerNumber: number | null | undefined): boolean =>
    partnerNumber === MANAGEMENT_COMPANY_PARTNER_NUMBER
  const managementCompanyStudioIds = new Set(
    ownerships
      .filter((row) => isManagementCompanySeries(row.series_id))
      .map((row) => row.studio_internal_id)
      .filter((value): value is number => Number.isFinite(value))
  )

  const ownershipStudioIds = new Set(
    ownerships
      .map((row) => row.studio_internal_id)
      .filter((value): value is number => Number.isFinite(value))
  )

  // The payload deliberately splits one ownership into phase rows (for example,
  // BUYING 01–03 and OWNED 04–30). The charge engine does not use ownership phase as
  // a segment identity axis: it only zeros BUYING/PLANNED revenue and segments by
  // continuity + MAIN contract + royalty term + contract family. Build the whole
  // series-month in one pass so phase rows cannot restart segment_no from 1.
  const ownershipSeriesGroups = groupRoyaltyProcessOwnershipsBySeries(ownerships)
  const ownershipProcessRows = ownershipSeriesGroups
    .flatMap((ownershipSeries) => {
      const seriesId = ownershipSeries.series_id
      const ownershipSlices = ownershipSeries.slices
      const partnerNumber = partnerBySeriesId.get(seriesId) ?? null
      const personsFio = partnerNumber != null ? (personsByPartner.get(partnerNumber) ?? null) : null
      const settlement = settlementBySeriesId.get(seriesId)
      const accrual = accrualBySeriesId.get(seriesId)
      const adjustmentRub = accrual?.adjustment_rub ?? null
      const adjustmentHistory = adjustmentHistoryBySeriesId.get(seriesId) ?? []
      const adjustmentBalanceHint = previousPeriod == null
        ? null
        : adjustmentHistory.find((item) => item.year === previousPeriod.year && item.month === previousPeriod.month) ?? null
      const activeContractsForSeries = activeContractsBySeriesId.get(seriesId) ?? []
      const ownershipStartDate = ownershipSlices.reduce((earliest, slice) => {
        const start = toDateKey(slice.start_date) ?? monthStart
        return start < earliest ? start : earliest
      }, monthEnd)
      // Payload end dates are inclusive. Its phase slices may cover one ownership
      // continuously, or several disjoint ownership runs of the same series.
      const ownershipEndDate = ownershipSlices.reduce((latest, slice) => {
        const end = toDateKey(slice.end_date) ?? monthEnd
        return end > latest ? end : latest
      }, monthStart)
      const periodStart = ownershipStartDate > monthStart ? ownershipStartDate : monthStart
      const periodEnd = ownershipEndDate < monthEnd ? ownershipEndDate : monthEnd
      const allOwnershipSlicesZeroRevenue = ownershipSlices.every((slice) =>
        isZeroRevenueBillableOwnershipPhase(slice.ownership_phase)
      )
      const hasActiveContractInPeriod = activeContractsForSeries.some((contract) =>
        contract.period_start <= periodEnd && contract.period_end >= periodStart
      )

      if (periodStart > periodEnd) return []
      if (allOwnershipSlicesZeroRevenue && !hasActiveContractInPeriod) return []

      type ProcessSegment = {
        identity: string
        family: 'regular' | 'subscription'
        term: ProcessTermInterval | null
        contract: ActiveContractPeriod | null
        studio_internal_id: number | null
        start: string
        end: string
        days_count: number
        studio_status: string | null
        portal_revenue_rub: number
        listok_revenue_rub: number
        portal_memberships_revenue_rub: number
        listok_memberships_revenue_rub: number
        portal_subscriptions_revenue_rub: number
        listok_subscriptions_revenue_rub: number
        // Те же суммы в валюте выручки студии (§7.2). Для RUB-студии совпадают с рублёвыми.
        portal_revenue_native: number
        listok_revenue_native: number
        portal_memberships_revenue_native: number
        listok_memberships_revenue_native: number
        portal_subscriptions_revenue_native: number
        listok_subscriptions_revenue_native: number
        // Те же суммы в валюте ОБЯЗАТЕЛЬСТВА (§1.8): каждый день приведён кросс-курсом
        // своего дня, а не месячная сумма курсом конца месяца. Ровно так же считает
        // движок после перевода на дневной пересчёт — иначе preview ≠ charge.
        portal_revenue_obl: number
        listok_revenue_obl: number
        portal_memberships_revenue_obl: number
        listok_memberships_revenue_obl: number
        portal_subscriptions_revenue_obl: number
        listok_subscriptions_revenue_obl: number
        revenue_days_count: number
        revenue_confirmed_days_count: number
        // Дни сегмента без активного MAIN-договора + оценочная выручка на них (диагностика).
        // Оценка копится в ДВУХ вариантах, потому что источник выручки решается на уровне
        // МЕСЯЦА (см. forceListok ниже), а месяц целиком известен только после дневного цикла:
        // `_rub` — по общему правилу «большей выручки», `_listok_rub` — по листку.
        uncovered_days_count: number
        uncovered_revenue_rub: number
        uncovered_revenue_listok_rub: number
        term_gap_days_count: number
        term_gap_revenue_rub: number
        term_gap_revenue_listok_rub: number
      }
      // Ordered continuous runs. Identity alone is insufficient: the same contract
      // and term before/after a real date gap must remain two engine segments.
      const segments: ProcessSegment[] = []

      // Family follows the royalty term's contract_type (that's what defines the charge);
      // falls back to the studio product when the series has no terms.
      const familyForTerm = (term: ProcessTermInterval | null): 'regular' | 'subscription' =>
        term ? (term.contract_type === 'SUBSCRIPTION' ? 'subscription' : 'regular') : null as never
      const segmentIdentityKey = (
        family: 'regular' | 'subscription',
        term: ProcessTermInterval | null,
        contract: ActiveContractPeriod | null
      ): string =>
        `${family}::${term?.term_id ?? term?.effective_from ?? 'noterm'}::${contract?.contract_id ?? 'nocontract'}`

      const ensureSegment = (
        family: 'regular' | 'subscription',
        term: ProcessTermInterval | null,
        contract: ActiveContractPeriod | null,
        studioInternalId: number | null,
        metricDate: string,
        studioStatus: string
      ) => {
        const identity = segmentIdentityKey(family, term, contract)
        const existing = segments[segments.length - 1] ?? null
        if (canExtendRoyaltyProcessSegment(existing, { identity, date: metricDate })) {
          existing.end = metricDate
          existing.days_count += 1
          if (existing.studio_internal_id == null) existing.studio_internal_id = studioInternalId
          return existing
        }

        const created: ProcessSegment = {
          identity,
          family,
          term,
          contract,
          studio_internal_id: studioInternalId,
          start: metricDate,
          end: metricDate,
          days_count: 1,
          studio_status: studioStatus,
          portal_revenue_rub: 0,
          listok_revenue_rub: 0,
          portal_memberships_revenue_rub: 0,
          listok_memberships_revenue_rub: 0,
          portal_subscriptions_revenue_rub: 0,
          listok_subscriptions_revenue_rub: 0,
          portal_revenue_native: 0,
          listok_revenue_native: 0,
          portal_memberships_revenue_native: 0,
          listok_memberships_revenue_native: 0,
          portal_subscriptions_revenue_native: 0,
          listok_subscriptions_revenue_native: 0,
          portal_revenue_obl: 0,
          listok_revenue_obl: 0,
          portal_memberships_revenue_obl: 0,
          listok_memberships_revenue_obl: 0,
          portal_subscriptions_revenue_obl: 0,
          listok_subscriptions_revenue_obl: 0,
          revenue_days_count: 0,
          revenue_confirmed_days_count: 0,
          uncovered_days_count: 0,
          uncovered_revenue_rub: 0,
          uncovered_revenue_listok_rub: 0,
          term_gap_days_count: 0,
          term_gap_revenue_rub: 0,
          term_gap_revenue_listok_rub: 0,
        }
        segments.push(created)
        return created
      }

      for (
          let cursor = parseIsoDate(periodStart);
          cursor <= parseIsoDate(periodEnd);
          cursor.setUTCDate(cursor.getUTCDate() + 1)
        ) {
          const metricDate = formatIsoDate(cursor)
          const dayOwnership = findRoyaltyProcessOwnershipForDate(ownershipSlices, metricDate)
          if (!dayOwnership) continue
          const dayStudioInternalId = dayOwnership.studio_internal_id
          const isZeroRevenueDay = isZeroRevenueBillableOwnershipPhase(dayOwnership.ownership_phase)
          const effectiveStatus = isZeroRevenueDay
            ? (dayOwnership.ownership_phase ?? 'BUYING')
            : getEffectiveStudioStatus(dayStudioInternalId, metricDate)
          if (!isZeroRevenueDay && !isActiveStudioStatus(effectiveStatus)) continue

          const dayContract = pickContractForDate(seriesId, metricDate)
          const term = getTermForDate(seriesId, metricDate, dayContract?.contract_id ?? null)
          const family = term
            ? familyForTerm(term)
            : toProcessMode(getEffectiveSubscriptionType(dayStudioInternalId, metricDate))
          const segment = ensureSegment(
            family,
            term,
            dayContract,
            dayStudioInternalId,
            metricDate,
            effectiveStatus
          )
          if (!isZeroRevenueDay) segment.studio_status = effectiveStatus
          const dayStudio = dayStudioInternalId != null ? studioById.get(dayStudioInternalId) : undefined
          const dayStudioNumber = dayStudio?.studio_number == null ? null : String(dayStudio.studio_number)
          const dayCity = dayStudio?.city_id != null ? cityById.get(dayStudio.city_id) : undefined
          const isForeignRevenueDay = isForeignRoyaltyCountry(dayCity?.country ?? null)
          const portalDay = !isZeroRevenueDay && dayStudioInternalId != null
            ? portalRevenueByStudioDate.get(dayStudioInternalId)?.get(metricDate)
            : undefined
          const listokDay = !isZeroRevenueDay && dayStudioNumber != null
            ? listokRevenueByStudioNumberDate.get(dayStudioNumber)?.get(metricDate)
            : undefined
          const confirmationDay = !isZeroRevenueDay && dayStudioInternalId != null
            ? confirmationByStudioDate.get(dayStudioInternalId)?.get(metricDate)
            : undefined

          // Нативные суммы дня (§7.2). Для рублёвой студии RPC нативных строк не отдаёт —
          // тогда натив тождествен рублю, поэтому падаем на рублёвый день, а не на ноль.
          const isForeignRevenueStudio = dayStudioInternalId != null
            && (revenueCurrencyByStudioId.get(dayStudioInternalId) ?? 'RUB') !== 'RUB'
          const portalDayNative = !isForeignRevenueStudio
            ? portalDay
            : (!isZeroRevenueDay && dayStudioInternalId != null
              ? portalNativeByStudioDate.get(dayStudioInternalId)?.get(metricDate)
              : undefined)
          const listokDayNative = !isForeignRevenueStudio
            ? listokDay
            : (!isZeroRevenueDay && dayStudioNumber != null
              ? listokNativeByStudioNumberDate.get(dayStudioNumber)?.get(metricDate)
              : undefined)

          // §1.8: кросс-курс ДНЯ выручка→обязательство. Курса нет (валюта вне карты ЦБ) →
          // приведения не существует, а не «считаем по единице»: тогда обязательственная
          // сторона остаётся нулевой и вниз по строке уедет month-end фолбэк с подписью.
          const dayRevenueCurrency = revenueCurrencyByStudioId.get(dayStudioInternalId ?? -1) ?? 'RUB'
          const dayObligationCurrency = (segment.term?.term_id != null
            ? termCurrencyByTermId.get(Number(segment.term.term_id))
            : null) ?? 'RUB'
          const dayRevRate = dailyFxToRub(dayRevenueCurrency, metricDate)
          const dayOblRate = dailyFxToRub(dayObligationCurrency, metricDate)
          const dayCrossRate = dayRevRate == null || dayOblRate == null || dayOblRate === 0
            ? null
            : dayRevRate / dayOblRate
          const toObl = (nativeAmount: number): number =>
            dayCrossRate == null ? 0 : roundMoney(nativeAmount * dayCrossRate)

          if (family === 'regular') {
            segment.portal_revenue_obl += toObl(Number(portalDayNative?.total ?? 0))
            segment.listok_revenue_obl += toObl(Number(listokDayNative?.total ?? 0))
            segment.portal_memberships_revenue_obl += toObl(Number(portalDayNative?.total ?? 0))
            segment.listok_memberships_revenue_obl += toObl(Number(listokDayNative?.total ?? 0))
          } else {
            segment.portal_revenue_obl += toObl(Number(portalDayNative?.memberships ?? 0) + Number(portalDayNative?.subscriptions ?? 0))
            segment.listok_revenue_obl += toObl(Number(listokDayNative?.memberships ?? 0) + Number(listokDayNative?.subscriptions ?? 0))
            segment.portal_memberships_revenue_obl += toObl(Number(portalDayNative?.memberships ?? 0))
            segment.listok_memberships_revenue_obl += toObl(Number(listokDayNative?.memberships ?? 0))
            segment.portal_subscriptions_revenue_obl += toObl(Number(portalDayNative?.subscriptions ?? 0))
            segment.listok_subscriptions_revenue_obl += toObl(Number(listokDayNative?.subscriptions ?? 0))
          }

          if (family === 'regular') {
            segment.portal_revenue_rub += Number(portalDay?.total ?? 0)
            segment.listok_revenue_rub += Number(listokDay?.total ?? 0)
            segment.portal_memberships_revenue_rub += Number(portalDay?.total ?? 0)
            segment.listok_memberships_revenue_rub += Number(listokDay?.total ?? 0)
            segment.portal_revenue_native += Number(portalDayNative?.total ?? 0)
            segment.listok_revenue_native += Number(listokDayNative?.total ?? 0)
            segment.portal_memberships_revenue_native += Number(portalDayNative?.total ?? 0)
            segment.listok_memberships_revenue_native += Number(listokDayNative?.total ?? 0)
          } else {
            segment.portal_revenue_rub += Number(portalDay?.memberships ?? 0) + Number(portalDay?.subscriptions ?? 0)
            segment.listok_revenue_rub += Number(listokDay?.memberships ?? 0) + Number(listokDay?.subscriptions ?? 0)
            segment.portal_memberships_revenue_rub += Number(portalDay?.memberships ?? 0)
            segment.listok_memberships_revenue_rub += Number(listokDay?.memberships ?? 0)
            segment.portal_subscriptions_revenue_rub += Number(portalDay?.subscriptions ?? 0)
            segment.listok_subscriptions_revenue_rub += Number(listokDay?.subscriptions ?? 0)
            segment.portal_revenue_native += Number(portalDayNative?.memberships ?? 0) + Number(portalDayNative?.subscriptions ?? 0)
            segment.listok_revenue_native += Number(listokDayNative?.memberships ?? 0) + Number(listokDayNative?.subscriptions ?? 0)
            segment.portal_memberships_revenue_native += Number(portalDayNative?.memberships ?? 0)
            segment.listok_memberships_revenue_native += Number(listokDayNative?.memberships ?? 0)
            segment.portal_subscriptions_revenue_native += Number(portalDayNative?.subscriptions ?? 0)
            segment.listok_subscriptions_revenue_native += Number(listokDayNative?.subscriptions ?? 0)
          }

          segment.revenue_days_count += Number(confirmationDay?.revenue_days_count ?? 0)
          segment.revenue_confirmed_days_count += Number(confirmationDay?.revenue_confirmed_days_count ?? 0)

          const dayListokRevenueRub = family === 'regular'
            ? Number(listokDay?.total ?? 0)
            : Number(listokDay?.memberships ?? 0) + Number(listokDay?.subscriptions ?? 0)
          const dayRevenueRub = family === 'regular'
            ? Math.max(Number(portalDay?.total ?? 0), Number(listokDay?.total ?? 0))
            : ALWAYS_USE_LISTOK_FOR_SUBSCRIPTION && !isForeignRevenueDay
              ? dayListokRevenueRub
              : Math.max(
                Number(portalDay?.memberships ?? 0) + Number(portalDay?.subscriptions ?? 0),
                Number(listokDay?.memberships ?? 0) + Number(listokDay?.subscriptions ?? 0)
              )
          // Тот же день, если месяц окажется подписочным/переходным. Иностранный день листок
          // не форсирует — правило домашнее, поэтому проверка та же, что в основной ветке.
          const dayRevenueRubListokPreferred = ALWAYS_USE_LISTOK_FOR_SUBSCRIPTION && !isForeignRevenueDay
            ? dayListokRevenueRub
            : dayRevenueRub

          // Покрытие MAIN-договором на этот день. Движок начисления требует активный договор
          // на каждый день; непокрытые дни считаем количественно (день + оценочная выручка).
          if (!dayContract) {
            segment.uncovered_days_count += 1
            segment.uncovered_revenue_rub += dayRevenueRub
            segment.uncovered_revenue_listok_rub += dayRevenueRubListokPreferred
          } else if (!term) {
            segment.term_gap_days_count += 1
            segment.term_gap_revenue_rub += dayRevenueRub
            segment.term_gap_revenue_listok_rub += dayRevenueRubListokPreferred
          }
        }

      const sortSegments = (list: ProcessSegment[]): ProcessSegment[] =>
        [...list].sort((left, right) => {
          const byStart = left.start.localeCompare(right.start)
          if (byStart !== 0) return byStart
          const byContract = (left.contract?.contract_id ?? 0) - (right.contract?.contract_id ?? 0)
          if (byContract !== 0) return byContract
          return (left.term?.term_id ?? 0) - (right.term?.term_id ?? 0)
        })
      const allSegments = sortSegments(segments)
      // P-04: движок (`sync_royalty_charge_from_accrual`) начисляет charge только для дней с
      // ownership + активным MAIN-договором + условиями (eligible days); дни без покрытия
      // договором он выбрасывает целиком и НЕ нумерует. Поэтому segment_no считаем ТОЛЬКО по
      // покрытым сегментам — иначе на месяцах с дырой покрытия (студия открылась/договор
      // сменился в середине месяца) фронтовая нумерация разъезжается с движком, а overlay P-03
      // (`apply_royalty_segment_accruals`) и пост-проверка P-21 матчат по (segment_no,
      // charge_type) и попадают не в тот charge. Непокрытые дни остаются только диагностикой.
      const isCoveredSegment = (segment: ProcessSegment): boolean =>
        segment.contract != null && segment.term != null
      const coveredSegments = allSegments.filter(isCoveredSegment)
      // Если покрытых сегментов нет вовсе (весь месяц без действующего договора), показываем
      // непокрытые как диагностические строки — начислить их движок всё равно не даст.
      const orderedSegments = coveredSegments.length > 0 ? coveredSegments : allSegments
      const uncoveredSegments = coveredSegments.length > 0
        ? allSegments.filter((segment) => !isCoveredSegment(segment))
        : []
      // Решение владельца: если в месяце есть подписочный сегмент, выручку по листку берут ОБЕ
      // половины месяца перехода, а не только подписочная. Признак месячный и вычисляется один
      // раз — им пользуются и диагностика непокрытых дней, и forceListok каждой строки.
      const monthHasSubscriptionSegment = orderedSegments.some((segment) => segment.family === 'subscription')
      const monthUncoveredDays = uncoveredSegments.reduce((sum, s) => sum + Math.round(s.uncovered_days_count), 0)
      const monthUncoveredRevenueRub = roundMoney(uncoveredSegments.reduce(
        (sum, s) => sum + (monthHasSubscriptionSegment ? s.uncovered_revenue_listok_rub : s.uncovered_revenue_rub),
        0
      ))
      const monthTermGapDays = uncoveredSegments.reduce((sum, s) => sum + Math.round(s.term_gap_days_count), 0)
      const monthTermGapRevenueRub = roundMoney(uncoveredSegments.reduce(
        (sum, s) => sum + (monthHasSubscriptionSegment ? s.term_gap_revenue_listok_rub : s.term_gap_revenue_rub),
        0
      ))
      const isMultiConditionMonth = orderedSegments.length > 1
      const segmentIdentity = (segment: ProcessSegment): string =>
        `${segment.start}|${segment.contract?.contract_id ?? 'nocontract'}|${segment.term?.term_id ?? 'noterm'}`
      // segment_no как в движке: row_number по (segment_start, contract_id, term_id).
      // orderedSegments уже отсортирован этим ключом и не пересекается по датам, поэтому индекс == движковому номеру.
      const segmentNoByIdentity = new Map<string, number>()
      orderedSegments.forEach((segment, index) => segmentNoByIdentity.set(segmentIdentity(segment), index + 1))
      // LISTOK_CRM — месячный фикс. P-22: он садится только на выбранный оператором
      // договор-носитель CRM-фикса (`royalty_series_terms.is_listok_crm_carrier`), а не на
      // каждую подписочную серию. Носитель = первый покрытый подписочный сегмент, чей терм
      // помечен носителем; если носитель не выбран — фикс не начисляется вовсе.
      const listokCarrierSegmentIdentity = (() => {
        const carrier = orderedSegments.find(
          (segment) => segment.family === 'subscription' && segment.term?.is_listok_crm_carrier === true
        )
        return carrier ? segmentIdentity(carrier) : null
      })()
      // P-29: удержания номинального счёта по дням сегмента. Каждая транзакция округляется
      // до копеек отдельно (`round(платёж × ставка × (1 + НДС), 2)`) — зеркало ветки
      // v_tx_mode в sync_royalty_charge_from_accrual. null = режим не применяется
      // (сегмент раньше даты включения, нет терма или нет студии) → месячный расчёт.
      const perTransactionForSegment = (segment: ProcessSegment): {
        txCount: number
        revenueRub: number
        splitRoyaltyRub: number
        splitAgencyRub: number
      } | null => {
        const segmentTerm = segment.term
        if (segment.studio_internal_id == null || !segmentTerm) return null
        if (!perTransactionEffectiveFrom || segment.start < perTransactionEffectiveFrom) return null
        const vatPercent = Number(segmentTerm.vat_percent ?? 22)
        const royaltyPercent = Number(segmentTerm.royalty_percent ?? 0)
        const agencyPercent = Number(segmentTerm.agency_percent ?? 0)
        let txCount = 0
        let revenueRub = 0
        let splitRoyaltyRub = 0
        let splitAgencyRub = 0
        for (const date of eachDateInclusive(segment.start, segment.end)) {
          const dayOwnership = findRoyaltyProcessOwnershipForDate(ownershipSlices, date)
          if (!dayOwnership || isZeroRevenueBillableOwnershipPhase(dayOwnership.ownership_phase)) continue
          const studioInternalId = dayOwnership.studio_internal_id
          if (studioInternalId == null) continue
          const byDate = txAmountsByStudioDate.get(studioInternalId)
          for (const amount of (byDate?.get(date) ?? [])) {
            txCount += 1
            revenueRub += amount
            splitRoyaltyRub += roundMoney((amount * royaltyPercent) / 100 * (1 + vatPercent / 100))
            splitAgencyRub += roundMoney((amount * agencyPercent) / 100 * (1 + vatPercent / 100))
          }
        }
        return {
          txCount,
          revenueRub: roundMoney(revenueRub),
          splitRoyaltyRub: roundMoney(splitRoyaltyRub),
          splitAgencyRub: roundMoney(splitAgencyRub),
        }
      }

      const segmentRows = orderedSegments
        .map((segment, segmentIndex) => {
          const processMode = segment.family
          const term = segment.term
          const segmentStudioInternalId = segment.studio_internal_id
          const studio = segmentStudioInternalId != null ? studioById.get(segmentStudioInternalId) : undefined
          const city = studio?.city_id != null ? cityById.get(studio.city_id) : undefined
          const country = city?.country ?? null
          const isForeignStudio = isForeignRoyaltyCountry(country)
          const studioNumber = studio?.studio_number == null ? null : String(studio.studio_number)
          const segmentNo = segmentNoByIdentity.get(segmentIdentity(segment)) ?? 1
          const persistedSegmentAccrual = segmentAccrualByKey.get(`${seriesId}|${segmentNo}`)
          // A segment number is only an ordering label, not a stable identity. Contract edits can
          // turn old `segment 1 / 01-27` into new `segment 1 / 01-31`; reusing the old amount in
          // that case silently drops the added days (studio 131, July 2026). A saved value may be
          // reused only when the complete billing identity still matches.
          const segmentAccrualIdentityMatches = persistedSegmentAccrual != null
            && persistedSegmentAccrual.segment_start === segment.start
            && persistedSegmentAccrual.segment_end === segment.end
            && persistedSegmentAccrual.contract_id === (segment.contract?.contract_id ?? null)
            && persistedSegmentAccrual.term_id === (segment.term?.term_id ?? null)
            && persistedSegmentAccrual.process_mode === segment.family
          const segmentAccrual = segmentAccrualIdentityMatches ? persistedSegmentAccrual : undefined
          const monthlySnapshotSelectedRub = accrual?.revenue_selected_rub ?? null
          const segmentSnapshotsTotalRub = segmentSnapshotTotalsBySeriesId.get(seriesId) ?? null
          const revenueSnapshotSumMismatch = Boolean(accrual?.revenue_checked)
            && monthlySnapshotSelectedRub != null
            && segmentSnapshotsTotalRub != null
            && Math.abs(monthlySnapshotSelectedRub - segmentSnapshotsTotalRub) > 0.005
          const revenueSnapshotIdentityStale = Boolean(accrual?.revenue_checked)
            && !segmentAccrualIdentityMatches
          const balance = balanceBySegmentKey.get(balanceSegmentKey(seriesId, segmentNo))
            ?? (!isMultiConditionMonth ? balanceBySeriesId.get(seriesId) : undefined)
          const useSavedRevenueSelection = Boolean(accrual?.revenue_checked) && (!isMultiConditionMonth || Boolean(segmentAccrual))
          // Правило домашних студий: источник решается на уровне МЕСЯЦА, а не сегмента. В месяце
          // перехода абонемент→подписка по листку идут ОБЕ половины, иначе одна строка месяца
          // считается по листку, а соседняя по «большей выручке» (боевой случай: студия 217
          // Домодедово, июнь 2026 — абонементный сегмент 01–12.06 ушёл на портал, +349,85 ₽ базы).
          const forceListok = ALWAYS_USE_LISTOK_FOR_SUBSCRIPTION
            && (processMode === 'subscription' || monthHasSubscriptionSegment)
            && !isForeignStudio
          const previewMembershipsRevenueRub = forceListok
            ? roundMoney(segment.listok_memberships_revenue_rub)
            : roundMoney(Math.max(
              segment.portal_memberships_revenue_rub,
              segment.listok_memberships_revenue_rub
            ))
          const previewSubscriptionsRevenueRub = forceListok
            ? roundMoney(segment.listok_subscriptions_revenue_rub)
            : roundMoney(Math.max(
              segment.portal_subscriptions_revenue_rub,
              segment.listok_subscriptions_revenue_rub
            ))
          const previewSelectedRevenueRub = roundMoney(previewMembershipsRevenueRub + previewSubscriptionsRevenueRub)
          const portalRevenueRub = roundMoney(segment.portal_revenue_rub)
          const listokRevenueRub = roundMoney(segment.listok_revenue_rub)
          const previewSelectedRevenueSource = resolveRevenueSource(portalRevenueRub, listokRevenueRub, forceListok)
          const previewMembershipsRevenueSource = resolveRevenueSource(
            roundMoney(segment.portal_memberships_revenue_rub),
            roundMoney(segment.listok_memberships_revenue_rub),
            forceListok
          )
          const previewSubscriptionsRevenueSource = resolveRevenueSource(
            roundMoney(segment.portal_subscriptions_revenue_rub),
            roundMoney(segment.listok_subscriptions_revenue_rub),
            forceListok
          )
          const selectedRevenueRub = useSavedRevenueSelection && segmentAccrual?.revenue_selected_rub != null
            ? roundMoney(segmentAccrual.revenue_selected_rub)
            : useSavedRevenueSelection && accrual?.revenue_selected_rub != null
              ? roundMoney(accrual.revenue_selected_rub)
            : previewSelectedRevenueRub
          const selectedRevenueSource = useSavedRevenueSelection && segmentAccrual?.revenue_selected_source != null
            ? segmentAccrual.revenue_selected_source
            : useSavedRevenueSelection && accrual?.revenue_selected_source != null
              ? accrual.revenue_selected_source
            : previewSelectedRevenueSource
          const selectedMembershipsRevenueRub = useSavedRevenueSelection && segmentAccrual?.revenue_selected_memberships_rub != null
            ? roundMoney(segmentAccrual.revenue_selected_memberships_rub)
            : useSavedRevenueSelection && accrual?.revenue_selected_memberships_rub != null
              ? roundMoney(accrual.revenue_selected_memberships_rub)
            : previewMembershipsRevenueRub
          const selectedMembershipsRevenueSource = useSavedRevenueSelection && segmentAccrual?.revenue_memberships_selected_source != null
            ? segmentAccrual.revenue_memberships_selected_source
            : useSavedRevenueSelection && accrual?.revenue_memberships_selected_source != null
              ? accrual.revenue_memberships_selected_source
            : previewMembershipsRevenueSource
          const selectedSubscriptionsRevenueRub = useSavedRevenueSelection && segmentAccrual?.revenue_selected_subscriptions_rub != null
            ? roundMoney(segmentAccrual.revenue_selected_subscriptions_rub)
            : useSavedRevenueSelection && accrual?.revenue_selected_subscriptions_rub != null
              ? roundMoney(accrual.revenue_selected_subscriptions_rub)
            : previewSubscriptionsRevenueRub
          const selectedSubscriptionsRevenueSource = useSavedRevenueSelection && segmentAccrual?.revenue_subscriptions_selected_source != null
            ? segmentAccrual.revenue_subscriptions_selected_source
            : useSavedRevenueSelection && accrual?.revenue_subscriptions_selected_source != null
              ? accrual.revenue_subscriptions_selected_source
              : previewSubscriptionsRevenueSource
          // Нативные суммы этапа 1 (§7.2). Выбранную сторону берём ПО ИСТОЧНИКУ, а не пересчётом
          // max: при сохранённом выборе оператора источник может отличаться от превью, и
          // «выручка к выставлению» обязана показывать в валюте ровно то, что выбрано в рублях.
          const pickNative = (source: RevenueSource, portalNative: number, listokNative: number): number =>
            source === 'listok' ? roundMoney(listokNative) : roundMoney(portalNative)
          const portalRevenueNative = roundMoney(segment.portal_revenue_native)
          const listokRevenueNative = roundMoney(segment.listok_revenue_native)
          const portalMembershipsRevenueNative = roundMoney(segment.portal_memberships_revenue_native)
          const listokMembershipsRevenueNative = roundMoney(segment.listok_memberships_revenue_native)
          const portalSubscriptionsRevenueNative = roundMoney(segment.portal_subscriptions_revenue_native)
          const listokSubscriptionsRevenueNative = roundMoney(segment.listok_subscriptions_revenue_native)
          // Сохранённый выбор оператора канонично хранит и нативную сторону (§7.7 A), поэтому
          // читаем её из снимка ровно так же, как рубли. Иначе в одной ячейке соседствуют
          // сохранённый рубль и живой пересчёт натива — расхождение «выставлено ≠ показано»
          // не видно ничем (Гродно, июль 2026: charge 37 076,12 BYN, экран 37 694,23 BYN).
          const fromSavedRevenue = (saved: number | undefined, fallback: number): number =>
            useSavedRevenueSelection && saved != null ? roundMoney(saved) : fallback
          const selectedRevenueNative = fromSavedRevenue(
            segmentAccrual?.revenue_selected_native,
            pickNative(selectedRevenueSource, portalRevenueNative, listokRevenueNative)
          )
          const selectedMembershipsRevenueNative = fromSavedRevenue(
            segmentAccrual?.revenue_selected_memberships_native,
            pickNative(selectedMembershipsRevenueSource, portalMembershipsRevenueNative, listokMembershipsRevenueNative)
          )
          const selectedSubscriptionsRevenueNative = fromSavedRevenue(
            segmentAccrual?.revenue_selected_subscriptions_native,
            pickNative(selectedSubscriptionsRevenueSource, portalSubscriptionsRevenueNative, listokSubscriptionsRevenueNative)
          )
          // Saved royalty snapshot (operator's step-2 numbers): on a single-segment month it comes
          // from the shared monthly accrual; on a multi-segment month the monthly accrual holds ONE
          // series-month value that can't map to per-segment rows, so we use the per-segment snapshot
          // (`royalty_period_accrual_segments`, P-03 variant A) when it exists and otherwise mirror
          // the per-segment preview.
          const useSavedRoyaltySnapshot = Boolean(accrual?.royalty_checked) && (!isMultiConditionMonth || Boolean(segmentAccrual?.royalty_revenue_rub != null))
          // §1.8 / §7.3: выручка в валюте ОБЯЗАТЕЛЬСТВА — посуточная сумма, а не
          // «рубли ÷ month-end». Именно от неё считается роялти на этапе 2, поэтому она
          // обязана совпадать с тем, что посчитает движок.
          //
          // Приоритет источников: снимок этапа 2 (там сторона обязательства сохранена
          // канонично) → зафиксированный на этапе 1 рубль, приведённый эффективным курсом
          // сегмента → живой расчёт. Средняя ветка нужна, чтобы в снимок этапа 2 не уехала
          // несогласованная пара «сохранённый рубль + свежий натив»: рубли туда шлёт
          // `selected_revenue_rub`, который уже канон из снимка этапа 1.
          const fromSavedObligation = (
            savedRoyaltyNative: number | undefined,
            savedRevenueRub: number,
            calcRub: number,
            calcObl: number
          ): number => {
            if (useSavedRoyaltySnapshot && savedRoyaltyNative != null) return roundMoney(savedRoyaltyNative)
            if (useSavedRevenueSelection && calcRub > 0 && calcObl > 0) {
              return roundMoney(savedRevenueRub * (calcObl / calcRub))
            }
            return calcObl
          }
          // Пара «рубли ↔ обязательство» для курса берётся ПО ОДНОМУ источнику, иначе их
          // отношение перестаёт быть курсом (портальные рубли на листковый натив).
          const selectedRevenueObl = fromSavedObligation(
            segmentAccrual?.royalty_revenue_native,
            selectedRevenueRub,
            pickNative(selectedRevenueSource, portalRevenueRub, listokRevenueRub),
            pickNative(selectedRevenueSource, roundMoney(segment.portal_revenue_obl), roundMoney(segment.listok_revenue_obl))
          )
          const selectedMembershipsRevenueObl = fromSavedObligation(
            segmentAccrual?.royalty_memberships_revenue_native,
            selectedMembershipsRevenueRub,
            pickNative(
              selectedMembershipsRevenueSource,
              roundMoney(segment.portal_memberships_revenue_rub),
              roundMoney(segment.listok_memberships_revenue_rub)
            ),
            pickNative(
              selectedMembershipsRevenueSource,
              roundMoney(segment.portal_memberships_revenue_obl),
              roundMoney(segment.listok_memberships_revenue_obl)
            )
          )
          const selectedSubscriptionsRevenueObl = fromSavedObligation(
            segmentAccrual?.royalty_subscriptions_revenue_native,
            selectedSubscriptionsRevenueRub,
            pickNative(
              selectedSubscriptionsRevenueSource,
              roundMoney(segment.portal_subscriptions_revenue_rub),
              roundMoney(segment.listok_subscriptions_revenue_rub)
            ),
            pickNative(
              selectedSubscriptionsRevenueSource,
              roundMoney(segment.portal_subscriptions_revenue_obl),
              roundMoney(segment.listok_subscriptions_revenue_obl)
            )
          )
          const portalRefundsRub = sumAmountsForRange(
            segmentStudioInternalId == null ? undefined : portalRefundsByStudioDate.get(segmentStudioInternalId),
            segment.start,
            segment.end
          )
          const listokRefundsByDate = studioNumber == null
            ? undefined
            : useRefundRegistry
              ? listokRegistryRefundsByStudioNumberDate.get(studioNumber)
              : listokMembershipRefundsByStudioNumberDate.get(studioNumber)
          const listokMembershipRefundsRub = sumAmountsForRange(
            listokRefundsByDate,
            segment.start,
            segment.end
          )
          const refundsSource = processMode === 'subscription'
            ? selectedMembershipsRevenueSource
            : selectedRevenueSource
          // При равных источниках портал предпочтительнее: только он хранит возврат отдельным
          // полем, тогда как в реестре Листка возврат определяется по отрицательной операции.
          const selectedRefundsRub = refundsSource === 'listok'
            ? listokMembershipRefundsRub
            : portalRefundsRub
          // Эффективный курс обязательства сегмента — единая точка для рублёвой стороны
          // минималки, нативной стороны роялти и `charge_fx_effective_rate` (та же формула,
          // что `v_royalty_fx_rate` в движке: рубли выручки ÷ выручка в валюте обязательства;
          // без выручки — month-end). Пара «рубли ↔ обязательство» — по одному источнику.
          const segmentChargeCurrency = term?.currency_code ?? 'RUB'
          const segmentObligationRate = resolveObligationEffectiveRate({
            chargeCurrency: segmentChargeCurrency,
            revenueRub: selectedRevenueRub,
            revenueObl: selectedRevenueObl,
            monthEndRate: segmentChargeCurrency === 'RUB' ? 1 : (fxToRubByCurrency.get(segmentChargeCurrency)?.rate ?? null),
          })
          const chargeRevenueRub = useSavedRoyaltySnapshot && segmentAccrual?.royalty_revenue_rub != null
            ? roundMoney(segmentAccrual.royalty_revenue_rub)
            : useSavedRoyaltySnapshot && accrual?.royalty_revenue_rub != null
              ? roundMoney(accrual.royalty_revenue_rub)
            : selectedRevenueRub
          const chargeMembershipsRevenueRub = useSavedRoyaltySnapshot && segmentAccrual?.royalty_memberships_revenue_rub != null
            ? roundMoney(segmentAccrual.royalty_memberships_revenue_rub)
            : useSavedRoyaltySnapshot && accrual?.royalty_memberships_revenue_rub != null
              ? roundMoney(accrual.royalty_memberships_revenue_rub)
            : selectedMembershipsRevenueRub
          // P-29: подписочная сторона считается от реестра автосписаний, поэтому и выручка
          // для charge берётся оттуда же — иначе gross у SPLIT_* не совпал бы с базой,
          // с которой реально удержали. Manual-сторона остаётся портальной.
          //
          // Канон «зафиксированное держится до перефиксации» (решение владельца 2026-08-04):
          // как только выручка шага 1 зафиксирована, база подписочной стороны берётся из
          // снимка, а не из ЖИВОГО реестра. Без этого синк листка после фиксации молча
          // менял базу начисления, и в charge уезжала пара «gross из снимка + суммы от
          // нового реестра» — числа разных моментов времени в одной строке.
          const perTransaction = processMode === 'subscription' ? perTransactionForSegment(segment) : null
          const perTransactionDriftFromSavedRub = perTransaction != null && useSavedRevenueSelection
            ? roundMoney(perTransaction.revenueRub - selectedSubscriptionsRevenueRub)
            : 0
          const perTransactionDriftsFromSaved = Math.abs(perTransactionDriftFromSavedRub) > 0.005
          const chargeSubscriptionsRevenueRub = useSavedRoyaltySnapshot && segmentAccrual?.royalty_subscriptions_revenue_rub != null
            ? roundMoney(segmentAccrual.royalty_subscriptions_revenue_rub)
            : useSavedRoyaltySnapshot && accrual?.royalty_subscriptions_revenue_rub != null
              ? roundMoney(accrual.royalty_subscriptions_revenue_rub)
            : useSavedRevenueSelection
              ? selectedSubscriptionsRevenueRub
            : perTransaction != null
              ? perTransaction.revenueRub
            : selectedSubscriptionsRevenueRub
          // When the month holds several conditions, the month-level accrual/balance percent is
          // meaningless per interval → use each segment's own term. Single-condition months keep
          // honoring a manually saved accrual/balance percent.
          const royaltyPercent = isMultiConditionMonth
            ? (segmentAccrual?.royalty_percent ?? term?.royalty_percent ?? null)
            : (accrual?.royalty_percent ?? balance?.royalty_percent ?? term?.royalty_percent ?? null)
          const componentAdjustments = componentAdjustmentsFor(seriesId, segmentNo, processMode)
          const manualRoyaltyAdjustmentRub = processMode === 'subscription'
            ? componentAdjustmentsTotal(componentAdjustments.filter((adj) => adj.charge_type === 'MANUAL_ROYALTY'))
            : 0
          const subscriptionBreakdown = calculateSubscriptionRoyaltyBreakdown({
            membershipsRevenueRub: chargeMembershipsRevenueRub,
            subscriptionsRevenueRub: chargeSubscriptionsRevenueRub,
            royaltyPercent,
            agencyPercent: term?.agency_percent ?? null,
            vatPercent: term?.vat_percent ?? null,
            listokCrmBaseAmountRub: segmentIdentity(segment) === listokCarrierSegmentIdentity
              ? (term?.listok_crm_base_amount ?? null)
              : 0,
            // Минималка в валюте min_royalty_currency → RUB как в движке (эффективный курс
            // сегмента), а не «число как RUB». Для RUB-терма тождественно (§14.11.5 item 2).
            minRoyaltyAmountRub: termMinRoyaltyRub(term, segmentObligationRate) ?? 0,
            // Минималка — месячная: на сегменте части месяца её применит месячный
            // top-up ниже (зеркало движка), а не каждый кусок по отдельности.
            applyMinRoyalty: !isMultiConditionMonth,
            // P-29: сохранённый операторский snapshot имеет приоритет — он уже содержит
            // потранзакционные суммы того выставления, которое оператор видел и утвердил.
            // Дрейф реестра относительно зафиксированной базы тоже отключает потранзакционный
            // путь: суммы обязаны быть посчитаны от той же базы, что уедет в charge, иначе
            // gross и роялти опять окажутся из разных моментов. Совпадает — путь сохраняется,
            // и точность `Σ round` (а не `round Σ`) не теряется.
            perTransactionSplitRoyaltyRub: useSavedRoyaltySnapshot || perTransactionDriftsFromSaved
              ? null
              : (perTransaction?.splitRoyaltyRub ?? null),
            perTransactionSplitAgencyRub: useSavedRoyaltySnapshot || perTransactionDriftsFromSaved
              ? null
              : (perTransaction?.splitAgencyRub ?? null),
          })

          const chargedRegularRub = balance?.charged_by_type.ROYALTY ?? 0
          const paidRegularRub = balance?.paid_by_type.ROYALTY ?? 0
          const chargedSubscriptionRub =
            (balance?.charged_by_type.SPLIT_ROYALTY ?? 0)
            + (balance?.charged_by_type.SPLIT_AGENCY ?? 0)
            + (balance?.charged_by_type.MANUAL_ROYALTY ?? 0)
            + (balance?.charged_by_type.LISTOK_CRM ?? 0)
          const paidSubscriptionRub =
            (balance?.paid_by_type.SPLIT_ROYALTY ?? 0)
            + (balance?.paid_by_type.SPLIT_AGENCY ?? 0)
            + (balance?.paid_by_type.MANUAL_ROYALTY ?? 0)
            + (balance?.paid_by_type.LISTOK_CRM ?? 0)
          // Не-денежное покрытие: зачёт возврата (§20) и списание долга (P-34). В «Оплачено»
          // не входит (денег не было), но долг закрывает — так же, как в royalty_balance.
          const coveredRegularRub = balance?.covered_by_type.ROYALTY ?? 0
          const coveredSubscriptionRub =
            (balance?.covered_by_type.SPLIT_ROYALTY ?? 0)
            + (balance?.covered_by_type.SPLIT_AGENCY ?? 0)
            + (balance?.covered_by_type.MANUAL_ROYALTY ?? 0)
            + (balance?.covered_by_type.LISTOK_CRM ?? 0)
          const chargedRub = processMode === 'regular' ? chargedRegularRub : chargedSubscriptionRub
          const paidRub = processMode === 'regular' ? paidRegularRub : paidSubscriptionRub
          const coveredRub = processMode === 'regular' ? coveredRegularRub : coveredSubscriptionRub
          const fixedRoyaltyDeltaRub = processMode === 'regular'
            ? (balance?.charged_by_type.FIXED_ROYALTY ?? 0) - (balance?.paid_by_type.FIXED_ROYALTY ?? 0) - (balance?.covered_by_type.FIXED_ROYALTY ?? 0)
            : 0
          const rbTaxOffsetRub = processMode === 'regular'
            ? (balance?.rb_tax_by_type.ROYALTY ?? 0)
            : (balance?.rb_tax_by_type.SPLIT_ROYALTY ?? 0) + (balance?.rb_tax_by_type.SPLIT_AGENCY ?? 0)
              + (balance?.rb_tax_by_type.MANUAL_ROYALTY ?? 0) + (balance?.rb_tax_by_type.LISTOK_CRM ?? 0)
          const chargedRevenueRub = processMode === 'regular'
            ? roundMoney(balance?.gross_by_type.ROYALTY ?? 0)
            : roundMoney(
              (balance?.gross_by_type.MANUAL_ROYALTY ?? 0)
              + (
                (balance?.gross_by_type.SPLIT_ROYALTY ?? 0) > 0
                  ? (balance?.gross_by_type.SPLIT_ROYALTY ?? 0)
                  : (balance?.gross_by_type.SPLIT_AGENCY ?? 0)
              )
            )
          const chargedMembershipsRevenueRub = processMode === 'subscription'
            ? roundMoney(balance?.gross_by_type.MANUAL_ROYALTY ?? 0)
            : chargedRevenueRub
          const chargedSubscriptionsRevenueRub = processMode === 'subscription'
            ? roundMoney(
              (balance?.gross_by_type.SPLIT_ROYALTY ?? 0) > 0
                ? (balance?.gross_by_type.SPLIT_ROYALTY ?? 0)
                : (balance?.gross_by_type.SPLIT_AGENCY ?? 0)
            )
            : 0
          // «Роялти X%, но не менее min»: минималка месячная, поэтому max() — только
          // когда сегмент покрывает весь месяц; для multi-segment месяца — top-up ниже.
          const percentRegularRoyaltyRub = royaltyPercent == null
            ? 0
            : roundMoney((chargeRevenueRub * royaltyPercent) / 100)
          const regularMinRoyaltyRub = termMinRoyaltyRub(term, segmentObligationRate) ?? 0
          const estimatedRegularRoyaltyRub = !isMultiConditionMonth && royaltyPercent != null
            ? Math.max(percentRegularRoyaltyRub, regularMinRoyaltyRub)
            : percentRegularRoyaltyRub
          const regularMinTopupRub = !isMultiConditionMonth && royaltyPercent != null
            ? roundMoney(Math.max(0, regularMinRoyaltyRub - percentRegularRoyaltyRub))
            : 0
          const previewBaseRoyaltyRub = processMode === 'regular'
            ? estimatedRegularRoyaltyRub
            : subscriptionBreakdown.total_rub
          const baseRoyaltyRub = useSavedRoyaltySnapshot && processMode === 'regular' && segmentAccrual?.royalty_amount_rub != null
            ? roundMoney(segmentAccrual.royalty_amount_rub)
            : useSavedRoyaltySnapshot && processMode === 'regular' && accrual?.royalty_amount_rub != null
              ? roundMoney(accrual.royalty_amount_rub)
            : previewBaseRoyaltyRub
          const splitRoyaltyRub = useSavedRoyaltySnapshot && processMode === 'subscription' && segmentAccrual?.royalty_split_royalty_rub != null
            ? roundMoney(segmentAccrual.royalty_split_royalty_rub)
            : useSavedRoyaltySnapshot && processMode === 'subscription' && accrual?.royalty_split_royalty_rub != null
              ? roundMoney(accrual.royalty_split_royalty_rub)
            : subscriptionBreakdown.split_royalty_rub
          const splitAgencyRub = useSavedRoyaltySnapshot && processMode === 'subscription' && segmentAccrual?.royalty_split_agency_rub != null
            ? roundMoney(segmentAccrual.royalty_split_agency_rub)
            : useSavedRoyaltySnapshot && processMode === 'subscription' && accrual?.royalty_split_agency_rub != null
              ? roundMoney(accrual.royalty_split_agency_rub)
            : subscriptionBreakdown.split_agency_rub
          const manualRoyaltyRub = useSavedRoyaltySnapshot && processMode === 'subscription' && segmentAccrual?.royalty_manual_royalty_rub != null
            ? roundMoney(segmentAccrual.royalty_manual_royalty_rub)
            : useSavedRoyaltySnapshot && processMode === 'subscription' && accrual?.royalty_manual_royalty_rub != null
              ? roundMoney(accrual.royalty_manual_royalty_rub)
            : subscriptionBreakdown.manual_royalty_rub
          const listokCrmRub = useSavedRoyaltySnapshot && processMode === 'subscription' && segmentAccrual?.royalty_listok_crm_rub != null
            ? roundMoney(segmentAccrual.royalty_listok_crm_rub)
            : useSavedRoyaltySnapshot && processMode === 'subscription' && accrual?.royalty_listok_crm_rub != null
              ? roundMoney(accrual.royalty_listok_crm_rub)
            : subscriptionBreakdown.listok_crm_rub
          const regularRoyaltyAdjustmentRub = processMode === 'regular'
            ? componentAdjustmentsTotal(componentAdjustments.filter((adj) => adj.charge_type === 'ROYALTY'))
            : 0
          const listokCrmAdjustmentRub = processMode === 'subscription'
            ? componentAdjustmentsTotal(componentAdjustments.filter((adj) => adj.charge_type === 'LISTOK_CRM'))
            : 0
          const manualRoyaltySettlement = splitAdjustedManualRoyalty(manualRoyaltyRub, manualRoyaltyAdjustmentRub)
          const adjustedListokCrmRub = applyNonNegativeChargeAdjustment(listokCrmRub, listokCrmAdjustmentRub)
          // FIXED_ROYALTY: фиксированная часть обычного терма = база `listok_crm_base_amount`
          // без НДС (договор абонементный). Сохранённый шаг 2 держит свою сумму до перефиксации —
          // зеркало движка (`sync_royalty_charge_from_accrual`: snapshot, иначе живой терм).
          const fixedRoyaltyRub = processMode !== 'regular'
            ? 0
            : useSavedRoyaltySnapshot && segmentAccrual?.royalty_fixed_royalty_rub != null
              ? roundMoney(segmentAccrual.royalty_fixed_royalty_rub)
              : term?.is_fixed_royalty_carrier
                ? roundMoney(Number(term.listok_crm_base_amount ?? 0))
                : 0
          const estimatedRoyaltyRub = processMode === 'regular'
            ? applyNonNegativeChargeAdjustment(baseRoyaltyRub, regularRoyaltyAdjustmentRub)
            : roundMoney(
              splitRoyaltyRub
              + splitAgencyRub
              + manualRoyaltySettlement.chargeRub
              + adjustedListokCrmRub
            )

          // Нативная сторона иностранной regular-строки — единственная реализация в
          // `computeForeignRegularRoyaltyNative` (шапка модуля объясняет правило и замер).
          // Здесь только сбор входов: выручка в валюте обязательства (посуточная или из
          // снимка), минимум в валюте обязательства, корректировки в рублях, эффективный курс.
          // Делить рублёвый итог на курс конца месяца нельзя: это давало 3 392,17 AED при
          // 33 297,41 AED × 10,5 % = 3 496,23 AED (замер 05.09.2026).
          const foreignRegularNative = processMode === 'regular'
            && term?.currency_code != null
            && term.currency_code !== 'RUB'
            && royaltyPercent != null
            && segmentObligationRate != null
            ? computeForeignRegularRoyaltyNative({
              revenueObl: selectedRevenueObl,
              royaltyPercent,
              minRoyaltyNative: termMinRoyaltyNative(term) ?? 0,
              applyMinimum: !isMultiConditionMonth,
              adjustmentsRub: regularRoyaltyAdjustmentRub,
              effectiveRate: segmentObligationRate,
              savedBaseNative: useSavedRoyaltySnapshot ? (segmentAccrual?.royalty_amount_native ?? null) : null,
            })
            : null
          const estimatedRoyaltyNativeOverride = foreignRegularNative?.estimatedNative ?? null

          // Непокрытые дни месяца (без договора) больше не образуют отдельную нумерованную
          // строку-сегмент (P-04) — их суммарный объём показываем как диагностику на первой
          // строке серии-месяца, чтобы оператор видел дыру покрытия целиком.
          const coverageGapDays = Math.round(segment.uncovered_days_count) + (segmentIndex === 0 ? monthUncoveredDays : 0)
          const coverageGapRevenueRub = roundMoney(segment.uncovered_revenue_rub + (segmentIndex === 0 ? monthUncoveredRevenueRub : 0))
          const termCoverageGapDays = Math.round(segment.term_gap_days_count) + (segmentIndex === 0 ? monthTermGapDays : 0)
          const termCoverageGapRevenueRub = roundMoney(segment.term_gap_revenue_rub + (segmentIndex === 0 ? monthTermGapRevenueRub : 0))
          const segmentContract = segment.contract ?? pickContractForSegment(seriesId, segment.start, segment.end)
          const persistedNegativeRefund = persistedNegativeRefundByKey.get(
            persistedRefundKey(seriesId, segmentNo, segmentContract?.contract_id)
          ) ?? null
          const recognizedExpiredContractIssue = segmentContract?.actual_end_date == null
            && segmentContract?.expected_end_date != null
            && segmentContract.expected_end_date < segment.end
              ? `Договор признаётся действующим: срок истёк ${formatDateDiagnostic(segmentContract.expected_end_date)}`
              : null
          const dataQualityIssues = activeContractsForSeries.length === 0
            ? ['Нет действующего договора в периоде']
            : coverageGapDays > 0
              ? [`Дней без покрытия договором: ${coverageGapDays} (≈${formatRubDiagnostic(coverageGapRevenueRub)})`]
              : []
          if (termCoverageGapDays > 0) {
            dataQualityIssues.push(`Дней с договором, но без условий роялти: ${termCoverageGapDays} (≈${formatRubDiagnostic(termCoverageGapRevenueRub)})`)
          }
          if (recognizedExpiredContractIssue && !dataQualityIssues.includes(recognizedExpiredContractIssue)) {
            dataQualityIssues.unshift(recognizedExpiredContractIssue)
          }
          // P-29: реестр молчит, а приложение показывает подписочные продажи — выставление
          // упадёт на серверном guard'е. Предупреждаем до нажатия, а не после ошибки RPC.
          if (perTransaction != null && perTransaction.txCount === 0 && roundMoney(segment.portal_subscriptions_revenue_rub) !== 0) {
            dataQualityIssues.push(
              `В реестре автосписаний нет транзакций, хотя приложение показывает подписки на ${formatRubDiagnostic(roundMoney(segment.portal_subscriptions_revenue_rub))} — проверьте синк реестра`
            )
          }

          return {
            process_row_key: `${seriesId}-${segmentStudioInternalId ?? 'na'}-${processMode}-${segment.start}-${segment.end}`,
            process_mode: processMode,
            row_kind: 'studio_ownership',
            data_quality_issues: dataQualityIssues,
            contract_coverage_gap_days: coverageGapDays,
            contract_coverage_gap_revenue_rub: coverageGapRevenueRub,
            term_coverage_gap_days: termCoverageGapDays,
            term_coverage_gap_revenue_rub: termCoverageGapRevenueRub,
            ...buildRowFxNative(
              term, estimatedRoyaltyRub, seriesId, segmentNo, segmentStudioInternalId, selectedRevenueRub, selectedRevenueObl,
              estimatedRoyaltyNativeOverride, foreignRegularNative?.baseNative ?? null, foreignRegularNative?.topupNative ?? null
            ),
            period_segment_no: segmentNo,
            component_adjustments: componentAdjustments,
            // P-29: признак и объём потранзакционного расчёта — для показа оператору.
            per_transaction_mode: perTransaction != null,
            per_transaction_tx_count: perTransaction?.txCount ?? null,
            per_transaction_subscriptions_revenue_rub: perTransaction?.revenueRub ?? null,
            period_segment_start: segment.start,
            period_segment_end: segment.end,
            period_segment_days_count: segment.days_count,
            contract_id: segmentContract?.contract_id ?? null,
            contract_number: segmentContract?.contract_number ?? null,
            contract_period_start: segmentContract?.period_start ?? null,
            contract_period_end: segmentContract?.period_end ?? null,
            contract_actual_start: segmentContract?.actual_start ?? null,
            contract_actual_end: segmentContract?.actual_end ?? null,
            series_id: seriesId,
            partner_number: partnerNumber,
            persons_fio: personsFio,
            studio_internal_id: segmentStudioInternalId,
            studio_number: studioNumber,
            studio_address: studio?.address ?? null,
            studio_status: segment.studio_status,
            city_name: city?.city_name ?? null,
            country,
            subscription_type: getEffectiveSubscriptionType(segmentStudioInternalId, segment.end),
            term_id: term?.term_id ?? null,
            term_contract_type: term?.contract_type ?? null,
            listok_crm_carrier: processMode === 'subscription' && term?.is_listok_crm_carrier === true,
            fixed_royalty_carrier: processMode === 'regular' && term?.is_fixed_royalty_carrier === true,
            listok_crm_carrier_term_id: processMode === 'subscription' ? (term?.term_id ?? null) : null,
            listok_crm_base_amount_rub: processMode === 'subscription' ? (term?.listok_crm_base_amount ?? null) : null,
            term_intervals: getTermsForRange(
              seriesId,
              String(periodStart).slice(0, 10),
              String(periodEnd).slice(0, 10)
            ).map((interval) => ({
              term_id: interval.term_id,
              contract_id: interval.contract_id,
              contract_number: interval.contract_number,
              legal_effective_from: interval.legal_effective_from,
              legal_expected_end_date: interval.legal_expected_end_date,
              legal_actual_end_date: interval.legal_actual_end_date,
              effective_from: interval.effective_from,
              effective_to: interval.effective_to,
              contract_type: interval.contract_type,
              royalty_percent: interval.royalty_percent,
              agency_percent: interval.agency_percent,
              vat_percent: interval.vat_percent,
              listok_crm_base_amount: interval.listok_crm_base_amount,
              min_royalty_amount: interval.min_royalty_amount,
              is_listok_crm_carrier: interval.is_listok_crm_carrier,
              is_fixed_royalty_carrier: interval.is_fixed_royalty_carrier,
            })),
            is_term_segmented: isMultiConditionMonth,
            month_min_topup_rub: null,
            min_royalty_topup_rub: processMode === 'subscription'
              ? subscriptionBreakdown.min_royalty_topup_rub
              : regularMinTopupRub,
            split_min_royalty_topup_rub: processMode === 'subscription'
              ? subscriptionBreakdown.split_min_royalty_topup_rub
              : 0,
            manual_min_royalty_topup_rub: processMode === 'subscription'
              ? subscriptionBreakdown.manual_min_royalty_topup_rub
              : 0,
            portal_revenue_rub: portalRevenueRub,
            listok_revenue_rub: listokRevenueRub,
            selected_revenue_rub: selectedRevenueRub,
            selected_revenue_source: selectedRevenueSource,
            selected_refunds_rub: selectedRefundsRub,
            portal_memberships_revenue_rub: roundMoney(segment.portal_memberships_revenue_rub),
            listok_memberships_revenue_rub: roundMoney(segment.listok_memberships_revenue_rub),
            selected_memberships_revenue_rub: selectedMembershipsRevenueRub,
            selected_memberships_revenue_source: selectedMembershipsRevenueSource,
            portal_subscriptions_revenue_rub: roundMoney(segment.portal_subscriptions_revenue_rub),
            listok_subscriptions_revenue_rub: roundMoney(segment.listok_subscriptions_revenue_rub),
            selected_subscriptions_revenue_rub: selectedSubscriptionsRevenueRub,
            selected_subscriptions_revenue_source: selectedSubscriptionsRevenueSource,
            portal_revenue_native: portalRevenueNative,
            listok_revenue_native: listokRevenueNative,
            selected_revenue_native: selectedRevenueNative,
            portal_memberships_revenue_native: portalMembershipsRevenueNative,
            listok_memberships_revenue_native: listokMembershipsRevenueNative,
            selected_memberships_revenue_native: selectedMembershipsRevenueNative,
            portal_subscriptions_revenue_native: portalSubscriptionsRevenueNative,
            listok_subscriptions_revenue_native: listokSubscriptionsRevenueNative,
            selected_subscriptions_revenue_native: selectedSubscriptionsRevenueNative,
            selected_revenue_obl: selectedRevenueObl,
            selected_memberships_revenue_obl: selectedMembershipsRevenueObl,
            selected_subscriptions_revenue_obl: selectedSubscriptionsRevenueObl,
            preview_selected_revenue_rub: previewSelectedRevenueRub,
            preview_selected_revenue_native: pickNative(
              previewSelectedRevenueSource, portalRevenueNative, listokRevenueNative
            ),
            // Подписочная половина сравнивается с РЕЕСТРОМ (P-29), а не с превью портал/листок:
            // именно реестр — источник её начисления, и именно он доезжает синком после фиксации.
            preview_selected_memberships_revenue_rub: previewMembershipsRevenueRub,
            preview_selected_subscriptions_revenue_rub: perTransaction != null
              ? perTransaction.revenueRub
              : previewSubscriptionsRevenueRub,
            revenue_snapshot_identity_stale: revenueSnapshotIdentityStale,
            revenue_snapshot_sum_mismatch: revenueSnapshotSumMismatch,
            revenue_snapshot_selected_rub: persistedSegmentAccrual?.revenue_selected_rub ?? null,
            revenue_snapshot_monthly_selected_rub: monthlySnapshotSelectedRub,
            revenue_snapshot_segments_total_rub: segmentSnapshotsTotalRub,
            revenue_checked: accrual?.revenue_checked ?? false,
            royalty_checked: accrual?.royalty_checked ?? false,
            revenue_checked_at: accrual?.revenue_checked_at ?? null,
            revenue_checked_by: accrual?.revenue_checked_by ?? null,
            royalty_checked_at: accrual?.royalty_checked_at ?? null,
            royalty_checked_by: accrual?.royalty_checked_by ?? null,
            revenue_days_count: segment.revenue_days_count,
            revenue_confirmed_days_count: segment.revenue_confirmed_days_count,
            royalty_percent: royaltyPercent,
            royalty_agency_percent: term?.agency_percent ?? null,
            royalty_vat_percent: term?.vat_percent ?? null,
            // Именно рубли (страница сравнивает с рублёвыми потоками месяца), а не номинал в валюте.
            royalty_min_amount_rub: term?.min_royalty_amount == null ? null : termMinRoyaltyRub(term, segmentObligationRate),
            royalty_listok_crm_base_amount_rub: segmentIdentity(segment) === listokCarrierSegmentIdentity
              ? (term?.listok_crm_base_amount ?? null)
              : 0,
            charge_revenue_rub: chargeRevenueRub,
            charge_memberships_revenue_rub: chargeMembershipsRevenueRub,
            charge_subscriptions_revenue_rub: chargeSubscriptionsRevenueRub,
            charged_revenue_rub: chargedRevenueRub,
            charged_memberships_revenue_rub: chargedMembershipsRevenueRub,
            charged_subscriptions_revenue_rub: chargedSubscriptionsRevenueRub,
            adjustment_rub: adjustmentRub,
            adjustment_comment: accrual?.adjustment_comment ?? null,
            adjustment_updated_at: accrual?.adjustment_updated_at ?? null,
            adjustment_updated_by_user_id: accrual?.adjustment_updated_by_user_id ?? null,
            adjustment_updated_by_name: accrual?.adjustment_updated_by_user_id
              ? (royaltyUserDisplayName.get(accrual.adjustment_updated_by_user_id) ?? accrual.adjustment_updated_by_user_id)
              : null,
            base_royalty_rub: baseRoyaltyRub,
            // Итог = база + адресные корректировки (их движок доначислит post-pass'ом).
            // В РПЦ выставления уходит base_royalty_rub, чтобы не задвоить корректировку.
            estimated_royalty_rub: estimatedRoyaltyRub,
            estimated_split_royalty_rub: processMode === 'subscription'
              ? splitRoyaltyRub
              : 0,
            estimated_split_agency_rub: processMode === 'subscription'
              ? splitAgencyRub
              : 0,
            estimated_manual_royalty_rub: processMode === 'subscription'
              ? manualRoyaltyRub
              : 0,
            manual_royalty_charge_rub: processMode === 'subscription'
              ? manualRoyaltySettlement.chargeRub
              : 0,
            negative_accrual_refund_rub: processMode === 'subscription'
              ? manualRoyaltySettlement.refundRub
              : 0,
            persisted_negative_accrual_refund_rub: processMode === 'subscription'
              ? (persistedNegativeRefund?.amountRub ?? null)
              : null,
            persisted_negative_accrual_refund_status: processMode === 'subscription'
              ? (persistedNegativeRefund?.status ?? null)
              : null,
            persisted_negative_accrual_refund_offset_rub: processMode === 'subscription'
              ? (persistedNegativeRefund?.offsetRub ?? null)
              : null,
            estimated_listok_crm_rub: processMode === 'subscription'
              ? listokCrmRub
              : 0,
            estimated_fixed_royalty_rub: fixedRoyaltyRub,
            charged_rub: chargedRub,
            paid_rub: paidRub,
            covered_rub: coveredRub,
            rb_tax_offset_rub: rbTaxOffsetRub,
            // У обычной строки ROYALTY и FIXED_ROYALTY — разные карманы: delta — общая разница
            // (переплата видна минусом), долг — по карманам, переплата одного другой не гасит.
            delta_rub: chargedRub - paidRub - coveredRub + fixedRoyaltyDeltaRub,
            debt_rub: Math.max(0, chargedRub - paidRub - coveredRub) + Math.max(0, fixedRoyaltyDeltaRub),
            charge_count: chargedRub > 0 || paidRub > 0
              || (processMode === 'regular' && ((balance?.charged_by_type.FIXED_ROYALTY ?? 0) > 0 || (balance?.paid_by_type.FIXED_ROYALTY ?? 0) > 0)) ? 1 : 0,
            charged_split_royalty_rub: processMode === 'subscription' ? (balance?.charged_by_type.SPLIT_ROYALTY ?? 0) : 0,
            charged_split_agency_rub: processMode === 'subscription' ? (balance?.charged_by_type.SPLIT_AGENCY ?? 0) : 0,
            charged_manual_royalty_rub: processMode === 'subscription' ? (balance?.charged_by_type.MANUAL_ROYALTY ?? 0) : 0,
            charged_listok_crm_rub: processMode === 'subscription' ? (balance?.charged_by_type.LISTOK_CRM ?? 0) : 0,
            charged_fixed_royalty_rub: processMode === 'regular' ? (balance?.charged_by_type.FIXED_ROYALTY ?? 0) : 0,
            paid_split_royalty_rub: processMode === 'subscription' ? (balance?.paid_by_type.SPLIT_ROYALTY ?? 0) : 0,
            paid_split_agency_rub: processMode === 'subscription' ? (balance?.paid_by_type.SPLIT_AGENCY ?? 0) : 0,
            paid_manual_royalty_rub: processMode === 'subscription' ? (balance?.paid_by_type.MANUAL_ROYALTY ?? 0) : 0,
            paid_listok_crm_rub: processMode === 'subscription' ? (balance?.paid_by_type.LISTOK_CRM ?? 0) : 0,
            paid_fixed_royalty_rub: processMode === 'regular' ? (balance?.paid_by_type.FIXED_ROYALTY ?? 0) : 0,
            covered_split_royalty_rub: processMode === 'subscription' ? (balance?.covered_by_type.SPLIT_ROYALTY ?? 0) : 0,
            covered_split_agency_rub: processMode === 'subscription' ? (balance?.covered_by_type.SPLIT_AGENCY ?? 0) : 0,
            covered_manual_royalty_rub: processMode === 'subscription' ? (balance?.covered_by_type.MANUAL_ROYALTY ?? 0) : 0,
            covered_listok_crm_rub: processMode === 'subscription' ? (balance?.covered_by_type.LISTOK_CRM ?? 0) : 0,
            covered_fixed_royalty_rub: processMode === 'regular' ? (balance?.covered_by_type.FIXED_ROYALTY ?? 0) : 0,
            settlement_status: chargedRub > 0 || (processMode === 'regular' && (balance?.charged_by_type.FIXED_ROYALTY ?? 0) > 0) ? (settlement?.status ?? null) : null,
            adjustment_balance_hint: adjustmentBalanceHint && Math.abs(adjustmentBalanceHint.closing_balance_rub) < 1500.005 && Math.abs(adjustmentBalanceHint.closing_balance_rub) > 0.005
              ? adjustmentBalanceHint
              : null,
            adjustment_balance_history: adjustmentHistory,
            is_reviewed: settlement?.is_reviewed ?? false,
            is_confirmed: chargedRub > 0 || (processMode === 'regular' && (balance?.charged_by_type.FIXED_ROYALTY ?? 0) > 0) ? (balance?.is_confirmed ?? false) : false,
            accounting_confirmed_at: balance?.accounting_confirmed_at ?? null,
            accounting_confirmed_by: balance?.accounting_confirmed_by_user_id
              ? (royaltyUserDisplayName.get(balance.accounting_confirmed_by_user_id) ?? balance.accounting_confirmed_by_user_id)
              : null,
            foreign_payment_method: null,
          } satisfies RoyaltyAccrualProcessRow
        })

      // Месячная минималка «со студии» для multi-segment месяца (зеркало движка):
      // сегменты посчитаны по процентам; если сумма роялти-потоков месяца
      // (regular-роялти + подписочные split+manual, без агентских и Листка) меньше
      // минималки по условиям ХРОНОЛОГИЧЕСКИ ПОСЛЕДНЕГО сегмента, разница
      // доначисляется на его строку. Для SUBSCRIPTION минималка — база, НДС сверху.
      if (isMultiConditionMonth && segmentRows.length > 1) {
        const lastRow = segmentRows.reduce((a, b) => (b.period_segment_end ?? '') > (a.period_segment_end ?? '') ? b : a)
        const lastSegment = orderedSegments.find(
          (segment) => segment.start === lastRow.period_segment_start && segment.end === lastRow.period_segment_end
        )
        const minBaseRub = termMinRoyaltyRub(lastSegment?.term ?? null, lastRow.charge_fx_effective_rate ?? lastRow.charge_fx_rate) ?? 0
        if (minBaseRub > 0) {
          const lastVatPercent = Number(lastSegment?.term?.vat_percent ?? 22)
          const minTotalRub = lastRow.process_mode === 'subscription'
            ? roundMoney(minBaseRub * (1 + lastVatPercent / 100))
            : minBaseRub
          const monthRoyaltyRub = roundMoney(segmentRows.reduce((sum, row) => sum + (
            row.process_mode === 'regular'
              ? row.base_royalty_rub
              : roundMoney(row.estimated_split_royalty_rub + row.estimated_manual_royalty_rub)
          ), 0))
          if (monthRoyaltyRub < minTotalRub - 0.005) {
            const topupRub = roundMoney(minTotalRub - monthRoyaltyRub)
            lastRow.month_min_topup_rub = topupRub
            lastRow.min_royalty_topup_rub = roundMoney(lastRow.min_royalty_topup_rub + topupRub)
            lastRow.base_royalty_rub = roundMoney(lastRow.base_royalty_rub + topupRub)
            if (lastRow.process_mode === 'subscription') {
              lastRow.manual_min_royalty_topup_rub = roundMoney(lastRow.manual_min_royalty_topup_rub + topupRub)
              lastRow.estimated_manual_royalty_rub = roundMoney(lastRow.estimated_manual_royalty_rub + topupRub)
              const manualAdjustmentRub = componentAdjustmentsTotal(
                lastRow.component_adjustments.filter((adj) => adj.charge_type === 'MANUAL_ROYALTY')
              )
              const manualSettlement = splitAdjustedManualRoyalty(
                lastRow.estimated_manual_royalty_rub,
                manualAdjustmentRub
              )
              lastRow.manual_royalty_charge_rub = manualSettlement.chargeRub
              lastRow.negative_accrual_refund_rub = manualSettlement.refundRub
              const listokAdjustmentRub = componentAdjustmentsTotal(
                lastRow.component_adjustments.filter((adj) => adj.charge_type === 'LISTOK_CRM')
              )
              lastRow.estimated_royalty_rub = roundMoney(
                lastRow.estimated_split_royalty_rub
                + lastRow.estimated_split_agency_rub
                + manualSettlement.chargeRub
                + applyNonNegativeChargeAdjustment(lastRow.estimated_listok_crm_rub, listokAdjustmentRub)
              )
            } else {
              const regularAdjustmentRub = componentAdjustmentsTotal(
                lastRow.component_adjustments.filter((adj) => adj.charge_type === 'ROYALTY')
              )
              lastRow.estimated_royalty_rub = applyNonNegativeChargeAdjustment(
                lastRow.base_royalty_rub,
                regularAdjustmentRub
              )
              // Месячный top-up — рублёвая величина; в валюту обязательства она идёт тем же
              // эффективным курсом, что в движке (`topup_rub / v_royalty_fx_rate`), а не
              // делением рублёвого итога на курс конца месяца.
              const lastObligationRate = lastRow.charge_fx_effective_rate ?? lastRow.charge_fx_rate
              if (
                lastRow.charge_currency !== 'RUB'
                && lastObligationRate != null
                && lastObligationRate > 0
                && lastRow.base_royalty_native != null
              ) {
                const topupNative = roundMoney(topupRub / lastObligationRate)
                lastRow.base_royalty_native = roundMoney(lastRow.base_royalty_native + topupNative)
                lastRow.min_royalty_topup_native = roundMoney((lastRow.min_royalty_topup_native ?? 0) + topupNative)
                lastRow.estimated_royalty_native = applyAdjustmentsNative(lastRow.base_royalty_native, regularAdjustmentRub, lastObligationRate)
              }
            }
          }
        }
      }

      return segmentRows
    })
    .filter((row) => {
      const country = row.country?.trim()
      return Boolean(country) && !isManagementCompanyPartner(row.partner_number)
    })

  // «Договор без студии» — не одно состояние, а минимум четыре, и оператору важно
  // различать их: закрытая студия с нерасторгнутым ДКК ждёт ДС о расторжении, проданная —
  // закрытия серии продавца, будущая — просто ещё не наступила, а серия совсем без
  // владения = незаполненные мастер-данные. Прежний текст («Нет привязанной студии
  // в этом периоде») читался как «данных нет» даже там, где студия была годами.
  const describeContextStudio = (studioInternalId: number | null | undefined): string => {
    if (studioInternalId == null) return 'студия'
    const studio = anyStudioById.get(studioInternalId)
    const cityName = studio?.city_id != null ? cityById.get(studio.city_id)?.city_name ?? null : null
    const label = studio?.studio_number ? `студия ${studio.studio_number}` : `студия #${studioInternalId}`
    return cityName ? `${label} (${cityName})` : label
  }

  const buildNoStudioDiagnostic = (seriesId: number): {
    reason: 'closed' | 'sold' | 'future' | 'never' | null
    issue: string
  } => {
    const context = seriesOwnershipContextBySeriesId.get(seriesId)
    if (context?.prev_studio_internal_id != null) {
      const studioLabel = describeContextStudio(context.prev_studio_internal_id)
      const endedOn = formatDateDiagnostic(context.prev_end_date)
      switch ((context.prev_ownership_phase ?? '').toUpperCase()) {
        case 'SOLD':
        case 'TRANSFERRED':
          return { reason: 'sold', issue: `Владения нет: ${studioLabel} передана другому франчайзи ${endedOn}` }
        case 'CLOSED':
          return { reason: 'closed', issue: `Владения нет: ${studioLabel} закрыта ${endedOn}` }
        default:
          return { reason: 'closed', issue: `Владения нет: владение по договору (${studioLabel}) закрыто ${endedOn}` }
      }
    }
    if (context?.next_studio_internal_id != null) {
      return {
        reason: 'future',
        issue: `Владения нет: ${describeContextStudio(context.next_studio_internal_id)} переходит к франчайзи ${formatDateDiagnostic(context.next_start_date)} — позже этого месяца`,
      }
    }
    // Payload без ключа series_ownership_context_rows (миграция ещё не применена)
    // или серия действительно никогда не имела владения.
    return seriesOwnershipContextRes.data.length > 0
      ? { reason: 'never', issue: 'Владения нет: к договору ни разу не привязывалась студия' }
      : { reason: null, issue: 'Владения по договору нет в этом месяце' }
  }

  const contractWithoutStudioRows = activeContracts
    .filter((contract) => !ownershipSeriesIds.has(contract.series_id) && !isManagementCompanySeries(contract.series_id))
    .map((contract) => {
      const partnerNumber = partnerBySeriesId.get(contract.series_id) ?? null
      const personsFio = partnerNumber != null ? (personsByPartner.get(partnerNumber) ?? null) : null
      const balance = balanceBySeriesId.get(contract.series_id)
      const settlement = settlementBySeriesId.get(contract.series_id)
      const accrual = accrualBySeriesId.get(contract.series_id)
      const adjustmentRub = accrual?.adjustment_rub ?? null
      const adjustmentHistory = adjustmentHistoryBySeriesId.get(contract.series_id) ?? []
      const adjustmentBalanceHint = previousPeriod == null
        ? null
        : adjustmentHistory.find((item) => item.year === previousPeriod.year && item.month === previousPeriod.month) ?? null
      const term = latestTermsBySeriesId.get(contract.series_id)
      const contractStart = toDateKey(contract.effective_from) ?? toDateKey(contract.signed_at) ?? monthStart
      const contractEnd = toDateKey(contract.actual_end_date)
      const periodStart = contractStart > monthStart ? contractStart : monthStart
      const periodEnd = contractEnd && contractEnd < monthEnd ? contractEnd : monthEnd
      const processMode: 'regular' | 'subscription' = term?.contract_type === 'SUBSCRIPTION' ? 'subscription' : 'regular'
      const royaltyPercent = accrual?.royalty_percent ?? balance?.royalty_percent ?? term?.royalty_percent ?? null
      const chargedRegularRub = balance?.charged_by_type.ROYALTY ?? 0
      const paidRegularRub = balance?.paid_by_type.ROYALTY ?? 0
      const chargedSubscriptionRub =
        (balance?.charged_by_type.SPLIT_ROYALTY ?? 0)
        + (balance?.charged_by_type.SPLIT_AGENCY ?? 0)
        + (balance?.charged_by_type.MANUAL_ROYALTY ?? 0)
        + (balance?.charged_by_type.LISTOK_CRM ?? 0)
      const paidSubscriptionRub =
        (balance?.paid_by_type.SPLIT_ROYALTY ?? 0)
        + (balance?.paid_by_type.SPLIT_AGENCY ?? 0)
        + (balance?.paid_by_type.MANUAL_ROYALTY ?? 0)
        + (balance?.paid_by_type.LISTOK_CRM ?? 0)
      // Не-денежное покрытие (зачёт возврата + списание долга): см. комментарий в
      // основной ветке — долг строки обязан совпадать с royalty_balance.debt_amount_rub.
      const coveredRegularRub = balance?.covered_by_type.ROYALTY ?? 0
      const coveredSubscriptionRub =
        (balance?.covered_by_type.SPLIT_ROYALTY ?? 0)
        + (balance?.covered_by_type.SPLIT_AGENCY ?? 0)
        + (balance?.covered_by_type.MANUAL_ROYALTY ?? 0)
        + (balance?.covered_by_type.LISTOK_CRM ?? 0)
      const chargedRub = processMode === 'regular' ? chargedRegularRub : chargedSubscriptionRub
      const paidRub = processMode === 'regular' ? paidRegularRub : paidSubscriptionRub
      const coveredRub = processMode === 'regular' ? coveredRegularRub : coveredSubscriptionRub
      const fixedRoyaltyDeltaRub = processMode === 'regular'
        ? (balance?.charged_by_type.FIXED_ROYALTY ?? 0) - (balance?.paid_by_type.FIXED_ROYALTY ?? 0) - (balance?.covered_by_type.FIXED_ROYALTY ?? 0)
        : 0
      const rbTaxOffsetRub = processMode === 'regular'
        ? (balance?.rb_tax_by_type.ROYALTY ?? 0)
        : (balance?.rb_tax_by_type.SPLIT_ROYALTY ?? 0) + (balance?.rb_tax_by_type.SPLIT_AGENCY ?? 0)
          + (balance?.rb_tax_by_type.MANUAL_ROYALTY ?? 0) + (balance?.rb_tax_by_type.LISTOK_CRM ?? 0)
      const noStudioDiagnostic = buildNoStudioDiagnostic(contract.series_id)
      const issues = [noStudioDiagnostic.issue]
      // Вторая половина ответа на «почему строка вообще здесь»: серия попадает в месяц
      // не из-за студии, а из-за живого MAIN-ДКК. Без этой подписи закрытая студия
      // выглядит багом экрана, хотя это незакрытый документ.
      const contractLabel = contract.contract_number ? `ДКК ${contract.contract_number}` : `ДКК ID ${contract.contract_id}`
      if (contract.actual_end_date == null) {
        issues.push(contract.expected_end_date
          ? `${contractLabel} не расторгнут (срок до ${formatDateDiagnostic(contract.expected_end_date)})`
          : `${contractLabel} не расторгнут`)
      } else {
        issues.push(`${contractLabel} расторгнут ${formatDateDiagnostic(contract.actual_end_date)}`)
      }
      if (!term) issues.push('Нет условий роялти')
      const componentAdjustments = componentAdjustmentsFor(contract.series_id, 1, processMode)
      const fallbackBaseRoyaltyRub = roundMoney((accrual?.royalty_amount_rub ?? 0) - (adjustmentRub ?? 0))
      const fallbackManualRoyaltyRub = processMode === 'subscription'
        ? roundMoney(accrual?.royalty_manual_royalty_rub ?? 0)
        : 0
      const fallbackManualAdjustmentRub = componentAdjustmentsTotal(
        componentAdjustments.filter((adj) => adj.charge_type === 'MANUAL_ROYALTY')
      )
      const fallbackManualSettlement = splitAdjustedManualRoyalty(
        fallbackManualRoyaltyRub,
        fallbackManualAdjustmentRub
      )
      const fallbackListokCrmRub = processMode === 'subscription'
        ? roundMoney(accrual?.royalty_listok_crm_rub ?? 0)
        : 0
      const fallbackListokAdjustmentRub = componentAdjustmentsTotal(
        componentAdjustments.filter((adj) => adj.charge_type === 'LISTOK_CRM')
      )
      const fallbackRegularAdjustmentRub = componentAdjustmentsTotal(
        componentAdjustments.filter((adj) => adj.charge_type === 'ROYALTY')
      )
      const fallbackEstimatedRoyaltyRub = processMode === 'subscription'
        ? roundMoney(
          (accrual?.royalty_split_royalty_rub ?? 0)
          + (accrual?.royalty_split_agency_rub ?? 0)
          + fallbackManualSettlement.chargeRub
          + applyNonNegativeChargeAdjustment(fallbackListokCrmRub, fallbackListokAdjustmentRub)
        )
        : applyNonNegativeChargeAdjustment(fallbackBaseRoyaltyRub, fallbackRegularAdjustmentRub)

      return {
        process_row_key: `${contract.series_id}-contract-${contract.contract_id}-${processMode}-${periodStart}-${periodEnd}`,
        process_mode: processMode,
        row_kind: 'contract_without_studio',
        no_studio_reason: noStudioDiagnostic.reason,
        data_quality_issues: issues,
        contract_coverage_gap_days: 0,
        contract_coverage_gap_revenue_rub: 0,
        term_coverage_gap_days: 0,
        term_coverage_gap_revenue_rub: 0,
        // Строка без студии не биллится — валютный/нативный блок вырождается в RUB.
        ...buildRowFxNative(null, 0, contract.series_id, 1, null, 0, 0),
        period_segment_no: 1,
        component_adjustments: componentAdjustments,
        period_segment_start: periodStart,
        period_segment_end: periodEnd,
        period_segment_days_count: Math.max(0, Math.round((parseIsoDate(periodEnd).getTime() - parseIsoDate(periodStart).getTime()) / 86400000) + 1),
        contract_id: contract.contract_id,
        contract_number: contract.contract_number ?? null,
        contract_period_start: periodStart,
        contract_period_end: periodEnd,
        contract_actual_start: periodStart,
        contract_actual_end: periodEnd,
        series_id: contract.series_id,
        partner_number: partnerNumber,
        persons_fio: personsFio,
        studio_internal_id: null,
        studio_number: null,
        studio_address: null,
        studio_status: null,
        city_name: null,
        country: null,
        subscription_type: processMode === 'subscription' ? 'SUBSCRIPTION' : 'REGULAR',
        term_id: null,
        term_contract_type: term?.contract_type ?? null,
        // Строка без привязанной студии не биллится и не носитель CRM-фикса (P-22).
        listok_crm_carrier: false,
        fixed_royalty_carrier: false,
        listok_crm_carrier_term_id: null,
        listok_crm_base_amount_rub: null,
        term_intervals: getTermsForRange(
          contract.series_id,
          String(periodStart).slice(0, 10),
          String(periodEnd).slice(0, 10)
        ).map((interval) => ({
          term_id: interval.term_id,
          contract_id: interval.contract_id,
          contract_number: interval.contract_number,
          legal_effective_from: interval.legal_effective_from,
          legal_expected_end_date: interval.legal_expected_end_date,
          legal_actual_end_date: interval.legal_actual_end_date,
          effective_from: interval.effective_from,
          effective_to: interval.effective_to,
          contract_type: interval.contract_type,
          royalty_percent: interval.royalty_percent,
          agency_percent: interval.agency_percent,
          vat_percent: interval.vat_percent,
          listok_crm_base_amount: interval.listok_crm_base_amount,
          min_royalty_amount: interval.min_royalty_amount,
          is_listok_crm_carrier: interval.is_listok_crm_carrier,
          is_fixed_royalty_carrier: interval.is_fixed_royalty_carrier,
        })),
        is_term_segmented: false,
        month_min_topup_rub: null,
        min_royalty_topup_rub: 0,
        split_min_royalty_topup_rub: 0,
        manual_min_royalty_topup_rub: 0,
        portal_revenue_rub: 0,
        listok_revenue_rub: 0,
        selected_revenue_rub: 0,
        selected_revenue_source: 'equal',
        selected_refunds_rub: 0,
        portal_memberships_revenue_rub: 0,
        listok_memberships_revenue_rub: 0,
        selected_memberships_revenue_rub: 0,
        selected_memberships_revenue_source: 'equal',
        portal_subscriptions_revenue_rub: 0,
        listok_subscriptions_revenue_rub: 0,
        selected_subscriptions_revenue_rub: 0,
        selected_subscriptions_revenue_source: 'equal',
        // Строка без дневной выручки: нативная сторона тоже нулевая.
        portal_revenue_native: 0,
        listok_revenue_native: 0,
        selected_revenue_native: 0,
        portal_memberships_revenue_native: 0,
        listok_memberships_revenue_native: 0,
        selected_memberships_revenue_native: 0,
        portal_subscriptions_revenue_native: 0,
        listok_subscriptions_revenue_native: 0,
        selected_subscriptions_revenue_native: 0,
        selected_revenue_obl: 0,
        selected_memberships_revenue_obl: 0,
        selected_subscriptions_revenue_obl: 0,
        preview_selected_revenue_rub: 0,
        preview_selected_revenue_native: 0,
        preview_selected_memberships_revenue_rub: 0,
        preview_selected_subscriptions_revenue_rub: 0,
        revenue_checked: accrual?.revenue_checked ?? false,
        royalty_checked: accrual?.royalty_checked ?? false,
        revenue_checked_at: accrual?.revenue_checked_at ?? null,
        revenue_checked_by: accrual?.revenue_checked_by ?? null,
        royalty_checked_at: accrual?.royalty_checked_at ?? null,
        royalty_checked_by: accrual?.royalty_checked_by ?? null,
        revenue_days_count: 0,
        revenue_confirmed_days_count: 0,
        royalty_percent: royaltyPercent,
        royalty_agency_percent: term?.agency_percent ?? null,
        royalty_vat_percent: term?.vat_percent ?? null,
        // Fallback-строка договора без владения: у `latestTermsBySeriesId` нет валюты терма,
        // выручки у строки нет, в расчёты поле не идёт — оставляем номинал как раньше.
        royalty_min_amount_rub: term?.min_royalty_amount ?? null,
        // P-22: строка без привязанной студии не биллится и не может быть носителем
        // CRM-фикса — LISTOK-базу здесь не показываем (фикс садится только на выбранный
        // договор-носитель на строке с ownership).
        royalty_listok_crm_base_amount_rub: null,
        charge_revenue_rub: roundMoney(accrual?.royalty_revenue_rub ?? 0),
        charge_memberships_revenue_rub: roundMoney(accrual?.royalty_memberships_revenue_rub ?? 0),
        charge_subscriptions_revenue_rub: roundMoney(accrual?.royalty_subscriptions_revenue_rub ?? 0),
        charged_revenue_rub: 0,
        charged_memberships_revenue_rub: 0,
        charged_subscriptions_revenue_rub: 0,
        adjustment_rub: adjustmentRub,
        adjustment_comment: accrual?.adjustment_comment ?? null,
        adjustment_updated_at: accrual?.adjustment_updated_at ?? null,
        adjustment_updated_by_user_id: accrual?.adjustment_updated_by_user_id ?? null,
        adjustment_updated_by_name: accrual?.adjustment_updated_by_user_id
          ? (royaltyUserDisplayName.get(accrual.adjustment_updated_by_user_id) ?? accrual.adjustment_updated_by_user_id)
          : null,
        base_royalty_rub: fallbackBaseRoyaltyRub,
        estimated_royalty_rub: fallbackEstimatedRoyaltyRub,
        estimated_split_royalty_rub: processMode === 'subscription' ? (accrual?.royalty_split_royalty_rub ?? 0) : 0,
        estimated_split_agency_rub: processMode === 'subscription' ? (accrual?.royalty_split_agency_rub ?? 0) : 0,
        estimated_manual_royalty_rub: fallbackManualRoyaltyRub,
        manual_royalty_charge_rub: processMode === 'subscription'
          ? fallbackManualSettlement.chargeRub
          : 0,
        negative_accrual_refund_rub: processMode === 'subscription'
          ? fallbackManualSettlement.refundRub
          : 0,
        persisted_negative_accrual_refund_rub: null,
        persisted_negative_accrual_refund_status: null,
        persisted_negative_accrual_refund_offset_rub: null,
        estimated_listok_crm_rub: fallbackListokCrmRub,
        // Строка без студии не биллится — фиксированной части у неё нет (как и LISTOK_CRM).
        estimated_fixed_royalty_rub: 0,
        charged_rub: chargedRub,
        paid_rub: paidRub,
        covered_rub: coveredRub,
        rb_tax_offset_rub: rbTaxOffsetRub,
        // У обычной строки ROYALTY и FIXED_ROYALTY — разные карманы: delta — общая разница
        // (переплата видна минусом), долг — по карманам, переплата одного другой не гасит.
        delta_rub: chargedRub - paidRub - coveredRub + fixedRoyaltyDeltaRub,
        debt_rub: Math.max(0, chargedRub - paidRub - coveredRub) + Math.max(0, fixedRoyaltyDeltaRub),
        charge_count: chargedRub > 0 || paidRub > 0
          || (processMode === 'regular' && ((balance?.charged_by_type.FIXED_ROYALTY ?? 0) > 0 || (balance?.paid_by_type.FIXED_ROYALTY ?? 0) > 0)) ? 1 : 0,
        charged_split_royalty_rub: processMode === 'subscription' ? (balance?.charged_by_type.SPLIT_ROYALTY ?? 0) : 0,
        charged_split_agency_rub: processMode === 'subscription' ? (balance?.charged_by_type.SPLIT_AGENCY ?? 0) : 0,
        charged_manual_royalty_rub: processMode === 'subscription' ? (balance?.charged_by_type.MANUAL_ROYALTY ?? 0) : 0,
        charged_listok_crm_rub: processMode === 'subscription' ? (balance?.charged_by_type.LISTOK_CRM ?? 0) : 0,
        charged_fixed_royalty_rub: processMode === 'regular' ? (balance?.charged_by_type.FIXED_ROYALTY ?? 0) : 0,
        paid_split_royalty_rub: processMode === 'subscription' ? (balance?.paid_by_type.SPLIT_ROYALTY ?? 0) : 0,
        paid_split_agency_rub: processMode === 'subscription' ? (balance?.paid_by_type.SPLIT_AGENCY ?? 0) : 0,
        paid_manual_royalty_rub: processMode === 'subscription' ? (balance?.paid_by_type.MANUAL_ROYALTY ?? 0) : 0,
        paid_listok_crm_rub: processMode === 'subscription' ? (balance?.paid_by_type.LISTOK_CRM ?? 0) : 0,
        paid_fixed_royalty_rub: processMode === 'regular' ? (balance?.paid_by_type.FIXED_ROYALTY ?? 0) : 0,
        covered_split_royalty_rub: processMode === 'subscription' ? (balance?.covered_by_type.SPLIT_ROYALTY ?? 0) : 0,
        covered_split_agency_rub: processMode === 'subscription' ? (balance?.covered_by_type.SPLIT_AGENCY ?? 0) : 0,
        covered_manual_royalty_rub: processMode === 'subscription' ? (balance?.covered_by_type.MANUAL_ROYALTY ?? 0) : 0,
        covered_listok_crm_rub: processMode === 'subscription' ? (balance?.covered_by_type.LISTOK_CRM ?? 0) : 0,
        covered_fixed_royalty_rub: processMode === 'regular' ? (balance?.covered_by_type.FIXED_ROYALTY ?? 0) : 0,
        settlement_status: chargedRub > 0 || (processMode === 'regular' && (balance?.charged_by_type.FIXED_ROYALTY ?? 0) > 0) ? (settlement?.status ?? null) : null,
        adjustment_balance_hint: adjustmentBalanceHint && Math.abs(adjustmentBalanceHint.closing_balance_rub) < 1500.005 && Math.abs(adjustmentBalanceHint.closing_balance_rub) > 0.005
          ? adjustmentBalanceHint
          : null,
        adjustment_balance_history: adjustmentHistory,
        is_reviewed: settlement?.is_reviewed ?? false,
        is_confirmed: chargedRub > 0 || (processMode === 'regular' && (balance?.charged_by_type.FIXED_ROYALTY ?? 0) > 0) ? (balance?.is_confirmed ?? false) : false,
        accounting_confirmed_at: balance?.accounting_confirmed_at ?? null,
        accounting_confirmed_by: balance?.accounting_confirmed_by_user_id
          ? (royaltyUserDisplayName.get(balance.accounting_confirmed_by_user_id) ?? balance.accounting_confirmed_by_user_id)
          : null,
        foreign_payment_method: null,
      } satisfies RoyaltyAccrualProcessRow
    })

  const sortProcessRows = (a: RoyaltyAccrualProcessRow, b: RoyaltyAccrualProcessRow): number => {
    const rowKindOrder = { studio_ownership: 0, contract_without_studio: 1, open_studio_without_ownership: 2 }
    const rowKindCompare = rowKindOrder[a.row_kind] - rowKindOrder[b.row_kind]
    if (rowKindCompare !== 0) return rowKindCompare
    const cityCompare = (a.city_name ?? 'яяя').localeCompare(b.city_name ?? 'яяя', 'ru')
    if (cityCompare !== 0) return cityCompare
    const partnerNameCompare = (a.persons_fio ?? 'яяя').localeCompare(b.persons_fio ?? 'яяя', 'ru')
    if (partnerNameCompare !== 0) return partnerNameCompare
    const partnerNumberCompare = (a.partner_number ?? 0) - (b.partner_number ?? 0)
    if (partnerNumberCompare !== 0) return partnerNumberCompare
    const studioCompare = (a.studio_number ?? '').localeCompare(b.studio_number ?? '', 'ru', { numeric: true })
    if (studioCompare !== 0) return studioCompare
    if (a.process_mode !== b.process_mode) return a.process_mode === 'regular' ? -1 : 1
    return a.process_row_key.localeCompare(b.process_row_key)
  }

  const rows = [...ownershipProcessRows, ...contractWithoutStudioRows].sort(sortProcessRows)

  const openStudios: RoyaltyAccrualProcessDiagnosticsStudio[] = []
  for (const studio of ((allStudiosRes.data ?? []) as Array<{
    studio_internal_id: number
    studio_number: string | number | null
    address: string | null
    status_code: string | null
    city_id: number | null
  }>)) {
    if (managementCompanyStudioIds.has(studio.studio_internal_id)) continue

    const city = studio.city_id != null ? cityById.get(studio.city_id) : undefined
    const country = city?.country?.trim()
    if (!country) continue

    let activeStart: string | null = null
    let activeEnd: string | null = null
    let activeDays = 0
    for (
      let cursor = parseIsoDate(monthStart);
      cursor <= parseIsoDate(monthEnd);
      cursor.setUTCDate(cursor.getUTCDate() + 1)
    ) {
      const metricDate = formatIsoDate(cursor)
      if (!isActiveStudioStatus(getEffectiveStudioStatus(studio.studio_internal_id, metricDate))) continue
      activeStart ??= metricDate
      activeEnd = metricDate
      activeDays += 1
    }

    if (!activeStart || !activeEnd) continue
    openStudios.push({
      studio_internal_id: studio.studio_internal_id,
      studio_number: studio.studio_number == null ? null : String(studio.studio_number),
      studio_address: studio.address ?? null,
      studio_status: getEffectiveStudioStatus(studio.studio_internal_id, activeEnd),
      city_name: city?.city_name ?? null,
      country: city?.country ?? null,
      active_start: activeStart,
      active_end: activeEnd,
      active_days_count: activeDays,
    })
  }

  const nonManagementCompanyActiveContracts = activeContracts.filter(
    (contract) => !isManagementCompanySeries(contract.series_id)
  )

  const diagnostics: RoyaltyAccrualProcessDiagnostics = {
    month_days_count: monthDaysCount,
    open_studios_count: openStudios.length,
    opened_during_month_count: openStudios.filter((studio) => studio.active_start > monthStart).length,
    active_contracts_count: nonManagementCompanyActiveContracts.length,
    active_contracts_without_ownership_count: contractWithoutStudioRows.length,
    open_studios_without_ownership_count: openStudios.filter((studio) => !ownershipStudioIds.has(studio.studio_internal_id)).length,
    opened_during_month: openStudios
      .filter((studio) => studio.active_start > monthStart)
      .sort((a, b) => {
        const cityCompare = (a.city_name ?? 'яяя').localeCompare(b.city_name ?? 'яяя', 'ru')
        if (cityCompare !== 0) return cityCompare
        return (a.studio_number ?? '').localeCompare(b.studio_number ?? '', 'ru', { numeric: true })
      }),
    open_studios_without_ownership: openStudios
      .filter((studio) => !ownershipStudioIds.has(studio.studio_internal_id))
      .sort((a, b) => {
        const cityCompare = (a.city_name ?? 'яяя').localeCompare(b.city_name ?? 'яяя', 'ru')
        if (cityCompare !== 0) return cityCompare
        return (a.studio_number ?? '').localeCompare(b.studio_number ?? '', 'ru', { numeric: true })
      }),
  }

  const openStudioWithoutOwnershipRows = diagnostics.open_studios_without_ownership.map((studio) => ({
    process_row_key: `open-studio-without-ownership-${studio.studio_internal_id}-${studio.active_start}-${studio.active_end}`,
    process_mode: 'regular' as const,
    row_kind: 'open_studio_without_ownership' as const,
    data_quality_issues: ['Открыта хотя бы 1 день в месяце, но нет ownership'],
    contract_coverage_gap_days: 0,
    contract_coverage_gap_revenue_rub: 0,
    term_coverage_gap_days: 0,
    term_coverage_gap_revenue_rub: 0,
    ...buildRowFxNative(null, 0, 0, 1, null, 0, 0),
    period_segment_no: 1,
    component_adjustments: [],
    period_segment_start: studio.active_start,
    period_segment_end: studio.active_end,
    period_segment_days_count: studio.active_days_count,
    contract_id: null,
    contract_number: null,
    contract_period_start: null,
    contract_period_end: null,
    contract_actual_start: null,
    contract_actual_end: null,
    series_id: 0,
    partner_number: null,
    persons_fio: null,
    studio_internal_id: studio.studio_internal_id,
    studio_number: studio.studio_number,
    studio_address: studio.studio_address,
    studio_status: studio.studio_status,
    city_name: studio.city_name,
    country: studio.country,
    subscription_type: 'REGULAR' as const,
    term_id: null,
    term_contract_type: null,
    listok_crm_carrier: false,
    fixed_royalty_carrier: false,
    listok_crm_carrier_term_id: null,
    listok_crm_base_amount_rub: null,
    term_intervals: [],
    is_term_segmented: false,
    month_min_topup_rub: null,
    min_royalty_topup_rub: 0,
    split_min_royalty_topup_rub: 0,
    manual_min_royalty_topup_rub: 0,
    portal_revenue_rub: 0,
    listok_revenue_rub: 0,
    selected_revenue_rub: 0,
    selected_revenue_source: 'equal' as const,
    selected_refunds_rub: 0,
    portal_memberships_revenue_rub: 0,
    listok_memberships_revenue_rub: 0,
    selected_memberships_revenue_rub: 0,
    selected_memberships_revenue_source: 'equal' as const,
    portal_subscriptions_revenue_rub: 0,
    listok_subscriptions_revenue_rub: 0,
    selected_subscriptions_revenue_rub: 0,
    selected_subscriptions_revenue_source: 'equal' as const,
    portal_revenue_native: 0,
    listok_revenue_native: 0,
    selected_revenue_native: 0,
    portal_memberships_revenue_native: 0,
    listok_memberships_revenue_native: 0,
    selected_memberships_revenue_native: 0,
    portal_subscriptions_revenue_native: 0,
    listok_subscriptions_revenue_native: 0,
    selected_subscriptions_revenue_native: 0,
    selected_revenue_obl: 0,
    selected_memberships_revenue_obl: 0,
    selected_subscriptions_revenue_obl: 0,
    preview_selected_revenue_rub: 0,
    preview_selected_revenue_native: 0,
    preview_selected_memberships_revenue_rub: 0,
    preview_selected_subscriptions_revenue_rub: 0,
    revenue_checked: false,
    royalty_checked: false,
    revenue_checked_at: null,
    revenue_checked_by: null,
    royalty_checked_at: null,
    royalty_checked_by: null,
    foreign_payment_method: null,
    revenue_days_count: 0,
    revenue_confirmed_days_count: 0,
    royalty_percent: null,
    royalty_agency_percent: null,
    royalty_vat_percent: null,
    royalty_min_amount_rub: null,
    royalty_listok_crm_base_amount_rub: null,
    charge_revenue_rub: 0,
    charge_memberships_revenue_rub: 0,
    charge_subscriptions_revenue_rub: 0,
    charged_revenue_rub: 0,
    charged_memberships_revenue_rub: 0,
    charged_subscriptions_revenue_rub: 0,
    adjustment_rub: null,
    adjustment_comment: null,
    adjustment_updated_at: null,
    adjustment_updated_by_user_id: null,
    adjustment_updated_by_name: null,
    base_royalty_rub: 0,
    estimated_royalty_rub: 0,
    estimated_split_royalty_rub: 0,
    estimated_split_agency_rub: 0,
    estimated_manual_royalty_rub: 0,
    manual_royalty_charge_rub: 0,
    negative_accrual_refund_rub: 0,
    persisted_negative_accrual_refund_rub: null,
    persisted_negative_accrual_refund_status: null,
    persisted_negative_accrual_refund_offset_rub: null,
    estimated_listok_crm_rub: 0,
    estimated_fixed_royalty_rub: 0,
    charged_rub: 0,
    paid_rub: 0,
    covered_rub: 0,
    rb_tax_offset_rub: 0,
    delta_rub: 0,
    debt_rub: 0,
    charge_count: 0,
    charged_split_royalty_rub: 0,
    charged_split_agency_rub: 0,
    charged_manual_royalty_rub: 0,
    charged_listok_crm_rub: 0,
    charged_fixed_royalty_rub: 0,
    paid_split_royalty_rub: 0,
    paid_split_agency_rub: 0,
    paid_manual_royalty_rub: 0,
    paid_listok_crm_rub: 0,
    paid_fixed_royalty_rub: 0,
    covered_split_royalty_rub: 0,
    covered_split_agency_rub: 0,
    covered_manual_royalty_rub: 0,
    covered_listok_crm_rub: 0,
    covered_fixed_royalty_rub: 0,
    settlement_status: null,
    adjustment_balance_hint: null,
    adjustment_balance_history: [],
    is_reviewed: false,
    is_confirmed: false,
    accounting_confirmed_at: null,
    accounting_confirmed_by: null,
  } satisfies RoyaltyAccrualProcessRow))

  const currencyCoverage: RoyaltyProcessCurrencyCoverage[] = (fxSupplement.currency_coverage_rows ?? []).map((r) => ({
    currency_code: r.currency_code,
    studio_count_country: Number(r.studio_count_country ?? 0),
    studio_count_listok: Number(r.studio_count_listok ?? 0),
    to_rub_rate: r.to_rub_rate == null ? null : Number(r.to_rub_rate),
    quote_date: r.quote_date ?? null,
    quote_age_days: r.quote_age_days == null ? null : Number(r.quote_age_days),
    state: r.state ?? 'FRESH',
  }))

  const resultRows = [...rows, ...openStudioWithoutOwnershipRows]
    .map((row) => ({ ...row, foreign_payment_method: foreignPaymentMethodBySeries.get(row.series_id) ?? null }))
    .sort(sortProcessRows)
  const result = {
    rows: resultRows,
    diagnostics,
    currency_coverage: currencyCoverage,
    fx_supplement_error_message: fxSupplementErrorMessage,
    // Фолбэк 14 нужен только когда фронт впереди миграции: RPC ещё не отдаёт порог.
    fx_staleness_threshold_days: Number(fxSupplement.fx_staleness_threshold_days ?? 14),
  }
  markRoyaltyPerf('load.royalty_process.complete', {
    year: input.year,
    month: input.month,
    durationMs: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - processStartedAt),
    rows: resultRows.length,
    diagnosticsOpenStudios: diagnostics.open_studios_without_ownership.length,
    currencyCoverageRows: currencyCoverage.length,
  })
  return result
}

export async function setRoyaltyForeignPaymentMethod(params: {
  seriesId: number; year: number; month: number; method: 'invoice' | 'link' | 'details'
}): Promise<void> {
  const { error } = await withAuthRecovery(() => (getSupabase() as any).rpc('set_royalty_foreign_payment_method', {
    p_series_id: params.seriesId, p_year: params.year, p_month: params.month, p_method: params.method,
  }))
  if (error) throw error
}

function parseBankAmount(raw: string | null): number {
  if (!raw) return 0
  const normalized = raw.replace(/\s/g, '').replace(',', '.')
  const parsed = parseFloat(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

export async function loadRoyaltyDiagnosticsData(
  studioSummaries: RoyaltyStudioSummary[] = []
): Promise<RoyaltyDiagnosticsData> {
  const supabase = getSupabase()

  const [
    termsCountRes,
    chargesCountRes,
    paymentsCountRes,
    allocationsCountRes,
    legalEntitiesCountRes,
    stagingCountRes,
    chargesRes,
    paymentsRes,
    allocationsRes,
    legalEntitiesRes,
    stagingRes,
  ] = await Promise.all([
    withAuthRecovery(() => supabase.from('royalty_series_terms').select('term_id', { head: true, count: 'exact' })),
    withAuthRecovery(() => supabase.from('royalty_charges').select('charge_id', { head: true, count: 'exact' })),
    withAuthRecovery(() => supabase.from('royalty_payments').select('payment_id', { head: true, count: 'exact' })),
    withAuthRecovery(() => supabase.from('royalty_payment_allocations').select('allocation_id', { head: true, count: 'exact' })),
    withAuthRecovery(() => supabase.from('partner_legal_entities').select('entity_id', { head: true, count: 'exact' })),
    withAuthRecovery(() => supabase.from('royalty_bank_statement_staging').select('row_id', { head: true, count: 'exact' })),
    withAuthRecovery(() => supabase.from('royalty_balance').select('year,month,series_id,royalty_amount_rub')),
    withAuthRecovery(() => supabase.from('royalty_payment_reconciliation_v').select('payment_id,contract_id,source,reconciliation_status,amount_rub')),
    withAuthRecovery(() => supabase.from('royalty_payment_allocations').select('allocation_id,payment_id,allocated_amount_rub')),
    withAuthRecovery(() => supabase.from('partner_legal_entities').select('partner_number,inn,legal_name')),
    withAuthRecovery(() => supabase.from('royalty_bank_statement_staging_v').select('processing_status,credit_amount,inn')),
  ])

  const errors = [
    termsCountRes.error,
    chargesCountRes.error,
    paymentsCountRes.error,
    allocationsCountRes.error,
    legalEntitiesCountRes.error,
    stagingCountRes.error,
    chargesRes.error,
    paymentsRes.error,
    allocationsRes.error,
    legalEntitiesRes.error,
    stagingRes.error,
  ].filter(Boolean)

  if (errors.length > 0) {
    throw errors[0]
  }

  const getExactCount = (result: unknown): number => {
    const value = (result as { count?: number } | null)?.count
    return typeof value === 'number' ? value : 0
  }

  const tableCounts = {
    terms_count: getExactCount(termsCountRes),
    charges_count: getExactCount(chargesCountRes),
    payments_count: getExactCount(paymentsCountRes),
    allocations_count: getExactCount(allocationsCountRes),
    legal_entities_count: getExactCount(legalEntitiesCountRes),
    staging_count: getExactCount(stagingCountRes),
  }

  const charges = (chargesRes.data ?? []) as Array<{
    year: number
    month: number
    series_id: number
    royalty_amount_rub: number | null
  }>
  const chargeMap = new Map<string, { series: Set<number>; total_rub: number }>()
  for (const row of charges) {
    const key = `${row.year}-${row.month}`
    const existing = chargeMap.get(key) ?? { series: new Set<number>(), total_rub: 0 }
    existing.series.add(row.series_id)
    existing.total_rub += row.royalty_amount_rub ?? 0
    chargeMap.set(key, existing)
  }
  const chargesByPeriods: RoyaltyDiagnosticsChargePeriod[] = [...chargeMap.entries()]
    .map(([key, value]) => {
      const [year, month] = key.split('-').map(Number)
      return {
        year,
        month,
        studios: value.series.size,
        total_rub: value.total_rub,
      }
    })
    .sort((a, b) => b.year - a.year || b.month - a.month)

  const payments = (paymentsRes.data ?? []) as Array<{
    payment_id: number
    contract_id: number | null
    source: 'MANUAL' | 'BANK_IMPORT' | 'OFFSET'
    reconciliation_status: RoyaltyDiagnosticsPaymentsByStatus['reconciliation_status']
    amount_rub: number | null
  }>
  const paymentStatusMap = new Map<string, RoyaltyDiagnosticsPaymentsByStatus>()
  for (const row of payments) {
    const key = `${row.source}:${row.reconciliation_status}`
    const existing = paymentStatusMap.get(key) ?? {
      source: row.source,
      reconciliation_status: row.reconciliation_status,
      cnt: 0,
      total_rub: 0,
    }
    existing.cnt += 1
    existing.total_rub += row.amount_rub ?? 0
    paymentStatusMap.set(key, existing)
  }
  const paymentsByStatus = [...paymentStatusMap.values()].sort((a, b) => {
    if (a.source === b.source) {
      return a.reconciliation_status.localeCompare(b.reconciliation_status)
    }
    return a.source.localeCompare(b.source)
  })

  const allocations = (allocationsRes.data ?? []) as Array<{
    allocation_id: number
    payment_id: number
    allocated_amount_rub: number | null
  }>
  const allocationsTotalRub = allocations.reduce((sum, row) => sum + (row.allocated_amount_rub ?? 0), 0)
  const allocationsTotalCount = allocations.length

  const allocByPayment = new Map<number, number>()
  for (const row of allocations) {
    allocByPayment.set(row.payment_id, (allocByPayment.get(row.payment_id) ?? 0) + 1)
  }
  const contractPaymentsMap = new Map<number, RoyaltyDiagnosticsContractWithoutAllocations>()
  for (const row of payments) {
    if (row.contract_id == null) continue
    const existing = contractPaymentsMap.get(row.contract_id) ?? {
      contract_id: row.contract_id,
      payments_count: 0,
      payments_sum_rub: 0,
      allocations_count: 0,
    }
    existing.payments_count += 1
    existing.payments_sum_rub += row.amount_rub ?? 0
    existing.allocations_count += allocByPayment.get(row.payment_id) ?? 0
    contractPaymentsMap.set(row.contract_id, existing)
  }
  const contractsWithoutAllocations = [...contractPaymentsMap.values()]
    .filter((row) => row.allocations_count === 0)
    .sort((a, b) => b.payments_sum_rub - a.payments_sum_rub)

  const legalEntities = (legalEntitiesRes.data ?? []) as Array<{
    partner_number: number
    inn: string | null
    legal_name: string | null
  }>
  const stagingRows = (stagingRes.data ?? []) as Array<{
    processing_status: string | null
    credit_amount: string | null
    inn: string | null
  }>

  const stagingInnSet = new Set(
    stagingRows
      .map((r) => (r.inn ?? '').trim())
      .filter((inn) => inn.length > 0)
  )
  const legalEntitiesCoverage: RoyaltyDiagnosticsLegalEntityCoverage[] = legalEntities
    .filter((e) => !!e.inn)
    .map((e) => ({
      inn: e.inn ?? '',
      legal_name: e.legal_name ?? '—',
      partner_number: e.partner_number,
      has_bank_rows: stagingInnSet.has((e.inn ?? '').trim()),
    }))
    .sort((a, b) => a.partner_number - b.partner_number)

  const stagingStatusMap = new Map<string, RoyaltyDiagnosticsStagingByStatus>()
  for (const row of stagingRows) {
    const status = row.processing_status ?? 'UNKNOWN'
    const existing = stagingStatusMap.get(status) ?? {
      processing_status: status,
      count: 0,
      total_rub: 0,
    }
    existing.count += 1
    existing.total_rub += parseBankAmount(row.credit_amount)
    stagingStatusMap.set(status, existing)
  }
  const stagingByStatus = [...stagingStatusMap.values()].sort((a, b) => b.count - a.count)

  const topDebtors = [...studioSummaries]
    .sort((a, b) => (b.total_debt_rub ?? 0) - (a.total_debt_rub ?? 0))
    .slice(0, 20)

  return {
    tableCounts,
    chargesByPeriods,
    paymentsByStatus,
    allocationsTotalRub,
    allocationsTotalCount,
    contractsWithoutAllocations,
    topDebtors,
    legalEntitiesCoverage,
    stagingByStatus,
  }
}

// ---------- Monthly balances for a series (=studio) ----------

// royalty_balance не отдаёт segment_no/segment_start/segment_end (движок 20260705160000),
// а посегментный месяц даёт несколько одинаково подписанных строк одного charge_type —
// дозагружаем границы сегментов из royalty_charges, чтобы UI мог их различать.
// Здесь же подтягиваем component adjustments, чтобы read-экраны объясняли
// итог как base + adjustment, не требуя отдельного SQL-разбора.
async function enrichBalancesWithChargeSegments(balances: RoyaltyBalance[]): Promise<RoyaltyBalance[]> {
  if (balances.length === 0) return balances
  const supabase = getSupabase()
  const chargeIds = balances.map((b) => b.charge_id)
  const segmentByCharge = new Map<number, { segment_no: number; segment_start: string | null; segment_end: string | null }>()
  for (let i = 0; i < chargeIds.length; i += 500) {
    const { data, error } = await withAuthRecovery(() =>
      supabase
        .from('royalty_charges')
        .select('charge_id,segment_no,segment_start,segment_end')
        .in('charge_id', chargeIds.slice(i, i + 500))
    )
    if (error) {
      // Сегменты — только подпись на UI; не валим экран балансов из-за них.
      console.error('Failed to load charge segments:', error)
      return balances
    }
    for (const row of (data ?? []) as Array<{ charge_id: number; segment_no: number; segment_start: string | null; segment_end: string | null }>) {
      segmentByCharge.set(row.charge_id, row)
    }
  }
  const adjustmentByKey = new Map<string, {
    signedRub: number
    reason: string | null
    updatedAt: string | null
  }>()
  const seriesIds = Array.from(new Set(balances.map((b) => b.series_id)))
  const years = Array.from(new Set(balances.map((b) => b.year)))
  if (seriesIds.length > 0 && years.length > 0) {
    for (let s = 0; s < seriesIds.length; s += 100) {
      for (let y = 0; y < years.length; y += 20) {
        const { data, error } = await withAuthRecovery(() =>
          supabase
            .from('royalty_charge_adjustments')
            .select('series_id,year,month,segment_no,charge_type,adjustment_type,amount_rub,reason,updated_at')
            .in('series_id', seriesIds.slice(s, s + 100))
            .in('year', years.slice(y, y + 20))
        )
        if (error) {
          // Корректировки — только breakdown read-модели; баланс должен открываться и без них.
          console.error('Failed to load charge adjustments:', error)
          return balances.map((b) => {
            const seg = segmentByCharge.get(b.charge_id)
            return seg ? { ...b, segment_no: seg.segment_no, segment_start: seg.segment_start, segment_end: seg.segment_end } : b
          })
        }
        for (const row of (data ?? []) as Array<{
          series_id: number
          year: number
          month: number
          segment_no: number | null
          charge_type: RoyaltyBalance['charge_type']
          adjustment_type: string | null
          amount_rub: number | string | null
          reason: string | null
          updated_at: string | null
        }>) {
          const segmentNo = row.segment_no == null ? 1 : Number(row.segment_no)
          const amount = Number(row.amount_rub ?? 0)
          if (Math.abs(amount) <= 0.005) continue
          const signedRub = row.adjustment_type === 'REDUCE' ? -amount : amount
          const key = `${row.series_id}|${row.year}|${row.month}|${segmentNo}|${row.charge_type}`
          const current = adjustmentByKey.get(key) ?? { signedRub: 0, reason: null, updatedAt: null }
          current.signedRub = roundMoney(current.signedRub + signedRub)
          current.reason = row.reason ?? current.reason
          current.updatedAt = row.updated_at ?? current.updatedAt
          adjustmentByKey.set(key, current)
        }
      }
    }
  }
  return balances.map((b) => {
    const seg = segmentByCharge.get(b.charge_id)
    const segmentNo = seg?.segment_no ?? b.segment_no ?? 1
    const adjustment = adjustmentByKey.get(`${b.series_id}|${b.year}|${b.month}|${segmentNo}|${b.charge_type}`)
    return {
      ...b,
      ...(seg ? { segment_no: seg.segment_no, segment_start: seg.segment_start, segment_end: seg.segment_end } : {}),
      ...(adjustment
        ? {
          charge_adjustment_rub: adjustment.signedRub,
          charge_base_amount_rub: roundMoney(Number(b.royalty_amount_rub ?? 0) - adjustment.signedRub),
          charge_adjustment_reason: adjustment.reason,
          charge_adjustment_updated_at: adjustment.updatedAt,
        }
        : {}),
    }
  })
}

export async function loadRoyaltyBalancesBySeries(seriesId: number): Promise<RoyaltyBalance[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_balance')
      .select('*')
      .eq('series_id', seriesId)
      .order('year', { ascending: false })
      .order('month', { ascending: false })
  )

  if (error) {
    console.error('Failed to load royalty balances:', error)
    throw error
  }

  return enrichBalancesWithChargeSegments((data ?? []) as RoyaltyBalance[])
}

export async function loadRoyaltyPeriodSettlementsBySeries(seriesId: number): Promise<RoyaltyPeriodSettlement[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_period_settlement_v')
      .select('*')
      .eq('series_id', seriesId)
      .order('year', { ascending: false })
      .order('month', { ascending: false })
  )

  if (error) {
    console.error('Failed to load royalty period settlements:', error)
    throw error
  }

  return ((data ?? []) as RoyaltyPeriodSettlement[]).map(normalizeRoyaltyPeriodSettlement)
}

export async function loadRoyaltyPeriodReviewEventsBySeries(seriesId: number): Promise<RoyaltyPeriodReviewEvent[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_period_review_events')
      .select('*')
      .eq('series_id', seriesId)
      .order('created_at', { ascending: false })
      .order('event_id', { ascending: false })
  )

  if (error) {
    console.error('Failed to load royalty period review history:', error)
    throw error
  }

  return ((data ?? []) as RoyaltyPeriodReviewEvent[]).map((row) => ({
    ...row,
    event_id: Number((row as any).event_id ?? 0),
    series_id: Number((row as any).series_id ?? 0),
    year: Number((row as any).year ?? 0),
    month: Number((row as any).month ?? 0),
    source_row_id: (row as any).source_row_id == null ? null : Number((row as any).source_row_id),
    actor_user_id: (row as any).actor_user_id ?? null,
    actor_name: (row as any).actor_name ?? null,
    reason_code: (row as any).reason_code ?? '',
    reason_detail: (row as any).reason_detail ?? null,
    source_table: (row as any).source_table ?? null,
    source_operation: (row as any).source_operation ?? null,
    created_at: (row as any).created_at ?? '',
    event_type: ((row as any).event_type ?? 'REVIEWED') as RoyaltyPeriodReviewEvent['event_type'],
  }))
}

export interface RoyaltyReviewProgressSummary {
  relevant_periods_count: number
  reviewed_periods_count: number
  unreviewed_periods_count: number
  reviewed_partners_count: number
  partially_reviewed_partners_count: number
  unreviewed_partners_count: number
  partner_stats: RoyaltyPartnerReviewStats[]
}

export async function loadRoyaltyReviewProgressSummary(seriesIds: number[]): Promise<RoyaltyReviewProgressSummary> {
  if (seriesIds.length === 0) {
    return {
      relevant_periods_count: 0,
      reviewed_periods_count: 0,
      unreviewed_periods_count: 0,
      reviewed_partners_count: 0,
      partially_reviewed_partners_count: 0,
      unreviewed_partners_count: 0,
      partner_stats: [],
    }
  }
  const supabase = getSupabase()
  const uniqueSeriesIds = [...new Set(seriesIds.filter((id) => Number.isFinite(id)))]
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('royalty_review_progress_summary', {
      p_series_ids: uniqueSeriesIds,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
  const summary = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
  return {
    relevant_periods_count: Number(summary.relevant_periods_count ?? 0),
    reviewed_periods_count: Number(summary.reviewed_periods_count ?? 0),
    unreviewed_periods_count: Number(summary.unreviewed_periods_count ?? 0),
    reviewed_partners_count: Number(summary.reviewed_partners_count ?? 0),
    partially_reviewed_partners_count: Number(summary.partially_reviewed_partners_count ?? 0),
    unreviewed_partners_count: Number(summary.unreviewed_partners_count ?? 0),
    partner_stats: ((summary.partner_stats ?? []) as RoyaltyPartnerReviewStats[]).map((row) => ({
      partner_number: Number(row.partner_number),
      relevant_periods_count: Number(row.relevant_periods_count ?? 0),
      reviewed_periods_count: Number(row.reviewed_periods_count ?? 0),
      is_fully_reviewed: Boolean(row.is_fully_reviewed),
    })),
  }
}

export interface RoyaltyPartnerFilterFlags {
  monthDebtPartnerNumbers: number[]
  noPeriodPartnerNumbers: number[]
  assignedNoChargePartnerNumbers: number[]
  assignedToPartnerNumbers: number[]
}

export async function loadRoyaltyPartnerFilterFlags(input: {
  year: number
  month: number
}): Promise<RoyaltyPartnerFilterFlags> {
  const supabase = getSupabase()

  const [seriesRes, chargesRes, paymentsRes, allocationsRes] = await Promise.all([
    withAuthRecovery(() =>
      (supabase as any)
        .from('royalty_studio_summary')
        .select('series_id,partner_number')
    ),
    withAuthRecovery(() =>
      (supabase as any)
        .from('royalty_charges')
        .select('series_id,royalty_amount_rub')
        .eq('year', input.year)
        .eq('month', input.month)
        .gt('royalty_amount_rub', 0)
    ),
    withAuthRecovery(() =>
      (supabase as any)
        .from('royalty_payments')
        .select('payment_id,partner_number,contract_id,assigned_series_id,assigned_year,assigned_month,is_deposit,amount_rub,refunded_amount_rub,payment_kind')
        .not('partner_number', 'is', null)
    ),
    withAuthRecovery(() =>
      (supabase as any)
        .from('royalty_payment_allocations')
        .select('payment_id,allocated_amount_rub')
    ),
  ])

  if (seriesRes.error) throw seriesRes.error
  if (chargesRes.error) throw chargesRes.error
  if (paymentsRes.error) throw paymentsRes.error
  if (allocationsRes.error) throw allocationsRes.error

  const seriesToPartner = new Map<number, number>()
  for (const row of (seriesRes.data ?? []) as Array<{ series_id: number; partner_number: number }>) {
    seriesToPartner.set(row.series_id, row.partner_number)
  }

  const monthDebtPartners = new Set<number>()
  for (const row of (chargesRes.data ?? []) as Array<{ series_id: number; royalty_amount_rub: number }>) {
    const partner = seriesToPartner.get(row.series_id)
    if (typeof partner === 'number' && Number(row.royalty_amount_rub ?? 0) > 0) monthDebtPartners.add(partner)
  }

  const allocatedByPayment = new Map<number, number>()
  for (const row of (allocationsRes.data ?? []) as Array<{ payment_id: number; allocated_amount_rub: number | null }>) {
    allocatedByPayment.set(row.payment_id, (allocatedByPayment.get(row.payment_id) ?? 0) + Number(row.allocated_amount_rub ?? 0))
  }

  const noPeriodPartners = new Set<number>()
  const assignedNoChargePartners = new Set<number>()
  const assignedToPartnerNumbers = new Set<number>()
  for (const row of (paymentsRes.data ?? []) as Array<{
    payment_id: number
    partner_number: number | null
    contract_id: number | null
    assigned_series_id: number | null
    is_deposit: boolean | null
    assigned_year: number | null
    assigned_month: number | null
    amount_rub: number | null
    refunded_amount_rub: number | null
    payment_kind: string | null
  }>) {
    if (row.partner_number == null || row.is_deposit) continue
    if (row.payment_kind != null && row.payment_kind !== 'ROYALTY') continue
    const remainingRub = Math.max(
      Number(row.amount_rub ?? 0) -
        (allocatedByPayment.get(row.payment_id) ?? 0) -
        Number(row.refunded_amount_rub ?? 0),
      0
    )
    if (remainingRub <= 0.005) continue
    if (
      (!row.assigned_year || !row.assigned_month) &&
      (allocatedByPayment.get(row.payment_id) ?? 0) === 0
    ) {
      noPeriodPartners.add(row.partner_number)
    }
    if (row.assigned_series_id != null && row.assigned_year != null && row.assigned_month != null) {
      const hasCharge = (chargesRes.data ?? []).some((charge: any) => Number(charge.series_id) === Number(row.assigned_series_id))
      if (!hasCharge) assignedNoChargePartners.add(row.partner_number)
    }
    if (row.contract_id == null && row.assigned_series_id == null) assignedToPartnerNumbers.add(row.partner_number)
  }

  return {
    monthDebtPartnerNumbers: [...monthDebtPartners],
    noPeriodPartnerNumbers: [...noPeriodPartners],
    assignedNoChargePartnerNumbers: [...assignedNoChargePartners],
    assignedToPartnerNumbers: [...assignedToPartnerNumbers],
  }
}

/**
 * Load period settlements for several series at once (partner-wide matrix).
 */
export async function loadRoyaltyPeriodSettlementsBySeriesIds(
  seriesIds: number[]
): Promise<RoyaltyPeriodSettlement[]> {
  if (seriesIds.length === 0) return []
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_period_settlement_v')
      .select('*')
      .in('series_id', seriesIds)
      .order('year', { ascending: false })
      .order('month', { ascending: false })
  )

  if (error) {
    console.error('Failed to load royalty period settlements:', error)
    throw error
  }

  return ((data ?? []) as RoyaltyPeriodSettlement[]).map((row) => ({
    ...row,
    charge_count: Number(row.charge_count ?? 0),
    assigned_payments_count: Number(row.assigned_payments_count ?? 0),
    charged_rub: Number(row.charged_rub ?? 0),
    assigned_payments_rub: Number(row.assigned_payments_rub ?? 0),
    allocated_rub: Number(row.allocated_rub ?? 0),
    recognized_rub: Number(row.recognized_rub ?? 0),
    unallocated_assigned_rub: Number(row.unallocated_assigned_rub ?? 0),
    debt_rub: Number(row.debt_rub ?? 0),
    credit_rub: Number(row.credit_rub ?? 0),
    closing_balance_rub: Number(row.closing_balance_rub ?? 0),
    paid_to_charges_rub: Number(row.paid_to_charges_rub ?? row.recognized_rub ?? 0),
    written_off_rub: Number((row as any).written_off_rub ?? 0),
    offset_rub: Number((row as any).offset_rub ?? 0),
    rb_tax_offset_rub: Number((row as any).rb_tax_offset_rub ?? 0),
    tolerance_rub: Number(row.tolerance_rub ?? 0),
    is_reviewed: Boolean((row as any).is_reviewed),
    reviewed_at: (row as any).reviewed_at ?? null,
    reviewed_by_user_id: (row as any).reviewed_by_user_id ?? null,
    reviewed_by: (row as any).reviewed_by ?? null,
    review_note: (row as any).review_note ?? null,
  }))
}

function normalizeRoyaltyPeriodSettlement(row: RoyaltyPeriodSettlement): RoyaltyPeriodSettlement {
  return {
    ...row,
    charge_count: Number(row.charge_count ?? 0),
    assigned_payments_count: Number(row.assigned_payments_count ?? 0),
    charged_rub: Number(row.charged_rub ?? 0),
    assigned_payments_rub: Number(row.assigned_payments_rub ?? 0),
    allocated_rub: Number(row.allocated_rub ?? 0),
    recognized_rub: Number(row.recognized_rub ?? 0),
    unallocated_assigned_rub: Number(row.unallocated_assigned_rub ?? 0),
    debt_rub: Number(row.debt_rub ?? 0),
    credit_rub: Number(row.credit_rub ?? 0),
    closing_balance_rub: Number(row.closing_balance_rub ?? 0),
    paid_to_charges_rub: Number(row.paid_to_charges_rub ?? row.recognized_rub ?? 0),
    written_off_rub: Number((row as any).written_off_rub ?? 0),
    offset_rub: Number((row as any).offset_rub ?? 0),
    rb_tax_offset_rub: Number((row as any).rb_tax_offset_rub ?? 0),
    tolerance_rub: Number(row.tolerance_rub ?? 0),
    is_reviewed: Boolean((row as any).is_reviewed),
    reviewed_at: (row as any).reviewed_at ?? null,
    reviewed_by_user_id: (row as any).reviewed_by_user_id ?? null,
    reviewed_by: (row as any).reviewed_by ?? null,
    review_note: (row as any).review_note ?? null,
  }
}

/**
 * Load settlement only for exact period keys visible on the current page.
 * This keeps /royalties/allocation/:category from fetching full series history
 * just to render 20 partner groups.
 */
export async function loadRoyaltyPeriodSettlementsByKeys(
  keys: Array<{ seriesId: number; year: number; month: number }>
): Promise<RoyaltyPeriodSettlement[]> {
  const uniqueKeys = Array.from(
    new Map(
      keys
        .filter((key) => Number.isFinite(key.seriesId) && Number.isFinite(key.year) && Number.isFinite(key.month))
        .map((key) => [`${key.seriesId}:${key.year}:${key.month}`, key])
    ).values()
  )
  if (uniqueKeys.length === 0) return []

  const supabase = getSupabase()
  const chunks: typeof uniqueKeys[] = []
  for (let index = 0; index < uniqueKeys.length; index += 50) {
    chunks.push(uniqueKeys.slice(index, index + 50))
  }

  const pages = await Promise.all(chunks.map(async (chunk) => {
    const filter = chunk
      .map((key) => `and(series_id.eq.${key.seriesId},year.eq.${key.year},month.eq.${key.month})`)
      .join(',')
    const { data, error } = await measureRoyaltyAsync(
      'query.royalty_period_settlement_v.page_keys',
      () => withAuthRecovery(() =>
        (supabase as any)
          .from('royalty_period_settlement_v')
          .select('*')
          .or(filter)
      ),
      (result) => ({
        keys: chunk.length,
        error: Boolean((result as any).error),
        ...summarizeRoyaltyPayload((result as any).data),
      })
    )
    if (error) {
      console.error('Failed to load royalty period settlements by keys:', error)
      throw error
    }
    return (data ?? []) as RoyaltyPeriodSettlement[]
  }))

  return pages.flat().map(normalizeRoyaltyPeriodSettlement)
}

export async function setRoyaltyPeriodReview(params: {
  seriesId: number
  year: number
  month: number
  isReviewed: boolean
  reviewedBy?: string | null
  note?: string | null
  reviewedAt?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_period_review', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
      p_is_reviewed: params.isReviewed,
      p_reviewed_by: params.reviewedBy ?? null,
      p_note: params.note ?? null,
      p_reviewed_at: params.reviewedAt ?? null,
    })
  )
  if (error) throw error
}

export async function setRoyaltyPeriodReviewBulk(params: {
  seriesIds: number[]
  year: number
  month: number
  isReviewed: boolean
  reviewedBy?: string | null
  note?: string | null
  reviewedAt?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_period_review_bulk', {
      p_series_ids: params.seriesIds,
      p_year: params.year,
      p_month: params.month,
      p_is_reviewed: params.isReviewed,
      p_reviewed_by: params.reviewedBy ?? null,
      p_note: params.note ?? null,
      p_reviewed_at: params.reviewedAt ?? null,
    })
  )
  if (error) throw error
  return Number(data ?? 0)
}

export async function setRoyaltyPeriodRevenueCheck(params: {
  seriesId: number
  year: number
  month: number
  isChecked: boolean
  portalRub: number
  listokRub: number
  selectedRub: number
  selectedSource: RevenueSource
  portalMembershipsRub?: number
  listokMembershipsRub?: number
  selectedMembershipsRub?: number
  selectedMembershipsSource?: RevenueSource
  portalSubscriptionsRub?: number
  listokSubscriptionsRub?: number
  selectedSubscriptionsRub?: number
  selectedSubscriptionsSource?: RevenueSource
  segmentRows?: RoyaltyAccrualSegmentSnapshot[]
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_period_revenue_check', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
      p_is_checked: params.isChecked,
      p_portal_rub: params.portalRub,
      p_listok_rub: params.listokRub,
      p_selected_rub: params.selectedRub,
      p_selected_source: params.selectedSource,
      p_portal_memberships_rub: params.portalMembershipsRub ?? null,
      p_listok_memberships_rub: params.listokMembershipsRub ?? null,
      p_selected_memberships_rub: params.selectedMembershipsRub ?? null,
      p_memberships_selected_source: params.selectedMembershipsSource ?? null,
      p_portal_subscriptions_rub: params.portalSubscriptionsRub ?? null,
      p_listok_subscriptions_rub: params.listokSubscriptionsRub ?? null,
      p_selected_subscriptions_rub: params.selectedSubscriptionsRub ?? null,
      p_subscriptions_selected_source: params.selectedSubscriptionsSource ?? null,
      p_segment_rows: params.segmentRows ?? null,
    })
  )
  if (error) throw error
}

export async function setRoyaltyPeriodRoyaltyCheck(params: {
  seriesId: number
  year: number
  month: number
  isChecked: boolean
  revenueRub: number
  royaltyPercent: number | null
  royaltyAmountRub: number
  membershipsRevenueRub?: number
  subscriptionsRevenueRub?: number
  splitRoyaltyRub?: number
  splitAgencyRub?: number
  manualRoyaltyRub?: number
  listokCrmRub?: number
  segmentRows?: RoyaltyAccrualSegmentSnapshot[]
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_period_royalty_check', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
      p_is_checked: params.isChecked,
      p_revenue_rub: params.revenueRub,
      p_royalty_percent: params.royaltyPercent,
      p_royalty_amount_rub: params.royaltyAmountRub,
      p_memberships_revenue_rub: params.membershipsRevenueRub ?? null,
      p_subscriptions_revenue_rub: params.subscriptionsRevenueRub ?? null,
      p_split_royalty_rub: params.splitRoyaltyRub ?? null,
      p_split_agency_rub: params.splitAgencyRub ?? null,
      p_manual_royalty_rub: params.manualRoyaltyRub ?? null,
      p_listok_crm_rub: params.listokCrmRub ?? null,
      p_segment_rows: params.segmentRows ?? null,
    })
  )
  if (error) throw error
}

// `saveRoyaltyPeriodAdjustment` (месячный adjustment_rub) удалён: корректировки теперь
// адресные по компонентам — см. setRoyaltyChargeAdjustment / clearRoyaltyChargeAdjustment.
// Серверный RPC save_royalty_period_adjustment ретайрен (REVOKE) миграцией 20260707140000.

export type RoyaltyChargeComponentTotal = {
  segment_no: number
    charge_type: RoyaltyChargeType
  amount_rub: number
}

export async function loadRoyaltyChargeComponentTotals(params: {
  seriesId: number
  year: number
  month: number
}): Promise<RoyaltyChargeComponentTotal[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_charges')
      .select('segment_no,charge_type,royalty_amount_rub')
      .eq('series_id', params.seriesId)
      .eq('year', params.year)
      .eq('month', params.month)
  )
  if (error) throw error

  const totals = new Map<string, RoyaltyChargeComponentTotal>()
  for (const row of (data ?? []) as Array<{
    segment_no: number | null
    charge_type: RoyaltyChargeComponentTotal['charge_type']
    royalty_amount_rub: number | string | null
  }>) {
    const segmentNo = row.segment_no == null ? 1 : Number(row.segment_no)
    const key = `${segmentNo}|${row.charge_type}`
    const current = totals.get(key) ?? {
      segment_no: segmentNo,
      charge_type: row.charge_type,
      amount_rub: 0,
    }
    current.amount_rub = roundMoney(current.amount_rub + Number(row.royalty_amount_rub ?? 0))
    totals.set(key, current)
  }

  return Array.from(totals.values()).sort((left, right) =>
    left.segment_no - right.segment_no || left.charge_type.localeCompare(right.charge_type)
  )
}

// Адресная корректировка компонента начисления. amountRub<=0 → очистка корректировки строки.
export async function setRoyaltyChargeAdjustment(params: {
  seriesId: number
  year: number
  month: number
  segmentNo: number
  chargeType: 'ROYALTY' | 'MANUAL_ROYALTY' | 'LISTOK_CRM' | 'FIXED_ROYALTY'
  adjustmentType: 'ADD' | 'REDUCE'
  amountRub: number
  /**
   * §7.7 п.2: сумма в валюте ОБЯЗАТЕЛЬСТВА. Прислана → она канон, рубли сервер выводит
   * сам по курсу сегмента. Не прислана → прежнее поведение (канон — рубли).
   */
  amountNative?: number
  reason: string
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_charge_adjustment', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
      p_segment_no: params.segmentNo,
      p_charge_type: params.chargeType,
      p_adjustment_type: params.adjustmentType,
      p_amount_rub: params.amountRub,
      p_reason: params.reason,
      ...(params.amountNative != null ? { p_amount_native: params.amountNative } : {}),
    })
  )
  if (error) throw error
}

export async function clearRoyaltyChargeAdjustment(params: {
  seriesId: number
  year: number
  month: number
  segmentNo: number
  chargeType: 'ROYALTY' | 'MANUAL_ROYALTY' | 'LISTOK_CRM' | 'FIXED_ROYALTY'
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('clear_royalty_charge_adjustment', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
      p_segment_no: params.segmentNo,
      p_charge_type: params.chargeType,
    })
  )
  if (error) throw error
}

export async function confirmRoyaltyPeriod(params: {
  seriesId: number
  year: number
  month: number
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('confirm_royalty_period', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
    })
  )
  if (error) throw error
  return Number(data ?? 0)
}

export async function unconfirmRoyaltyPeriod(params: {
  seriesId: number
  year: number
  month: number
  reason: string
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('unconfirm_royalty_period', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
      p_reason: params.reason,
    })
  )
  if (error) throw error
  return Number(data ?? 0)
}

export type RoyaltyPaymentRequestStatus = 'DRAFT' | 'READY' | 'SENT' | 'CANCELLED' | 'STALE'
export type RoyaltyPaymentRequestChargeType =
  | 'ROYALTY'
  | 'SPLIT_ROYALTY'
  | 'SPLIT_AGENCY'
  | 'MANUAL_ROYALTY'
  | 'LISTOK_CRM'
  | 'FIXED_ROYALTY'

export interface RoyaltyPaymentRequestOffsetAllocation {
  charge_id: number
  amount: number
  amount_rub: number
  charge_year: number
  charge_month: number
  charge_type: RoyaltyPaymentRequestChargeType
}

export interface RoyaltyPaymentRequestOffset {
  offset_id: number
  payment_id: number
  amount: number
  amount_rub: number
  currency_code: string
  created_at: string
  payment_date: string
  bank_document_number: string | null
  bank_document_date: string | null
  payment_purpose: string | null
  allocations: RoyaltyPaymentRequestOffsetAllocation[]
}

export interface RoyaltyPaymentRequest {
  request_id: number
  series_id: number
  year: number
  month: number
  charge_type: RoyaltyPaymentRequestChargeType
  recipient_partner_number: number
  contract_id: number
  amount: number
  currency_code: string
  status: RoyaltyPaymentRequestStatus
  payment_purpose: string
  accountant_comment: string | null
  final_message: string
  template_code: string
  params: Record<string, unknown>
  snapshot_at: string | null
  sent_at: string | null
  sent_by: string | null
  sent_by_name: string | null
  updated_at: string
  current_debt: number
  snapshot_debt: number
  paid_since_snapshot: number
  charge_ids: number[]
  persons_fio: string | null
  /**
   * LVREF — код требования (миграция 20261026120000). Есть только у требований,
   * созданных после внедрения; у валютных он стоит первым в назначении.
   */
  payment_reference_code?: string | null
  payment_offsets?: RoyaltyPaymentRequestOffset[]
  /** Живой зачёт РБ требования (миграция 20260925140000); null — нет. */
  rb_tax_offset?: RoyaltyRbTaxOffset | null
}

/**
 * Зачёт РБ: налог, удержанный белорусским франчайзи в бюджет РБ, гасит часть начисления
 * без денег. Начисление не уменьшается. Вводится в окне требования на оплату.
 */
export interface RoyaltyRbTaxOffset {
  offset_id: number
  request_id: number
  series_id: number
  year: number
  month: number
  currency_code: string
  amount: number
  amount_rub: number
  note: string | null
  doc_number: string | null
  doc_date: string | null
  created_at: string
  created_by_name: string | null
  voided_at: string | null
  void_reason: string | null
  allocations: RoyaltyPaymentRequestOffsetAllocation[]
}

export interface RoyaltyPaymentRequestRbTaxContext {
  request_id: number
  payer_country: string | null
  is_belarus_payer: boolean
  currency_code: string
  /** Начислено по требованию (до любых покрытий). */
  charged_amount: number
  /** Непогашенный остаток сейчас — уже за вычетом живого зачёта РБ. */
  current_debt: number
  active_offset: RoyaltyRbTaxOffset | null
}

function normalizeRbTaxOffset(raw: any): RoyaltyRbTaxOffset | null {
  if (!raw) return null
  return {
    ...raw,
    offset_id: Number(raw.offset_id),
    request_id: Number(raw.request_id),
    series_id: Number(raw.series_id),
    year: Number(raw.year),
    month: Number(raw.month),
    amount: Number(raw.amount ?? 0),
    amount_rub: Number(raw.amount_rub ?? 0),
    allocations: (raw.allocations ?? []).map((allocation: any) => ({
      ...allocation,
      charge_id: Number(allocation.charge_id),
      amount: Number(allocation.amount ?? 0),
      amount_rub: Number(allocation.amount_rub ?? 0),
      charge_year: Number(allocation.charge_year),
      charge_month: Number(allocation.charge_month),
    })),
  }
}

export async function loadRoyaltyPaymentRequestRbTaxContext(
  requestId: number
): Promise<RoyaltyPaymentRequestRbTaxContext> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_payment_request_rb_tax_context', { p_request_id: requestId })
  )
  if (error) throw error
  const raw = (data ?? {}) as any
  return {
    request_id: Number(raw.request_id ?? requestId),
    payer_country: raw.payer_country ?? null,
    is_belarus_payer: raw.is_belarus_payer === true,
    currency_code: raw.currency_code ?? 'RUB',
    charged_amount: Number(raw.charged_amount ?? 0),
    current_debt: Number(raw.current_debt ?? 0),
    active_offset: normalizeRbTaxOffset(raw.active_offset),
  }
}

/** Зачесть (или заменить прежний зачёт) в черновике требования. Возвращает новую версию требования. */
export async function setRoyaltyPaymentRequestRbTaxOffset(params: {
  requestId: number
  amount: number
  expectedUpdatedAt: string
  note?: string | null
  docNumber?: string | null
  docDate?: string | null
}): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_payment_request_rb_tax_offset', {
      p_request_id: params.requestId,
      p_amount: params.amount,
      p_expected_request_updated_at: params.expectedUpdatedAt,
      p_note: params.note ?? null,
      p_doc_number: params.docNumber ?? null,
      p_doc_date: params.docDate ?? null,
    })
  )
  if (error) throw error
  return String(data)
}

/** Отменить зачёт РБ. После передачи требования на отправку нужна причина. */
export async function revokeRoyaltyRbTaxOffset(offsetId: number, reason?: string | null): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('revoke_royalty_rb_tax_offset', { p_offset_id: offsetId, p_reason: reason ?? null })
  )
  if (error) throw error
}

export async function loadRoyaltyRbTaxOffsets(seriesId: number): Promise<RoyaltyRbTaxOffset[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_royalty_rb_tax_offsets', { p_series_id: seriesId })
  )
  if (error) throw error
  return ((data ?? []) as any[]).map(normalizeRbTaxOffset).filter((row): row is RoyaltyRbTaxOffset => row != null)
}

export interface RoyaltyPaymentRequestPurposeContext {
  contract_id: number
  contract_number: string | null
  contract_signed_at: string | null
  payer_inn: string | null
  /** Страна того же юрлица, чей номер лежит в payer_inn. */
  payer_country: string | null
}

export async function loadRoyaltyPaymentRequestPurposeContexts(
  requests: Array<Pick<RoyaltyPaymentRequest, 'contract_id' | 'recipient_partner_number'>>
): Promise<Record<number, RoyaltyPaymentRequestPurposeContext>> {
  if (requests.length === 0) return {}
  const supabase = getSupabase()
  const contractIds = Array.from(new Set(requests.map((request) => request.contract_id)))
  const contracts: Array<{
    contract_id: number
    entity_id: number | null
    contract_number: string | null
    signed_at: string | null
  }> = []
  const legalEntities: Array<{
    entity_id: number
    partner_number: number
    inn: string | null
    country: string | null
    is_primary: boolean | null
  }> = []

  for (let offset = 0; offset < contractIds.length; offset += 200) {
    const ids = contractIds.slice(offset, offset + 200)
    const { data, error } = await fetchAllSupabaseRows<{
      contract_id: number
      entity_id: number | null
      contract_number: string | null
      signed_at: string | null
    }>(() => supabase
      .from('contracts')
      .select('contract_id,entity_id,contract_number,signed_at')
      .in('contract_id', ids)
      .order('contract_id'))
    if (error) throw error
    contracts.push(...data)
  }

  // Юрлица берём по entity_id самого договора, а не по партнёру: сторона может
  // быть кросс-партнёрской (partner_entity_links), и выборка «юрлица партнёра»
  // такую просто не вернула бы.
  const contractEntityIds = Array.from(
    new Set(contracts.map((contract) => contract.entity_id).filter((id): id is number => id != null))
  )
  for (let offset = 0; offset < contractEntityIds.length; offset += 200) {
    const ids = contractEntityIds.slice(offset, offset + 200)
    const { data, error } = await fetchAllSupabaseRows<{
      entity_id: number
      partner_number: number
      inn: string | null
      country: string | null
      is_primary: boolean | null
    }>(() => supabase
      .from('partner_legal_entities')
      .select('entity_id,partner_number,inn,country,is_primary')
      .in('entity_id', ids)
      .order('entity_id'))
    if (error) throw error
    legalEntities.push(...data)
  }

  const entityById = new Map(legalEntities.map((entity) => [entity.entity_id, entity]))

  // ⚠️ Фронт плательщика НЕ угадывает. Сервер определяет его строго — сторона
  // ДКК на периоде начисления плюс цепочка debt_policy применённых ДС
  // (`royalty_resolve_charge_debt_party`), а `contract_party_intervals` фронту
  // не гранится. Поэтому здесь остаётся ровно один случай, где ответ совпадает
  // с серверным по построению: юрлицо указано в самом договоре и ни одного
  // применённого ENTITY_CHANGE у него нет. Фолбэк «единственное юрлицо
  // партнёра» снят вместе с серверным: этот текст фронт СОХРАНЯЕТ в требование,
  // то есть подставленный наугад номер уходил бы франчайзи.
  const entityChangedContracts = new Set<number>()
  for (let offset = 0; offset < contractIds.length; offset += 200) {
    const ids = contractIds.slice(offset, offset + 200)
    const { data, error } = await fetchAllSupabaseRows<{ contract_id: number }>(() => supabase
      .from('contract_amendments')
      .select('contract_id')
      .eq('amendment_type', 'ENTITY_CHANGE')
      .in('contract_id', ids)
      .order('contract_id'))
    if (error) throw error
    for (const row of data) entityChangedContracts.add(row.contract_id)
  }

  return Object.fromEntries(contracts.map((contract) => {
    const contractEntity = contract.entity_id == null ? null : entityById.get(contract.entity_id) ?? null
    const payer = entityChangedContracts.has(contract.contract_id) ? null : contractEntity
    return [contract.contract_id, {
      contract_id: contract.contract_id,
      contract_number: contract.contract_number,
      contract_signed_at: contract.signed_at,
      payer_inn: payer?.inn?.trim() || null,
      payer_country: payer?.country?.trim() || null,
    }]
  }))
}

export async function loadRoyaltyPaymentRequests(year: number, month: number): Promise<RoyaltyPaymentRequest[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_payment_requests', { p_year: year, p_month: month })
  )
  if (error) throw error
  return ((data ?? []) as RoyaltyPaymentRequest[]).map((row) => ({
    ...row,
    amount: Number(row.amount ?? 0),
    current_debt: Number(row.current_debt ?? 0),
    snapshot_debt: Number(row.snapshot_debt ?? 0),
    paid_since_snapshot: Number(row.paid_since_snapshot ?? 0),
    payment_offsets: (row.payment_offsets ?? []).map((offset) => ({
      ...offset,
      offset_id: Number(offset.offset_id),
      payment_id: Number(offset.payment_id),
      amount: Number(offset.amount ?? 0),
      amount_rub: Number(offset.amount_rub ?? 0),
      allocations: (offset.allocations ?? []).map((allocation) => ({
        ...allocation,
        charge_id: Number(allocation.charge_id),
        amount: Number(allocation.amount ?? 0),
        amount_rub: Number(allocation.amount_rub ?? 0),
        charge_year: Number(allocation.charge_year),
        charge_month: Number(allocation.charge_month),
      })),
    })),
    rb_tax_offset: normalizeRbTaxOffset((row as any).rb_tax_offset),
  }))
}

export async function generateRoyaltyPaymentRequest(params: {
  seriesId: number; year: number; month: number; chargeType: RoyaltyPaymentRequestChargeType
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() => (supabase as any).rpc('generate_royalty_payment_request', {
    p_series_id: params.seriesId, p_year: params.year, p_month: params.month, p_charge_type: params.chargeType,
  }))
  if (error) throw error
  return Number(data)
}

export async function generateRoyaltyPaymentRequestsBulk(year: number, month: number): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('generate_royalty_payment_requests_bulk', { p_year: year, p_month: month })
  )
  if (error) throw error
  return Number(data ?? 0)
}

type RoyaltyPaymentRequestsBatchResult = {
  processed: number
  has_more: boolean
  last_series_id?: number
  last_charge_type?: string
  last_request_id?: number
  skipped?: number
  skipped_details?: RoyaltyPaymentRequestSkippedGroup[]
}

/** Требование, у которого сервер не смог однозначно определить плательщика. */
export interface RoyaltyPaymentRequestSkippedGroup {
  series_id: number
  contract_number: string | null
  charge_type: string
  currency_code: string
  problem: string
}

export interface RoyaltyPaymentRequestsBulkOutcome {
  processed: number
  skipped: number
  skippedDetails: RoyaltyPaymentRequestSkippedGroup[]
}

/**
 * По умолчанию сервер строг: месяц с неопределившимся плательщиком не создаёт
 * ни одного черновика и падает перечнем проблем. `allowSkipUnresolved` — тот же
 * перечень, но пропущенный осознанно; это НЕ возврат к молчаливому пропуску:
 * флаг ставит оператор, уже увидевший список, и пропущенное приходит обратно.
 */
export async function generateRoyaltyPaymentRequestsBulkBatched(
  year: number,
  month: number,
  processMode: 'regular' | 'subscription' | 'foreign',
  options: { allowSkipUnresolved?: boolean } = {}
): Promise<RoyaltyPaymentRequestsBulkOutcome> {
  const supabase = getSupabase()
  let processed = 0
  let skipped = 0
  let skippedDetails: RoyaltyPaymentRequestSkippedGroup[] = []
  let afterSeriesId = 0
  let afterChargeType = ''
  while (true) {
    const { data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('generate_royalty_payment_requests_bulk_batch_mode', {
        p_year: year,
        p_month: month,
        p_process_mode: processMode,
        p_after_series_id: afterSeriesId,
        p_after_charge_type: afterChargeType,
        p_batch_size: 25,
        p_allow_skip_unresolved: options.allowSkipUnresolved ?? false,
      })
    )
    if (error) throw error
    const result = (data ?? {}) as RoyaltyPaymentRequestsBatchResult
    processed += Number(result.processed ?? 0)
    skipped += Number(result.skipped ?? 0)
    // Перечень одинаков на всех страницах (он собирается по всему месяцу до
    // цикла) — берём первый непустой, а не склеиваем дубли.
    if (skippedDetails.length === 0 && Array.isArray(result.skipped_details)) {
      skippedDetails = result.skipped_details
    }
    if (!result.has_more) return { processed, skipped, skippedDetails }
    const nextSeriesId = Number(result.last_series_id ?? afterSeriesId)
    const nextChargeType = String(result.last_charge_type ?? afterChargeType)
    if (nextSeriesId === afterSeriesId && nextChargeType === afterChargeType) {
      throw new Error('Bulk draft generation cursor did not advance')
    }
    afterSeriesId = nextSeriesId
    afterChargeType = nextChargeType
  }
}

export async function saveRoyaltyPaymentRequest(request: RoyaltyPaymentRequest): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() => (supabase as any).rpc('save_royalty_payment_request', {
    p_request_id: request.request_id,
    p_expected_updated_at: request.updated_at,
    p_payment_purpose: request.payment_purpose,
    p_accountant_comment: request.accountant_comment ?? '',
    p_final_message: request.final_message,
  }))
  if (error) throw error
  return String(data)
}

export async function setRoyaltyPaymentRequestStatus(
  request: RoyaltyPaymentRequest,
  status: 'READY' | 'SENT' | 'CANCELLED'
): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() => (supabase as any).rpc('set_royalty_payment_request_status', {
    p_request_id: request.request_id, p_status: status, p_expected_updated_at: request.updated_at,
  }))
  if (error) throw error
  return String(data)
}

export interface RoyaltyPaymentRequestDebtCandidate {
  charge_id: number
  year: number
  month: number
  charge_type: RoyaltyPaymentRequestChargeType
  // Native (obligation-currency) remaining debt is canonical; *_rub is the FX snapshot.
  // Both come from get_royalty_payment_request_debt_candidates (P1, §14.11.10). The
  // request only offers candidates of its own currency, so currency_code == request's.
  remaining_debt_amount: number
  currency_code: string
  remaining_debt_rub: number
}

export async function loadRoyaltyPaymentRequestDebtCandidates(requestId: number): Promise<RoyaltyPaymentRequestDebtCandidate[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_payment_request_debt_candidates', { p_request_id: requestId })
  )
  if (error) throw error
  return ((data ?? []) as RoyaltyPaymentRequestDebtCandidate[]).map((row) => ({
    ...row,
    remaining_debt_amount: Number(row.remaining_debt_amount ?? row.remaining_debt_rub ?? 0),
    currency_code: String(row.currency_code ?? 'RUB'),
    remaining_debt_rub: Number(row.remaining_debt_rub),
  }))
}

export interface RoyaltyPaymentRequestCreditCandidate {
  offset_id: number | null
  is_applied: boolean
  payment_id: number
  payment_updated_at: string
  payment_date: string
  bank_document_number: string | null
  bank_document_date: string | null
  payment_purpose: string | null
  currency_code: string
  remaining_amount: number
  offsettable_amount: number
  applied_amount: number
  offset_created_at: string | null
  covered_period_year: number | null
  covered_period_month: number | null
  covered_period_closed: boolean
  closed_by_payment_id: number | null
  closed_by_payment_date: string | null
  closed_by_document_number: string | null
}

export async function loadRoyaltyPaymentRequestCreditCandidates(
  requestId: number
): Promise<RoyaltyPaymentRequestCreditCandidate[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_payment_request_credit_candidates', { p_request_id: requestId })
  )
  if (error) throw error
  return ((data ?? []) as RoyaltyPaymentRequestCreditCandidate[]).map((row) => ({
    ...row,
    offset_id: row.offset_id == null ? null : Number(row.offset_id),
    is_applied: Boolean(row.is_applied),
    payment_id: Number(row.payment_id),
    remaining_amount: Number(row.remaining_amount ?? 0),
    offsettable_amount: Number(row.offsettable_amount ?? 0),
    applied_amount: Number(row.applied_amount ?? 0),
    covered_period_year: row.covered_period_year == null ? null : Number(row.covered_period_year),
    covered_period_month: row.covered_period_month == null ? null : Number(row.covered_period_month),
    covered_period_closed: Boolean(row.covered_period_closed),
    closed_by_payment_id: row.closed_by_payment_id == null ? null : Number(row.closed_by_payment_id),
  }))
}

export async function offsetPaymentToPaymentRequest(params: {
  requestId: number
  paymentId: number
  expectedRequestUpdatedAt: string
  expectedPaymentUpdatedAt: string
}): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('offset_payment_to_payment_request', {
      p_request_id: params.requestId,
      p_payment_id: params.paymentId,
      p_expected_request_updated_at: params.expectedRequestUpdatedAt,
      p_expected_payment_updated_at: params.expectedPaymentUpdatedAt,
    })
  )
  if (error) throw error
  return String(data)
}

export async function revokePaymentOffsetFromPaymentRequest(params: {
  requestId: number
  paymentId: number
  expectedRequestUpdatedAt: string
}): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('revoke_payment_offset_from_payment_request', {
      p_request_id: params.requestId,
      p_payment_id: params.paymentId,
      p_expected_request_updated_at: params.expectedRequestUpdatedAt,
    })
  )
  if (error) throw error
  return String(data)
}

export async function addRoyaltyPaymentRequestCharge(
  request: RoyaltyPaymentRequest,
  chargeId: number
): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() => (supabase as any).rpc('add_royalty_payment_request_charge', {
    p_request_id: request.request_id, p_charge_id: chargeId, p_expected_updated_at: request.updated_at,
  }))
  if (error) throw error
  return String(data)
}

export async function readyRoyaltyPaymentRequestsBulk(year: number, month: number): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('ready_royalty_payment_requests_bulk', { p_year: year, p_month: month })
  )
  if (error) throw error
  return Number(data ?? 0)
}

export async function readyRoyaltyPaymentRequestsBulkBatched(year: number, month: number, processMode: 'regular' | 'subscription' | 'foreign'): Promise<number> {
  const supabase = getSupabase()
  let processed = 0
  let afterRequestId = 0
  while (true) {
    const { data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('ready_royalty_payment_requests_bulk_batch_mode', {
        p_year: year,
        p_month: month,
        p_process_mode: processMode,
        p_after_request_id: afterRequestId,
        p_batch_size: 25,
      })
    )
    if (error) throw error
    const result = (data ?? {}) as RoyaltyPaymentRequestsBatchResult
    processed += Number(result.processed ?? 0)
    if (!result.has_more) return processed
    const nextRequestId = Number(result.last_request_id ?? afterRequestId)
    if (nextRequestId === afterRequestId) {
      throw new Error('Bulk request readiness cursor did not advance')
    }
    afterRequestId = nextRequestId
  }
}

export type RoyaltyLatePaymentNoticeStatus = 'DRAFT' | 'READY' | 'SENT' | 'CANCELLED' | 'STALE'

export interface RoyaltyLatePaymentNotice {
  notice_id: number
  series_id: number
  year: number
  month: number
  recipient_partner_number: number
  contract_id: number
  amount: number
  paid_amount: number
  debt_amount: number
  currency_code: string
  royalty_percent: number | null
  status: RoyaltyLatePaymentNoticeStatus
  accountant_comment: string | null
  final_message: string
  template_code: string
  params: Record<string, unknown>
  snapshot_at: string | null
  sent_at: string | null
  sent_by: string | null
  sent_by_name: string | null
  updated_by: string | null
  updated_by_name: string | null
  updated_at: string
}

export async function loadRoyaltyLatePaymentNotices(year: number, month: number): Promise<RoyaltyLatePaymentNotice[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_late_payment_notices', { p_year: year, p_month: month })
  )
  if (error) throw error
  return ((data ?? []) as RoyaltyLatePaymentNotice[]).map((row) => ({
    ...row,
    notice_id: Number(row.notice_id),
    amount: Number(row.amount ?? 0),
    paid_amount: Number(row.paid_amount ?? 0),
    debt_amount: Number(row.debt_amount ?? 0),
    royalty_percent: row.royalty_percent == null ? null : Number(row.royalty_percent),
  }))
}

export async function generateRoyaltyLatePaymentNotice(params: {
  seriesId: number
  year: number
  month: number
  contractId?: number | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('generate_royalty_late_payment_notice', {
      p_series_id: params.seriesId,
      p_year: params.year,
      p_month: params.month,
      p_contract_id: params.contractId ?? null,
    })
  )
  if (error) throw error
  return Number(data)
}

export async function saveRoyaltyLatePaymentNotice(notice: RoyaltyLatePaymentNotice): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('save_royalty_late_payment_notice', {
      p_notice_id: notice.notice_id,
      p_expected_updated_at: notice.updated_at,
      p_accountant_comment: notice.accountant_comment ?? '',
      p_final_message: notice.final_message,
    })
  )
  if (error) throw error
  return String(data)
}

export async function setRoyaltyLatePaymentNoticeStatus(
  notice: RoyaltyLatePaymentNotice,
  status: 'READY' | 'SENT' | 'CANCELLED'
): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_late_payment_notice_status', {
      p_notice_id: notice.notice_id,
      p_status: status,
      p_expected_updated_at: notice.updated_at,
    })
  )
  if (error) throw error
  return String(data)
}

// ---------------------------------------------------------------------------
// Валютные исключения оператора (план §14.11.5 item 8). Read-only, отчётные списки:
// FX_MISSING charges/payments, mixed-currency периоды, legacy-аллокации без native.
// Через RPC get_royalty_fx_exceptions (миграция 20260724160000). Ничего не правит.
export interface RoyaltyFxExceptionsCounts {
  fx_missing_charges: number
  fx_missing_payments: number
  mixed_currency_periods: number
  legacy_allocations_no_native: number
}
export interface RoyaltyFxMissingCharge {
  charge_id: number
  series_id: number
  contract_id: number | null
  year: number
  month: number
  charge_type: string
  gross_revenue_currency: string | null
  royalty_currency: string | null
  royalty_amount: number | null
  royalty_amount_rub: number | null
  fx_rate: number | null
  is_confirmed: boolean
  reason: string
}
export interface RoyaltyFxMissingPayment {
  payment_id: number
  contract_id: number | null
  payment_date: string | null
  currency_code: string
  amount: number | null
  amount_rub: number | null
  fx_rate: number | null
  reason: string
}
export interface RoyaltyMixedCurrencyPeriod {
  series_id: number
  year: number
  month: number
  currency_count: number
  currencies: string[]
}
export interface RoyaltyLegacyAllocationNoNative {
  allocation_id: number
  payment_id: number
  charge_id: number
  series_id: number
  contract_id: number | null
  year: number
  month: number
  charge_type: string
  royalty_currency: string | null
  allocated_amount_rub: number | null
}
export interface RoyaltyFxExceptions {
  counts: RoyaltyFxExceptionsCounts
  fx_missing_charges: RoyaltyFxMissingCharge[]
  fx_missing_payments: RoyaltyFxMissingPayment[]
  mixed_currency_periods: RoyaltyMixedCurrencyPeriod[]
  legacy_allocations_no_native: RoyaltyLegacyAllocationNoNative[]
}

export async function loadRoyaltyFxExceptions(limit = 500): Promise<RoyaltyFxExceptions> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_fx_exceptions', { p_limit: limit })
  )
  if (error) throw error
  const d = (data ?? {}) as Partial<RoyaltyFxExceptions>
  return {
    counts: d.counts ?? { fx_missing_charges: 0, fx_missing_payments: 0, mixed_currency_periods: 0, legacy_allocations_no_native: 0 },
    fx_missing_charges: d.fx_missing_charges ?? [],
    fx_missing_payments: d.fx_missing_payments ?? [],
    mixed_currency_periods: d.mixed_currency_periods ?? [],
    legacy_allocations_no_native: d.legacy_allocations_no_native ?? [],
  }
}

/**
 * Load every royalty_balance row for every series owned by a partner.
 * Used by the cross-series allocation modal so the user can target charges
 * outside the payment's own series.
 */
export async function loadRoyaltyBalancesByPartner(partnerNumber: number): Promise<RoyaltyBalance[]> {
  const supabase = getSupabase()
  // contract_series.series_id is the join key; royalty_balance has series_id directly.
  const { data: seriesRows, error: seriesErr } = await withAuthRecovery(() =>
    supabase
      .from('contract_series')
      .select('series_id')
      .eq('partner_number', partnerNumber)
  )
  if (seriesErr) throw seriesErr
  const seriesIds = ((seriesRows ?? []) as Array<{ series_id: number }>).map(r => r.series_id)
  if (seriesIds.length === 0) return []

  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_balance')
      .select('*')
      .in('series_id', seriesIds)
      .order('year', { ascending: false })
      .order('month', { ascending: false })
  )
  if (error) {
    console.error('Failed to load partner royalty balances:', error)
    throw error
  }
  return enrichBalancesWithChargeSegments((data ?? []) as RoyaltyBalance[])
}

// ---------- Payments for a contract ----------

async function enrichRoyaltyPaymentsWithAllocationTotals(payments: RoyaltyPayment[]): Promise<RoyaltyPayment[]> {
  if (payments.length === 0) return payments

  const paymentIds = payments.map((p) => p.payment_id)
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_payment_allocations')
      .select('payment_id, allocated_amount_rub')
      .in('payment_id', paymentIds)
  )
  if (error) throw error

  const allocatedByPayment = new Map<number, number>()
  for (const row of (data ?? []) as Array<{ payment_id: number; allocated_amount_rub: number | null }>) {
    allocatedByPayment.set(
      row.payment_id,
      (allocatedByPayment.get(row.payment_id) ?? 0) + Number(row.allocated_amount_rub ?? 0)
    )
  }

  return payments.map((payment) => {
    const allocatedRub = allocatedByPayment.get(payment.payment_id) ?? 0
    const refundedRub = Number(payment.refunded_amount_rub ?? 0)
    return {
      ...payment,
      allocated_rub: allocatedRub,
      refunded_amount: Number(payment.refunded_amount ?? 0),
      refunded_amount_rub: refundedRub,
      remaining_rub: Math.max(Number(payment.amount_rub ?? 0) - allocatedRub - refundedRub, 0),
    }
  })
}

export async function loadRoyaltyPayments(contractId: number): Promise<RoyaltyPayment[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_payments')
      .select('*')
      .eq('contract_id', contractId)
      .order('payment_date', { ascending: false })
  )

  if (error) {
    console.error('Failed to load royalty payments:', error)
    throw error
  }

  return enrichRoyaltyPaymentsWithAllocationTotals((data ?? []) as RoyaltyPayment[])
}

// ---------- Payments for all contracts of a series ----------

export async function loadRoyaltyPaymentsBySeries(seriesId: number): Promise<RoyaltyPayment[]> {
  const supabase = getSupabase()
  // First: contract ids in this series
  const { data: contracts, error: cErr } = await withAuthRecovery(() =>
    supabase.from('contracts').select('contract_id').eq('series_id', seriesId)
  )
  if (cErr) throw cErr
  const ids = (contracts ?? []).map((c: { contract_id: number }) => c.contract_id)
  if (ids.length === 0) return []

  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_payments')
      .select('*')
      .in('contract_id', ids)
      .order('payment_date', { ascending: false })
  )
  if (error) throw error
  return enrichRoyaltyPaymentsWithAllocationTotals((data ?? []) as RoyaltyPayment[])
}

// ---------- Terms history for a series ----------

export async function loadRoyaltySeriesTerms(seriesId: number): Promise<RoyaltySeriesTerms[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_series_terms')
      .select('*')
      .eq('series_id', seriesId)
      .order('effective_from', { ascending: false })
  )

  if (error) {
    console.error('Failed to load royalty terms:', error)
    throw error
  }

  return (data ?? []) as RoyaltySeriesTerms[]
}

// Расчёт роялти выполняется только через процесс-флоу выставления
// (`setRoyaltyPeriodRoyaltyCheck` → `set_royalty_period_royalty_check`), который уважает
// посегментный выбор оператора. Прежние engine-recompute обёртки `calculateRoyaltyForMonth`
// и `bulkCalculateRoyalties` удалены вместе с RPC миграцией
// 20260717160000_remove_engine_recompute_royalty_paths.sql.

// ---------- Save royalty terms (series-based) ----------

export interface CreateRoyaltySeriesTermsInput {
  series_id: number
  contract_id: number
  effective_from: string
  effective_to?: string | null
  contract_type?: 'REGULAR' | 'SUBSCRIPTION'
  // NULL у подписочного — сервер наследует вариант предыдущего подписочного условия серии.
  sales_model?: 'SUBSCRIPTION' | 'MIXED' | null
  royalty_percent: number
  agency_percent?: number | null
  vat_percent?: number | null
  listok_crm_base_amount?: number | null
  is_listok_crm_carrier?: boolean
  is_fixed_royalty_carrier?: boolean
  min_royalty_amount: number
  currency_code: string
  // Currency of the contractual minimum; omit/NULL = same as `currency_code`. Set per
  // contract (decision 2026-07-24) — each ДКК names its own min amount + currency; no
  // single canonical network value. Independent from the obligation currency.
  min_royalty_currency?: string | null
  source_contract_id?: number | null
  source_document_note?: string | null
  notes?: string | null
}

export async function createRoyaltySeriesTerms(
  terms: CreateRoyaltySeriesTermsInput
): Promise<RoyaltySeriesTerms> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { is_fixed_royalty_carrier: isFixedRoyaltyCarrier, ...restTerms } = terms
  // Флаг шлём только включённым: так создание условий не зависит от того, применена ли
  // миграция 20261016160000 (у колонки DEFAULT false).
  const primaryPayload = {
    ...restTerms,
    ...(isFixedRoyaltyCarrier ? { is_fixed_royalty_carrier: true } : {}),
    created_by: actorUserId,
    updated_by: actorUserId,
  } as any
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_series_terms')
      .insert(primaryPayload)
      .select()
      .single()
  )

  if (!error) {
    return data as unknown as RoyaltySeriesTerms
  }

  console.error('Failed to save royalty terms:', error)
  throw error
}

export async function updateRoyaltySeriesTerms(
  termId: number,
  patch: Partial<Omit<CreateRoyaltySeriesTermsInput, 'series_id'>>
): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const payload = { ...patch, updated_by: actorUserId, updated_at: new Date().toISOString() } as any
  const update = (body: any) => withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_series_terms')
      .update(body)
      .eq('term_id', termId)
  )
  let { error } = await update(payload)
  // До миграции 20261016160000 колонки нет: выключенный флаг ничего не меняет — повторяем без него.
  if (error && patch.is_fixed_royalty_carrier !== true
    && String(error.message ?? '').includes('is_fixed_royalty_carrier')) {
    const { is_fixed_royalty_carrier: _omitted, ...withoutFlag } = payload
    ;({ error } = await update(withoutFlag))
  }
  if (error) throw error
}

export async function deleteRoyaltySeriesTerms(termId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.from('royalty_series_terms').delete().eq('term_id', termId)
  )
  if (error) throw error
}

// ---------- Save manual payment ----------

export async function saveRoyaltyPayment(payment: {
  contract_id?: number | null
  partner_number?: number | null
  entity_id?: number | null
  payment_date: string
  amount: number
  currency_code: string
  amount_rub: number
  fx_rate?: number | null
  payment_purpose?: string | null
  payer_inn?: string | null
  beneficiary_inn?: string | null
  payer_name?: string | null
  source?: 'MANUAL' | 'BANK_IMPORT' | 'OFFSET'
  notes?: string | null
}): Promise<RoyaltyPayment> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_payments')
      .insert({ source: 'MANUAL', ...payment, created_by: actorUserId, updated_by: actorUserId } as any)
      .select()
      .single()
  )

  if (error) {
    console.error('Failed to save royalty payment:', error)
    throw error
  }

  return data as unknown as RoyaltyPayment
}

// ---------- Single contract summary ----------

export async function loadContractSummary(contractId: number): Promise<RoyaltyContractSummary | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_contract_summary')
      .select('*')
      .eq('contract_id', contractId)
      .single()
  )
  if (error) {
    if (error.code === 'PGRST116') return null  // not found
    throw error
  }
  return data as RoyaltyContractSummary
}

// ---------- Single partner summary (for FIO) ----------

export async function loadRoyaltyPartnerSummary(
  partnerNumber: number
): Promise<RoyaltyPartnerSummary | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_partner_summary')
      .select('*')
      .eq('partner_number', partnerNumber)
      .maybeSingle()
  )
  if (error) throw error
  return (data as RoyaltyPartnerSummary | null) ?? null
}

// ---------- Legal entities for one partner ----------

export async function loadPartnerLegalEntities(
  partnerNumber: number
): Promise<PartnerLegalEntity[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('partner_legal_entities')
      .select('*')
      .eq('partner_number', partnerNumber)
      .order('is_primary', { ascending: false })
  )
  if (error) throw error
  return (data ?? []) as PartnerLegalEntity[]
}

// ---------- Load all partner legal entities ----------

export async function loadAllPartnerLegalEntities(): Promise<PartnerLegalEntity[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('partner_legal_entities')
      .select('*')
      .order('partner_number')
      .order('is_primary', { ascending: false })
  )
  if (error) throw error
  return (data ?? []) as PartnerLegalEntity[]
}

// ---------- Load contracts for import matching ----------

export async function loadContractsForMatching(): Promise<ContractForMatching[]> {
  const supabase = getSupabase()

  const [
    { data: contracts, error: ce },
    { data: entities, error: ee },
    { data: contractEntities, error: cte },
  ] = await Promise.all([
    fetchAllSupabaseRows(() =>
      supabase
        .from('royalty_contract_summary')
        .select('contract_id,series_id,partner_number,contract_number,persons_fio,studio_number,studio_address,city_name,partner_inn')
        .order('contract_id', { ascending: true })
    ),
    fetchAllSupabaseRows(() =>
      supabase
        .from('partner_legal_entities')
        .select('entity_id,partner_number,inn,legal_name,legal_name_key')
        .order('entity_id', { ascending: true })
    ),
    fetchAllSupabaseRows(() =>
      supabase
        .from('contracts')
        .select('contract_id,entity_id')
        .order('contract_id', { ascending: true })
    ),
  ])

  if (ce) throw ce
  if (ee) throw ee
  if (cte) throw cte

  // Build INN lookup per partner
  const innByPartner: Record<number, string[]> = {}
  const innByEntityId: Record<number, string> = {}
  // Legal-name-key lookup per partner (for foreign payers matched by name, no INN).
  const legalNameKeysByPartner: Record<number, string[]> = {}
  for (const e of (entities ?? []) as { entity_id: number; partner_number: number; inn: string | null; legal_name_key: string | null }[]) {
    const key = (e.legal_name_key ?? '').trim()
    if (key) {
      if (!legalNameKeysByPartner[e.partner_number]) legalNameKeysByPartner[e.partner_number] = []
      if (!legalNameKeysByPartner[e.partner_number].includes(key)) {
        legalNameKeysByPartner[e.partner_number].push(key)
      }
    }
    const normalizedInn = normalizeInnForMatching(e.inn ?? '')
    if (!normalizedInn) continue
    if (!innByPartner[e.partner_number]) innByPartner[e.partner_number] = []
    if (!innByPartner[e.partner_number].includes(normalizedInn)) {
      innByPartner[e.partner_number].push(normalizedInn)
    }
    innByEntityId[e.entity_id] = normalizedInn
  }

  const entityIdByContractId: Record<number, number | null> = {}
  for (const row of (contractEntities ?? []) as { contract_id: number; entity_id: number | null }[]) {
    entityIdByContractId[row.contract_id] = row.entity_id ?? null
  }

  return ((contracts ?? []) as any[]).map((c) => {
    const contractEntityId = entityIdByContractId[c.contract_id]
    const contractEntityInn = contractEntityId ? innByEntityId[contractEntityId] : null
    const partnerInn = normalizeInnForMatching(c.partner_inn)

    return {
      contract_id: c.contract_id,
      series_id: c.series_id,
      partner_number: c.partner_number,
      contract_number: c.contract_number ?? null,
      persons_fio: c.persons_fio ?? null,
      studio_number: c.studio_number ?? null,
      studio_address: c.studio_address ?? null,
      city_name: c.city_name ?? null,
      inn_list: contractEntityInn
        ? [contractEntityInn]
        : innByPartner[c.partner_number] ?? (partnerInn ? [partnerInn] : []),
      contract_inn_list: contractEntityInn
        ? [contractEntityInn]
        : innByPartner[c.partner_number] ?? (partnerInn ? [partnerInn] : []),
      partner_inn_list: innByPartner[c.partner_number] ?? (partnerInn ? [partnerInn] : []),
      partner_legal_name_keys: legalNameKeysByPartner[c.partner_number] ?? [],
    }
  }) as ContractForMatching[]
}

// ---------- Contracts by partner (for manual editing) ----------

export async function loadPartnerContracts(partnerNumber: number): Promise<PartnerContractEditable[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('contracts')
      .select(`
        contract_id,
        series_id,
        contract_status,
        contract_number,
        signed_at,
        effective_from,
        expected_end_date,
        actual_end_date,
        entity_id,
        lump_sum_amount,
        lump_sum_currency,
        contract_series!inner(partner_number)
      `)
      .eq('contract_series.partner_number', partnerNumber)
      .order('contract_id', { ascending: false })
  )
  if (error) throw error

  const contractIds = ((data ?? []) as Array<{ contract_id: number }>)
    .map((row) => row.contract_id)
    .filter((value): value is number => Number.isFinite(value))
  const { data: legalRows, error: legalRowsError } = contractIds.length === 0
    ? { data: [], error: null as any }
    : await withAuthRecovery(() =>
        (supabase as any)
          .from('contract_legal_state_v')
          .select('contract_id,current_expected_end_date,actual_end_date,legal_status_code,legal_status_label,legal_status_reason,legal_end_basis')
          .in('contract_id', contractIds)
      )
  if (legalRowsError && (legalRowsError as any)?.code !== '42P01') throw legalRowsError

  const legalByContractId = new Map<number, {
    current_expected_end_date: string | null
    actual_end_date: string | null
    legal_status_code: PartnerContractEditable['legal_status_code']
    legal_status_label: string | null
    legal_status_reason: string | null
    legal_end_basis: PartnerContractEditable['legal_end_basis']
  }>()
  for (const row of ((legalRows ?? []) as Array<{
    contract_id: number
    current_expected_end_date: string | null
    actual_end_date: string | null
    legal_status_code: PartnerContractEditable['legal_status_code']
    legal_status_label: string | null
    legal_status_reason: string | null
    legal_end_basis: PartnerContractEditable['legal_end_basis']
  }>)) {
    legalByContractId.set(row.contract_id, {
      current_expected_end_date: row.current_expected_end_date ?? null,
      actual_end_date: row.actual_end_date ?? null,
      legal_status_code: row.legal_status_code ?? null,
      legal_status_label: row.legal_status_label ?? null,
      legal_status_reason: row.legal_status_reason ?? null,
      legal_end_basis: row.legal_end_basis ?? null,
    })
  }

  return ((data ?? []) as any[]).map((row) => {
    const partnerNumberFromJoin = Array.isArray(row.contract_series)
      ? row.contract_series[0]?.partner_number
      : row.contract_series?.partner_number
    const legal = legalByContractId.get(row.contract_id)

    return {
      contract_id: row.contract_id,
      series_id: row.series_id,
      partner_number: partnerNumberFromJoin ?? partnerNumber,
      contract_status: row.contract_status,
      contract_number: row.contract_number ?? null,
      signed_at: row.signed_at,
      effective_from: row.effective_from,
      expected_end_date: legal?.current_expected_end_date ?? row.expected_end_date ?? null,
      actual_end_date: legal?.actual_end_date ?? row.actual_end_date ?? null,
      legal_status_code: legal?.legal_status_code ?? null,
      legal_status_label: legal?.legal_status_label ?? null,
      legal_status_reason: legal?.legal_status_reason ?? null,
      legal_end_basis: legal?.legal_end_basis ?? null,
      entity_id: row.entity_id ?? null,
      lump_sum_amount: row.lump_sum_amount == null ? null : Number(row.lump_sum_amount),
      lump_sum_currency: row.lump_sum_currency ?? null,
    }
  }) as PartnerContractEditable[]
}

// ---------- Update contract metadata ----------

const CONTRACT_FIELDS_SELECT =
  'contract_number,signed_at,effective_from,expected_end_date,actual_end_date,entity_id,lump_sum_amount,lump_sum_currency,levita_entity,renewal_status'

function toDateOnly(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || !value) return null
  return value.slice(0, 10)
}

/**
 * Правка полей договора.
 *
 * ⚠️ Идёт через RPC set_contract_fields, а не прямым UPDATE: с 01.09.2026 у ролей
 * anon/authenticated отозваны табличные INSERT/UPDATE на contracts, потому что прямая
 * запись обходила гейт «юрист или супер-админ» (миграция 20260901140000).
 *
 * ⚠️ set_contract_fields — ПОЛНАЯ перезапись полей, а не патч: не переданный параметр
 * записывается как NULL. Поэтому текущие значения читаются и сливаются с патчем здесь —
 * иначе правка одного поля обнулила бы паушальный взнос, юрлицо LEVITA и «Активность».
 */
export async function updateContractMetadata(
  contractId: number,
  patch: {
    contract_number?: string | null
    signed_at?: string | null
    effective_from?: string | null
    expected_end_date?: string | null
    actual_end_date?: string | null
    entity_id?: number | null
  }
): Promise<void> {
  const supabase = getSupabase()

  if (Object.keys(patch).length === 0) return

  const { data: current, error: readError } = await withAuthRecovery(() =>
    (supabase as any)
      .from('contracts')
      .select(CONTRACT_FIELDS_SELECT)
      .eq('contract_id', contractId)
      .single()
  )
  if (readError) throw readError
  if (!current) throw new Error(`Договор ${contractId} не найден`)

  const pick = <K extends keyof typeof patch>(key: K, fallback: unknown) =>
    Object.prototype.hasOwnProperty.call(patch, key) ? (patch[key] ?? null) : (fallback ?? null)

  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_contract_fields', {
      p_contract_id: contractId,
      p_contract_number: pick('contract_number', (current as any).contract_number),
      p_signed_at: toDateOnly(pick('signed_at', (current as any).signed_at) as string | null),
      p_effective_from: toDateOnly(pick('effective_from', (current as any).effective_from) as string | null),
      p_expected_end_date: toDateOnly(pick('expected_end_date', (current as any).expected_end_date) as string | null),
      p_actual_end_date: toDateOnly(pick('actual_end_date', (current as any).actual_end_date) as string | null),
      p_entity_id: pick('entity_id', (current as any).entity_id),
      p_lump_sum_amount: (current as any).lump_sum_amount ?? null,
      p_lump_sum_currency: (current as any).lump_sum_currency ?? null,
      p_levita_entity: (current as any).levita_entity ?? null,
      p_renewal_status: (current as any).renewal_status ?? null,
    })
  )
  if (error) throw error
}

// ---------- Create contract in an existing series ----------

/**
 * Заведение ДКК в существующую серию.
 *
 * ⚠️ Идёт через RPC create_contract_for_series, а не прямым insert: прямая запись
 * обходила гейт «юрист или супер-админ» и не знала доменных правил RPC — именно так
 * 01.09.2026 в серии 222 (студия 228 Смоленск) появился ВТОРОЙ действующий MAIN при
 * уже живом продлении, а раньше — ДКК 2005 без номера. RPC требует номер, живое
 * владение у серии и отсутствие второго MAIN.
 *
 * ⚠️ RPC создаёт договор всегда действующим (contract_status = OPEN, actual_end_date
 * = null): статус договора — легаси-колонка, юридическое состояние считает
 * contract_legal_state_v из дат. Закрытие выражается датой фактического окончания,
 * поэтому она досылается отдельным вызовом set_contract_fields.
 */
export async function createContractForSeries(input: {
  series_id: number
  contract_number?: string | null
  signed_at?: string | null
  effective_from?: string | null
  expected_end_date?: string | null
  actual_end_date?: string | null
  entity_id?: number | null
}): Promise<PartnerContractEditable> {
  const supabase = getSupabase()

  const { data: newContractId, error: createError } = await withAuthRecovery(() =>
    (supabase as any).rpc('create_contract_for_series', {
      p_series_id: input.series_id,
      p_contract_number: input.contract_number?.trim() || null,
      p_signed_at: toDateOnly(input.signed_at),
      p_effective_from: toDateOnly(input.effective_from ?? input.signed_at),
      p_expected_end_date: toDateOnly(input.expected_end_date),
      p_entity_id: input.entity_id ?? null,
    })
  )
  if (createError) throw createError

  const contractId = Number(newContractId)
  if (!Number.isFinite(contractId)) {
    throw new Error('RPC create_contract_for_series не вернула contract_id')
  }

  if (input.actual_end_date) {
    await updateContractMetadata(contractId, { actual_end_date: input.actual_end_date })
  }

  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('contracts')
      .select(`
        contract_id,
        series_id,
        contract_status,
        contract_number,
        signed_at,
        effective_from,
        expected_end_date,
        actual_end_date,
        entity_id,
        lump_sum_amount,
        lump_sum_currency,
        contract_series!inner(partner_number)
      `)
      .eq('contract_id', contractId)
      .single()
  )
  if (error) throw error

  const partnerNumberFromJoin = Array.isArray((data as any).contract_series)
    ? (data as any).contract_series[0]?.partner_number
    : (data as any).contract_series?.partner_number
  if (typeof partnerNumberFromJoin !== 'number') {
    throw new Error('Не удалось определить partner_number для нового договора')
  }

  return {
    contract_id: (data as any).contract_id,
    series_id: (data as any).series_id,
    partner_number: partnerNumberFromJoin,
    contract_status: (data as any).contract_status,
    contract_number: (data as any).contract_number ?? null,
    signed_at: (data as any).signed_at,
    effective_from: (data as any).effective_from,
    expected_end_date: (data as any).expected_end_date ?? null,
    actual_end_date: (data as any).actual_end_date ?? null,
    entity_id: (data as any).entity_id ?? null,
    lump_sum_amount: (data as any).lump_sum_amount == null ? null : Number((data as any).lump_sum_amount),
    lump_sum_currency: (data as any).lump_sum_currency ?? null,
  } as PartnerContractEditable
}

// ---------- Safely delete an unused contract from a series ----------

export async function deleteContractFromSeries(contractId: number, seriesId: number): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('delete_contract_from_series', {
      p_contract_id: contractId,
      p_series_id: seriesId,
      p_actor_user_id: actorUserId,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

export async function mergeContractIntoContractAndDelete(input: {
  sourceContractId: number
  targetContractId: number
  seriesId: number
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('merge_contract_into_contract_and_delete', {
      p_source_contract_id: input.sourceContractId,
      p_target_contract_id: input.targetContractId,
      p_series_id: input.seriesId,
      p_note: input.note ?? null,
      p_actor_user_id: actorUserId,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

// ---------- Batch import bank payments ----------

export async function batchImportBankPayments(
  rows: Array<{
    contract_id: number
    payment_date: string
    amount: number
    amount_rub: number
    payment_purpose: string
    payer_inn: string
    beneficiary_inn?: string | null
    payer_name: string
    bank_document_number: string
    bank_document_date: string
    intended_year?: number | null
    intended_month?: number | null
    source_staging_row_id?: number | null
    import_batch_id?: number | null
  }>
): Promise<number> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const payload = rows.map((r) => ({
    ...r,
    currency_code: 'RUB',
    source: 'BANK_IMPORT',
    created_by: actorUserId,
    updated_by: actorUserId,
  }))
  const { error } = await withAuthRecovery(() =>
    supabase.from('royalty_payments').insert(payload as any)
  )
  if (error) throw error
  return rows.length
}

// Выборки «что уже загружено» для обоих дедупов импорта читаются страницами до
// пустой страницы: PostgREST режет любой ответ до max-rows МОЛЧА, а обрезанная
// выборка перестаёт ловить повторы. Так 03.09.2026 повторная загрузка выписки
// за 01.09 создала вторые копии 32 платежей: 139 номеров документов совпали с
// 1 296 строками истории, и в первую 1000 попали не все строки прошлой загрузки.
// Конец — по пустой странице, а не по «страница меньше запрошенной»: иначе
// лимит ниже размера страницы снова обрезал бы выборку молча.
const DEDUP_LOOKUP_PAGE_SIZE = 1000

async function selectAllDedupLookupPages<T>(buildOrderedQuery: () => any): Promise<T[]> {
  const rows: T[] = []
  let offset = 0
  for (;;) {
    const { data, error } = await withAuthRecovery(() =>
      buildOrderedQuery().range(offset, offset + DEDUP_LOOKUP_PAGE_SIZE - 1)
    )
    if (error) throw error
    const page = (data ?? []) as T[]
    if (page.length === 0) break
    rows.push(...page)
    offset += page.length
  }
  return rows
}

export async function filterDuplicateBankPayments(
  rows: Array<{
    contract_id: number
    payment_date: string
    amount: number
    amount_rub: number
    payment_purpose: string
    payer_inn: string
    beneficiary_inn?: string | null
    payer_name: string
    bank_document_number: string
    bank_document_date: string
    intended_year?: number | null
    intended_month?: number | null
    source_staging_row_id?: number | null
    import_batch_id?: number | null
  }>
): Promise<{
  rowsToImport: Array<{
    contract_id: number
    payment_date: string
    amount: number
    amount_rub: number
    payment_purpose: string
    payer_inn: string
    beneficiary_inn?: string | null
    payer_name: string
    bank_document_number: string
    bank_document_date: string
    intended_year?: number | null
    intended_month?: number | null
    source_staging_row_id?: number | null
    import_batch_id?: number | null
  }>
  duplicateRows: Array<{
    contract_id: number
    payment_date: string
    amount: number
    amount_rub: number
    payment_purpose: string
    payer_inn: string
    payer_name: string
    bank_document_number: string
    bank_document_date: string
    intended_year?: number | null
    intended_month?: number | null
    source_staging_row_id?: number | null
    import_batch_id?: number | null
  }>
  duplicateCount: number
}> {
  if (rows.length === 0) {
    return { rowsToImport: [], duplicateRows: [], duplicateCount: 0 }
  }

  const supabase = getSupabase()
  const contractIds = [...new Set(rows.map((row) => row.contract_id))]
  // Оба ключа дедупа несут дату платежа, поэтому кандидат в дубли может лежать
  // только на дате из самой партии: выборка сужается по датам и читается
  // страницами (см. selectAllDedupLookupPages).
  const paymentDates = bankImportPaymentDates(rows)
  type ExistingPayment = {
    contract_id: number
    payment_date: string
    amount_rub: number | null
    payer_inn: string | null
    payment_purpose: string | null
    bank_document_number: string | null
    bank_document_date: string | null
  }
  const existingRows: ExistingPayment[] = []
  const chunkSize = 200
  const dateChunks: Array<string[] | null> = paymentDates.length > 0
    ? Array.from(
        { length: Math.ceil(paymentDates.length / chunkSize) },
        (_, index) => paymentDates.slice(index * chunkSize, (index + 1) * chunkSize)
      )
    : [null]

  for (let i = 0; i < contractIds.length; i += chunkSize) {
    const contractChunk = contractIds.slice(i, i + chunkSize)
    for (const dateChunk of dateChunks) {
      existingRows.push(...await selectAllDedupLookupPages<ExistingPayment>(() => {
        let query = (supabase as any)
          .from('royalty_payments')
          .select('contract_id,payment_date,amount_rub,payer_inn,payment_purpose,bank_document_number,bank_document_date')
          .eq('source', 'BANK_IMPORT')
          .in('contract_id', contractChunk)
        if (dateChunk) query = query.in('payment_date', dateChunk)
        return query.order('payment_id', { ascending: true })
      }))
    }
  }

  const { rowsToImport, duplicateRows } = partitionBankImportDuplicates(
    existingRows.map((row) => ({
      contract_id: row.contract_id,
      payment_date: row.payment_date,
      amount_rub: row.amount_rub ?? 0,
      payer_inn: row.payer_inn,
      payment_purpose: row.payment_purpose,
      bank_document_number: row.bank_document_number,
      bank_document_date: row.bank_document_date,
    })),
    rows
  )

  return { rowsToImport, duplicateRows, duplicateCount: duplicateRows.length }
}

// ---------- Update intended period on a payment ----------

export async function updatePaymentPeriod(
  paymentId: number,
  intendedYear: number | null,
  intendedMonth: number | null,
  allocationNote?: string | null
): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payments')
      .update({
        intended_year: intendedYear,
        intended_month: intendedMonth,
        assigned_year: intendedYear,
        assigned_month: intendedMonth,
        allocation_note: allocationNote ?? null,
        updated_by: actorUserId,
        updated_at: new Date().toISOString(),
      })
      .eq('payment_id', paymentId)
  )
  if (error) throw error
}

export async function markPaymentAsLumpSum(paymentId: number): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payments')
      .update({
        payment_kind: 'LUMP_SUM',
        is_deposit: false,
        intended_year: null,
        intended_month: null,
        assigned_year: null,
        assigned_month: null,
        allocation_note: 'Marked as lump sum',
        updated_by: actorUserId,
        updated_at: new Date().toISOString(),
      })
      .eq('payment_id', paymentId)
      .eq('is_deposit', false)
      .or('payment_kind.eq.ROYALTY,payment_kind.is.null')
  )
  if (error) throw error
}

export interface AssignPaymentContractAndPeriodResult {
  payment_id: number
  assignment_changed: boolean
  cleared_allocations_count: number
  cleared_allocations_rub: number
  contract_id: number
  series_id: number
  intended_year: number | null
  intended_month: number | null
}

export async function assignPaymentContractAndPeriod(input: {
  paymentId: number
  contractId: number
  intendedYear: number | null
  intendedMonth: number | null
  expectedUpdatedAt: string
  clearAllocations: boolean
  note?: string | null
}): Promise<AssignPaymentContractAndPeriodResult> {
  const rpcClient = getUntypedRpcClient()
  const { data, error } = await withAuthRecovery(() =>
    rpcClient.rpc('assign_payment_contract_and_period', {
      p_payment_id: input.paymentId,
      p_contract_id: input.contractId,
      p_intended_year: input.intendedYear,
      p_intended_month: input.intendedMonth,
      p_note: input.note?.trim() || null,
      p_expected_updated_at: input.expectedUpdatedAt,
      p_clear_allocations: input.clearAllocations,
    })
  )
  if (error) throw error
  return data as AssignPaymentContractAndPeriodResult
}

export async function assignLumpSumPaymentContract(input: {
  paymentId: number
  contractId: number
  expectedUpdatedAt: string
  note?: string | null
}): Promise<AssignPaymentContractAndPeriodResult> {
  return assignPaymentContractAndPeriod({
    paymentId: input.paymentId,
    contractId: input.contractId,
    intendedYear: null,
    intendedMonth: null,
    expectedUpdatedAt: input.expectedUpdatedAt,
    clearAllocations: false,
    note: input.note ?? 'Lump sum contract attribution',
  })
}

// ---------- Reset allocations for a payment and re-run ----------

export async function reallocatePayment(paymentId: number): Promise<void> {
  const supabase = getSupabase()
  // Remove old allocations for this payment
  const { error: delErr } = await withAuthRecovery(() =>
    supabase
      .from('royalty_payment_allocations')
      .delete()
      .eq('payment_id', paymentId)
  )
  if (delErr) throw delErr

  // Re-run only this payment. A single-payment edit must not unexpectedly
  // launch a long global sweep in a UI action that has no progress display.
  const { data: payment, error: paymentError } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payments')
      .select('updated_at')
      .eq('payment_id', paymentId)
      .single()
  )
  if (paymentError) throw paymentError
  if (!payment?.updated_at) throw new Error(`Не удалось получить версию платежа #${paymentId}`)

  await allocateOnePaymentToCurrentTarget({
    paymentId,
    expectedUpdatedAt: String(payment.updated_at),
    preview: false,
  })
}

// ---------- Return an allocated payment back to the allocation queue ----------

/**
 * Drops all allocations of a payment so it re-enters the /royalties/allocation
 * queue through royalty_payment_reconciliation_v.
 *
 * Unlike reallocatePayment it does NOT re-run auto-allocation — the payment is
 * deliberately left for manual queue processing.
 */
export async function returnPaymentToQueue(
  paymentId: number,
  expectedUpdatedAt?: string | null,
  note?: string | null
): Promise<void> {
  const supabase = getSupabase()
  let version = expectedUpdatedAt ?? null

  if (!version) {
    const { data: payment, error: paymentErr } = await withAuthRecovery(() =>
      supabase
        .from('royalty_payments')
        .select('updated_at')
        .eq('payment_id', paymentId)
        .maybeSingle()
    )
    if (paymentErr) throw paymentErr
    version = payment?.updated_at ?? null
  }

  if (!version) throw new Error(`Не удалось получить версию платежа #${paymentId}`)

  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('clear_royalty_payment_allocation_period', {
      p_payment_id: paymentId,
      p_note: note?.trim() || null,
      p_expected_updated_at: version,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

export async function skipRoyaltyPaymentToAllocationQueue(input: {
  paymentId: number
  expectedUpdatedAt: string
  note?: string | null
}): Promise<{ payment_id: number; cleared_allocations_count: number; cleared_allocations_rub: number }> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('skip_royalty_payment_to_allocation_queue', {
      p_payment_id: input.paymentId,
      p_note: input.note?.trim() || null,
      p_expected_updated_at: input.expectedUpdatedAt,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
  return data as { payment_id: number; cleared_allocations_count: number; cleared_allocations_rub: number }
}

export async function removePaymentAllocation(allocationId: number): Promise<void> {
  const supabase = getSupabase()

  const { error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_payment_allocations')
      .delete()
      .eq('allocation_id', allocationId)
  )
  if (error) throw error
}

// ---------- Allocate unmatched bank payments (strict 1:1) ----------

export async function allocateUnmatchedBankPayments(
  onProgress?: (progress: RoyaltyAutoAllocationProgress) => void
): Promise<number> {
  const result = await allocateReadyBankPayments({ onProgress })
  return result.allocated
}

// ---------- Allocate payment to charge ----------

export async function allocatePayment(allocation: {
  payment_id: number
  charge_id: number
  allocated_amount: number
  allocated_currency_code: string
  allocated_amount_rub: number
}): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_payment_allocations')
      .insert({ ...allocation, created_by: actorUserId, updated_by: actorUserId } as any)
  )

  if (error) {
    console.error('Failed to allocate payment:', error)
    throw error
  }

}

// ============================================================
// Bank staging table
// ============================================================

export async function insertBankStagingRows(
  rows: Array<{
    doc_number?: string
    doc_date?: string
    operation_date?: string
    counterparty?: string
    inn?: string
    beneficiary_inn?: string | null
    credit_amount?: string
    credit_currency?: string | null
    source_amount?: string | number | null
    source_currency?: string | null
    payment_purpose?: string
    parsed_dkk_number?: string | null
    parsed_month?: number | null
    parsed_year?: number | null
    matched_contract_id?: number | null
    match_status?: string
    inference_status?: string
    skip_reason?: string | null
    import_batch_id?: number | null
  }>
): Promise<number[]> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const nowIso = new Date().toISOString()
  const payload = rows.map((r) => {
    // A non-empty skip_reason means the cleaner flagged this row as noise. We
    // preserve it (nothing is dropped) but mark it SKIPPED / IRRELEVANT so it
    // stays out of the active queue — mirrors skipStagingRow(), reversible.
    const skipReason = (r.skip_reason ?? '').trim() || null
    return {
      ...r,
      credit_currency: r.credit_currency ?? 'RUB',
      source_amount: r.source_amount ?? null,
      source_currency: r.source_currency ?? null,
      match_status: r.match_status ?? 'PENDING',
      inference_status: skipReason
        ? 'IRRELEVANT'
        : (r.inference_status ?? deriveInferenceFromMatchStatus(r.match_status)),
      skip_reason: skipReason,
      skipped_at: skipReason ? nowIso : null,
      skipped_by: skipReason ? actorUserId : null,
      imported_by: actorUserId,
      updated_by: actorUserId,
    }
  })
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_bank_statement_staging')
      .insert(payload)
      .select('row_id')
  )
  if (error) throw error
  return ((data ?? []) as Array<{ row_id: number }>).map((r) => r.row_id)
}

function deriveInferenceFromMatchStatus(
  matchStatus: string | null | undefined,
): 'PENDING' | 'RESOLVED' | 'PARTIAL' | 'AMBIGUOUS' | 'UNRESOLVED' | 'IRRELEVANT' {
  switch (matchStatus) {
    case 'MATCHED':   return 'RESOLVED'
    case 'AMBIGUOUS': return 'AMBIGUOUS'
    case 'UNMATCHED': return 'UNRESOLVED'
    case 'SKIPPED':   return 'IRRELEVANT'
    default:          return 'PENDING'
  }
}

function normalizeStagingDate(raw: string | null | undefined): string {
  const value = (raw ?? '').trim()
  if (!value) return ''
  const ddmmyyyy = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value)
  if (ddmmyyyy) return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`
  return value.slice(0, 10)
}

function normalizeStagingText(raw: string | null | undefined): string {
  return (raw ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
}

function normalizeStagingAmount(raw: string | null | undefined): string {
  return parseBankAmount(raw).toFixed(2)
}

// Credited currency is part of a row's identity: an AED credit and a RUB credit
// of the same numeric amount, same day, same payer and purpose are DIFFERENT
// payments and must never collapse into one dedup key (plan §14.11.4). Default RUB
// so historical rows (currency defaulted to RUB) keep matching on re-import.
function normalizeStagingCurrency(raw: string | null | undefined): string {
  const c = (raw ?? '').trim().toUpperCase()
  return c || 'RUB'
}

function makeStagingPrimaryKey(row: {
  doc_number?: string
  doc_date?: string
  operation_date?: string
  counterparty?: string
  inn?: string
  credit_amount?: string
  credit_currency?: string | null
  payment_purpose?: string
}): string | null {
  const docNumber = normalizeStagingText(row.doc_number)
  const docDate = normalizeStagingDate(row.doc_date)
  if (!docNumber || !docDate) return null
  const opDate = normalizeStagingDate(row.operation_date || row.doc_date)
  const counterparty = normalizeStagingText(row.counterparty)
  const inn = normalizeStagingText(row.inn)
  const amount = normalizeStagingAmount(row.credit_amount)
  const currency = normalizeStagingCurrency(row.credit_currency)
  const purpose = normalizeStagingText(row.payment_purpose)
  // Strict duplicate rule: full row match, including document number + document date.
  return ['STRONG', docNumber, docDate, opDate, counterparty, inn, amount, currency, purpose].join('|')
}

// Fallback dedup key used when the bank export does not provide doc_number or
// doc_date (some bank statements ship "weak" rows like that). The composition is
// intentionally narrower than the strict key: operation date + ИНН + сумма + контрагент
// + начало назначения. Two physical bank operations on the same day from the same
// payer for the same amount with the same purpose prefix are treated as the same
// row on re-import. This is not perfect, but it stops the most common case —
// re-uploading the same statement twice.
function makeStagingFallbackKey(row: {
  doc_number?: string
  doc_date?: string
  operation_date?: string
  counterparty?: string
  inn?: string
  credit_amount?: string
  credit_currency?: string | null
  payment_purpose?: string
}): string | null {
  const opDate = normalizeStagingDate(row.operation_date || row.doc_date)
  if (!opDate) return null
  const counterparty = normalizeStagingText(row.counterparty)
  const inn = normalizeStagingText(row.inn)
  const amount = normalizeStagingAmount(row.credit_amount)
  const currency = normalizeStagingCurrency(row.credit_currency)
  const purpose = normalizeStagingText(row.payment_purpose).slice(0, 200)
  // The amount alone wouldn't be enough; we also require either ИНН or контрагент
  // to be present — otherwise the key is too loose and would collapse different
  // operations on the same day.
  if (!inn && !counterparty) return null
  return ['WEAK', opDate, counterparty, inn, amount, currency, purpose].join('|')
}

function makeStagingDedupKey(row: {
  doc_number?: string
  doc_date?: string
  operation_date?: string
  counterparty?: string
  inn?: string
  credit_amount?: string
  credit_currency?: string | null
  payment_purpose?: string
}): string | null {
  return makeStagingPrimaryKey(row) ?? makeStagingFallbackKey(row)
}

export async function filterDuplicateBankStatementRows(
  rows: Array<{
    doc_number?: string
    doc_date?: string
    operation_date?: string
    counterparty?: string
    inn?: string
    beneficiary_inn?: string | null
    credit_amount?: string
    credit_currency?: string | null
    source_amount?: string | number | null
    source_currency?: string | null
    payment_purpose?: string
    parsed_dkk_number?: string | null
    parsed_month?: number | null
    parsed_year?: number | null
    matched_contract_id?: number | null
    match_status?: string
  }>
): Promise<{
  rowsToInsert: Array<{
    doc_number?: string
    doc_date?: string
    operation_date?: string
    counterparty?: string
    inn?: string
    beneficiary_inn?: string | null
    credit_amount?: string
    credit_currency?: string | null
    source_amount?: string | number | null
    source_currency?: string | null
    payment_purpose?: string
    parsed_dkk_number?: string | null
    parsed_month?: number | null
    parsed_year?: number | null
    matched_contract_id?: number | null
    match_status?: string
  }>
  duplicateRows: Array<{
    doc_number?: string
    doc_date?: string
    operation_date?: string
    counterparty?: string
    inn?: string
    beneficiary_inn?: string | null
    credit_amount?: string
    credit_currency?: string | null
    source_amount?: string | number | null
    source_currency?: string | null
    payment_purpose?: string
    parsed_dkk_number?: string | null
    parsed_month?: number | null
    parsed_year?: number | null
    matched_contract_id?: number | null
    match_status?: string
  }>
  duplicateCount: number
}> {
  if (rows.length === 0) {
    return { rowsToInsert: [], duplicateRows: [], duplicateCount: 0 }
  }

  const supabase = getSupabase()
  type ExistingRow = {
    doc_number: string | null
    doc_date: string | null
    operation_date: string | null
    counterparty: string | null
    inn: string | null
    credit_amount: string | null
    credit_currency: string | null
    payment_purpose: string | null
  }
  const existingRows: ExistingRow[] = []

  // Strong rows have doc_number — we look up DB rows by that exact set of
  // document numbers. Weak rows (no doc_number/date) are matched separately by
  // operation_date (see below), so that re-importing a statement which lacks
  // document numbers still detects duplicates.
  // ⚠️ Банк повторяет номера документов изо дня в день, поэтому выборка по номерам
  // тянет всю их историю (03.09.2026: 139 номеров → 1 296 строк) и обязана
  // читаться страницами. Сужать её по дате документа здесь нельзя: формат даты
  // во входной строке и в базе может различаться, а ключ нормализует его сам.
  const docNumbers = [...new Set(rows.map((row) => row.doc_number?.trim()).filter(Boolean))] as string[]
  const chunkSize = 200

  for (let i = 0; i < docNumbers.length; i += chunkSize) {
    const chunk = docNumbers.slice(i, i + chunkSize)
    existingRows.push(...await selectAllDedupLookupPages<ExistingRow>(() =>
      (supabase as any)
        .from('royalty_bank_statement_staging')
        .select('doc_number,doc_date,operation_date,counterparty,inn,credit_amount,credit_currency,payment_purpose')
        .in('doc_number', chunk)
        .order('row_id', { ascending: true })
    ))
  }

  // Fallback lookup for weak rows: pull existing staging rows by operation_date.
  // Only the dates that show up in the weak subset of the new batch are queried,
  // so we don't load the entire archive.
  const weakOperationDates = [...new Set(
    rows
      .filter((r) => !makeStagingPrimaryKey(r))
      .map((r) => normalizeStagingDate(r.operation_date || r.doc_date))
      .filter(Boolean)
  )]

  for (let i = 0; i < weakOperationDates.length; i += chunkSize) {
    const chunk = weakOperationDates.slice(i, i + chunkSize)
    existingRows.push(...await selectAllDedupLookupPages<ExistingRow>(() =>
      (supabase as any)
        .from('royalty_bank_statement_staging')
        .select('doc_number,doc_date,operation_date,counterparty,inn,credit_amount,credit_currency,payment_purpose')
        .in('operation_date', chunk)
        .order('row_id', { ascending: true })
    ))
  }

  const existingKeys = new Set<string>()

  for (const row of existingRows) {
    const normalized = {
      doc_number: row.doc_number ?? undefined,
      doc_date: row.doc_date ?? undefined,
      operation_date: row.operation_date ?? undefined,
      counterparty: row.counterparty ?? undefined,
      inn: row.inn ?? undefined,
      credit_amount: row.credit_amount ?? undefined,
      credit_currency: row.credit_currency ?? undefined,
      payment_purpose: row.payment_purpose ?? undefined,
    }
    const strong = makeStagingPrimaryKey(normalized)
    if (strong) existingKeys.add(strong)
    const weak = makeStagingFallbackKey(normalized)
    if (weak) existingKeys.add(weak)
  }

  const rowsToInsert: typeof rows = []
  const duplicateRows: typeof rows = []

  for (const row of rows) {
    const key = makeStagingDedupKey(row)
    const isDuplicate = !!key && existingKeys.has(key)

    if (isDuplicate) {
      duplicateRows.push(row)
      continue
    }

    rowsToInsert.push(row)
    if (key) existingKeys.add(key)
  }

  return {
    rowsToInsert,
    duplicateRows,
    duplicateCount: duplicateRows.length,
  }
}

export interface RoyaltyImportHistoryItem {
  import_id: number
  imported_at: string
  imported_by: string | null
  imported_by_name: string | null
  file_name: string | null
  period_from: string | null
  period_to: string | null
  total_rows: number
  staging_rows_inserted: number
  duplicates_skipped: number
  matched_count: number
  no_inn_count: number
  no_contract_count: number
  no_period_count: number
  payments_inserted: number
  payments_allocated: number
}

export async function createRoyaltyImportHistory(entry: Omit<RoyaltyImportHistoryItem, 'import_id' | 'imported_at'>): Promise<RoyaltyImportHistoryItem> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_import_history')
      .insert(entry)
      .select(`
        import_id,
        imported_at,
        imported_by,
        imported_by_name,
        file_name,
        period_from,
        period_to,
        total_rows,
        staging_rows_inserted,
        duplicates_skipped,
        matched_count,
        no_inn_count,
        no_contract_count,
        no_period_count,
        payments_inserted,
        payments_allocated
      `)
      .single()
  )
  if (error) throw error
  return data as RoyaltyImportHistoryItem
}

export async function updateRoyaltyImportHistory(
  importId: number,
  patch: Partial<Omit<RoyaltyImportHistoryItem, 'import_id' | 'imported_at'>>
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_import_history')
      .update(patch)
      .eq('import_id', importId)
  )
  if (error) throw error
}

/** Returns the current number of payments from an import that have allocations. */
export async function loadAllocatedPaymentCountForImport(importId: number): Promise<number> {
  const supabase = getSupabase()
  const response = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payment_reconciliation_v')
      .select('payment_id', { count: 'exact', head: true })
      .eq('import_batch_id', importId)
      .gt('allocations_count', 0)
  ) as { count?: number | null; error: unknown | null }
  const { count, error } = response
  if (error) throw error
  return Number(count ?? 0)
}

export async function loadRoyaltyImportHistory(limit = 30): Promise<RoyaltyImportHistoryItem[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_import_history')
      .select(`
        import_id,
        imported_at,
        imported_by,
        imported_by_name,
        file_name,
        period_from,
        period_to,
        total_rows,
        staging_rows_inserted,
        duplicates_skipped,
        matched_count,
        no_inn_count,
        no_contract_count,
        no_period_count,
        payments_inserted,
        payments_allocated
      `)
      .order('imported_at', { ascending: false })
      .limit(limit)
  )
  if (error) throw error
  return (data ?? []) as RoyaltyImportHistoryItem[]
}

/**
 * Load staging rows filtered by processing_status (two-axis model).
 * Defaults to OPEN — the only bucket where the user has work to do.
 * Pass `['COMMITTED','SPLIT']` for the «Обработано» tab,
 * `['SKIPPED']` for «Пропущено», etc.
 */
export async function loadBankStagingRows(
  processingStatuses: ProcessingStatus[] = ['OPEN'],
): Promise<BankStagingRow[]> {
  const supabase = getSupabase()
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
  const pageSize = 1000
  const rows: BankStagingRow[] = []
  let offset = 0

  while (true) {
    const currentOffset = offset
    const { data, error } = await measureRoyaltyAsync(
      'rpc.royalty_bank_staging_rows_page',
      () => withAuthRecovery(() =>
        (supabase as any).rpc('royalty_bank_staging_rows_page', {
          p_processing_statuses: processingStatuses,
          p_offset: currentOffset,
          p_limit: pageSize,
        })
      ),
      (result) => ({
        statuses: processingStatuses.join(','),
        offset: currentOffset,
        limit: pageSize,
        error: Boolean((result as any).error),
        ...summarizeRoyaltyPayload((result as any).data),
      })
    )
    if (error) throw error

    const chunk = (typeof data === 'string' ? JSON.parse(data) : (data ?? [])) as BankStagingRow[]
    rows.push(...chunk)

    if (chunk.length < pageSize) break
    offset += pageSize
  }

  markRoyaltyPerf('load.bank_staging_rows.complete', {
    statuses: processingStatuses.join(','),
    durationMs: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt),
    rows: rows.length,
  })
  return rows
}

export interface BankStagingRowsCursor {
  imported_at: string | null
  row_id: number
}

export interface LoadBankStagingRowsPageInput {
  processingStatuses?: ProcessingStatus[]
  cursor?: BankStagingRowsCursor | null
  limit?: number
  importId?: number | null
  uploadedDate?: string | null
  uploadedBy?: string | null
  search?: string | null
}

export interface BankStagingRowsPage {
  rows: BankStagingRow[]
  hasMore: boolean
  nextCursor: BankStagingRowsCursor | null
}

export async function loadBankStagingRowsPage(
  input: LoadBankStagingRowsPageInput = {}
): Promise<BankStagingRowsPage> {
  const supabase = getSupabase()
  const processingStatuses = input.processingStatuses ?? ['OPEN']
  const limit = Math.max(1, Math.min(input.limit ?? 100, 500))
  const payload = {
    p_processing_statuses: processingStatuses,
    p_cursor_imported_at: input.cursor?.imported_at ?? null,
    p_cursor_row_id: input.cursor?.row_id ?? null,
    p_limit: limit,
    p_import_batch_id: input.importId ?? null,
    p_uploaded_date: input.uploadedDate ?? null,
    p_uploaded_by: input.uploadedBy ?? null,
    p_search: input.search?.trim() || null,
  }

  const { data, error } = await measureRoyaltyAsync(
    'rpc.royalty_bank_staging_rows_page_v2',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('royalty_bank_staging_rows_page_v2', payload)
    ),
    (result) => ({
      statuses: processingStatuses.join(','),
      limit,
      cursorRowId: input.cursor?.row_id ?? null,
      importId: input.importId ?? null,
      uploadedDate: input.uploadedDate ?? null,
      uploadedBy: input.uploadedBy ?? null,
      search: payload.p_search ? true : false,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (error) throw error

  const parsed = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
  const rows = Array.isArray(parsed.rows) ? parsed.rows as BankStagingRow[] : []
  const nextCursor = parsed.nextCursor && typeof parsed.nextCursor === 'object'
    ? parsed.nextCursor as BankStagingRowsCursor
    : null

  markRoyaltyPerf('load.bank_staging_rows_page.complete', {
    statuses: processingStatuses.join(','),
    rows: rows.length,
    hasMore: Boolean(parsed.hasMore),
    nextCursorRowId: nextCursor?.row_id ?? null,
  })

  return {
    rows,
    hasMore: Boolean(parsed.hasMore),
    nextCursor,
  }
}

export type PaymentKind = 'ROYALTY' | 'LUMP_SUM' | 'DEPOSIT'

export async function commitStagingRow(
  rowId: number,
  contractId: number | null,
  intendedYear: number | null,
  intendedMonth: number | null,
  note?: string | null,
  paymentKind: PaymentKind = 'ROYALTY'
): Promise<number> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const payloadWithKind = {
    p_row_id:         rowId,
    p_contract_id:    contractId,
    p_intended_year:  intendedYear,
    p_intended_month: intendedMonth,
    p_note:           note ?? null,
    p_actor_user_id:  actorUserId,
    p_payment_kind:   paymentKind,
  }
  const payloadWithActor = {
    p_row_id:         rowId,
    p_contract_id:    contractId,
    p_intended_year:  intendedYear,
    p_intended_month: intendedMonth,
    p_note:           note ?? null,
    p_actor_user_id:  actorUserId,
  }
  const payloadLegacy = {
    p_row_id:         rowId,
    p_contract_id:    contractId,
    p_intended_year:  intendedYear,
    p_intended_month: intendedMonth,
    p_note:           note ?? null,
  }

  // Try the new 7-arg signature first; fall back to 6-arg, then 5-arg.
  let { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('commit_bank_staging_row', payloadWithKind)
  )

  let errorText = formatSupabaseLikeError(error)
  const looksLikeSignatureMismatch = (txt: string) =>
    txt.includes('Could not find the function public.commit_bank_staging_row') ||
    txt.includes('function public.commit_bank_staging_row') ||
    txt.includes('schema cache')

  if (error && looksLikeSignatureMismatch(errorText)) {
    ;({ data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('commit_bank_staging_row', payloadWithActor)
    ))
    errorText = formatSupabaseLikeError(error)
  }

  if (error && looksLikeSignatureMismatch(errorText)) {
    ;({ data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('commit_bank_staging_row', payloadLegacy)
    ))
  }

  if (error) {
    throw new Error(formatSupabaseLikeError(error))
  }
  return data as number
}

export interface SplitStagingPart {
  contract_id: number
  /** Сумма части в ВАЛЮТЕ ВЫПИСКИ (`staging.credit_currency`), не в рублях. */
  amount: number
  intended_year: number | null
  intended_month: number | null
  allocation_note?: string | null
}

export interface SplitStagingOptions {
  /** Валюта строки; нужна, чтобы для RUB продублировать legacy-ключ `amount_rub`. */
  currencyCode?: string | null
  /**
   * Курс валюты строки к ₽, введённый оператором. Нужен только когда даты платежа
   * нет в `fx_daily_rates` (RPC иначе блокирует FX_MISSING и не считает 1:1).
   */
  fxRateToRub?: number | null
}

/**
 * Split ONE bank staging row into N royalty_payments (multi-contract bank transfer).
 * See docs/PARTNER_PAYMENT_POOL_REWORK_PLAN.md, stage 1.
 *
 * Суммы частей — в валюте выписки (миграция 20260726140000). Для RUB-строк
 * дополнительно шлём legacy-ключ `amount_rub` с тем же значением, чтобы вызов
 * работал и против ещё не обновлённой RPC; на foreign-строке legacy-ключ
 * отправлять нельзя — новая RPC отбивает его как CURRENCY_MISMATCH, а старая
 * записала бы AED в рубли.
 * Returns created payment_id values, in the same order as parts.
 */
export async function splitStagingRow(
  rowId: number,
  parts: SplitStagingPart[],
  options?: SplitStagingOptions
): Promise<number[]> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const isRubRow = (options?.currencyCode ?? 'RUB').toUpperCase() === 'RUB'
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('split_staging_row', {
      p_row_id:        rowId,
      p_parts:         parts.map(p => ({
        contract_id:     p.contract_id,
        amount:          p.amount,
        ...(isRubRow ? { amount_rub: p.amount } : {}),
        intended_year:   p.intended_year,
        intended_month:  p.intended_month,
        allocation_note: p.allocation_note ?? null,
      })),
      p_actor_user_id: actorUserId,
      ...(options?.fxRateToRub != null ? { p_fx_rate: options.fxRateToRub } : {}),
    })
  )
  if (error) throw error
  return (data as number[]) ?? []
}

export type RoyaltySkipBucket = import('@/types').SkipBucket

export async function skipStagingRow(
  rowId: number,
  reason?: string,
  bucket: RoyaltySkipBucket = 'REVIEW_SKIP',
): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const nowIso = new Date().toISOString()
  const { error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_bank_statement_staging')
      .update({
        inference_status: 'IRRELEVANT',
        skipped_at:       nowIso,
        skipped_by:       actorUserId,
        skip_reason:      reason ?? 'MANUAL',
        skip_bucket:      bucket,
        updated_by:       actorUserId,
        updated_at:       nowIso,
      })
      .eq('row_id', rowId)
  )
  if (error) throw error
}

export async function moveSkippedStagingRowToBucket(
  rowId: number,
  bucket: RoyaltySkipBucket,
): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_bank_statement_staging')
      .update({
        skip_bucket: bucket,
        updated_by: actorUserId,
        updated_at: new Date().toISOString(),
      })
      .eq('row_id', rowId)
      .not('skipped_at', 'is', null)
  )
  if (error) throw error
}

export async function restoreStagingRow(rowId: number): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_bank_statement_staging')
      .update({
        inference_status: 'UNRESOLVED',
        skipped_at:       null,
        skipped_by:       null,
        skip_reason:      null,
        skip_bucket:      null,
        updated_by:       actorUserId,
        updated_at:       new Date().toISOString(),
      })
      .eq('row_id', rowId)
  )
  if (error) throw error
}

export type BankStagingMatchStatus = 'MATCHED' | 'AMBIGUOUS' | 'UNMATCHED'

export interface BankStagingRowMatchingUpdate {
  row_id: number
  matched_contract_id: number | null
  match_status: BankStagingMatchStatus
  parsed_dkk_number?: string | null
  parsed_month?: number | null
  parsed_year?: number | null
  // Optional: caller knows whether MATCHED has a period and may pass
  // 'RESOLVED' or 'PARTIAL' explicitly. If omitted, falls back to a
  // safe derivation (MATCHED → RESOLVED).
  inference_status?: import('@/types').InferenceStatus
}

export async function updateBankStagingRowsMatching(
  items: BankStagingRowMatchingUpdate[]
): Promise<void> {
  if (items.length === 0) return

  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const updatedAt = new Date().toISOString()
  const chunkSize = 5

  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize)

    await Promise.all(chunk.map(async (item) => {
      const { error } = await withAuthRecovery(() =>
        (supabase as any)
          .from('royalty_bank_statement_staging')
          .update({
            matched_contract_id: item.matched_contract_id,
            match_status: item.match_status,
            parsed_dkk_number: item.parsed_dkk_number ?? null,
            parsed_month: item.parsed_month ?? null,
            parsed_year: item.parsed_year ?? null,
            inference_status: item.inference_status ?? deriveInferenceFromMatchStatus(item.match_status),
            updated_by: actorUserId,
            updated_at: updatedAt,
          })
          .eq('row_id', item.row_id)
      )
      if (error) throw error
    }))
  }
}

// Payments without period across all contracts (for the unallocated page)
export async function loadPaymentsWithoutPeriod({
  offset = 0,
  limit = 40,
}: {
  offset?: number
  limit?: number
} = {}): Promise<{ payments: RoyaltyPayment[]; total: number }> {
  const supabase = getSupabase()
  const { data, error, count } = await withAuthRecovery(() =>
    supabase
      .from('royalty_payments')
      .select(`
        payment_id,
        contract_id,
        payment_date,
        amount_rub,
        payment_purpose,
        payer_inn,
        beneficiary_inn,
        payer_name,
        allocation_note
      `, { count: 'planned' })
      .is('intended_year', null)
      .order('payment_date', { ascending: false })
      .order('payment_id', { ascending: false })
      .range(offset, offset + limit - 1)
  )
  if (error) throw error
  return {
    payments: (data ?? []) as RoyaltyPayment[],
    total: typeof count === 'number' ? count : 0,
  }
}

// ---------- Charge hints by contracts (for bank import diagnostics) ----------

export async function loadRoyaltyChargeHintsByContracts(
  contractIds: number[]
): Promise<RoyaltyChargeHint[]> {
  if (contractIds.length === 0) return []
  const supabase = getSupabase()
  const uniqueIds = [...new Set(contractIds.filter((id) => Number.isFinite(id)))]
  const chunkSize = 200
  const pageSize = 1000
  const result: RoyaltyChargeHint[] = []

  for (let i = 0; i < uniqueIds.length; i += chunkSize) {
    const chunk = uniqueIds.slice(i, i + chunkSize)
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await withAuthRecovery(() =>
        (supabase as any)
          .from('royalty_balance')
          .select('charge_id,contract_id,year,month,royalty_amount,royalty_currency,royalty_amount_rub,debt_amount,debt_amount_rub')
          .in('contract_id', chunk)
          .order('contract_id', { ascending: true })
          .order('year', { ascending: false })
          .order('month', { ascending: false })
          .order('charge_id', { ascending: true })
          .range(from, from + pageSize - 1)
      )
      if (error) throw error
      const page = (data ?? []) as RoyaltyChargeHint[]
      result.push(...page)
      if (page.length < pageSize) break
    }
  }

  return result
}

// ============================================================
// Save legal entity
// ============================================================

/**
 * Понятное объяснение вместо сырой ошибки уникальности.
 *
 * ⚠️ ИНН уникален ГЛОБАЛЬНО (`partner_legal_entities_inn_unique`), и при отказе
 * оператор видел «duplicate key value violates unique constraint». Живой случай
 * 01.09.2026: юрлицо не сохранялось с ИНН, человек убрал номер — и завёл пустой
 * дубль, из-за которого сторона договора осталась без реквизита (ДКК 839-Н-ПП).
 */
async function describeLegalEntityConflict(
  error: { code?: string; message?: string; details?: string },
  entity: { partner_number: number; inn?: string | null; legal_name: string }
): Promise<string | null> {
  const text = `${error?.code ?? ''} ${error?.message ?? ''} ${error?.details ?? ''}`
  if (!text.includes('23505') && !/duplicate key/i.test(text)) return null

  const supabase = getSupabase()
  if (/inn/i.test(text) && entity.inn) {
    const { data } = await supabase
      .from('partner_legal_entities')
      .select('entity_id,legal_name,partner_number')
      .eq('inn', entity.inn)
      .limit(1)
    const owner = (data ?? [])[0] as { legal_name?: string; partner_number?: number } | undefined
    if (!owner) return `ИНН ${entity.inn} уже используется другим юрлицом.`
    return owner.partner_number === entity.partner_number
      ? `ИНН ${entity.inn} уже привязан к юрлицу «${owner.legal_name}» этого же франчайзи — выберите его в списке вместо создания нового.`
      : `ИНН ${entity.inn} уже привязан к юрлицу «${owner.legal_name}» франчайзи № ${owner.partner_number}. Проверьте номер: одно юрлицо не может числиться за двумя франчайзи.`
  }
  if (/name_key/i.test(text)) {
    return `У этого франчайзи уже есть юрлицо с названием «${entity.legal_name}» — выберите его в списке.`
  }
  return null
}

export async function savePartnerLegalEntity(entity: {
  partner_number: number
  inn?: string | null
  legal_name: string
  is_primary?: boolean
  country?: string
}): Promise<PartnerLegalEntity> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .rpc('create_partner_legal_entity', {
        p_partner_number: entity.partner_number,
        p_inn: entity.inn?.trim() || null,
        p_legal_name: entity.legal_name,
        p_country: entity.country?.trim() || 'Россия',
        p_is_primary: entity.is_primary ?? false,
      })
      .select()
      .single()
  )
  if (error) {
    const explained = await describeLegalEntityConflict(error as any, entity)
    if (explained) throw new Error(explained)
    throw error
  }
  return data as unknown as PartnerLegalEntity
}

/**
 * Правка реквизитов уже заведённого юрлица: название, страна и НЕЗАПОЛНЕННЫЙ
 * налоговый номер.
 *
 * ⚠️ Смена уже заполненного номера здесь запрещена намеренно: другой ИНН — это
 * другое лицо, то есть смена СТОРОНЫ договора (новое юрлицо + ДС `ENTITY_CHANGE`),
 * а не правка реквизита. Через эту ручку такая замена выглядела бы как опечатка и
 * молча переадресовала бы все прошлые требования другому налогоплательщику.
 * Отдельный случай — белорусские строки, где в поле номера лежит IBAN (ключ
 * автомэтча платежей): его не вычищают, а заводят отдельное юрлицо с УНП.
 */
export async function updatePartnerLegalEntityDetails(
  entityId: number,
  patch: { legal_name: string; country: string; inn?: string | null },
  current: { partner_number: number; inn?: string | null }
): Promise<PartnerLegalEntity> {
  const supabase = getSupabase()
  const currentInn = current.inn?.trim() || null
  const nextInn = patch.inn?.trim() || null
  if (currentInn && nextInn !== currentInn) {
    throw new Error(
      'Налоговый номер уже заполнен. Другой номер означает другое лицо — это смена стороны договора: заведите новое юрлицо и оформите ДС «Сменить юрлицо».'
    )
  }
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .rpc('update_partner_legal_entity_details', {
        p_entity_id: entityId,
        p_legal_name: patch.legal_name,
        p_country: patch.country.trim(),
        p_inn: nextInn,
      })
      .select()
      .single()
  )
  if (error) {
    const explained = await describeLegalEntityConflict(error as any, {
      partner_number: current.partner_number,
      inn: nextInn,
      legal_name: patch.legal_name,
    })
    if (explained) throw new Error(explained)
    throw error
  }
  return data as unknown as PartnerLegalEntity
}

export async function deletePartnerLegalEntity(entityId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('delete_partner_legal_entity', { p_entity_id: entityId })
  )
  if (error) throw error
}

// ============================================================
// Skip rules (Stage 4)
// ============================================================

export type RoyaltySkipRuleType = 'INN' | 'COUNTERPARTY_NAME' | 'PAYMENT_PURPOSE' | 'AMOUNT_LT'
export type RoyaltySkipRuleTargetBucket = RoyaltySkipBucket

export interface RoyaltySkipRule {
  rule_id: number
  rule_type: RoyaltySkipRuleType
  rule_value: string
  target_bucket: RoyaltySkipRuleTargetBucket
  description: string | null
  is_active: boolean
  created_at: string
  created_by: string | null
}

export async function loadRoyaltySkipRules(): Promise<RoyaltySkipRule[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_skip_rules')
      .select('rule_id, rule_type, rule_value, target_bucket, description, is_active, created_at, created_by')
      .order('rule_id', { ascending: false })
  )
  if (error) throw error
  return (data ?? []) as RoyaltySkipRule[]
}

export async function createRoyaltySkipRule(input: {
  rule_type: RoyaltySkipRuleType
  rule_value: string
  target_bucket?: RoyaltySkipRuleTargetBucket
  description?: string | null
}): Promise<RoyaltySkipRule> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_skip_rules')
      .insert({
        ...input,
        target_bucket: input.target_bucket ?? 'REVIEW_SKIP',
        is_active: true,
        created_by: actorUserId,
      })
      .select()
      .single()
  )
  if (error) throw error
  return data as RoyaltySkipRule
}

export async function updateRoyaltySkipRule(input: {
  rule_id: number
  rule_type: RoyaltySkipRuleType
  rule_value: string
  target_bucket?: RoyaltySkipRuleTargetBucket
  description?: string | null
}): Promise<RoyaltySkipRule> {
  const supabase = getSupabase()
  const payload = {
    rule_type: input.rule_type,
    rule_value: input.rule_value,
    target_bucket: input.target_bucket ?? 'REVIEW_SKIP',
    description: input.description ?? null,
  }
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_skip_rules')
      .update(payload)
      .eq('rule_id', input.rule_id)
      .select()
      .single()
  )
  if (error) throw error
  return data as RoyaltySkipRule
}

export async function deleteRoyaltySkipRule(ruleId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_skip_rules')
      .delete()
      .eq('rule_id', ruleId)
  )
  if (error) throw error
}

export async function setRoyaltySkipRuleActive(ruleId: number, isActive: boolean): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_skip_rules')
      .update({ is_active: isActive })
      .eq('rule_id', ruleId)
  )
  if (error) throw error
}

// Apply active rules to staging rows. NULL = all unresolved rows.
export async function applyRoyaltySkipRules(rowIds: number[] | null = null): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('apply_royalty_skip_rules', {
      p_row_ids: rowIds,
    })
  )
  if (error) throw error
  return (data as number) ?? 0
}

// ============================================================
// LVREF: код платёжного требования в назначении (миграция 20261026120000)
// ============================================================

export type StagingPaymentReferenceStatus =
  | 'ABSENT'
  | 'MALFORMED'
  | 'INVALID_CHECKSUM'
  | 'UNKNOWN'
  | 'MULTIPLE'
  | 'VALID_RESERVED'
  | 'VALID_ACTIVE'
  | 'VALID_SUSPENDED'
  | 'VALID_RETIRED'

export type StagingPaymentReferenceMatchStatus =
  | 'RESOLVED_BY_REFERENCE'
  | 'RESOLVED_BY_REFERENCE_UNVERIFIED_PAYER'
  | 'REFERENCE_CONFLICT'
  | 'REFERENCE_ALREADY_SETTLED'
  | 'REFERENCE_REVIEW'

/** Разбор строки выписки сервером (`royalty_payment_reference_analyze`); решает сервер. */
export interface StagingPaymentReference {
  row_id: number
  reference_status: StagingPaymentReferenceStatus
  reference_code?: string | null
  match_status?: StagingPaymentReferenceMatchStatus | null
  request_id?: number
  request_status?: string
  series_id?: number
  contract_id?: number
  contract_number?: string | null
  year?: number
  month?: number
  charge_type?: string
  currency_code?: string
  recipient_partner_number?: number
  problems?: string[]
  payer_check?: 'MATCH' | 'SAME_PARTNER' | 'OTHER' | 'UNKNOWN'
  current_debt?: number
  override?: { reason: string; created_at: string; created_by: string | null; is_current: boolean } | null
}

/** Записать подсказки распознавания LVREF в строки выписки (после загрузки банка и Zbooni). */
export async function recognizeStagingPaymentReferences(rowIds: number[] | null): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('recognize_royalty_staging_payment_references', { p_row_ids: rowIds })
  )
  if (error) throw error
  return Number(data ?? 0)
}

export async function loadStagingPaymentReferences(rowIds: number[]): Promise<StagingPaymentReference[]> {
  if (rowIds.length === 0) return []
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_staging_payment_references', { p_row_ids: rowIds })
  )
  if (error) throw error
  return (data ?? []) as StagingPaymentReference[]
}

/**
 * Ручное решение по строке, которую нельзя провести по коду (конфликт, отменённое
 * требование, испорченный код). null — снять решение.
 */
export async function setStagingPaymentReferenceOverride(rowId: number, reason: string | null): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_staging_reference_override', { p_row_id: rowId, p_reason: reason })
  )
  if (error) throw error
}

// ============================================================
// Bulk commit staging rows (Stage 2)
// ============================================================

export interface BulkCommitInput {
  row_id: number
  contract_id: number
  intended_year: number | null
  intended_month: number | null
  note?: string | null
}

export interface BulkCommitResult {
  committed: number
  skipped_no_period: number
  errors: Array<{ row_id: number; message: string }>
}

// ============================================================
// Manual split of one payment across N charges (Stage 5)
// ============================================================

/**
 * Replace the full allocation set of a (non-deposit) payment.
 * Amounts are expressed both in the charge currency (for debt validation) and
 * in the payment currency (for payment-balance validation).
 * Pass an empty array to clear allocations entirely.
 *
 * Note: charges may belong to a different series than payment.contract_id.
 * The server auto-fills cross_partner_note for cross-partner allocations.
 *
 * Stage 2 of partner payment pool rework.
 * See docs/PARTNER_PAYMENT_POOL_REWORK_PLAN.md.
 */
export interface ManualAllocationExpectation {
  updatedAt: string
  contractId: number | null
  partnerNumber: number | null
  seriesId: number | null
  year: number | null
  month: number | null
}

export async function splitPaymentAcrossCharges(
  paymentId: number,
  allocations: Array<{
    charge_id: number
    amount: number
    currency_code: string
    payment_amount: number
    note?: string | null
  }>,
  expected?: ManualAllocationExpectation,
  matchDecision?: {
    comment?: string | null
    context?: Record<string, unknown>
  }
): Promise<void> {
  const rpcClient = getUntypedRpcClient()
  const allocationPayload = allocations.map(a => ({
    charge_id:      a.charge_id,
    amount:         a.amount,
    currency_code:  a.currency_code,
    payment_amount: a.payment_amount,
    note:           a.note ?? null,
  }))
  const { error } = await withAuthRecovery(() =>
    expected && matchDecision
      ? rpcClient.rpc('allocate_payment_manual_v3', {
          p_payment_id: paymentId,
          p_allocations: allocationPayload,
          p_expected_updated_at: expected.updatedAt,
          p_expected_contract_id: expected.contractId,
          p_expected_partner_number: expected.partnerNumber,
          p_expected_series_id: expected.seriesId,
          p_expected_year: expected.year,
          p_expected_month: expected.month,
          p_decision_comment: matchDecision.comment?.trim() || null,
          p_decision_context: matchDecision.context ?? {},
        })
      : expected
        ? rpcClient.rpc('allocate_payment_manual_v2', {
          p_payment_id: paymentId,
          p_allocations: allocationPayload,
          p_expected_updated_at: expected.updatedAt,
          p_expected_contract_id: expected.contractId,
          p_expected_partner_number: expected.partnerNumber,
          p_expected_series_id: expected.seriesId,
          p_expected_year: expected.year,
          p_expected_month: expected.month,
        })
        : rpcClient.rpc('allocate_payment_manual', {
          p_payment_id: paymentId,
          p_allocations: allocationPayload,
          p_actor_user_id: null,
        })
  )
  if (error) throw error
}

export interface OnePaymentTargetAllocationResult {
  payment_id: number
  preview: boolean
  executed: boolean
  target_charge_type: string
  payment_amount_rub: number
  reserved_by_earlier_payments_rub: number
  remaining_rub: number
  allocations: Array<{
    charge_id: number
    amount: number
    currency_code: string
    payment_amount: number
    note: string | null
  }>
}

export async function allocateOnePaymentToCurrentTarget(input: {
  paymentId: number
  expectedUpdatedAt: string
  preview: boolean
}): Promise<OnePaymentTargetAllocationResult> {
  const rpcClient = getUntypedRpcClient()
  const { data, error } = await withAuthRecovery(() =>
    rpcClient.rpc('allocate_one_payment_to_current_target', {
      p_payment_id: input.paymentId,
      p_expected_updated_at: input.expectedUpdatedAt,
      p_preview: input.preview,
    })
  )
  if (error) throw error
  return data as OnePaymentTargetAllocationResult
}

export interface SelectedPaymentTargetAllocationResult {
  preview: boolean
  executed: boolean
  ready_count: number
  skipped_count: number
  executed_count: number
  total_amount_rub: number
  ready: OnePaymentTargetAllocationResult[]
  skipped: Array<{
    payment_id: number | null
    reason: string
    sqlstate: string
  }>
}

export async function allocateSelectedPaymentsToCurrentTarget(input: {
  items: Array<{
    paymentId: number
    expectedUpdatedAt: string
  }>
  preview: boolean
}): Promise<SelectedPaymentTargetAllocationResult> {
  const rpcClient = getUntypedRpcClient()
  const { data, error } = await withAuthRecovery(() =>
    rpcClient.rpc('allocate_selected_payments_to_current_target', {
      p_items: input.items.map((item) => ({
        payment_id: item.paymentId,
        expected_updated_at: item.expectedUpdatedAt,
      })),
      p_preview: input.preview,
    })
  )
  if (error) throw error
  return data as SelectedPaymentTargetAllocationResult
}

export type ImportedBankPaymentsAllocationResult = RoyaltyAutoAllocationResult

export interface BulkAllocatePartnerPaymentsResult {
  allocated: number
  skipped: Array<{ payment_id: number; reason: string }>
}

export interface BulkPaymentChargeTarget {
  charge_id: number
  amount_rub: number
}

function isRubCurrency(currency: string | null | undefined): boolean {
  return (currency?.trim() || 'RUB') === 'RUB'
}

/**
 * Allocate explicitly selected RUB partner payments to operator-selected charge
 * rows. The selected charge amounts are treated as the target plan; any payment
 * tail above that target remains unallocated credit on the payment.
 */
export async function bulkAllocateSelectedPartnerPayments(
  payments: PartnerPaymentRow[],
  balances: RoyaltyBalance[],
  targets: BulkPaymentChargeTarget[],
  decisionComment?: string | null
): Promise<BulkAllocatePartnerPaymentsResult> {
  const result: BulkAllocatePartnerPaymentsResult = { allocated: 0, skipped: [] }
  const balanceByChargeId = new Map(balances.map((balance) => [balance.charge_id, balance]))
  const targetDebts = targets
    .map((target) => {
      const balance = balanceByChargeId.get(target.charge_id)
      const amount = roundMoney(Number(target.amount_rub ?? 0))
      if (!balance || amount <= 0.005) return null
      if (!isRubCurrency(balance.royalty_currency)) {
        throw new Error(`Charge ${target.charge_id} is not RUB`)
      }
      const available = roundMoney(Number(balance.debt_amount_rub ?? 0))
      if (amount > available + 0.005) {
        throw new Error(`Charge ${target.charge_id}: amount exceeds open debt`)
      }
      return {
        charge_id: target.charge_id,
        remaining_rub: amount,
      }
    })
    .filter((row): row is { charge_id: number; remaining_rub: number } => row !== null)

  if (targetDebts.length === 0) {
    throw new Error('No charge targets selected')
  }

  const targetTotal = roundMoney(targetDebts.reduce((sum, target) => sum + target.remaining_rub, 0))
  const paymentTotal = roundMoney(payments.reduce((sum, payment) => sum + Number(payment.remaining_rub ?? payment.amount_rub ?? 0), 0))
  if (targetTotal > paymentTotal + 0.005) {
    throw new Error('Selected charge amount exceeds selected payments total')
  }

  for (const payment of payments) {
    if (payment.is_deposit) {
      result.skipped.push({ payment_id: payment.payment_id, reason: 'deposit' })
      continue
    }
    if (!isRubCurrency(payment.currency_code)) {
      result.skipped.push({ payment_id: payment.payment_id, reason: 'non-RUB payment requires manual FX rate' })
      continue
    }
    if (Number(payment.allocations_count ?? 0) > 0) {
      result.skipped.push({ payment_id: payment.payment_id, reason: 'payment already has allocations' })
      continue
    }

    let remaining = roundMoney(Number(payment.remaining_rub ?? payment.amount_rub ?? 0))
    if (remaining <= 0.005) {
      result.skipped.push({ payment_id: payment.payment_id, reason: 'no remaining amount' })
      continue
    }

    const allocations: Array<{
      charge_id: number
      amount: number
      currency_code: string
      payment_amount: number
      note?: string | null
    }> = []

    for (const debt of targetDebts) {
      if (remaining <= 0.005) break
      if (debt.remaining_rub <= 0.005) continue

      const amount = roundMoney(Math.min(remaining, debt.remaining_rub))
      if (amount <= 0.005) continue

      allocations.push({
        charge_id: debt.charge_id,
        amount,
        currency_code: 'RUB',
        payment_amount: amount,
      })
      remaining = roundMoney(remaining - amount)
      debt.remaining_rub = roundMoney(debt.remaining_rub - amount)
    }

    if (allocations.length === 0) {
      result.skipped.push({ payment_id: payment.payment_id, reason: 'selected charge targets already filled' })
      continue
    }

    try {
      await splitPaymentAcrossCharges(
        payment.payment_id,
        allocations,
        payment.updated_at
          ? {
              updatedAt: payment.updated_at,
              contractId: payment.contract_id,
              partnerNumber: payment.partner_number > 0 ? payment.partner_number : null,
              seriesId: payment.series_id,
              year: payment.intended_year ?? payment.assigned_year,
              month: payment.intended_month ?? payment.assigned_month,
            }
          : undefined,
        {
          comment: decisionComment?.trim() || null,
          context: {
            source: 'bulk_selected_payments_allocate_modal',
            selected_payment_count: payments.length,
            targets,
          },
        }
      )
      result.allocated += 1
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      result.skipped.push({ payment_id: payment.payment_id, reason: message })
      for (const allocation of allocations) {
        const debt = targetDebts.find((row) => row.charge_id === allocation.charge_id)
        if (debt) debt.remaining_rub = roundMoney(debt.remaining_rub + allocation.amount)
      }
    }
  }

  return result
}

async function bulkCommitStagingRowsLegacy(items: BulkCommitInput[]): Promise<BulkCommitResult> {
  const result: BulkCommitResult = { committed: 0, skipped_no_period: 0, errors: [] }
  for (const item of items) {
    if (item.intended_year === null || item.intended_month === null) {
      result.skipped_no_period += 1
      continue
    }
    try {
      await commitStagingRow(item.row_id, item.contract_id, item.intended_year, item.intended_month, item.note ?? null)
      result.committed += 1
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      result.errors.push({ row_id: item.row_id, message })
    }
  }
  return result
}

// Commits staging rows through a set-based DB RPC. Rows without a known period
// are skipped (returned in skipped_no_period); they need manual entry.
export async function bulkCommitStagingRows(items: BulkCommitInput[]): Promise<BulkCommitResult> {
  if (items.length === 0) {
    return { committed: 0, skipped_no_period: 0, errors: [] }
  }

  const supabase = getSupabase()
  const actorUserId = getActorUserId()

  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('bulk_commit_bank_staging_rows', {
      p_items: items.map((item) => ({
        row_id: item.row_id,
        contract_id: item.contract_id,
        intended_year: item.intended_year,
        intended_month: item.intended_month,
        note: item.note ?? null,
      })),
      p_actor_user_id: actorUserId,
    })
  )

  const errorText = formatSupabaseLikeError(error)
  const looksLikeMissingBulkRpc =
    errorText.includes('Could not find the function public.bulk_commit_bank_staging_rows') ||
    errorText.includes('function public.bulk_commit_bank_staging_rows') ||
    errorText.includes('schema cache')

  const result: BulkCommitResult = error && looksLikeMissingBulkRpc
    ? await bulkCommitStagingRowsLegacy(items)
    : error
      ? (() => { throw new Error(errorText) })()
      : {
          committed: Number((data as any)?.committed ?? 0),
          skipped_no_period: Number((data as any)?.skipped_no_period ?? 0),
          errors: Array.isArray((data as any)?.errors)
            ? (data as any).errors.map((row: any) => ({
                row_id: Number(row?.row_id ?? 0),
                message: String(row?.message ?? 'unknown error'),
              }))
            : [],
        }

  if (result.committed > 0 && !error) {
    try {
      await allocateUnmatchedBankPayments()
    } catch (e) {
      console.warn('Bulk commit: allocator failed', e)
    }
  }

  if (result.committed > 0 && error && looksLikeMissingBulkRpc) {
    try {
      await allocateUnmatchedBankPayments()
    } catch (e) {
      console.warn('Bulk commit: allocator failed', e)
    }
  }
  return result
}

// ============================================================
// Data gaps summary for /royalties progress block (Stage 7)
// ============================================================

export interface RoyaltyDataGapsSummary {
  pending_rows_count: number       // staging rows still in queue (UNMATCHED/AMBIGUOUS/PENDING)
  pending_amount_rub: number       // their total amount
  unknown_inn_count: number        // unique INNs in queue not found in partner_legal_entities
  unknown_contract_count: number   // unique contract numbers in queue not found in contracts
  no_period_payments_count: number // committed payments still without intended_year
  assigned_partner_payments_count: number // payments bound to partner but not to series/period
  assigned_partner_remaining_rub: number  // their unallocated remaining amount
}

export async function loadRoyaltyDataGapsSummary(): Promise<RoyaltyDataGapsSummary> {
  const supabase = getSupabase()

  const [stagingRes, innRes, contractRes, paymentsRes, assignedPartnerPaymentsRes] = await Promise.all([
    withAuthRecovery(() =>
      (supabase as any)
        .from('royalty_bank_statement_staging_v')
        .select('inn, parsed_dkk_number, payment_purpose, credit_amount')
        .eq('processing_status', 'OPEN')
        .not('credit_amount', 'is', null)
    ),
    withAuthRecovery(() =>
      supabase.from('partner_legal_entities').select('inn')
    ),
    withAuthRecovery(() =>
      (supabase as any)
        .from('contracts')
        .select('contract_number')
        .not('contract_number', 'is', null)
    ),
    withAuthRecovery(() =>
      supabase
        .from('royalty_payment_reconciliation_v')
        .select('payment_id', { count: 'exact', head: true })
        .is('intended_year', null)
        .eq('is_deposit', false)
        .gt('remaining_rub', 0.005)
    ),
    withAuthRecovery(() =>
      (supabase as any)
        .from('royalty_payment_reconciliation_v')
        .select('remaining_rub')
        .eq('reconciliation_status', 'ASSIGNED_TO_PARTNER')
        .eq('is_deposit', false)
        .or('payment_kind.eq.ROYALTY,payment_kind.is.null')
        .gt('remaining_rub', 0.005)
    ),
  ])

  if (stagingRes.error) throw stagingRes.error
  if (innRes.error) throw innRes.error
  if (contractRes.error) throw contractRes.error
  if (paymentsRes.error) throw paymentsRes.error
  if (assignedPartnerPaymentsRes.error) throw assignedPartnerPaymentsRes.error

  const stagingRows = (stagingRes.data ?? []) as Array<{
    inn: string | null
    parsed_dkk_number: string | null
    payment_purpose: string | null
    credit_amount: string | null
  }>
  const knownInns = new Set(
    ((innRes.data ?? []) as Array<{ inn: string | null }>)
      .map((e) => normalizeInnForMatching(e.inn))
      .filter(Boolean) as string[]
  )
  const knownContracts = new Set(
    ((contractRes.data ?? []) as Array<{ contract_number: string | null }>)
      .map((c) => normalizeContractNumberForMatching(c.contract_number))
      .filter(Boolean) as string[]
  )

  const unknownInnSet = new Set<string>()
  const unknownContractSet = new Set<string>()
  let pendingAmount = 0

  for (const row of stagingRows) {
    const inn = normalizeInnForMatching(row.inn)
    if (inn && !knownInns.has(inn)) unknownInnSet.add(inn)

    const contractNum = normalizeContractNumberForMatching(
      resolveDkkForMatching(row.parsed_dkk_number, row.payment_purpose)
    )
    if (contractNum && !knownContracts.has(contractNum)) unknownContractSet.add(contractNum)

    const raw = (row.credit_amount ?? '').replace(/\s/g, '').replace(',', '.')
    const n = parseFloat(raw)
    if (isFinite(n)) pendingAmount += n
  }

  const assignedPartnerPaymentRows = (assignedPartnerPaymentsRes.data ?? []) as Array<{ remaining_rub: number | null }>
  const assignedPartnerRemainingRub = assignedPartnerPaymentRows.reduce(
    (sum, row) => sum + Number(row.remaining_rub ?? 0),
    0
  )

  return {
    pending_rows_count: stagingRows.length,
    pending_amount_rub: pendingAmount,
    unknown_inn_count: unknownInnSet.size,
    unknown_contract_count: unknownContractSet.size,
    no_period_payments_count: paymentsRes.count ?? 0,
    assigned_partner_payments_count: assignedPartnerPaymentRows.length,
    assigned_partner_remaining_rub: assignedPartnerRemainingRub,
  }
}

// ============================================================
// SERIES LOOKUP (for deposit allocation modal)
// ============================================================

export async function loadSeriesSummary(seriesId: number): Promise<RoyaltyStudioSummary | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_studio_summary')
      .select('*')
      .eq('series_id', seriesId)
      .maybeSingle()
  )
  if (error) throw error
  return data as RoyaltyStudioSummary | null
}

// ============================================================
// CONTRACT SEARCH (for reassignment modal)
// ============================================================

export interface RoyaltyContractSearchResult {
  contract_id: number
  contract_number: string | null
  series_id: number
  partner_number: number
}

export async function loadContractSummaryById(
  contractId: number
): Promise<RoyaltyContractSearchResult | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_contract_summary')
      .select('contract_id, contract_number, series_id, partner_number')
      .eq('contract_id', contractId)
      .maybeSingle()
  )
  if (error) throw error
  return data as RoyaltyContractSearchResult | null
}

export async function searchContractsByNumber(query: string): Promise<RoyaltyContractSearchResult[]> {
  if (!query.trim()) return []
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_contract_summary')
      .select('contract_id, contract_number, series_id, partner_number')
      .ilike('contract_number', `%${query.trim()}%`)
      .limit(10)
  )
  if (error) throw error
  return (data ?? []) as RoyaltyContractSearchResult[]
}

// ============================================================
// CROSS-PARTNER ALLOCATIONS (for deposit badge on charges)
// ============================================================

export async function loadCrossPartnerAllocationsForSeries(seriesId: number): Promise<{
  charge_id: number
  year: number
  month: number
  allocated_amount_rub: number
  cross_partner_note: string
}[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payment_allocations')
      .select(`
        charge_id,
        allocated_amount_rub,
        cross_partner_note,
        royalty_charges!inner(year, month, series_id)
      `)
      .eq('royalty_charges.series_id', seriesId)
      .not('cross_partner_note', 'is', null)
  )
  if (error) throw error
  return ((data ?? []) as any[]).map((r: any) => ({
    charge_id: r.charge_id,
    year: r.royalty_charges.year,
    month: r.royalty_charges.month,
    allocated_amount_rub: r.allocated_amount_rub,
    cross_partner_note: r.cross_partner_note,
  }))
}

export async function loadDepositAllocationsForPartner(partnerNumber: number): Promise<{
  allocation_id: number
  charge_id: number
  payment_id: number
  allocated_amount_rub: number
  cross_partner_note: string
  year: number
  month: number
  series_id: number
  charge_partner_number: number
  created_at: string
}[]> {
  const supabase = getSupabase()
  // Allocations WHERE the payment belongs to this partner (deposit listed FROM this partner)
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payment_allocations')
      .select(`
        allocation_id,
        charge_id,
        payment_id,
        allocated_amount_rub,
        cross_partner_note,
        created_at,
        royalty_charges!inner(year, month, series_id),
        royalty_payments!inner(contract_id, contracts!inner(contract_series!inner(partner_number)))
      `)
      .eq('royalty_payments.contracts.contract_series.partner_number', partnerNumber)
      .not('cross_partner_note', 'is', null)
      .order('created_at', { ascending: false })
      .limit(50)
  )
  if (error) throw error
  return ((data ?? []) as any[]).map((r: any) => ({
    allocation_id: r.allocation_id,
    charge_id: r.charge_id,
    payment_id: r.payment_id,
    allocated_amount_rub: r.allocated_amount_rub,
    cross_partner_note: r.cross_partner_note,
    year: r.royalty_charges.year,
    month: r.royalty_charges.month,
    series_id: r.royalty_charges.series_id,
    charge_partner_number: 0, // filled separately if needed
    created_at: r.created_at,
  }))
}

// ============================================================
// DEPOSIT SYSTEM
// ============================================================

export async function loadPartnerDepositBalance(partnerNumber: number): Promise<PartnerDepositBalance[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('partner_deposit_balance')
      .select('*')
      .eq('partner_number', partnerNumber)
      .order('payment_date', { ascending: false })
  )
  if (error) throw error
  return (data ?? []) as PartnerDepositBalance[]
}

/**
 * Load every royalty_payment that belongs (currently) to the given partner.
 * Joins payment -> contract -> series, aggregates allocations into allocated_rub.
 * Stage 3 of partner payment pool rework.
 */
type PartnerPaymentReconciliationRow = PartnerPaymentRow & RoyaltyPayment

async function mapPartnerPaymentRows(
  partnerNumber: number,
  payRows: unknown[] | null
): Promise<PartnerPaymentRow[]> {
  const supabase = getSupabase()
  const payments = (payRows ?? []) as PartnerPaymentReconciliationRow[]
  if (payments.length === 0) return []

  // Studio per series for display labels.
  const seriesIds = Array.from(new Set(
    payments
      .map((p) => p.series_id)
      .filter((id): id is number => typeof id === 'number')
  ))
  const { data: studioRows, error: sErr } = seriesIds.length === 0
    ? { data: [], error: null as any }
    : await withAuthRecovery(() =>
        (supabase as any)
          .from('studio_ownerships')
          .select('series_id, studio_internal_id, studios:studio_internal_id(studio_number)')
          .in('series_id', seriesIds)
      )
  if (sErr) throw sErr
  const studioBySeries = new Map<number, { studio_internal_id: number | null; studio_number: string | null }>()
  for (const r of (studioRows ?? []) as any[]) {
    if (!studioBySeries.has(r.series_id)) {
      studioBySeries.set(r.series_id, {
        studio_internal_id: r.studio_internal_id ?? null,
        studio_number: r.studios?.studio_number ?? null,
      })
    }
  }

  return payments.map(p => {
    const seriesId = p.series_id ?? null
    const studio = seriesId != null
      ? (studioBySeries.get(seriesId) ?? { studio_internal_id: null, studio_number: null })
      : { studio_internal_id: null, studio_number: null }

    return {
      payment_id: p.payment_id,
      contract_id: p.contract_id,
      series_id: seriesId,
      studio_internal_id: studio.studio_internal_id,
      studio_number: studio.studio_number,
      partner_number: partnerNumber,
      entity_id: p.entity_id ?? null,
      assigned_year: p.assigned_year ?? null,
      assigned_month: p.assigned_month ?? null,
      assignment_note: p.assignment_note ?? null,
      payment_date: p.payment_date,
      amount_rub: p.amount_rub,
      amount: p.amount,
      currency_code: p.currency_code,
      fx_rate: p.fx_rate ?? null,
      payment_purpose: p.payment_purpose,
      payer_inn: p.payer_inn,
      beneficiary_inn: p.beneficiary_inn ?? null,
      payer_name: p.payer_name,
      bank_document_number: p.bank_document_number,
      bank_document_date: p.bank_document_date,
      source: p.source,
      intended_year: p.intended_year,
      intended_month: p.intended_month,
      allocation_note: p.allocation_note,
      is_deposit: p.is_deposit,
      deposit_note: p.deposit_note,
      payer_inn_external: p.payer_inn_external ?? false,
      payer_inn_note: p.payer_inn_note ?? null,
      payment_kind: p.payment_kind ?? 'ROYALTY',
      is_manually_reassigned: p.is_manually_reassigned,
      original_contract_id: p.original_contract_id,
      reassignment_note: p.reassignment_note,
      source_staging_row_id: p.source_staging_row_id,
      payment_group_id: p.payment_group_id,
      refunded_amount: Number(p.refunded_amount ?? 0),
      refunded_amount_rub: Number(p.refunded_amount_rub ?? 0),
      refund_date: p.refund_date ?? null,
      refund_reason_code: p.refund_reason_code ?? null,
      refund_payment_purpose: p.refund_payment_purpose ?? null,
      refund_note: p.refund_note ?? null,
      refund_bank_fx_rate: p.refund_bank_fx_rate == null ? null : Number(p.refund_bank_fx_rate),
      refund_bank_amount_rub: p.refund_bank_amount_rub == null ? null : Number(p.refund_bank_amount_rub),
      refund_recorded_by: p.refund_recorded_by ?? null,
      refund_recorded_at: p.refund_recorded_at ?? null,
      refund_id: p.refund_id == null ? null : Number(p.refund_id),
      refund_status: p.refund_status ?? null,
      refund_case_count: Number(p.refund_case_count ?? 0),
      allocated_rub: Number(p.allocated_rub ?? 0),
      remaining_rub: Number(p.remaining_rub ?? 0),
      remaining_amount: Number(p.remaining_amount ?? Math.max(
        Number(p.amount ?? 0) - Number(p.refunded_amount ?? 0),
        0
      )),
      allocations_count: Number(p.allocations_count ?? 0),
      reconciliation_status: p.reconciliation_status,
      candidate_charge_count: Number(p.candidate_charge_count ?? 0),
      candidate_debt_rub: Number(p.candidate_debt_rub ?? 0),
    }
  })
}

/**
 * Load every royalty_payment that belongs (currently) to the given partner.
 * Joins payment -> contract -> series, aggregates allocations into allocated_rub.
 * Stage 3 of partner payment pool rework.
 */
export async function loadPartnerPayments(partnerNumber: number): Promise<PartnerPaymentRow[]> {
  const supabase = getSupabase()

  const { data: payRows, error: pErr } = await fetchAllSupabaseRows<unknown>(() =>
    (supabase as any)
      .from('royalty_payment_reconciliation_v')
      .select('*')
      .eq('partner_number', partnerNumber)
      .order('payment_date', { ascending: false })
      .order('payment_id', { ascending: false })
  )
  if (pErr) throw pErr
  return mapPartnerPaymentRows(partnerNumber, payRows ?? [])
}

export type AllocationPaymentCategory =
  | 'unmatched'
  | 'work'
  | 'ready'
  | 'needs_decision'
  | 'waiting_charge'
  | 'needs_assignment'
  | 'lump_sum'
  | 'partial'
  | 'within_tolerance'

export interface AllocationQueuePaymentRow extends PartnerPaymentRow {
  persons_fio: string | null
  queue_bucket: string
  block_reason_code: string | null
  recommended_action: string | null
  target_charge_type: string | null
  target_charge_id: number | null
  target_debt_rub: number
  target_available_rub: number
  reserved_by_earlier_payments_rub: number
  period_summary: {
    charged_rub: number
    paid_rub: number
    delta_rub: number
    unallocated_assigned_rub: number
  } | null
}

export interface AllocationQueuePaymentsPage {
  rows: AllocationQueuePaymentRow[]
  totalCount: number
  totalPaymentsCount: number
  hasMore: boolean
  nextCursor: AllocationQueuePaymentsCursor | null
}

export interface AllocationQueuePaymentsCursor {
  partnerNumber?: number
  noPartner?: boolean
}

export async function loadAllocationQueuePayments(input: {
  category: AllocationPaymentCategory
  offset: number
  limit: number
  cursor?: AllocationQueuePaymentsCursor | null
  importId?: number | null
  uploadedDate?: string | null
  uploadedBy?: string | null
}): Promise<AllocationQueuePaymentsPage> {
  const supabase = getSupabase()
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
  if (input.category === 'lump_sum') {
    let query = (supabase as any)
      .from('royalty_payment_reconciliation_v')
      .select('*')
      .eq('payment_kind', 'LUMP_SUM')
      .order('payment_date', { ascending: false })
      .order('payment_id', { ascending: false })
      .range(input.offset, input.offset + input.limit - 1)

    if (input.importId != null) {
      const { data: importRows, error: importErr } = await withAuthRecovery(() =>
        (supabase as any)
          .from('royalty_payments')
          .select('payment_id')
          .eq('import_batch_id', input.importId)
          .eq('payment_kind', 'LUMP_SUM')
      )
      if (importErr) throw new Error(formatSupabaseLikeError(importErr))
      const importPaymentIds = ((importRows ?? []) as Array<{ payment_id: number }>).map((row) => row.payment_id)
      if (importPaymentIds.length === 0) {
        return {
          rows: [],
          totalCount: input.offset,
          totalPaymentsCount: 0,
          hasMore: false,
          nextCursor: null,
        }
      }
      query = query.in('payment_id', importPaymentIds)
    } else {
      if (input.uploadedDate) {
        const start = `${input.uploadedDate}T00:00:00`
        const endDate = new Date(`${input.uploadedDate}T00:00:00`)
        endDate.setDate(endDate.getDate() + 1)
        query = query.gte('created_at', start).lt('created_at', endDate.toISOString())
      }
      if (input.uploadedBy) query = query.eq('created_by', input.uploadedBy)
    }

    const { data, error } = await withAuthRecovery(() => query)
    if (error) throw new Error(formatSupabaseLikeError(error))

    const lumpPayments = (data ?? []) as Array<PartnerPaymentRow & RoyaltyPayment>
    const partnerNumbers = Array.from(new Set(
      lumpPayments
        .map((payment) => payment.partner_number)
        .filter((value): value is number => typeof value === 'number' && value > 0)
    ))
    const seriesIds = Array.from(new Set(
      lumpPayments
        .map((payment) => payment.series_id)
        .filter((value): value is number => typeof value === 'number')
    ))
    const [
      { data: partnerRows, error: partnerErr },
      { data: studioRows, error: studioErr },
    ] = await Promise.all([
      partnerNumbers.length === 0
        ? { data: [], error: null as any }
        : withAuthRecovery(() =>
            (supabase as any)
              .from('royalty_partner_summary')
              .select('partner_number,persons_fio')
              .in('partner_number', partnerNumbers)
          ),
      seriesIds.length === 0
        ? { data: [], error: null as any }
        : withAuthRecovery(() =>
            (supabase as any)
              .from('studio_ownerships')
              .select('series_id, studio_internal_id, studios:studio_internal_id(studio_number)')
              .in('series_id', seriesIds)
          ),
    ])
    if (partnerErr) throw new Error(formatSupabaseLikeError(partnerErr))
    if (studioErr) throw new Error(formatSupabaseLikeError(studioErr))

    const personsByPartner = new Map<number, string | null>()
    for (const r of (partnerRows ?? []) as Array<{ partner_number: number; persons_fio: string | null }>) {
      personsByPartner.set(r.partner_number, r.persons_fio ?? null)
    }
    const studioBySeries = new Map<number, { studio_internal_id: number | null; studio_number: string | null }>()
    for (const r of (studioRows ?? []) as any[]) {
      if (!studioBySeries.has(r.series_id)) {
        studioBySeries.set(r.series_id, {
          studio_internal_id: r.studio_internal_id ?? null,
          studio_number: r.studios?.studio_number ?? null,
        })
      }
    }

    const rows = lumpPayments.map((p) => {
      const partnerNumber = typeof p.partner_number === 'number' ? p.partner_number : 0
      const seriesId = p.series_id ?? null
      const studio = seriesId != null
        ? (studioBySeries.get(seriesId) ?? { studio_internal_id: null, studio_number: null })
        : { studio_internal_id: null, studio_number: null }

      return {
      payment_id: p.payment_id,
      contract_id: p.contract_id,
      series_id: seriesId,
      studio_internal_id: studio.studio_internal_id,
      studio_number: studio.studio_number,
      partner_number: partnerNumber,
      persons_fio: partnerNumber > 0 ? (personsByPartner.get(partnerNumber) ?? null) : null,
      entity_id: p.entity_id ?? null,
      assigned_year: p.assigned_year ?? null,
      assigned_month: p.assigned_month ?? null,
      assignment_note: p.assignment_note ?? null,
      payment_date: p.payment_date,
      amount_rub: Number(p.amount_rub ?? 0),
      amount: Number(p.amount ?? 0),
      currency_code: p.currency_code ?? 'RUB',
      fx_rate: p.fx_rate ?? null,
      payment_purpose: p.payment_purpose,
      payer_inn: p.payer_inn,
      beneficiary_inn: p.beneficiary_inn ?? null,
      payer_name: p.payer_name,
      bank_document_number: p.bank_document_number,
      bank_document_date: p.bank_document_date,
      source: p.source,
      created_by: p.created_by ?? null,
      updated_by: p.updated_by ?? null,
      created_at: p.created_at ?? null,
      updated_at: p.updated_at ?? null,
      intended_year: p.intended_year,
      intended_month: p.intended_month,
      allocation_note: p.allocation_note,
      is_deposit: p.is_deposit,
      deposit_note: p.deposit_note,
      payer_inn_external: p.payer_inn_external ?? false,
      payer_inn_note: p.payer_inn_note ?? null,
      payment_kind: 'LUMP_SUM',
      is_manually_reassigned: p.is_manually_reassigned,
      original_contract_id: p.original_contract_id,
      reassignment_note: p.reassignment_note,
      source_staging_row_id: p.source_staging_row_id,
      payment_group_id: p.payment_group_id,
      refunded_amount: Number(p.refunded_amount ?? 0),
      refunded_amount_rub: Number(p.refunded_amount_rub ?? 0),
      refund_date: p.refund_date ?? null,
      refund_reason_code: p.refund_reason_code ?? null,
      refund_payment_purpose: p.refund_payment_purpose ?? null,
      refund_note: p.refund_note ?? null,
      refund_bank_fx_rate: p.refund_bank_fx_rate == null ? null : Number(p.refund_bank_fx_rate),
      refund_bank_amount_rub: p.refund_bank_amount_rub == null ? null : Number(p.refund_bank_amount_rub),
      refund_recorded_by: p.refund_recorded_by ?? null,
      refund_recorded_at: p.refund_recorded_at ?? null,
      refund_id: p.refund_id == null ? null : Number(p.refund_id),
      refund_status: p.refund_status ?? null,
      refund_case_count: Number(p.refund_case_count ?? 0),
      allocated_rub: Number(p.allocated_rub ?? 0),
      remaining_rub: Number(p.remaining_rub ?? 0),
      remaining_amount: Number(p.remaining_amount ?? Math.max(Number(p.amount ?? 0) - Number(p.refunded_amount ?? 0), 0)),
      allocations_count: Number(p.allocations_count ?? 0),
      reconciliation_status: 'LUMP_SUM',
      candidate_charge_count: 0,
      candidate_debt_rub: 0,
      queue_bucket: 'lump_sum',
      block_reason_code: p.contract_id == null ? 'LUMP_SUM_NEEDS_CONTRACT' : 'LUMP_SUM',
      recommended_action: p.contract_id == null ? 'Выбрать договор на вкладке Пауши у франчайзи' : 'Паушальный взнос',
      target_charge_type: null,
      target_charge_id: null,
      target_debt_rub: 0,
      target_available_rub: 0,
      reserved_by_earlier_payments_rub: 0,
      period_summary: null,
      }
    }) as AllocationQueuePaymentRow[]

    return {
      rows,
      totalCount: input.offset + rows.length + (rows.length === input.limit ? 1 : 0),
      totalPaymentsCount: rows.length,
      hasMore: rows.length === input.limit,
      nextCursor: null,
    }
  }
  const cursorPartnerNumber = input.cursor?.partnerNumber == null
    ? null
    : Number(input.cursor.partnerNumber)
  const cursorIsNoPartner = Boolean(input.cursor?.noPartner)

  const { data: pageData, error: pErr } = await measureRoyaltyAsync(
    'rpc.royalty_allocation_queue_payment_page_cursor_v1',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('royalty_allocation_queue_payment_page_cursor_v1', {
        p_category: input.category,
        p_cursor_partner_number: cursorPartnerNumber,
        p_cursor_is_no_partner: cursorIsNoPartner,
        p_seen_count: input.offset,
        p_limit: input.limit,
        p_import_batch_id: input.importId ?? null,
        p_uploaded_date: input.importId == null ? (input.uploadedDate || null) : null,
        p_uploaded_by: input.importId == null ? (input.uploadedBy || null) : null,
      })
    ),
    (result) => ({
      category: input.category,
      offset: input.offset,
      limit: input.limit,
      cursor: input.cursor ? true : false,
      cursorPartnerNumber,
      cursorIsNoPartner,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (pErr) throw new Error(formatSupabaseLikeError(pErr))

  const pagePayload = typeof pageData === 'string'
    ? JSON.parse(pageData)
    : (pageData ?? {})
  const allPayments = ((pagePayload.rows ?? []) as Array<PartnerPaymentRow & RoyaltyPayment & AllocationQueuePaymentRow>)
  if (allPayments.length === 0) {
    markRoyaltyPerf('load.allocation_category.complete', {
      category: input.category,
      durationMs: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt),
      rows: 0,
      totalCount: Number(pagePayload.totalCount ?? 0),
      totalPaymentsCount: Number(pagePayload.totalPaymentsCount ?? 0),
      hasNextCursor: Boolean(pagePayload.nextCursor),
    })
    return {
      rows: [],
      totalCount: Number(pagePayload.totalCount ?? 0),
      totalPaymentsCount: Number(pagePayload.totalPaymentsCount ?? 0),
      hasMore: Boolean(pagePayload.hasMore),
      nextCursor: pagePayload.nextCursor && typeof pagePayload.nextCursor === 'object'
        ? pagePayload.nextCursor as AllocationQueuePaymentsCursor
        : null,
    }
  }

  const payments = allPayments.filter((payment) => (payment.payment_kind ?? 'ROYALTY') !== 'LUMP_SUM')

  const seriesIds = Array.from(new Set(
    payments
      .map((p) => p.series_id)
      .filter((id): id is number => typeof id === 'number')
  ))
  const partnerNumbers = Array.from(new Set(
    payments
      .map((p) => p.partner_number)
      .filter((id): id is number => typeof id === 'number')
  ))
  const periodKeys = Array.from(new Map(
    payments
      .filter((p) => p.series_id != null && p.assigned_year != null && p.assigned_month != null)
      .map((p) => {
        const key = {
          seriesId: Number(p.series_id),
          year: Number(p.assigned_year),
          month: Number(p.assigned_month),
        }
        return [`${key.seriesId}:${key.year}:${key.month}`, key] as const
      })
  ).values())

  const [
    { data: studioRows, error: sErr },
    { data: partnerRows, error: partnerErr },
    settlements,
  ] = await Promise.all([
    seriesIds.length === 0
      ? { data: [], error: null as any }
      : withAuthRecovery(() =>
          (supabase as any)
            .from('studio_ownerships')
            .select('series_id, studio_internal_id, studios:studio_internal_id(studio_number)')
            .in('series_id', seriesIds)
        ),
    partnerNumbers.length === 0
      ? { data: [], error: null as any }
      : withAuthRecovery(() =>
          (supabase as any)
            .from('royalty_partner_summary')
            .select('partner_number,persons_fio')
            .in('partner_number', partnerNumbers)
        ),
    loadRoyaltyPeriodSettlementsByKeys(periodKeys),
  ])
  if (sErr) throw new Error(formatSupabaseLikeError(sErr))
  if (partnerErr) throw new Error(formatSupabaseLikeError(partnerErr))

  const studioBySeries = new Map<number, { studio_internal_id: number | null; studio_number: string | null }>()
  for (const r of (studioRows ?? []) as any[]) {
    if (!studioBySeries.has(r.series_id)) {
      studioBySeries.set(r.series_id, {
        studio_internal_id: r.studio_internal_id ?? null,
        studio_number: r.studios?.studio_number ?? null,
      })
    }
  }

  const personsByPartner = new Map<number, string | null>()
  for (const r of (partnerRows ?? []) as Array<{ partner_number: number; persons_fio: string | null }>) {
    personsByPartner.set(r.partner_number, r.persons_fio ?? null)
  }

  const settlementByPeriod = new Map<string, RoyaltyPeriodSettlement>()
  for (const settlement of settlements) {
    settlementByPeriod.set(`${settlement.series_id}:${settlement.year}:${settlement.month}`, settlement)
  }

  const result = {
    totalCount: Number(pagePayload.totalCount ?? 0),
    totalPaymentsCount: Number(pagePayload.totalPaymentsCount ?? 0),
    hasMore: typeof pagePayload.hasMore === 'boolean'
      ? pagePayload.hasMore
      : Number(pagePayload.totalCount ?? 0) > input.offset + input.limit,
    nextCursor: pagePayload.nextCursor && typeof pagePayload.nextCursor === 'object'
      ? pagePayload.nextCursor as AllocationQueuePaymentsCursor
      : null,
    rows: payments.map(p => {
      const seriesId = p.series_id ?? null
      const studio = seriesId != null
        ? (studioBySeries.get(seriesId) ?? { studio_internal_id: null, studio_number: null })
        : { studio_internal_id: null, studio_number: null }
      const partnerNumber = Number(p.partner_number ?? 0)
      const periodSettlement = seriesId != null && p.assigned_year != null && p.assigned_month != null
        ? settlementByPeriod.get(`${seriesId}:${p.assigned_year}:${p.assigned_month}`) ?? null
        : null

      return {
        payment_id: p.payment_id,
        contract_id: p.contract_id,
        series_id: seriesId,
        studio_internal_id: studio.studio_internal_id,
        studio_number: studio.studio_number,
        partner_number: partnerNumber,
        persons_fio: p.partner_number == null ? null : (personsByPartner.get(partnerNumber) ?? null),
        entity_id: p.entity_id ?? null,
        assigned_year: p.assigned_year ?? null,
        assigned_month: p.assigned_month ?? null,
        assignment_note: p.assignment_note ?? null,
        payment_date: p.payment_date,
        amount_rub: Number(p.amount_rub ?? 0),
        amount: Number(p.amount ?? 0),
        currency_code: p.currency_code ?? 'RUB',
        fx_rate: p.fx_rate ?? null,
        payment_purpose: p.payment_purpose,
        payer_inn: p.payer_inn,
        beneficiary_inn: p.beneficiary_inn ?? null,
        payer_name: p.payer_name,
        bank_document_number: p.bank_document_number,
        bank_document_date: p.bank_document_date,
        source: p.source,
        created_by: p.created_by ?? null,
        updated_by: p.updated_by ?? null,
        created_at: p.created_at ?? null,
        updated_at: p.updated_at ?? null,
        intended_year: p.intended_year,
        intended_month: p.intended_month,
        allocation_note: p.allocation_note,
        is_deposit: p.is_deposit,
        deposit_note: p.deposit_note,
        payer_inn_external: p.payer_inn_external ?? false,
        payer_inn_note: p.payer_inn_note ?? null,
        payment_kind: p.payment_kind ?? 'ROYALTY',
        is_manually_reassigned: p.is_manually_reassigned,
        original_contract_id: p.original_contract_id,
        reassignment_note: p.reassignment_note,
        source_staging_row_id: p.source_staging_row_id,
        payment_group_id: p.payment_group_id,
        refunded_amount: Number(p.refunded_amount ?? 0),
        refunded_amount_rub: Number(p.refunded_amount_rub ?? 0),
        refund_date: p.refund_date ?? null,
        refund_reason_code: p.refund_reason_code ?? null,
        refund_payment_purpose: p.refund_payment_purpose ?? null,
        refund_note: p.refund_note ?? null,
        refund_bank_fx_rate: p.refund_bank_fx_rate == null ? null : Number(p.refund_bank_fx_rate),
        refund_bank_amount_rub: p.refund_bank_amount_rub == null ? null : Number(p.refund_bank_amount_rub),
        refund_recorded_by: p.refund_recorded_by ?? null,
        refund_recorded_at: p.refund_recorded_at ?? null,
        refund_id: p.refund_id == null ? null : Number(p.refund_id),
        refund_status: p.refund_status ?? null,
        refund_case_count: Number(p.refund_case_count ?? 0),
        allocated_rub: Number(p.allocated_rub ?? 0),
        remaining_rub: Number(p.remaining_rub ?? 0),
        remaining_amount: Number(p.remaining_amount ?? Math.max(
          Number(p.amount ?? 0) - Number(p.refunded_amount ?? 0),
          0
        )),
        allocations_count: Number(p.allocations_count ?? 0),
        reconciliation_status: p.reconciliation_status,
        candidate_charge_count: Number(p.candidate_charge_count ?? 0),
        candidate_debt_rub: Number(p.candidate_debt_rub ?? 0),
        queue_bucket: (p as any).exact_queue_bucket ?? p.queue_bucket ?? 'needs_decision',
        block_reason_code: p.block_reason_code ?? null,
        recommended_action: p.recommended_action ?? null,
        target_charge_type: p.target_charge_type ?? null,
        target_charge_id: p.target_charge_id == null ? null : Number(p.target_charge_id),
        target_debt_rub: Number(p.target_debt_rub ?? 0),
        target_available_rub: Number(p.target_available_rub ?? 0),
        reserved_by_earlier_payments_rub: Number(p.reserved_by_earlier_payments_rub ?? 0),
        period_summary: periodSettlement
          ? {
              charged_rub: periodSettlement.charged_rub,
              paid_rub: periodSettlement.allocated_rub,
              // Не-денежные покрытия (списание, зачёт возврата, зачёт РБ) долг тоже закрывают.
              delta_rub: periodSettlement.charged_rub - periodSettlement.allocated_rub
                - (periodSettlement.written_off_rub ?? 0)
                - (periodSettlement.offset_rub ?? 0)
                - (periodSettlement.rb_tax_offset_rub ?? 0),
              unallocated_assigned_rub: periodSettlement.unallocated_assigned_rub,
            }
          : null,
      }
    }),
  }
  markRoyaltyPerf('load.allocation_category.complete', {
    category: input.category,
    durationMs: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt),
    rows: result.rows.length,
    totalCount: result.totalCount,
    totalPaymentsCount: result.totalPaymentsCount,
  })
  return result
}

function isAllocationQueueRowSelectableForCurrentTarget(row: AllocationQueuePaymentRow): boolean {
  return (
    row.queue_bucket === 'ready' &&
    Boolean(row.updated_at) &&
    (row.currency_code || 'RUB') === 'RUB' &&
    Number(row.allocations_count ?? 0) === 0 &&
    Number(row.remaining_rub ?? 0) > 0.005
  )
}

async function loadNextReadyAllocationItems(input: {
  importId?: number | null
  uploadedDate?: string | null
  uploadedBy?: string | null
  attemptedPaymentIds: ReadonlySet<number>
  maxItems: number
}): Promise<Array<{ paymentId: number; expectedUpdatedAt: string }>> {
  const items: Array<{ paymentId: number; expectedUpdatedAt: string }> = []
  let offset = 0
  let cursor: AllocationQueuePaymentsCursor | null = null

  while (items.length < input.maxItems) {
    const page = await loadAllocationQueuePayments({
      category: 'ready',
      offset,
      limit: input.maxItems,
      cursor,
      importId: input.importId ?? null,
      uploadedDate: input.uploadedDate ?? null,
      uploadedBy: input.uploadedBy ?? null,
    })

    for (const row of page.rows) {
      if (items.length >= input.maxItems) break
      if (input.attemptedPaymentIds.has(row.payment_id)) continue
      if (!isAllocationQueueRowSelectableForCurrentTarget(row) || !row.updated_at) continue
      items.push({
        paymentId: row.payment_id,
        expectedUpdatedAt: row.updated_at,
      })
    }

    if (items.length > 0 || !page.hasMore || page.rows.length === 0) break

    offset += page.rows.length
    cursor = page.nextCursor
  }

  return items
}

function isRoyaltyAllocationTimeout(error: unknown): boolean {
  const text = formatSupabaseLikeError(error).toLowerCase()
  return (
    text.includes('statement timeout') ||
    text.includes('canceling statement due to statement timeout') ||
    text.includes('query timeout') ||
    text.includes('57014') ||
    text.includes('timed out')
  )
}

export interface AllocateReadyBankPaymentsInput {
  importId?: number | null
  uploadedDate?: string | null
  uploadedBy?: string | null
  batchSize?: number
  maxPayments?: number
  onProgress?: (progress: RoyaltyAutoAllocationProgress) => void
}

/**
 * Full exact-target allocation sweep. Unlike the legacy FIFO allocator, this
 * walks only rows currently classified as `ready`, remembers business skips,
 * and keeps advancing until the selected scope has no ready payments left.
 */
export async function allocateReadyBankPayments(
  input: AllocateReadyBankPaymentsInput = {}
): Promise<RoyaltyAutoAllocationResult> {
  const scope = {
    importId: input.importId ?? null,
    uploadedDate: input.importId == null ? (input.uploadedDate ?? null) : null,
    uploadedBy: input.importId == null ? (input.uploadedBy ?? null) : null,
  }
  const initialCounts = await loadAllocationQueueCounts(scope)

  return runRoyaltyAutoAllocation({
    initialReady: initialCounts.payments_ready,
    batchSize: input.batchSize ?? 20,
    maxPayments: input.maxPayments ?? Math.max(initialCounts.payments_ready + 100, 10_000),
    loadItems: (attemptedPaymentIds, maxItems) => loadNextReadyAllocationItems({
      ...scope,
      attemptedPaymentIds,
      maxItems,
    }),
    executeItems: (items) => allocateSelectedPaymentsToCurrentTarget({
      items,
      preview: false,
    }),
    loadRemainingReady: async () => {
      const counts = await loadAllocationQueueCounts(scope)
      return counts.payments_ready
    },
    onProgress: input.onProgress,
    isTimeoutError: isRoyaltyAllocationTimeout,
    errorMessage: formatSupabaseLikeError,
  })
}

export async function allocateImportedBankPayments(
  importId: number,
  onProgress?: (progress: RoyaltyAutoAllocationProgress) => void
): Promise<ImportedBankPaymentsAllocationResult> {
  return allocateReadyBankPayments({ importId, onProgress })
}

export async function setRoyaltyPaymentRefund(params: {
  paymentId: number
  refundDate: string
  amount: number
  reasonCode: RoyaltyPaymentRefundReason
  paymentPurpose?: string | null
  note?: string | null
  bankFxRate?: number | null
  bankAmountRub?: number | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_royalty_payment_refund', {
      p_payment_id: params.paymentId,
      p_refund_date: params.refundDate,
      p_amount: params.amount,
      p_reason_code: params.reasonCode,
      p_payment_purpose: params.paymentPurpose?.trim() || null,
      p_note: params.note?.trim() || null,
      p_bank_fx_rate: params.bankFxRate ?? null,
      p_bank_amount_rub: params.bankAmountRub ?? null,
    })
  )
  if (error) throw error
}

export async function clearRoyaltyPaymentRefund(paymentId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('clear_royalty_payment_refund', {
      p_payment_id: paymentId,
    })
  )
  if (error) throw error
}

export async function loadRoyaltyRefundCases(filters: {
  status?: RoyaltyRefundStatus | 'ALL'
  kind?: RoyaltyRefundKind | 'ALL'
} = {}): Promise<RoyaltyRefundCase[]> {
  const supabase = getSupabase()
  let query = (supabase as any)
    .from('royalty_refund_cases_v')
    .select('*')
    .order('updated_at', { ascending: false })
    .order('refund_id', { ascending: false })
    .limit(300)

  if (filters.status && filters.status !== 'ALL') {
    query = query.eq('status', filters.status)
  }
  if (filters.kind && filters.kind !== 'ALL') {
    query = query.eq('refund_kind', filters.kind)
  }

  const { data, error } = await withAuthRecovery(() => query)
  if (error) throw new Error(formatSupabaseLikeError(error))

  return ((data ?? []) as any[]).map((row) => ({
    refund_id: Number(row.refund_id),
    refund_kind: row.refund_kind,
    status: row.status,
    partner_number: Number(row.partner_number),
    persons_fio: row.persons_fio ?? null,
    recipient_entity_id: row.recipient_entity_id == null ? null : Number(row.recipient_entity_id),
    recipient_name_snapshot: row.recipient_name_snapshot ?? null,
    recipient_inn_snapshot: row.recipient_inn_snapshot ?? null,
    amount: Number(row.amount ?? 0),
    currency_code: row.currency_code ?? 'RUB',
    amount_rub: Number(row.amount_rub ?? 0),
    fx_rate: row.fx_rate == null ? null : Number(row.fx_rate),
    fx_effective_date: row.fx_effective_date ?? null,
    reason: row.reason ?? null,
    note: row.note ?? null,
    payment_date: row.payment_date ?? null,
    payment_purpose: row.payment_purpose ?? null,
    bank_document_number: row.bank_document_number ?? null,
    bank_document_date: row.bank_document_date ?? null,
    bank_amount_rub: row.bank_amount_rub == null ? null : Number(row.bank_amount_rub),
    bank_fx_rate: row.bank_fx_rate == null ? null : Number(row.bank_fx_rate),
    source_payment_id: row.source_payment_id == null ? null : Number(row.source_payment_id),
    source_payment_assigned_series_id: row.source_payment_assigned_series_id == null ? null : Number(row.source_payment_assigned_series_id),
    source_payment_date: row.source_payment_date ?? null,
    source_payment_purpose: row.source_payment_purpose ?? null,
    source_series_id: row.source_series_id == null ? null : Number(row.source_series_id),
    source_studio_internal_id: row.source_studio_internal_id == null ? null : Number(row.source_studio_internal_id),
    source_contract_id: row.source_contract_id == null ? null : Number(row.source_contract_id),
    source_year: row.source_year == null ? null : Number(row.source_year),
    source_month: row.source_month == null ? null : Number(row.source_month),
    source_segment_no: row.source_segment_no == null ? null : Number(row.source_segment_no),
    source_signed_auto_amount_rub: row.source_signed_auto_amount_rub == null ? null : Number(row.source_signed_auto_amount_rub),
    offset_amount: Number(row.offset_amount ?? 0),
    offset_amount_rub: Number(row.offset_amount_rub ?? 0),
    created_at: row.created_at,
    updated_at: row.updated_at,
    approved_at: row.approved_at ?? null,
    paid_at: row.paid_at ?? null,
    voided_at: row.voided_at ?? null,
    created_by_name: row.created_by_name ?? null,
    approved_by_name: row.approved_by_name ?? null,
    paid_by_name: row.paid_by_name ?? null,
    voided_by_name: row.voided_by_name ?? null,
    void_reason: row.void_reason ?? null,
  }))
}

const EMPTY_REFUND_SUMMARY = (): RoyaltyRefundSummary => ({
  draft_rub: 0,
  approved_rub: 0,
  outstanding_rub: 0,
  paid_rub: 0,
  offset_rub: 0,
  total_rub: 0,
  outstanding_count: 0,
  paid_count: 0,
  offset_count: 0,
  total_count: 0,
  by_kind: [],
})

function refundBreakdownFromRow(row: any): RoyaltyRefundSummaryBreakdown {
  return {
    refund_kind: row.refund_kind as RoyaltyRefundKind,
    draft_rub: Number(row.refund_draft_rub ?? 0),
    approved_rub: Number(row.refund_approved_rub ?? 0),
    outstanding_rub: Number(row.refund_outstanding_rub ?? 0),
    paid_rub: Number(row.refund_paid_rub ?? 0),
    offset_rub: Number(row.refund_offset_rub ?? 0),
    total_rub: Number(row.refund_total_rub ?? 0),
    outstanding_count: Number(row.refund_outstanding_count ?? 0),
    paid_count: Number(row.refund_paid_count ?? 0),
    offset_count: Number(row.refund_offset_count ?? 0),
    total_count: Number(row.refund_case_count ?? 0),
  }
}

function addRefundBreakdown(summary: RoyaltyRefundSummary, bucket: RoyaltyRefundSummaryBreakdown) {
  summary.draft_rub += bucket.draft_rub
  summary.approved_rub += bucket.approved_rub
  summary.outstanding_rub += bucket.outstanding_rub
  summary.paid_rub += bucket.paid_rub
  summary.offset_rub += bucket.offset_rub
  summary.total_rub += bucket.total_rub
  summary.outstanding_count += bucket.outstanding_count
  summary.paid_count += bucket.paid_count
  summary.offset_count += bucket.offset_count
  summary.total_count += bucket.total_count
  summary.by_kind.push(bucket)
}

function sortRefundBreakdown(summary: RoyaltyRefundSummary) {
  summary.by_kind = summary.by_kind
    .filter((row) => row.total_count > 0)
    .sort((a, b) => a.refund_kind.localeCompare(b.refund_kind))
}

/**
 * Сводка возвратов по партнёру или по серии.
 *
 * Читает СЕРВЕРНЫЙ агрегат (`royalty_partner_refund_totals_v` / `royalty_series_refund_totals_v`),
 * а не сами кейсы: выборка кейсов постранично упиралась бы в `max-rows = 1000` по мере
 * роста реестра, и повторяла бы на клиенте арифметику, которая обязана быть одна.
 *
 * Возвращает разложение по статусам, а не пару «due/paid»: «к возврату» (DRAFT + APPROVED)
 * и «возвращено» (PAID) — разные вещи, и до Этапа 7 карточки «К возврату» и «Осталось
 * вернуть» показывали одно и то же число.
 */
export async function loadRoyaltyRefundSummary(filters: {
  partnerNumber?: number
  seriesId?: number
} = {}): Promise<RoyaltyRefundSummary> {
  const summary = EMPTY_REFUND_SUMMARY()

  const usesSeries = typeof filters.seriesId === 'number'
  let query = (getSupabase() as any)
    .from(usesSeries ? 'royalty_series_refund_totals_v' : 'royalty_partner_refund_totals_v')
    .select('*')

  if (usesSeries) {
    query = query.eq('series_id', filters.seriesId)
  } else if (typeof filters.partnerNumber === 'number') {
    query = query.eq('partner_number', filters.partnerNumber)
  }

  const { data, error } = await withAuthRecovery(() => query)
  if (error) throw new Error(formatSupabaseLikeError(error))

  for (const row of (data ?? []) as any[]) {
    addRefundBreakdown(summary, refundBreakdownFromRow(row))
  }
  sortRefundBreakdown(summary)
  return summary
}

/**
 * Те же сводки сразу по нескольким сериям — для списка серий на экране партнёра
 * (одним запросом, без N+1 по карточкам).
 */
export async function loadRoyaltyRefundSummariesBySeries(params: {
  seriesIds: number[]
  partnerNumber?: number
}): Promise<Map<number, RoyaltyRefundSummary>> {
  const targetSeriesIds = [...new Set(params.seriesIds.filter((seriesId) => Number.isFinite(seriesId)))]
  const summaries = new Map<number, RoyaltyRefundSummary>()
  for (const seriesId of targetSeriesIds) {
    summaries.set(seriesId, EMPTY_REFUND_SUMMARY())
  }
  if (targetSeriesIds.length === 0) return summaries

  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any)
      .from('royalty_series_refund_totals_v')
      .select('*')
      .in('series_id', targetSeriesIds)
  )
  if (error) throw new Error(formatSupabaseLikeError(error))

  for (const row of (data ?? []) as any[]) {
    const summary = summaries.get(Number(row.series_id))
    if (!summary) continue
    addRefundBreakdown(summary, refundBreakdownFromRow(row))
  }
  for (const summary of summaries.values()) sortRefundBreakdown(summary)

  return summaries
}

/**
 * Возвраты по месяцам НАЧИСЛЕНИЯ одной серии (`royalty_period_refund_totals_v`).
 *
 * Ключ карты — `${year}-${month}`, как в месячной таблице экрана серии. Только
 * `NEGATIVE_ACCRUAL`: возврат переплаты платежа месяца начисления не имеет и в месячную
 * строку попадать не должен — иначе кассовый возврат повиснет на чужом месяце.
 */
export async function loadRoyaltyPeriodRefundTotals(
  seriesId: number
): Promise<Map<string, RoyaltyPeriodRefundTotals>> {
  const totals = new Map<string, RoyaltyPeriodRefundTotals>()
  if (!Number.isFinite(seriesId)) return totals

  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any)
      .from('royalty_period_refund_totals_v')
      .select('*')
      .eq('series_id', seriesId)
  )
  if (error) throw new Error(formatSupabaseLikeError(error))

  for (const row of (data ?? []) as any[]) {
    const year = Number(row.year)
    const month = Number(row.month)
    totals.set(`${year}-${month}`, {
      series_id: Number(row.series_id),
      year,
      month,
      refund_currency: row.refund_currency ?? null,
      draft_rub: Number(row.refund_draft_rub ?? 0),
      approved_rub: Number(row.refund_approved_rub ?? 0),
      paid_rub: Number(row.refund_paid_rub ?? 0),
      outstanding_rub: Number(row.refund_outstanding_rub ?? 0),
      accrual_rub: Number(row.refund_accrual_rub ?? 0),
      accrual_amount: Number(row.refund_accrual_amount ?? 0),
      outstanding_amount: Number(row.refund_outstanding_amount ?? 0),
      offset_rub: Number(row.refund_offset_rub ?? 0),
      offset_amount: Number(row.refund_offset_amount ?? 0),
      case_count: Number(row.refund_case_count ?? 0),
    })
  }

  return totals
}

export async function approveRoyaltyRefund(refundId: number): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    getUntypedRpcClient().rpc('approve_royalty_refund', { p_refund_id: refundId })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

export async function unapproveRoyaltyRefund(refundId: number, reason: string): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    getUntypedRpcClient().rpc('unapprove_royalty_refund', {
      p_refund_id: refundId,
      p_reason: reason.trim() || null,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

export async function markRoyaltyRefundPaid(input: {
  refundId: number
  paymentDate: string
  paymentPurpose: string
  bankDocumentNumber?: string | null
  bankDocumentDate?: string | null
  bankAmountRub?: number | null
  bankFxRate?: number | null
  note?: string | null
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    getUntypedRpcClient().rpc('mark_royalty_refund_paid', {
      p_refund_id: input.refundId,
      p_payment_date: input.paymentDate,
      p_payment_purpose: input.paymentPurpose.trim(),
      p_bank_document_number: input.bankDocumentNumber?.trim() || null,
      p_bank_document_date: input.bankDocumentDate || null,
      p_bank_amount_rub: input.bankAmountRub ?? null,
      p_bank_fx_rate: input.bankFxRate ?? null,
      p_note: input.note?.trim() || null,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

export async function voidRoyaltyRefund(refundId: number, reason: string): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    getUntypedRpcClient().rpc('void_royalty_refund', {
      p_refund_id: refundId,
      p_reason: reason.trim(),
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

/**
 * Снятие ОШИБОЧНОЙ отметки «выплачено» (§6.4 плана возвратов): перевода не было.
 * Это не отмена реальной выплаты — если деньги ушли, исправление делается отдельной
 * компенсирующей банковской операцией, а исходный `PAID` остаётся.
 * Серверный гейт — royalty `admin`, причина обязательна и пишется в аудит
 * событием `PAID_MARK_CORRECTED`.
 */
export async function correctRoyaltyRefundPaidMark(refundId: number, reason: string): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    getUntypedRpcClient().rpc('correct_refund_paid_mark', {
      p_refund_id: refundId,
      p_reason: reason.trim(),
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

/**
 * Начисления партнёра, в счёт которых можно зачесть возврат (§20.4 плана возвратов).
 *
 * Сервер отдаёт ФАКТЫ, а не подобранный вариант: выставленные начисления с остатком долга,
 * FIFO по возрасту. Выбирает человек — та же линия, что в осевых очередях разноски.
 */
export async function loadRefundOffsetCandidates(refundId: number): Promise<RoyaltyRefundOffsetCandidate[]> {
  const { data, error } = await withAuthRecovery(() =>
    getUntypedRpcClient().rpc('list_refund_offset_candidates', { p_refund_id: refundId })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))

  return ((data ?? []) as any[]).map((row) => ({
    charge_id: Number(row.charge_id),
    series_id: Number(row.series_id),
    studio_internal_id: row.studio_internal_id == null ? null : Number(row.studio_internal_id),
    studio_number: row.studio_number == null ? null : Number(row.studio_number),
    studio_name: row.studio_name ?? null,
    year: Number(row.year),
    month: Number(row.month),
    segment_no: row.segment_no == null ? null : Number(row.segment_no),
    charge_type: row.charge_type,
    royalty_currency: row.royalty_currency ?? 'RUB',
    royalty_amount: Number(row.royalty_amount ?? 0),
    remaining_amount: Number(row.remaining_amount ?? 0),
    remaining_amount_rub: Number(row.remaining_amount_rub ?? 0),
    is_same_series: Boolean(row.is_same_series),
  }))
}

/**
 * Зачёт возврата в счёт начисления. Charge не мутируется — заводится запись применения,
 * поэтому «Начислено» остаётся сверяемым с актом, а долг гасится отдельной величиной.
 *
 * `refundAmount` нужен только при разных валютах кейса и начисления (кросс-валютный зачёт);
 * при одной валюте сервер приравнивает его к `chargeAmount`.
 */
export async function settleRoyaltyRefundByOffset(input: {
  refundId: number
  chargeId: number
  chargeAmount: number
  refundAmount?: number | null
  offsetDate?: string | null
  documentNumber?: string | null
  note?: string | null
}): Promise<number> {
  const { data, error } = await withAuthRecovery(() =>
    getUntypedRpcClient().rpc('settle_royalty_refund_by_offset', {
      p_refund_id: input.refundId,
      p_charge_id: input.chargeId,
      p_charge_amount: input.chargeAmount,
      p_refund_amount: input.refundAmount ?? null,
      p_offset_date: input.offsetDate || null,
      p_document_number: input.documentNumber?.trim() || null,
      p_note: input.note?.trim() || null,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
  return Number(data)
}

/**
 * Отзыв зачёта: долг возвращается на начисление, кейс — в `APPROVED`. Обратимо в отличие
 * от `PAID`, потому что денег никто не двигал. Причина обязательна.
 */
export async function revokeRoyaltyRefundOffset(offsetId: number, reason: string): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    getUntypedRpcClient().rpc('revoke_royalty_refund_offset', {
      p_offset_id: offsetId,
      p_reason: reason.trim(),
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
}

/**
 * Непогашенные возвраты по партнёрам — для этапа «Бухгалтер» на `/royalties/process`.
 *
 * Бухгалтер там решает один вопрос: сколько партнёру реально предъявить к оплате. Ответ
 * зависит и от нераспределённых денег партнёра, и от того, сколько УК ему должна. Второе
 * до этого нигде на экране выставления не показывалось, поэтому требование могло уйти на
 * полную сумму при живом встречном обязательстве.
 *
 * Берём серверный агрегат по обоим основаниям: зачесть можно только `NEGATIVE_ACCRUAL`,
 * но знать про переплату платежа бухгалтеру тоже нужно — она гасится разноской.
 */
export async function loadPartnerOutstandingRefunds(
  partnerNumbers: number[]
): Promise<Map<number, { outstandingRub: number; offsettableRub: number; count: number }>> {
  const targets = [...new Set(partnerNumbers.filter((value) => Number.isFinite(value)))]
  const result = new Map<number, { outstandingRub: number; offsettableRub: number; count: number }>()
  if (targets.length === 0) return result

  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any)
      .from('royalty_partner_refund_totals_v')
      .select('partner_number,refund_kind,refund_outstanding_rub,refund_outstanding_count')
      .in('partner_number', targets)
  )
  if (error) throw new Error(formatSupabaseLikeError(error))

  for (const row of (data ?? []) as any[]) {
    const partner = Number(row.partner_number)
    const outstanding = Number(row.refund_outstanding_rub ?? 0)
    if (outstanding <= 0.005) continue
    const bucket = result.get(partner) ?? { outstandingRub: 0, offsettableRub: 0, count: 0 }
    bucket.outstandingRub += outstanding
    bucket.count += Number(row.refund_outstanding_count ?? 0)
    // Зачётом гасится только возврат по отрицательному начислению: переплату платежа
    // разносят остатком самого платежа, второго механизма для неё нет (§20.1).
    if (row.refund_kind === 'NEGATIVE_ACCRUAL') bucket.offsettableRub += outstanding
    result.set(partner, bucket)
  }
  return result
}

/** Применённые зачёты одного кейса (активные сверху, аннулированные — историей). */
export async function loadRoyaltyRefundOffsets(refundId: number): Promise<RoyaltyRefundOffset[]> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase() as any)
      .from('royalty_refund_offsets')
      .select('*')
      .eq('refund_id', refundId)
      .order('offset_id', { ascending: false })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))

  const rows = (data ?? []) as any[]
  // Контекст начисления добираем отдельным запросом, а не embed'ом: у PostgREST
  // разрешение связи зависит от того, как он видит FK, и молчаливая потеря вложенного
  // объекта оставила бы строку без подписи «за какой месяц зачли».
  const chargeIds = [...new Set(rows.map((row) => Number(row.charge_id)).filter(Number.isFinite))]
  const chargeById = new Map<number, any>()
  if (chargeIds.length > 0) {
    const { data: charges, error: chargeError } = await withAuthRecovery(() =>
      (getSupabase() as any)
        .from('royalty_charges')
        .select('charge_id,series_id,year,month,charge_type')
        .in('charge_id', chargeIds)
    )
    if (chargeError) throw new Error(formatSupabaseLikeError(chargeError))
    for (const charge of (charges ?? []) as any[]) chargeById.set(Number(charge.charge_id), charge)
  }

  return rows.map((row) => {
    const charge = chargeById.get(Number(row.charge_id)) ?? {}
    return {
      offset_id: Number(row.offset_id),
      refund_id: Number(row.refund_id),
      charge_id: Number(row.charge_id),
      refund_amount: Number(row.refund_amount ?? 0),
      refund_currency_code: row.refund_currency_code ?? 'RUB',
      refund_amount_rub: Number(row.refund_amount_rub ?? 0),
      charge_amount: Number(row.charge_amount ?? 0),
      charge_currency_code: row.charge_currency_code ?? 'RUB',
      charge_amount_rub: Number(row.charge_amount_rub ?? 0),
      fx_rate: row.fx_rate == null ? null : Number(row.fx_rate),
      offset_date: row.offset_date,
      document_number: row.document_number ?? null,
      note: row.note ?? null,
      created_at: row.created_at,
      voided_at: row.voided_at ?? null,
      void_reason: row.void_reason ?? null,
      series_id: charge.series_id == null ? null : Number(charge.series_id),
      studio_number: null,
      year: charge.year == null ? null : Number(charge.year),
      month: charge.month == null ? null : Number(charge.month),
      charge_type: charge.charge_type ?? null,
    }
  })
}

/**
 * Активные возвраты по отрицательному начислению за месяц (`NEGATIVE_ACCRUAL`).
 * Ключ обязательства — `series_id × segment_no × contract_id`, ровно как у charge:
 * так экран выставления сверяет сохранённый возврат с preview (P-21, план §11.7).
 */
export interface RoyaltyNegativeAccrualRefundRow {
  refund_id: number
  status: RoyaltyRefundStatus
  partner_number: number
  series_id: number
  studio_internal_id: number | null
  contract_id: number | null
  year: number
  month: number
  segment_no: number
  segment_start: string | null
  segment_end: string | null
  signed_auto_amount_rub: number
  amount: number
  currency_code: string
  amount_rub: number
  payment_date: string | null
}

export async function loadNegativeAccrualRefunds(
  year: number,
  month: number
): Promise<RoyaltyNegativeAccrualRefundRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_negative_accrual_refunds_v')
      .select('*')
      .eq('year', year)
      .eq('month', month)
      .order('series_id', { ascending: true })
      .order('segment_no', { ascending: true })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))

  return ((data ?? []) as any[]).map((row) => ({
    refund_id: Number(row.refund_id),
    status: row.status,
    partner_number: Number(row.partner_number),
    series_id: Number(row.series_id),
    studio_internal_id: row.studio_internal_id == null ? null : Number(row.studio_internal_id),
    contract_id: row.contract_id == null ? null : Number(row.contract_id),
    year: Number(row.year),
    month: Number(row.month),
    segment_no: Number(row.segment_no),
    segment_start: row.segment_start ?? null,
    segment_end: row.segment_end ?? null,
    signed_auto_amount_rub: Number(row.signed_auto_amount_rub ?? 0),
    amount: Number(row.amount ?? 0),
    currency_code: row.currency_code ?? 'RUB',
    amount_rub: Number(row.amount_rub ?? 0),
    payment_date: row.payment_date ?? null,
  }))
}

/**
 * Detailed allocations for a payment, with charge metadata for display.
 * Used by the partner payment pool view to show "куда ушёл этот платёж".
 */
const PAYMENT_ALLOCATION_SUMMARY_SELECT = `
  allocation_id, payment_id, charge_id, allocated_amount, allocated_currency_code, allocated_amount_rub, charge_amount, charge_currency_code, cross_partner_note, from_deposit,
  royalty_charges:charge_id (
    year, month, series_id, studio_internal_id, charge_type, segment_no, segment_start, segment_end,
    studios:studio_internal_id (studio_number)
  )
`

function mapPaymentAllocationSummaryRows(rows: unknown[] | null): PartnerPaymentAllocationSummary[] {
  return ((rows ?? []) as any[]).map(r => ({
    allocation_id: r.allocation_id,
    payment_id: r.payment_id,
    charge_id: r.charge_id,
    allocated_amount: Number(r.allocated_amount) || 0,
    allocated_currency_code: r.allocated_currency_code ?? 'RUB',
    allocated_amount_rub: Number(r.allocated_amount_rub) || 0,
    charge_amount: r.charge_amount != null ? Number(r.charge_amount) : null,
    charge_currency_code: r.charge_currency_code ?? null,
    cross_partner_note: r.cross_partner_note ?? null,
    from_deposit: r.from_deposit ?? false,
    charge_year: r.royalty_charges?.year ?? 0,
    charge_month: r.royalty_charges?.month ?? 0,
    charge_series_id: r.royalty_charges?.series_id ?? 0,
    charge_studio_internal_id: r.royalty_charges?.studio_internal_id ?? null,
    charge_studio_number: r.royalty_charges?.studios?.studio_number ?? null,
    charge_type: (r.royalty_charges?.charge_type ?? 'ROYALTY') as RoyaltyChargeType,
    charge_segment_no: r.royalty_charges?.segment_no ?? null,
    charge_segment_start: r.royalty_charges?.segment_start ?? null,
    charge_segment_end: r.royalty_charges?.segment_end ?? null,
  }))
}

export async function loadPaymentAllocationSummaries(paymentId: number): Promise<PartnerPaymentAllocationSummary[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payment_allocations')
      .select(PAYMENT_ALLOCATION_SUMMARY_SELECT)
      .eq('payment_id', paymentId)
  )
  if (error) throw error
  return mapPaymentAllocationSummaryRows(data ?? [])
}

/**
 * Карман начисления, которому принадлежит платёж по назначению и плательщику, —
 * то же правило, по которому его разносит авторазнос (`royalty_payment_target_decision_page`:
 * Билгтон + «роялти» → SPLIT_ROYALTY, Билгтон → SPLIT_AGENCY, «роялти» → MANUAL_ROYALTY и т. д.).
 * Платежи без определимого кармана в результат не попадают.
 */
export async function loadPaymentTargetChargeTypes(
  paymentIds: number[]
): Promise<Map<number, RoyaltyChargeType>> {
  const result = new Map<number, RoyaltyChargeType>()
  const uniqueIds = [...new Set(paymentIds)]
  const rpcClient = getUntypedRpcClient()
  for (let offset = 0; offset < uniqueIds.length; offset += 200) {
    const chunk = uniqueIds.slice(offset, offset + 200)
    const { data, error } = await withAuthRecovery(() =>
      rpcClient.rpc('royalty_payment_target_decision_page', { p_payment_ids: chunk })
    )
    if (error) throw new Error(formatSupabaseLikeError(error))
    for (const row of (data ?? []) as Array<{ payment_id: number; target_charge_type: RoyaltyChargeType | null }>) {
      if (row.target_charge_type) result.set(Number(row.payment_id), row.target_charge_type)
    }
  }
  return result
}

export async function loadPaymentAllocationSummariesByPayments(
  paymentIds: number[]
): Promise<PartnerPaymentAllocationSummary[]> {
  if (paymentIds.length === 0) return []

  const supabase = getSupabase()
  const uniqueIds = [...new Set(paymentIds)]
  const chunks: number[][] = []
  for (let offset = 0; offset < uniqueIds.length; offset += 200) {
    chunks.push(uniqueIds.slice(offset, offset + 200))
  }
  // Пачки идут параллельно (не больше 4 сразу): у крупного франчайзи их больше десятка,
  // и последовательно это секунды чистой сетевой задержки. Порядок строк — порядок пачек.
  const chunkRows: unknown[][] = new Array(chunks.length)
  let next = 0
  async function worker() {
    while (next < chunks.length) {
      const index = next++
      const { data, error } = await fetchAllSupabaseRows<unknown>(() =>
        (supabase as any)
          .from('royalty_payment_allocations')
          .select(PAYMENT_ALLOCATION_SUMMARY_SELECT)
          .in('payment_id', chunks[index])
          .order('allocation_id', { ascending: true })
      )
      if (error) throw error
      chunkRows[index] = data
    }
  }
  await Promise.all(Array.from({ length: Math.min(4, chunks.length) }, () => worker()))
  return mapPaymentAllocationSummaryRows(chunkRows.flat())
}

// Marking a payment as a deposit is a pure status flip — allocations are never
// touched. The deposit balance is the unallocated remainder of the payment.
export async function loadPartnerPaymentsForSeriesPeriod(input: {
  partnerNumber: number
  seriesId: number
  year: number
  month: number
}): Promise<{
  payments: PartnerPaymentRow[]
  allocations: PartnerPaymentAllocationSummary[]
}> {
  const supabase = getSupabase()

  const { data: chargeRows, error: chargesError } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_charges')
      .select('charge_id')
      .eq('series_id', input.seriesId)
      .eq('year', input.year)
      .eq('month', input.month)
  )
  if (chargesError) throw chargesError

  const chargeIds = Array.from(new Set(
    ((chargeRows ?? []) as Array<{ charge_id: number | null }>)
      .map((row) => row.charge_id)
      .filter((id): id is number => typeof id === 'number')
  ))

  const { data: allocationRows, error: allocationsError } = chargeIds.length === 0
    ? { data: [], error: null as any }
    : await withAuthRecovery(() =>
        (supabase as any)
          .from('royalty_payment_allocations')
          .select(PAYMENT_ALLOCATION_SUMMARY_SELECT)
          .in('charge_id', chargeIds)
      )
  if (allocationsError) throw allocationsError

  const allocations = mapPaymentAllocationSummaryRows(allocationRows ?? [])
  const allocatedPaymentIds = Array.from(new Set(
    allocations
      .map((allocation) => allocation.payment_id)
      .filter((id): id is number => typeof id === 'number')
  ))

  const { data: assignedRows, error: assignedError } = await withAuthRecovery(() =>
    (supabase as any)
      .from('royalty_payment_reconciliation_v')
      .select('*')
      .eq('partner_number', input.partnerNumber)
      .eq('series_id', input.seriesId)
      .eq('assigned_year', input.year)
      .eq('assigned_month', input.month)
      .order('payment_date', { ascending: false })
      .order('payment_id', { ascending: false })
  )
  if (assignedError) throw assignedError

  const { data: allocatedPaymentRows, error: allocatedPaymentsError } = allocatedPaymentIds.length === 0
    ? { data: [], error: null as any }
    : await withAuthRecovery(() =>
        (supabase as any)
          .from('royalty_payment_reconciliation_v')
          .select('*')
          .eq('partner_number', input.partnerNumber)
          .in('payment_id', allocatedPaymentIds)
      )
  if (allocatedPaymentsError) throw allocatedPaymentsError

  const paymentRowsById = new Map<number, PartnerPaymentReconciliationRow>()
  for (const row of [
    ...((assignedRows ?? []) as PartnerPaymentReconciliationRow[]),
    ...((allocatedPaymentRows ?? []) as PartnerPaymentReconciliationRow[]),
  ]) {
    if (typeof row.payment_id === 'number' && row.payment_kind !== 'LUMP_SUM') {
      paymentRowsById.set(row.payment_id, row)
    }
  }

  const paymentRows = Array.from(paymentRowsById.values()).sort((a, b) => {
    const dateDiff = String(b.payment_date ?? '').localeCompare(String(a.payment_date ?? ''))
    if (dateDiff !== 0) return dateDiff
    return Number(b.payment_id ?? 0) - Number(a.payment_id ?? 0)
  })

  return {
    payments: await mapPartnerPaymentRows(input.partnerNumber, paymentRows),
    allocations,
  }
}

export async function loadRoyaltyProcessPaymentsBatch(input: {
  year: number
  month: number
  items: Array<{
    rowKey: string
    partnerNumber: number
    seriesId: number
  }>
}): Promise<Record<string, {
  payments: PartnerPaymentRow[]
  allocations: PartnerPaymentAllocationSummary[]
}>> {
  if (input.items.length === 0) return {}

  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_royalty_process_payments_batch', {
      p_year: input.year,
      p_month: input.month,
      p_items: input.items.map((item) => ({
        row_key: item.rowKey,
        partner_number: item.partnerNumber,
        series_id: item.seriesId,
      })),
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))

  const payload = (data ?? {}) as {
    payments?: Array<PartnerPaymentReconciliationRow & { _process_row_key?: string | null }>
    allocations?: Array<Record<string, unknown> & { _process_row_key?: string | null }>
  }
  const itemByKey = new Map(input.items.map((item) => [item.rowKey, item]))
  const paymentRowsByKey = new Map<string, PartnerPaymentReconciliationRow[]>()
  const allocationRowsByKey = new Map<string, Array<Record<string, unknown>>>()

  for (const row of payload.payments ?? []) {
    const key = row._process_row_key ?? ''
    if (!itemByKey.has(key)) continue
    const rows = paymentRowsByKey.get(key) ?? []
    rows.push(row)
    paymentRowsByKey.set(key, rows)
  }

  for (const row of payload.allocations ?? []) {
    const key = row._process_row_key ?? ''
    if (!itemByKey.has(key)) continue
    const rows = allocationRowsByKey.get(key) ?? []
    rows.push(row)
    allocationRowsByKey.set(key, rows)
  }

  const result: Record<string, {
    payments: PartnerPaymentRow[]
    allocations: PartnerPaymentAllocationSummary[]
  }> = {}

  for (const item of input.items) {
    result[item.rowKey] = {
      payments: await mapPartnerPaymentRows(item.partnerNumber, paymentRowsByKey.get(item.rowKey) ?? []),
      allocations: mapPaymentAllocationSummaryRows(allocationRowsByKey.get(item.rowKey) ?? []),
    }
  }

  return result
}

export async function markPaymentAsDeposit(
  paymentId: number,
  isDeposit: boolean,
  note?: string | null,
): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('mark_payment_as_deposit', {
      p_payment_id:    paymentId,
      p_is_deposit:    isDeposit,
      p_note:          note ?? null,
      p_actor_user_id: actorUserId,
    })
  )
  if (error) throw error
}

export async function allocateDepositToCharge(
  paymentId: number,
  chargeId: number,
  amountRub: number,
  note?: string | null,
): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('allocate_deposit_to_charge', {
      p_payment_id:    paymentId,
      p_charge_id:     chargeId,
      p_amount_rub:    amountRub,
      p_note:          note ?? null,
      p_actor_user_id: actorUserId,
    })
  )
  if (error) throw error
}

// ============================================================
// ROYALTY DEBT WRITE-OFFS ("списание долга")
// ============================================================

export async function writeOffRoyaltyDebtForSeries(input: {
  seriesId: number
  amountRub: number
  approvedBy?: string | null
  writtenOffAt?: string | null
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('write_off_royalty_debt_for_series', {
      p_series_id:            input.seriesId,
      p_amount_rub:           input.amountRub,
      p_approved_by:          input.approvedBy ?? null,
      p_written_off_at:       input.writtenOffAt ?? null,
      p_note:                 input.note ?? null,
    })
  )
  if (error) throw error
  return Number(data)
}

export async function writeOffRoyaltyDebtForSeriesMonth(input: {
  seriesId: number
  year: number
  month: number
  amountRub: number
  approvedBy?: string | null
  writtenOffAt?: string | null
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('write_off_royalty_debt_for_series_month', {
      p_series_id:      input.seriesId,
      p_year:           input.year,
      p_month:          input.month,
      p_amount_rub:     input.amountRub,
      p_approved_by:    input.approvedBy ?? null,
      p_written_off_at: input.writtenOffAt ?? null,
      p_note:           input.note ?? null,
    })
  )
  if (error) throw error
  return Number(data)
}

export async function writeOffRoyaltyDebtForPartner(input: {
  partnerNumber: number
  amountRub: number
  approvedBy?: string | null
  writtenOffAt?: string | null
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('write_off_royalty_debt_for_partner', {
      p_partner_number:       input.partnerNumber,
      p_amount_rub:           input.amountRub,
      p_approved_by:          input.approvedBy ?? null,
      p_written_off_at:       input.writtenOffAt ?? null,
      p_note:                 input.note ?? null,
    })
  )
  if (error) throw error
  return Number(data)
}

export async function loadRoyaltyDebtWriteoffsBySeriesIds(
  seriesIds: number[]
): Promise<RoyaltyDebtWriteoff[]> {
  if (seriesIds.length === 0) return []
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_debt_writeoffs')
      .select('writeoff_id,series_id,amount_rub,approved_by_user_id,approved_by,approved_at,written_off_by_user_id,written_off_at,note')
      .in('series_id', seriesIds)
      .order('written_off_at', { ascending: false })
  )
  // Local dev convenience: allow UI to work before migrations are applied.
  // Postgres error 42P01 = undefined_table.
  if (error) {
    if ((error as any)?.code === '42P01') return []
    throw error
  }
  return (data ?? []) as RoyaltyDebtWriteoff[]
}

export async function loadRoyaltyDebtWriteoffsByMonth(seriesIds: number[]): Promise<Array<{
  series_id: number
  year: number
  month: number
  written_off_rub: number
}>> {
  if (seriesIds.length === 0) return []
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('royalty_debt_writeoffs_by_month_v')
      .select('series_id,year,month,written_off_rub')
      .in('series_id', seriesIds)
  )
  if (error) {
    if ((error as any)?.code === '42P01') return []
    throw error
  }
  return (data ?? []) as any
}

// ============================================================
// PAYMENT REASSIGNMENT
// ============================================================

export async function reassignPaymentSeries(
  paymentId: number,
  targetContractId: number,
  note: string,
  beneficiaryInn?: string | null,
): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const payloadWithBeneficiary = {
    p_payment_id:          paymentId,
    p_target_contract_id:  targetContractId,
    p_note:                note,
    p_beneficiary_inn:     beneficiaryInn ?? null,
    p_actor_user_id:       actorUserId,
  }
  const payloadLegacy = {
    p_payment_id:          paymentId,
    p_target_contract_id:  targetContractId,
    p_note:                note,
    p_actor_user_id:       actorUserId,
  }
  let { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('reassign_payment_series', payloadWithBeneficiary)
  )

  const errorText = formatSupabaseLikeError(error)
  const looksLikeSignatureMismatch =
    errorText.includes('Could not find the function public.reassign_payment_series') ||
    errorText.includes('function public.reassign_payment_series') ||
    errorText.includes('schema cache')

  if (error && looksLikeSignatureMismatch) {
    ;({ error } = await withAuthRecovery(() =>
      (supabase as any).rpc('reassign_payment_series', payloadLegacy)
    ))
  }

  if (error) throw error

  // Re-run allocation immediately so a newly re-bound payment moves out of the
  // waiting queue in the same action when matching charges exist.
  await reallocatePayment(paymentId)
}

// ============================================================
// ATTACH PAYMENT TO PARTNER — single entry point for partner attribution.
// See docs/ROYALTY_ALLOCATION_REWORK_PLAN.md.
//
// Two modes:
//   * addInnToPartner = true  → INN added to partner_legal_entities, payer_inn_external = false.
//   * addInnToPartner = false → payer_inn_external = true, optional payer_inn_note saved.
//
// In both cases contract_id and entity_id are cleared and any existing
// allocations are dropped (partner change invalidates them).
// ============================================================

export async function attachPaymentToPartner(input: {
  paymentId:          number
  partnerNumber:      number
  addInnToPartner:    boolean
  beneficiaryInn?:    string | null
  externalInnNote?:   string | null
}): Promise<void> {
  const supabase = getSupabase()
  const actorUserId = getActorUserId()
  const payloadWithBeneficiary = {
    p_payment_id:          input.paymentId,
    p_partner_number:      input.partnerNumber,
    p_add_inn_to_partner:  input.addInnToPartner,
    p_beneficiary_inn:     input.beneficiaryInn ?? null,
    p_external_inn_note:   input.externalInnNote ?? null,
    p_actor_user_id:       actorUserId,
  }
  const payloadLegacy = {
    p_payment_id:          input.paymentId,
    p_partner_number:      input.partnerNumber,
    p_add_inn_to_partner:  input.addInnToPartner,
    p_external_inn_note:   input.externalInnNote ?? null,
    p_actor_user_id:       actorUserId,
  }

  let { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('attach_payment_to_partner', payloadWithBeneficiary)
  )

  const errorText = formatSupabaseLikeError(error)
  const looksLikeSignatureMismatch =
    errorText.includes('Could not find the function public.attach_payment_to_partner') ||
    errorText.includes('function public.attach_payment_to_partner') ||
    errorText.includes('schema cache')

  if (error && looksLikeSignatureMismatch) {
    ;({ error } = await withAuthRecovery(() =>
      (supabase as any).rpc('attach_payment_to_partner', payloadLegacy)
    ))
  }

  if (error) throw new Error(formatSupabaseLikeError(error))
}

// ============================================================
// ALLOCATION QUEUE — counters and paginated partner list.
// Replaces in-memory aggregation over all staging rows / payments.
// ============================================================

export interface RoyaltyAllocationQueueCounts {
  to_commit:          number
  needs_review:       number
  processed:          number
  skipped:            number
  skipped_review:     number
  skipped_trash:      number
  payments_unmatched: number
  payments_ready_to_allocate: number
  payments_assigned_to_period: number
  payments_need_assignment: number
  payments_partially_allocated: number
  payments_all_nonzero: number
  payments_work: number
  payments_ready: number
  payments_needs_decision: number
  payments_waiting_charge: number
  payments_needs_assignment: number
  payments_lump_sum: number
  payments_partial: number
  payments_within_tolerance: number
}

export async function loadAllocationQueueCounts(input?: {
  importId?: number | null
  uploadedDate?: string | null
  uploadedBy?: string | null
}): Promise<RoyaltyAllocationQueueCounts> {
  const supabase = getSupabase()
  const { data, error } = await measureRoyaltyAsync(
    'rpc.royalty_allocation_queue_counts_rpc',
    () => withAuthRecovery(() =>
      (supabase as any).rpc('royalty_allocation_queue_counts_rpc', {
        p_import_batch_id: input?.importId ?? null,
        p_uploaded_date: input?.importId == null ? (input?.uploadedDate || null) : null,
        p_uploaded_by: input?.importId == null ? (input?.uploadedBy || null) : null,
      })
    ),
    (result) => ({
      importFiltered: input?.importId != null,
      dateFiltered: input?.uploadedDate != null,
      userFiltered: input?.uploadedBy != null,
      error: Boolean((result as any).error),
      ...summarizeRoyaltyPayload((result as any).data),
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
  const counts = typeof data === 'string' ? JSON.parse(data) : (data ?? {})
  let lumpSumCount = Number(counts?.payments_lump_sum ?? 0)

  if (counts?.payments_lump_sum == null) {
    if (input?.importId != null) {
      const { count, error: lumpErr } = await withAuthRecovery(() =>
        (supabase as any)
          .from('royalty_payments')
          .select('payment_id', { count: 'exact', head: true })
          .eq('payment_kind', 'LUMP_SUM')
          .eq('import_batch_id', input.importId)
      ) as { count?: number | null; error?: unknown }
      if (lumpErr) throw new Error(formatSupabaseLikeError(lumpErr))
      lumpSumCount = Number(count ?? 0)
    } else {
      let lumpQuery = (supabase as any)
        .from('royalty_payment_reconciliation_v')
        .select('payment_id', { count: 'exact', head: true })
        .eq('payment_kind', 'LUMP_SUM')

      if (input?.uploadedDate) {
        const start = `${input.uploadedDate}T00:00:00`
        const endDate = new Date(`${input.uploadedDate}T00:00:00`)
        endDate.setDate(endDate.getDate() + 1)
        lumpQuery = lumpQuery.gte('created_at', start).lt('created_at', endDate.toISOString())
      }
      if (input?.uploadedBy) lumpQuery = lumpQuery.eq('created_by', input.uploadedBy)

      const { count, error: lumpErr } = await withAuthRecovery(() => lumpQuery) as { count?: number | null; error?: unknown }
      if (lumpErr) throw new Error(formatSupabaseLikeError(lumpErr))
      lumpSumCount = Number(count ?? 0)
    }
  }

  const rawPaymentsWork = Number(counts?.payments_work ?? 0)
  const rawNeedsAssignment = Number(counts?.payments_needs_assignment ?? counts?.payments_need_assignment ?? 0)

  return {
    to_commit:          Number(counts?.to_commit          ?? 0),
    needs_review:       Number(counts?.needs_review       ?? 0),
    processed:          Number(counts?.processed          ?? 0),
    skipped:            Number(counts?.skipped            ?? 0),
    skipped_review:     Number(counts?.skipped_review     ?? counts?.skipped ?? 0),
    skipped_trash:      Number(counts?.skipped_trash      ?? 0),
    payments_unmatched: Number(counts?.payments_unmatched ?? 0),
    payments_ready_to_allocate: Number(counts?.payments_ready_to_allocate ?? 0),
    payments_assigned_to_period: Number(counts?.payments_assigned_to_period ?? 0),
    payments_need_assignment: Number(counts?.payments_need_assignment ?? 0),
    payments_partially_allocated: Number(counts?.payments_partially_allocated ?? 0),
    payments_all_nonzero: Number(counts?.payments_all_nonzero ?? counts?.payments_unmatched ?? 0),
    payments_work: Math.max(0, rawPaymentsWork - (counts?.payments_lump_sum == null ? lumpSumCount : 0)),
    payments_ready: Number(counts?.payments_ready ?? counts?.payments_ready_to_allocate ?? 0),
    payments_needs_decision: Number(counts?.payments_needs_decision ?? 0),
    payments_waiting_charge: Number(counts?.payments_waiting_charge ?? counts?.payments_assigned_to_period ?? 0),
    payments_needs_assignment: Math.max(0, rawNeedsAssignment - (counts?.payments_lump_sum == null ? lumpSumCount : 0)),
    payments_lump_sum: lumpSumCount,
    payments_partial: Number(counts?.payments_partial ?? counts?.payments_partially_allocated ?? 0),
    payments_within_tolerance: Number(counts?.payments_within_tolerance ?? 0),
  }
}

export interface AllocationQueuePartner {
  partner_number:              number | null
  persons_fio:                 string | null
  unresolved_rows_count:       number
  unallocated_payments_count:  number
  total_remaining_rub:         number
}

export async function loadAllocationQueuePartners(input: {
  offset: number
  limit:  number
}): Promise<AllocationQueuePartner[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('royalty_allocation_queue_partners', {
      p_offset: input.offset,
      p_limit:  input.limit,
    })
  )
  if (error) throw new Error(formatSupabaseLikeError(error))
  return ((data ?? []) as any[]).map(r => ({
    partner_number:              r.partner_number == null ? null : Number(r.partner_number),
    persons_fio:                 r.persons_fio ?? null,
    unresolved_rows_count:       Number(r.unresolved_rows_count       ?? 0),
    unallocated_payments_count:  Number(r.unallocated_payments_count  ?? 0),
    total_remaining_rub:         Number(r.total_remaining_rub         ?? 0),
  }))
}
